import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import type { User } from '@supabase/supabase-js';
import { RoughCanvas } from 'roughjs/bin/canvas';
import { MinusSquare, PlusSquare, ArrowLeft, ArrowRight, ArrowUp, Download, Home, Maximize, Minimize, Settings, XCircle, Maximize2 as CornerAdjustIcon, Scan, RectangleVertical, DoorOpen, Wind, Flame, Lightbulb, AlertTriangle, Gauge, Trash2, Zap, RotateCcw, Database, Grid3X3, Thermometer, Fan, Square, Battery, Target, ChevronDown, ChevronUp, ClipboardList, Compass, FileText, FileCode2, Volume2, Users, Bell, Snowflake, Coffee, Minus, Bath, Droplets, CircleDot } from 'lucide-react';
import type { 
  Wall, Point, WallType, BayWindow, TextElement, Compass as CompassType,
  ElementType, FloorPlanElement, BayWindowStyle,
  RoomCorner, ResizingRoomInfo, Room, TextBox, CornerDragPreview, ARRoomScan, RadiatorType, HeatingType, BoilerElement, LightElement, LightType, SmokeAlarmElement, UtilityElement, UtilityType, LoftHatchElement, DoorElement, WindowElement, HighlightArea, CounterElement, StairsElement
} from '../types';
import type { ARMultiRoomScanResult } from '../../ar-noor-room/src/definitions';
import {
  RadiatorElement
} from '../utils/radiatorPlacement';
import Toolbar from './Toolbar';
import ARRoomScanner from './ARRoomScanner';
import NativeARRoomScanner from './NativeARRoomScannerWrapper';
import AROrientationPreview from './AROrientationPreview';
import ARWallAnglePrompt from './ARWallAnglePrompt';
import ARScanLimitModal from './ARScanLimitModal';
import { useARScanning } from '../hooks/useARScanning'; // Add AR scanning hook
import { useUsageTracking } from '../hooks/useUsageTracking';
import { useSubscription } from '../hooks/useSubscription';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import {
  PerformanceMonitor,
  RenderScheduler,
  createThrottledDraw,
  getVisibleWalls,
  shouldRenderDetail,
  memoizedCalculateRooms,
  createSmartDebounce,
  invalidateRoomCalculationCache,
  detectNewRoomsAndInvalidateCache
} from '../utils/performanceOptimizations';
import {
  GRID_SIZE, GRID_COLOR, WALL_THICKNESS, POINT_SNAP_THRESHOLD, SNAP_THRESHOLD, PIXELS_PER_METER,
  ROOM_LABEL_FONT, MEASUREMENT_FONT, CANVAS_SIZE, SCALE_FACTOR,
  MAX_SCALE, MIN_SCALE, INITIAL_SCALE, getZoomResponsiveTextBoxFont, RADIATOR_WALL_OFFSET, BOILER_WALL_OFFSET,
  CM_SNAP_SIZE
} from '../constants/floorPlanConstants';
import { 
  calculateArea, getRoomCenter, getRoomBounds, isNearPoint, distanceToLine,
  doLinesIntersect, isPointInsideRoom, findAlignedCorners, 
  findWallEndpoint, doesWallIntersect, getResizeHandle as getResizeHandleUtil, // Renamed local getResizeHandle if it exists
  findRoomAtPoint,
  isPointOnWall, shouldSnapToWall, shouldSnapToEndpoint, calculateWallLength,
  pointToLineDistance,
  calculateRooms, getPersistentRoomId, RoomCalculationContext, migrateRoomLabels, migrateOldRoomLabelsToFloorFormat, // Added migrateRoomLabels and migrateOldRoomLabelsToFloorFormat
  snapPointToGrid, snapToFineGridOrEndpoints,
  calculateOverallWallLengthsAndArea,
  getElementBounds, 
  snapToEndpointsOrGrid as snapToEndpointsOrGridImported, // Import with a distinct name
  snapRoomPointToNearbyStructures, snapRoomForAdjacentCreation, // Add new enhanced snapping functions
  pointsAreEqual // Add pointsAreEqual for extrusion fix
} from '../utils/floorPlanUtils';
import {
  drawExteriorMeasurements, getOutsideDirection, isWallExternal, renderCanvasView as renderCanvasUtil
} from '../utils/canvasRenderer';
import TextBoxEditor from './TextBoxEditor';
import { useFullscreen } from '../hooks/useFullscreen';
import { useTouchGestures } from '../hooks/useTouchGestures';
import type { CurrentPage } from '../App';
import RoomLabelPrompt from './RoomLabelPrompt';
import RoomHeightPrompt from './RoomHeightPrompt';
import RoomRenameModal from './RoomRenameModal';
import RoomContextMenu from './RoomContextMenu';
import RoomFloatingActions from './RoomFloatingActions';
import RoomMergeNotification from './RoomMergeNotification';
import FloorPlanDiagnosticsOnly from './FloorPlanDiagnosticsOnly';
import { loadFloorPlanFromFile, combineAddressFields, parseAddressFromString } from '../utils/fileUtils';
import { generateNextWindowId, calculateNextWindowCounter, assignMissingWindowIds } from '../utils/windowIdUtils';
import { saveFloorPlanToSupabase, loadFloorPlanFromSupabase } from '../utils/supabaseStorage';
import { calculateResizedWalls } from '../utils/floorPlanGeometry';
import { getUniqueCornerPoints } from '../utils/floorPlanGeometry'; // Added getUniqueCornerPoints
import { 
  drawGrid, drawRoomCornerPoints, drawEraserPreview, drawBayWindow, 
  drawText, drawCompass,
  drawRoomPreview as drawRoomPreviewUtil, // Renamed to avoid conflict if a local one existed
  drawConnectionPoints as drawConnectionPointsUtil,
  drawGuideLines as drawGuideLinesUtil,
  drawTouchFeedback as drawTouchFeedbackUtil,
  drawResizeHandles as drawResizeHandlesUtil
} from '../utils/drawingHelpers';
// Estate agency floor plan uses estate-specific export service
import { exportToPDFOffline } from '../services/offlneestateexport';
import ExportModal from './ExportModal';
import ExportLoadingModal from './ExportLoadingModal';
import { splitWallAndProcessState, WallSplitResult } from '../utils/wallUtils'; // New import
import { isMobileDevice, isIOSDevice, getCursorStyle as getCursorStyleUtil } from '../utils/uiUtils'; // Added getCursorStyleUtil
import { orientation, getWallResizeDirection } from '../utils/floorPlanUtils'; // Import new functions from floorPlanUtils
import { enforceWallRoomMembership } from '../utils/roomMembershipEnforcement'; // Import wall-room membership enforcement
import { constrainViewOffset as constrainViewOffsetUtil } from '../utils/viewUtils'; // Import from new viewUtils
import { renderCanvasView } from '../utils/canvasRenderer'; // New import
import { useFloorPlanInteractions } from '../hooks/useFloorPlanInteractions'; // New import
import { 
  findWallForWindow, 
  createWindowElement, 
  createRoofWindowElement,
  createSolidBoxWindowElement,
  drawRoofWindowPreview,
  drawSolidBoxWindow 
} from '../utils/windowPlacement';
import { createCantedWindowElement, drawCantedWindow } from '../utils/cantedWindowPlacement';
import { createRadiatorElement, drawRadiators, drawRadiatorPreview, findWallForRadiator, findRoomContainingRadiatorText } from '../utils/radiatorPlacement';
import { createBoilerElement, drawBoilers, drawBoilerPreview, findWallForBoiler } from '../utils/boilerPlacement';
import { createDoorElement, findWallForDoor } from '../utils/doorPlacement';
import { detectEnclosedGaps, findGapForResize, type DetectedGap, type GapFillSuggestion } from '../utils/gapDetection';
import GapFillPrompt from './GapFillPrompt';
import { createLightElement, drawLights, drawLightPreview } from '../utils/lightPlacement';
import { createSmokeAlarmElement, drawSmokeAlarms, drawSmokeAlarmPreview } from '../utils/smokeAlarmPlacement';
import { createFireSafetyElement, drawFireSafetyElements, drawFireSafetyPreview, type FireSafetyType, type FireSafetyElement } from '../utils/fireSafetyPlacement';
import { createUtilityElement, drawUtilities, drawUtilityPreview, findWallForSink, createSinkElementOnWall, createBathroomFixtureOnWall, isPointInsideGarden, getGardenResizeHandle, resizeGarden, isPointOnGardenLabel } from '../utils/utilityPlacement';
import { createLoftHatch } from '../utils/loftHatchPlacement';
import { createCompass, isPointOnCompassRotationControl, isPointInsideCompass } from '../utils/compassPlacement';
import { snapGardenToWalls, calculatePolygonArea, calculatePolygonCenter, isPointInsidePolygon } from '../utils/gardenSnapping';
import { createCounterElement, createCounterWithAutoSnap, drawCounters, drawCounterPreview, findWallForCounter, isPointInsideCounter, getCounterResizeHandle, resizeCounter, isPointOnRotationControl, isPointOnMoveControl, dragCounterAlongWall } from '../utils/counterPlacement';
import { createStairsElement, createStairsWithAutoSnap, drawStairs, drawStairsPreview, findWallForStairs, isPointInsideStairs, getStairsResizeHandle, resizeStairs, isPointOnStairsRotationControl, isPointOnStairsMoveControl, dragStairsAlongWall } from '../utils/stairsPlacement';
import { 
  createHighlightArea, 
  createHighlightPreview, 
  createHighlightAreaWithWallSnapping,
  createHighlightPreviewWithWallSnapping,
  snapHighlightAreaToWalls,
  drawHighlightAreas, 
  drawHighlightPreview, 
  drawHighlightPreviewWithSnapIndicators,
  findHighlightAtPoint,
  HIGHLIGHT_COLORS,
  type HighlightAreaSnap
} from '../utils/highlightingTool';
import { calculateHighlightMetrics } from '../utils/highlightCalculations';
import HighlightCalculationsPopup from './HighlightCalculationsPopup';
import EstateAreaPanel from './EstateAreaPanel';
import WindowDimensionPrompt from './WindowDimensionPrompt';
import WindowTypePrompt, { WindowType } from './WindowTypePrompt';
import RadiatorDimensionPrompt from './RadiatorDimensionPrompt';
import BoilerInfoPrompt from './BoilerInfoPrompt';
import DoorUndercutPrompt from './DoorUndercutPrompt';
import { 
  hasFrontDoor, 
  findFrontDoor, 
  autoUpdatePropertyOrientation,
  autoCalculateWindowOrientations,
  autoCalculateWindowOrientationsAllFloors,
  calculateWindowOrientation,
  calculateWallAngle
} from '../utils/orientationUtils';
import { useAutosave } from '../hooks/useAutosave';
import type { SiteNotesData } from '../utils/fileUtils';

import AutosaveIndicator from './AutosaveIndicator';
import OnboardingModal from './OnboardingModal';
import AddressInputModal from './AddressInputModal';
import { useWallSegmentation } from '../hooks/useWallSegmentation';
import OrientationModal from './OrientationModal';
import CompassIndicator from './CompassIndicator';
import SurveyorSiteNotes from './SurveyorSiteNotes';
import RoomSyncIndicator from './RoomSyncIndicator';
import { createRoomSyncHook, syncAllRoomsToSurveyorDataWithConsolidation, findElementRoomWithConsolidation, comprehensiveSyncWithRedetection, clearFloorLightCounts, updateRoomLightCount, smartSyncLightsToRooms, smartSyncWindowsToRoom, syncDoorUndercutsToSiteNotes, smartSyncRadiatorsToRoom, syncBoilerLocationsToSiteNotes, syncUtilityLocationsToSiteNotes, syncAllFloorsWindowsToSiteNotesWithFloorPlans, syncAllFloorsDoorUndercutsToSiteNotesWithFloorPlans, syncAllFloorsLightsToAdditionalInfo, syncAllFloorsRadiatorsToHeatingSystem, cleanupDuplicateRooms, findBestRoomForDoor } from '../services/roomSyncService';
import type { WallWithTempId } from '../types';
import FileRepairDialog from './FileRepairDialog';
import ElementInfoModal from './ElementInfoModal';
import { debounce } from '../utils/helpers'; // Add debounce import for room label optimization

// 🆕 ADD: Professional wall snapping system imports
// SIMPLIFIED: Replace all complex snapping with one simple system
import { snapToWalls, SimpleWallSnapping, type SimpleSnapResult } from '../utils/simpleWallSnapping';
import { RoomConnectionAssistant } from '../utils/roomDragWithWallSnapping';

// 🆕 ADD: Partial room dragging system
import { 
  analyzeRoomConnectivity, 
  selectRoomPartForDragging, 
  dragRoomPartially, 
  connectPartialRoomToTarget,
  type WallGroup,
  type PartialRoomDragResult 
} from '../utils/partialRoomDrag';

// 🔗 ADD: Shared wall system imports
import { analyzeSharedWallSystem } from '../utils/sharedWallSystem';

// Define the props for RetrofitFloorPlan
export interface RetrofitFloorPlanProps {
  onNavigate: (page: CurrentPage, associatedId?: string) => void;
  floorPlanId?: string | null;
  currentUser?: User | null;
}

type ActivelyResizingWallInfo = {
  wallId: string;
  originalStart: Point;
  originalEnd: Point;
  resizeDirection: string;
};
const CSS_FULLSCREEN_TOOLBAR_RESERVED_HEIGHT = '70px'; // Actual height of the fullscreen toolbar

const POINT_MATCH_TOLERANCE = 0.1; // For precise endpoint matching

// Define the structure for undo/redo state
type UndoRedoState = {
  floorPlans: Record<number, Wall[]>;
  elements: FloorPlanElement[];
  textBoxes: TextBox[];
  roomCustomLabels: Record<string, string>;
  propertyAddress: string;
  windowCount: { single: number, double: number };
  lightCount: { nonLed: number, led: number };
  currentFloor: number;
};

const RetrofitFloorPlan: React.FC<RetrofitFloorPlanProps> = ({ onNavigate, floorPlanId, currentUser }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null); // Ref for the element to make fullscreen
  const layoutRef = useRef<HTMLDivElement>(null); // Ref for the main layout div for fullscreen target
  const [floorPlans, setFloorPlans] = useState<Record<number, Wall[]>>({ 1: [] });
  const [currentFloor, setCurrentFloor] = useState<number>(1);
  const [scale, setScale] = useState(INITIAL_SCALE);
  const [undoStack, setUndoStack] = useState<UndoRedoState[]>([]);
  const [redoStack, setRedoStack] = useState<UndoRedoState[]>([]);
  const [selectedWall, setSelectedWall] = useState<WallWithTempId | null>(null);
  const selectedWallRef = useRef<WallWithTempId | null>(null);
  const [currentTool, setCurrentTool] = useState<ElementType>('wall' as ElementType);
  const currentToolInternalRef = useRef<ElementType>(currentTool); // Ref for synchronous access
  const [elements, setElements] = useState<FloorPlanElement[]>([]);
  const [showTextInput, setShowTextInput] = useState(false);

  const [textConfig, setTextConfig] = useState({
    content: '',
    fontSize: 12,
    rotation: 0
  });

  const [currentPoint, setCurrentPoint] = useState<Point | null>(null);

  const [selectedElement, setSelectedElement] = useState<FloorPlanElement | null>(null);

  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Point | null>(null);
  const [viewOffset, setViewOffset] = useState<Point>({ x: 0, y: 0 }); // Initial view offset

  const [isDraggingRoom, setIsDraggingRoom] = useState(false);
  const [roomStart, setRoomStart] = useState<Point | null>(null);
  const [roomEnd, setRoomEnd] = useState<Point | null>(null);

  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);

  const [tempWalls, setTempWalls] = useState<WallWithTempId[] | null>(null); // Correctly typed tempWalls

  const [hoveredHandle, setHoveredHandle] = useState<string | null>(null);

  const [propertyAddress, setPropertyAddress] = useState('');

  const [windowCount, setWindowCount] = useState({ single: 0, double: 0 });
  const [lightCount, setLightCount] = useState({ nonLed: 0, led: 0 });

  const [hasTouchScreen, setHasTouchScreen] = useState(false);

  const [lastTap, setLastTap] = useState<number>(0);
  const [touchStartPos, setTouchStartPos] = useState<Point | null>(null);
  const [isLongPress, setIsLongPress] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [touchMoved, setTouchMoved] = useState(false);

  // SIMPLIFIED: Simple wall snapping state
  const [currentSnapResult, setCurrentSnapResult] = useState<SimpleSnapResult | null>(null);
  
  // Room connection assistant for auto-connecting rooms
  const roomConnectionAssistant = useMemo(() => new RoomConnectionAssistant(), []);

  // 🆕 PARTIAL ROOM DRAGGING: State for partial room functionality
  const [partialRoomAnalysis, setPartialRoomAnalysis] = useState<PartialRoomDragResult | null>(null);
  const [selectedWallGroup, setSelectedWallGroup] = useState<WallGroup | null>(null);
  const [isPartialRoomMode, setIsPartialRoomMode] = useState<boolean>(false);
  const [partialRoomPreview, setPartialRoomPreview] = useState<Wall[] | null>(null);

  const [pinchStartDistance, setPinchStartDistance] = useState<number | null>(null);
  const [pinchStartScale, setPinchStartScale] = useState<number | null>(null);
  const [pinchCenter, setPinchCenter] = useState<Point | null>(null);

  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false); // Added for immediate check

  const [touchVelocity, setTouchVelocity] = useState<Point>({ x: 0, y: 0 });
  const [lastTouchTime, setLastTouchTime] = useState<number>(0);
  const [lastTouchPosition, setLastTouchPosition] = useState<Point | null>(null);
  const [momentumAnimationFrame, setMomentumAnimationFrame] = useState<number | null>(null);

  const [roomCorners, setRoomCorners] = useState<RoomCorner[]>([]);

  const [splitPoint, setSplitPoint] = useState<Point | null>(null);

  const [textBoxes, setTextBoxes] = useState<TextBox[]>([]);
  const [selectedTextBox, setSelectedTextBox] = useState<TextBox | null>(null);

  const [showMeasurements, setShowMeasurements] = useState(false);
  

  
  // Room label positions - store custom positions for room labels
  const [roomLabelPositions, setRoomLabelPositions] = useState<Record<string, Point>>({});
  const [isDraggingRoomLabel, setIsDraggingRoomLabel] = useState(false);
  
  // NEW: Room positioning mode for AR scanned rooms
  const [isPositioningRoom, setIsPositioningRoom] = useState(false);
  const [positioningRoomId, setPositioningRoomId] = useState<string>('');
  const [positioningRoomWallIds, setPositioningRoomWallIds] = useState<string[]>([]);
  const [isPositioningModalExpanded, setIsPositioningModalExpanded] = useState(true);
  const [roomDragStart, setRoomDragStart] = useState<Point | null>(null);
  const [roomDragOffset, setRoomDragOffset] = useState<Point>({ x: 0, y: 0 });
  const [positioningTransformedWalls, setPositioningTransformedWalls] = useState<Wall[] | null>(null);
  const [draggedRoomLabelId, setDraggedRoomLabelId] = useState<string | null>(null);

  // State for expandable icons
  const [isIconsExpanded, setIsIconsExpanded] = useState(false);

  // State for room label prompt
  const [isRoomLabelPromptOpen, setIsRoomLabelPromptOpen] = useState(false);
  const [roomToLabel, setRoomToLabel] = useState<Room | null>(null);
  const [roomCustomLabels, setRoomCustomLabels] = useState<Record<string, string>>({});
  const [promptedRoomIds, setPromptedRoomIds] = useState<Set<string>>(new Set());
  const roomMigrationMapRef = useRef<Record<string, string>>({}); // ADDED: Ref for migration map
  // Store wall images captured during room creation, keyed by room name (label)
  const [roomWallImages, setRoomWallImages] = useState<Record<string, any[]>>({});

  // State for room height prompt
  const [isRoomHeightPromptOpen, setIsRoomHeightPromptOpen] = useState(false);
  const [roomToSetHeight, setRoomToSetHeight] = useState<Room | null>(null);

  // State for room rename functionality
  const [isRoomRenameModalOpen, setIsRoomRenameModalOpen] = useState(false);
  
  // State for diagnostics panel
  const [showDiagnosticsPanel, setShowDiagnosticsPanel] = useState(false);
  const [roomToRename, setRoomToRename] = useState<Room | null>(null);

  // State for room context menu
  const [isRoomContextMenuOpen, setIsRoomContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [contextMenuRoom, setContextMenuRoom] = useState<Room | null>(null);

  // State for touch-based room interactions
  const [touchRoomInteraction, setTouchRoomInteraction] = useState<{
    room: Room | null;
    startTime: number;
    position: Point;
    isLongPress: boolean;
  } | null>(null);
  const [lastTouchEndTime, setLastTouchEndTime] = useState(0);
  const [lastTouchedRoom, setLastTouchedRoom] = useState<Room | null>(null);
  const [showFloatingActions, setShowFloatingActions] = useState(false);

  // State for preserving room label during resize
  const [resizingRoomInfo, setResizingRoomInfo] = useState<{ originalStableId: string, label: string } | null>(null);
  const [activelyResizedWalls, setActivelyResizedWalls] = useState<Wall[] | null>(null);

  const previousCurrentWallsRef = useRef<Wall[] | null>(null); // For tracking changes to currentWalls
  const previousFloorRef = useRef<number | null>(null); // For tracking floor switches
  const justResizedRef = useRef(false); // Added to manage resize completion for label prompt
  const undoRedoInProgressRef = useRef(false); // Ref to track undo/redo operations
  const labelsOnlyOperation = useRef(false); // 🔧 LABEL PRESERVATION FIX: Track label-only operations
  const previousCurrentRoomsRef = useRef<Room[]>([]); // Track previous currentRooms for label-only operations
  const [resizingRoomLiveDimensions, setResizingRoomLiveDimensions] = useState<{ width: number, height: number, area: number } | null>(null); // For live dimensions during resize
  const [splitPreviewDimensions, setSplitPreviewDimensions] = useState<{ distToStart: number; distToEnd: number } | null>(null); // For live split dimensions
  const [cornerDragPreview, setCornerDragPreview] = useState<CornerDragPreview | null>(null);

  // State for actual canvas render dimensions, set by ResizeObserver
  const [canvasRenderDimensions, setCanvasRenderDimensions] = useState<{ width: number, height: number } | null>(null);
  // Removed: const initialZoomFitDone = useRef(false);

  const [hoveredWallForResize, setHoveredWallForResize] = useState<Wall | null>(null);
  const draggedWallSegmentRef = useRef<Wall | null>(null); // Added this ref

  const [isFileLoading, setIsFileLoading] = useState<boolean>(false); // New state
  const [isPlacingDoor, setIsPlacingDoor] = useState<boolean>(false);
  const [doorPreview, setDoorPreview] = useState<DoorElement | null>(null);
  const [wallForDoor, setWallForDoor] = useState<WallWithTempId | null>(null);
  const [liveUpdatedElements, setLiveUpdatedElements] = useState<FloorPlanElement[] | null>(null);
  const [isPlacingWindow, setIsPlacingWindow] = useState<boolean>(false);
  const [windowPreview, setWindowPreview] = useState<WindowElement | null>(null);
  const [wallForWindow, setWallForWindow] = useState<WallWithTempId | null>(null);
  const [isWindowPromptOpen, setIsWindowPromptOpen] = useState(false);
  const [isWindowTypePromptOpen, setIsWindowTypePromptOpen] = useState(false);
  const [selectedWindowType, setSelectedWindowType] = useState<WindowType | null>(null);
  const [windowToEdit, setWindowToEdit] = useState<WindowElement | null>(null);
  const [isPlacingRadiator, setIsPlacingRadiator] = useState<boolean>(false);
  const [radiatorPreview, setRadiatorPreview] = useState<RadiatorElement | null>(null);
  const [wallForRadiator, setWallForRadiator] = useState<WallWithTempId | null>(null);
  const [isRadiatorPromptOpen, setIsRadiatorPromptOpen] = useState(false);
  const [isPlacingCounter, setIsPlacingCounter] = useState<boolean>(false);
  const [counterPreview, setCounterPreview] = useState<CounterElement | null>(null);
  const [wallForCounter, setWallForCounter] = useState<WallWithTempId | null>(null);
  const [selectedCounterId, setSelectedCounterId] = useState<string | null>(null);
  const [counterResizeHandle, setCounterResizeHandle] = useState<string | null>(null);
  const [isResizingCounter, setIsResizingCounter] = useState<boolean>(false);
  const [isRotatingCounter, setIsRotatingCounter] = useState<boolean>(false);
  const [isDraggingCounter, setIsDraggingCounter] = useState<boolean>(false);
  const [counterDragStart, setCounterDragStart] = useState<Point | null>(null);
  const [rotationStartAngle, setRotationStartAngle] = useState<number>(0);
  const [isPlacingStairs, setIsPlacingStairs] = useState<boolean>(false);
  const [stairsPreview, setStairsPreview] = useState<StairsElement | null>(null);
  const [wallForStairs, setWallForStairs] = useState<WallWithTempId | null>(null);
  const [selectedStairsId, setSelectedStairsId] = useState<string | null>(null);
  const [stairsResizeHandle, setStairsResizeHandle] = useState<string | null>(null);
  const [isResizingStairs, setIsResizingStairs] = useState<boolean>(false);
  const [isDraggingStairs, setIsDraggingStairs] = useState<boolean>(false);
  const [isRotatingStairs, setIsRotatingStairs] = useState<boolean>(false);
  const [stairsDirection, setStairsDirection] = useState<'up' | 'down'>('up');
  const [radiatorToEdit, setRadiatorToEdit] = useState<RadiatorElement | null>(null);
  const [isPlacingBoiler, setIsPlacingBoiler] = useState<boolean>(false);
  const [boilerPreview, setBoilerPreview] = useState<BoilerElement | null>(null);
  const [wallForBoiler, setWallForBoiler] = useState<WallWithTempId | null>(null);
  const [isBoilerPromptOpen, setIsBoilerPromptOpen] = useState(false);
  const [boilerToEdit, setBoilerToEdit] = useState<BoilerElement | null>(null);
  const [isPlacingLight, setIsPlacingLight] = useState<boolean>(false);

  // Room sync indicator state
  const [showRoomSyncIndicator, setShowRoomSyncIndicator] = useState(false);
  const [syncedRoomName, setSyncedRoomName] = useState('');
  
  // Room merge notification state
  const [showMergeNotification, setShowMergeNotification] = useState(false);
  const [mergeNotificationData, setMergeNotificationData] = useState<{
    message: string;
    roomCount: number;
    wallCount: number;
  } | null>(null);
  
  // Gap detection and auto-fill state
  const [detectedGaps, setDetectedGaps] = useState<DetectedGap[]>([]);
  const [gapFillSuggestion, setGapFillSuggestion] = useState<GapFillSuggestion | null>(null);
  const [cursorPosition, setCursorPosition] = useState<Point | null>(null);
  const [isWallSplitting, setIsWallSplitting] = useState(false);
  
  // Throttled room calculation to prevent performance issues during split tool usage
  const [roomCalculationThrottle, setRoomCalculationThrottle] = useState<number>(0);
  const [lightPreview, setLightPreview] = useState<LightElement | null>(null);
  const [isPlacingSmokeAlarm, setIsPlacingSmokeAlarm] = useState<boolean>(false);
  const [smokeAlarmPreview, setSmokeAlarmPreview] = useState<SmokeAlarmElement | null>(null);
  const [isPlacingFireSafety, setIsPlacingFireSafety] = useState<boolean>(false);
  const [fireSafetyPreview, setFireSafetyPreview] = useState<FireSafetyElement | null>(null);
  const [isPlacingUtility, setIsPlacingUtility] = useState<boolean>(false);
  const [utilityPreview, setUtilityPreview] = useState<UtilityElement | null>(null);
  const [utilityRotation, setUtilityRotation] = useState<number>(0); // Rotation for sinks
  const [wallForSink, setWallForSink] = useState<WallWithTempId | null>(null); // Wall for sink placement
  const [isPlacingLoftHatch, setIsPlacingLoftHatch] = useState<boolean>(false);
  const [loftHatchPreview, setLoftHatchPreview] = useState<LoftHatchElement | null>(null);
  const [isPlacingCompass, setIsPlacingCompass] = useState<boolean>(false);
  const [compassPreview, setCompassPreview] = useState<CompassType | null>(null);
  const [selectedCompassId, setSelectedCompassId] = useState<string | null>(null);
  const [isRotatingCompass, setIsRotatingCompass] = useState<boolean>(false);
  const [compassRotationStartAngle, setCompassRotationStartAngle] = useState<number>(0);
  const [selectedGardenId, setSelectedGardenId] = useState<string | null>(null);
  const [gardenResizeHandle, setGardenResizeHandle] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null>(null);
  const [isResizingGarden, setIsResizingGarden] = useState<boolean>(false);
  const [isDraggingGardenLabel, setIsDraggingGardenLabel] = useState<boolean>(false);
  const [gardenLabelDragStart, setGardenLabelDragStart] = useState<Point | null>(null);
  const [gardenToEdit, setGardenToEdit] = useState<UtilityElement | null>(null);
  const [isGardenEditPromptOpen, setIsGardenEditPromptOpen] = useState<boolean>(false);
  const [gardenLabelInput, setGardenLabelInput] = useState<string>('');
  const [gardenAreaInput, setGardenAreaInput] = useState<string>('');
  const [isDraggingGarden, setIsDraggingGarden] = useState<boolean>(false);
  const [gardenDragStart, setGardenDragStart] = useState<Point | null>(null);
  const [gardenPreview, setGardenPreview] = useState<{start: Point, end: Point, resizing?: string} | null>(null);
  
  // Highlighting tool state
  const [isHighlighting, setIsHighlighting] = useState<boolean>(false);
  const [highlightStart, setHighlightStart] = useState<Point | null>(null);
  const [highlightPreview, setHighlightPreview] = useState<HighlightArea | null>(null);
  const [highlightColor, setHighlightColor] = useState<string>(HIGHLIGHT_COLORS.ORANGE);
  const [highlightSnap, setHighlightSnap] = useState<HighlightAreaSnap | null>(null);
  
  // Highlight calculations popup state
  const [showHighlightPopup, setShowHighlightPopup] = useState<boolean>(false);
  const [highlightPopupData, setHighlightPopupData] = useState<{
    highlight: HighlightArea;
    calculations: {
      area: number;
      hlp: number;
      pw: number;
      sw: number;
    };
    position: { x: number; y: number };
  } | null>(null);
  
  const [isDoorPromptOpen, setIsDoorPromptOpen] = useState(false);
  const [doorToEdit, setDoorToEdit] = useState<DoorElement | null>(null);
  const [currentFloorPlanId, setCurrentFloorPlanId] = useState<string | null>(floorPlanId || null);
  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(!floorPlanId && !propertyAddress); // Show modal for new plans without address
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showARScanLimitModal, setShowARScanLimitModal] = useState(false);
  const [showOrientationModal, setShowOrientationModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showExportLoadingModal, setShowExportLoadingModal] = useState(false);

  // AR Wall Angle Configuration for Multi-Room Scan (raw scanning - no alignment)
  const [shouldAlignWallsTo90Degrees] = useState(false); // Always use raw AR data
  
  // AR orientation preview state
  const [showAROrientationPreview, setShowAROrientationPreview] = useState(false);
  const [pendingARWalls, setPendingARWalls] = useState<Wall[]>([]);
  const [pendingARScanData, setPendingARScanData] = useState<ARRoomScan | null>(null);
  const scannedWallsCenter = useRef<Point | null>(null);
  
  // Multi-room label collection state
  const [isCollectingMultiRoomLabels, setIsCollectingMultiRoomLabels] = useState(false);
  const [currentLabelingRoomIndex, setCurrentLabelingRoomIndex] = useState(0);
  const [collectedRoomLabels, setCollectedRoomLabels] = useState<Record<string, string>>({});
  const [multiRoomCount, setMultiRoomCount] = useState(0);

  const [exportSteps, setExportSteps] = useState<Array<{
    id: string;
    label: string;
    icon: React.ComponentType<any>;
    status: 'pending' | 'in-progress' | 'completed' | 'error';
    description?: string;
  }>>([]);
  const [currentExportStep, setCurrentExportStep] = useState<string | undefined>();

  // State for element info modal
  const [isElementInfoModalOpen, setIsElementInfoModalOpen] = useState(false);
  const [elementToView, setElementToView] = useState<FloorPlanElement | null>(null);
  
  // Window counter for sequential IDs (W1, W2, W3, etc.)
  const [windowCounter, setWindowCounter] = useState(1);
  const [windowIdsMigrated, setWindowIdsMigrated] = useState(false);

  // Initialize window display IDs for existing elements (migration)
  useEffect(() => {
    if (!windowIdsMigrated && elements.length > 0) {
      const { updatedElements, nextCounter } = assignMissingWindowIds(elements);
      
      // Check if any windows were missing display IDs
      const windowsNeedingIds = elements.filter(el => 
        (el.type === 'window' || el.type === 'bayWindow') && !(el as any).displayId
      );
      
      if (windowsNeedingIds.length > 0) {
        console.log(`🪟 Migrating ${windowsNeedingIds.length} windows to sequential ID system`);
        setElements(updatedElements);
      }
      
      // Update counter to next available number
      setWindowCounter(nextCounter);
      setWindowIdsMigrated(true);
      
      console.log(`🪟 Window ID system initialized. Next counter: W${nextCounter}`);
    }
  }, [elements.length, windowIdsMigrated]);
  const [propertyOrientation, setPropertyOrientation] = useState<number>(0); // 0-360 degrees
  
  const [orientationHasBeenSet, setOrientationHasBeenSet] = useState<boolean>(false); // Track if orientation was set during initial setup
  const lastOrientationCalculationRef = useRef<string | null>(null); // Track last calculation to prevent duplicates
  
  const [showSiteNotes, setShowSiteNotes] = useState(false);
  const [showFileRepairDialog, setShowFileRepairDialog] = useState(false);
  const [siteNotesDataReady, setSiteNotesDataReady] = useState(false); // Track if site notes data is ready
  const [siteNotesData, setSiteNotesData] = useState<SiteNotesData>({
    formData: {
      address: {
        houseNumber: '',
        street: '',
        town: '',
        postcode: '',
      },
      propertyType: '',
      detachment: '',
      ageBand: '',
      groundLevel: '',
      roofTypeRdSAP: '',
      wallType: '',
      partyWall: '',
      wallThickness: '',
      roofType: '',
      roofInsulation: '',
      floorType: '',
      // Habitable rooms fields
      totalHabitableRooms: '',
      totalHeatedHabitableRooms: '',
      heatingType: '',
      heatingSystemType: '',
      heatingFuelType: '',
      heatingBrand: '',
      heatingModel: '',
      heatingSerialNumber: '',
      totalRads: '0',
      trvs: '0',
      tRad: '',
      heatingSourceLocation: '',
      tankLocation: '',
      totalWindows: '0',
      glazingType: '',
      windowType: '',
      windowFrameType: '',
      glazingThickness: '',
      windowsMissingTrickleVents: '0',
      missingTrickleVentsLocation: '',
      mevsKitchen: '',
      mevsBathroom: '',
      mevsEnSuite: '',
      mevsWC: '',
      mevsUtility: '',
      totalDoors: '0',
      doorsWithoutUndercuts: '0',
      doorsWithoutUndercutsLocation: '',
      // Additional ventilation fields
      openChimneys: '0',
      openFlues: '0',
      openChimneysFluesAttachedToClose: '0',
      fluesAttachedToSolidFuelBoiler: '0',
      openFluesAttachedToOtherHeater: '0',
      blockedChimneys: '0',
      intermittentExtractFans: '0',
      passiveVents: '0',
      fluelessGasFires: '0',
      fixedSpaceCooling: '',
      draughtLobby: '',
      gmLocation: '',
      emLocations: '',
      stapLocation: '',
      additionalNotes: '',
      totalLightsX: '',
      totalLightsO: '',
      totalLEL: '',
      wallInsulationType: '',
      wallInsulationElevationsCount: '',
      wallInsulationElevations: [],
    },
    floors: [
      {
        id: 1,
        floorLevel: 'ground floor',
        buildingType: 'main building',
        height: '',
        floorArea: '',
        hlp: '',
        pw: '',
        rooms: []
      }
    ]
  });

  // Track if there are unsaved changes
  const [hasSiteNotesChanges, setHasSiteNotesChanges] = useState(false);
  const [hasFloorPlanChanges, setHasFloorPlanChanges] = useState(false);

  // Define a directory for saving floor plans
  const FLOOR_PLANS_DIR = 'floorplans';

  // Initialize room sync service
  const roomSync = createRoomSyncHook(
    setSiteNotesData,
    () => siteNotesData
  );

  // Auto-sync labeled rooms when site notes are opened
  useEffect(() => {
    if (showSiteNotes && roomSync?.syncAllRooms) {
      console.log('🔄 Site notes opened - auto-syncing labeled rooms...');
      // Small delay to ensure the modal is fully rendered
      setTimeout(() => {
        roomSync.syncAllRooms(currentRooms, currentFloor, roomCustomLabels);
      }, 100);
    }
  }, [showSiteNotes]); // Remove roomSync from dependencies to prevent infinite loop

  // Auto-sync ALL elements to site notes when elements change
  // Track the last sync to prevent infinite loops
  const lastSyncHash = useRef<string>('');
  
  useEffect(() => {
    if (!roomSync || elements.length === 0) return;
    
    // 🔧 RACE CONDITION PROTECTION: Skip element sync during label-only operations
    if (labelsOnlyOperation.current) {
      console.log('🔧 ELEMENT SYNC: Skipping element sync during label-only operation to prevent interference');
      return;
    }

    // Create a hash of ALL relevant elements to detect actual changes
    const allWindows = elements.filter(el => el.type === 'window') as unknown as WindowElement[];
    const allDoors = elements.filter(el => el.type === 'door') as DoorElement[];
    const allUtilities = elements.filter(el => el.type === 'utility') as UtilityElement[];
    const allLights = elements.filter(el => el.type === 'light') as LightElement[];
    const allRadiators = elements.filter(el => el.type === 'radiator') as RadiatorElement[];
    
    const elementsHash = [
      // Windows hash
      allWindows.map(w => `w:${w.id}-${w.position.x}-${w.position.y}-${w.floor}-${w.trickleVents}`).sort().join('|'),
      // Doors hash
      allDoors.map(d => `d:${d.id}-${d.position.x}-${d.position.y}-${d.floor}-${d.hasUndercut}`).sort().join('|'),
      // Utilities hash
      allUtilities.map(u => `u:${u.id}-${u.position.x}-${u.position.y}-${u.floor}-${u.utilityType}`).sort().join('|'),
      // Lights hash
      allLights.map(l => `l:${l.id}-${l.position.x}-${l.position.y}-${l.floor}-${l.lightType}`).sort().join('|'),
      // Radiators hash
      allRadiators.map(r => `r:${r.id}-${r.position.x}-${r.position.y}-${r.floor}-${r.radiatorType}`).sort().join('|')
    ].join('::');

    // Skip sync if elements haven't actually changed
    if (elementsHash === lastSyncHash.current) {
      return;
    }

    // 🪟 MIGRATION PROTECTION: Check if this is during file loading or has migration windows
    const hasMigrationWindows = allWindows.some(w => (w as any).migrationPreserved || (w as any).migrationRestored);
    const isDuringFileLoading = isFileLoading;
    
    // Debounce the sync to avoid excessive calls during rapid changes
    const timeoutId = setTimeout(() => {
      // Double-check the hash hasn't changed during debounce
      const currentElements = {
        windows: elements.filter(el => el.type === 'window') as unknown as WindowElement[],
        doors: elements.filter(el => el.type === 'door') as DoorElement[],
        utilities: elements.filter(el => el.type === 'utility') as UtilityElement[],
        lights: elements.filter(el => el.type === 'light') as LightElement[],
        radiators: elements.filter(el => el.type === 'radiator') as RadiatorElement[]
      };
      
      const currentElementsHash = [
        currentElements.windows.map(w => `w:${w.id}-${w.position.x}-${w.position.y}-${w.floor}-${w.trickleVents}`).sort().join('|'),
        currentElements.doors.map(d => `d:${d.id}-${d.position.x}-${d.position.y}-${d.floor}-${d.hasUndercut}`).sort().join('|'),
        currentElements.utilities.map(u => `u:${u.id}-${u.position.x}-${u.position.y}-${u.floor}-${u.utilityType}`).sort().join('|'),
        currentElements.lights.map(l => `l:${l.id}-${l.position.x}-${l.position.y}-${l.floor}-${l.lightType}`).sort().join('|'),
        currentElements.radiators.map(r => `r:${r.id}-${r.position.x}-${r.position.y}-${r.floor}-${r.radiatorType}`).sort().join('|')
      ].join('::');
      
      if (currentElementsHash === lastSyncHash.current) {
        return; // No change, skip sync
      }
      
      lastSyncHash.current = currentElementsHash;
      
      // Get all floor numbers that have any elements
      const floorNumbers = Array.from(new Set([
        ...currentElements.windows.map(w => w.floor),
        ...currentElements.doors.map(d => d.floor),
        ...currentElements.utilities.map(u => u.floor),
        ...currentElements.lights.map(l => l.floor),
        ...currentElements.radiators.map(r => r.floor)
      ]));

      // console.log('🔄 Auto-syncing ALL elements to site notes...', {
      //   totalWindows: currentElements.windows.length,
      //   totalDoors: currentElements.doors.length,
      //   totalUtilities: currentElements.utilities.length,
      //   totalLights: currentElements.lights.length,
      //   totalRadiators: currentElements.radiators.length,
      //   floorsWithElements: floorNumbers,
      //   hasMigrationWindows,
      //   isDuringFileLoading
      // });

      // Sync elements for ALL floors that have elements
      for (const floorNum of floorNumbers) {
        const floorWalls = floorPlans[floorNum] || [];
        const floorRooms = calculateRooms(floorWalls, roomCustomLabels, 'labelPreservation', floorNum);
        
        const floorWindows = currentElements.windows.filter(w => w.floor === floorNum);
        const floorDoors = currentElements.doors.filter(d => d.floor === floorNum);
        const floorUtilities = currentElements.utilities.filter(u => u.floor === floorNum);
        const floorLights = currentElements.lights.filter(l => l.floor === floorNum);
        const floorRadiators = currentElements.radiators.filter(r => r.floor === floorNum);
        
        if (floorRooms.length > 0) {
          // Sync windows
          if (floorWindows.length > 0) {
            roomSync.smartSyncWindows(floorWindows, floorRooms, floorNum, false);
            // console.log(`🪟 Auto-synced ${floorWindows.length} windows for floor ${floorNum}`);
          }
          
          // Sync doors (for door undercuts)
          if (floorDoors.length > 0) {
            roomSync.syncDoorUndercuts(floorDoors, floorRooms, floorNum, floorWalls);
            // console.log(`🚪 Auto-synced ${floorDoors.length} doors for floor ${floorNum}`);
          }
          
          // Sync utilities (gas meter, electric meter, stop tap locations)
          if (floorUtilities.length > 0) {
            roomSync.syncUtilityLocations(floorUtilities, floorRooms);
            // console.log(`🔧 Auto-synced ${floorUtilities.length} utilities for floor ${floorNum}`);
          }
          
          // Sync lights (for light counts)
          if (floorLights.length > 0) {
            roomSync.smartSyncLights(floorLights, floorRooms, floorNum, false);
            // console.log(`💡 Auto-synced ${floorLights.length} lights for floor ${floorNum}`);
          }
          
          // Sync radiators (for heating system counts)
          if (floorRadiators.length > 0) {
            roomSync.smartSyncRadiators(floorRadiators, floorRooms, floorNum, false);
            // console.log(`🔥 Auto-synced ${floorRadiators.length} radiators for floor ${floorNum}`);
          }
        }
      }

      // Sync cross-floor aggregations
      roomSync.syncAllFloorsWindows(currentElements.windows, floorPlans, roomCustomLabels);
      roomSync.syncAllFloorsDoorUndercuts(currentElements.doors, floorPlans, roomCustomLabels);
      roomSync.syncAllFloorsLightsToAdditionalInfo();
      roomSync.syncAllFloorsRadiatorsToHeatingSystem();
      
      // console.log('🔄 Auto-sync completed - all element counts should now be updated');
        
      // 🧹 CLEANUP: Only run orphaned element cleanup if NOT during file loading or migration
      if (!isDuringFileLoading && !hasMigrationWindows) {
        // console.log('🧹 Running orphaned element cleanup (not during file loading/migration)');
        Object.keys(floorPlans).forEach(floorKey => {
          const floorNum = parseInt(floorKey, 10);
          const floorElements = elements.filter(el => (el as any).floor === floorNum);
          const roomsForFloor = labelsOnlyOperation.current ? [] : calculateRooms(floorPlans[floorNum] || [], roomCustomLabels, 'explicitRoomCreation', floorNum);
          
          // Clean up orphaned elements for this floor
          roomSync.syncOrphanedElementsCleanup(floorElements, roomsForFloor, floorNum);
        });
      } else {
        console.log('🔄 [MIGRATION-SAFE] Skipping orphaned element cleanup during file loading or for migration elements');
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [elements, roomSync, floorPlans, roomCustomLabels, currentFloor, siteNotesData, isFileLoading]); // Added isFileLoading as dependency

  // 🧹 PHANTOM ELEMENT FIX: Listen for room operation completion to trigger element cleanup
  useEffect(() => {
    if (!roomSync) return;

    const handleRoomOperationComplete = (event: CustomEvent) => {
      const { type, floor, timestamp } = event.detail;
      console.log(`🧹 Room operation completed: ${type} on floor ${floor}`);
      
      // Trigger comprehensive element cleanup for the affected floor
      try {
        const wallsForFloor = floorPlans[floor] || [];
        if (wallsForFloor.length === 0) return;
        
        const roomsForFloor = calculateRooms(wallsForFloor, roomCustomLabels, 'explicitRoomCreation', floor);
        const floorElements = elements.filter(el => (el as any).floor === floor);
        
        console.log(`🧹 Cleaning up ${floorElements.length} elements across ${roomsForFloor.length} rooms on floor ${floor}`);
        
        // Clean up orphaned elements first
        roomSync.syncOrphanedElementsCleanup(floorElements, roomsForFloor, floor);
        
        // Re-sync all elements to ensure proper room assignments
        const windowElements = floorElements.filter(el => el.type === 'window') as WindowElement[];
        const radiatorElements = floorElements.filter(el => el.type === 'radiator') as RadiatorElement[];
        
        if (windowElements.length > 0) {
          roomSync.smartSyncWindows(windowElements, roomsForFloor, floor, true); // force sync
        }
        
        if (radiatorElements.length > 0) {
          roomSync.smartSyncRadiators(radiatorElements, roomsForFloor, floor, true); // force sync
        }
        
        console.log(`✅ Phantom element cleanup completed for floor ${floor}`);
      } catch (error) {
        console.error(`❌ Failed to cleanup phantom elements on floor ${floor}:`, error);
      }
    };

    window.addEventListener('floorplan-room-operation-complete', handleRoomOperationComplete as EventListener);
    
    return () => {
      window.removeEventListener('floorplan-room-operation-complete', handleRoomOperationComplete as EventListener);
    };
  }, [roomSync, floorPlans, elements, roomCustomLabels]);

  // 🔧 CRITICAL FIX: Listen for element sync events triggered after wall splits
  // This fixes the bug where windows and radiators on split walls become uneditable and show default values
  useEffect(() => {
    if (!roomSync) return;

    const handleElementSyncRequired = (event: CustomEvent) => {
      const { reason, attachedElementsCount, elementTypes = ['window', 'radiator'] } = event.detail;
      console.log(`🔧 [WALL_SPLIT_FIX] Element sync triggered: ${reason}, elements: ${attachedElementsCount}, types: ${elementTypes.join(', ')}`);
      
      try {
        // Get current elements and recalculate rooms to establish proper relationships
        const allWindows = elements.filter(el => el.type === 'window') as unknown as WindowElement[];
        const allRadiators = elements.filter(el => el.type === 'radiator') as unknown as RadiatorElement[];
        
        // Sync elements for each floor to reestablish element-room relationships after wallId changes
        Object.keys(floorPlans).forEach(floorNumStr => {
          const floorNum = parseInt(floorNumStr);
          const floorWalls = floorPlans[floorNum] || [];
          
          if (floorWalls.length > 0) {
            const floorRooms = calculateRooms(floorWalls, roomCustomLabels, 'explicitRoomCreation', floorNum);
            
            // Sync windows if included in elementTypes
            if (elementTypes.includes('window')) {
              const floorWindows = allWindows.filter(w => w.floor === floorNum);
              if (floorWindows.length > 0) {
                roomSync.smartSyncWindows(floorWindows, floorRooms, floorNum, true); // Force sync
                console.log(`🪟 [WALL_SPLIT_FIX] Re-synced ${floorWindows.length} windows on floor ${floorNum}`);
              }
            }
            
            // Sync radiators if included in elementTypes
            if (elementTypes.includes('radiator')) {
              const floorRadiators = allRadiators.filter(r => r.floor === floorNum);
              if (floorRadiators.length > 0) {
                roomSync.smartSyncRadiators(floorRadiators, floorRooms, floorNum, true); // Force sync
                console.log(`🔥 [WALL_SPLIT_FIX] Re-synced ${floorRadiators.length} radiators on floor ${floorNum}`);
              }
            }
          }
        });
        
        // Also sync cross-floor aggregations
        if (elementTypes.includes('window') && allWindows.length > 0) {
          roomSync.syncAllFloorsWindows(allWindows, floorPlans, roomCustomLabels);
        }
        if (elementTypes.includes('radiator') && allRadiators.length > 0) {
          roomSync.syncAllFloorsRadiatorsToHeatingSystem();
        }
        
        console.log(`🔧 [WALL_SPLIT_FIX] Successfully re-synced elements after wall split: ${allWindows.length} windows, ${allRadiators.length} radiators`);
      } catch (error) {
        console.error('🔧 [WALL_SPLIT_FIX] Error during element sync:', error);
      }
    };

    // Add event listener for element sync requests
    window.addEventListener('elementSyncRequired', handleElementSyncRequired as EventListener);

    // Cleanup event listener
    return () => {
      window.removeEventListener('elementSyncRequired', handleElementSyncRequired as EventListener);
    };
  }, [roomSync, elements, floorPlans, roomCustomLabels]);

  // Auto-update floor calculations when walls or room labels change
  const lastFloorCalcHash = useRef<string>('');
  
  useEffect(() => {
    if (!roomSync || isFileLoading) return;
    
    // 🔧 RACE CONDITION PROTECTION: Skip floor calculations during label-only operations
    if (labelsOnlyOperation.current) {
      console.log('🔧 FLOOR CALC: Skipping floor calculations during label-only operation to prevent interference');
      return;
    }

    // Create a hash of floor plan structure to detect meaningful changes
    const floorPlanHash = Object.keys(floorPlans)
      .sort()
      .map(floorNum => {
        const walls = floorPlans[parseInt(floorNum)] || [];
        return `${floorNum}:${walls.length}:${walls.map(w => `${w.start.x},${w.start.y}-${w.end.x},${w.end.y}`).join('|')}`;
      })
      .join('::');
    
    const roomLabelsHash = Object.keys(roomCustomLabels)
      .sort()
      .map(key => `${key}=${roomCustomLabels[key]}`)
      .join('|');
    
    const combinedHash = `${floorPlanHash}::labels:${roomLabelsHash}`;

    // Skip if nothing actually changed
    if (combinedHash === lastFloorCalcHash.current) {
      return;
    }

    // Debounce the calculation to avoid excessive calls during rapid changes
    const timeoutId = setTimeout(() => {
      // Double-check the hash hasn't changed during debounce
      const currentHash = Object.keys(floorPlans)
        .sort()
        .map(floorNum => {
          const walls = floorPlans[parseInt(floorNum)] || [];
          return `${floorNum}:${walls.length}:${walls.map(w => `${w.start.x},${w.start.y}-${w.end.x},${w.end.y}`).join('|')}`;
        })
        .join('::') + '::labels:' + Object.keys(roomCustomLabels)
        .sort()
        .map(key => `${key}=${roomCustomLabels[key]}`)
        .join('|');
      
      if (currentHash === lastFloorCalcHash.current) {
        return; // No change, skip calculation
      }
      
      lastFloorCalcHash.current = currentHash;
      
      // console.log('📐 Auto-updating floor calculations due to floor plan changes...');
      
      // Update calculations for all floors with walls
      Object.keys(floorPlans).forEach(floorKey => {
        const floorNumber = parseInt(floorKey);
        const walls = floorPlans[floorNumber] || [];
        
        if (walls.length > 0) {
          try {
            const rooms = calculateRooms(walls, roomCustomLabels, 'explicitRoomCreation', floorNumber);
            // Removed: roomSync.syncFloorDimensions(rooms, walls, floorNumber);
            // console.log(`  📐 Updated calculations for floor ${floorNumber}`);
          } catch (error) {
            console.warn(`Failed to update calculations for floor ${floorNumber}:`, error);
          }
        }
      });
      
      // console.log('📐 Auto-calculation complete');
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
  }, [floorPlans, roomCustomLabels, roomSync, isFileLoading]);

  const onSaveSuccess = useCallback((savedId: string) => {
    setCurrentFloorPlanId(savedId);
    // Clear all changes flags since we've successfully saved
    setHasSiteNotesChanges(false);
    setHasFloorPlanChanges(false);
  }, []);

  const onSaveError = useCallback((error: Error) => {
    console.error('[Autosave] Failed to save floor plan:', error);
  }, []);
  
  const getDataToSave = useCallback(() => ({
    floorPlans: floorPlans,
    currentFloor: currentFloor,
    elements: elements,
    textBoxes: textBoxes,
    roomCustomLabels: roomCustomLabels,
    roomLabelPositions: roomLabelPositions,
    propertyAddress: propertyAddress,
    propertyOrientation: propertyOrientation,
    windowCount: windowCount,
    lightCount: lightCount,
    siteNotesData: siteNotesData,
  }), [floorPlans, currentFloor, elements, textBoxes, roomCustomLabels, roomLabelPositions, propertyAddress, propertyOrientation, windowCount, lightCount, siteNotesData]);

  const getFileName = useCallback(() => {
    if (!propertyAddress || propertyAddress.trim().length < 3) {
      return `plan-${Date.now()}`;
    }
    return propertyAddress
      .trim()
      .replace(/[^a-zA-Z0-9\s\-_]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 50);
  }, [propertyAddress]);

  const validateBeforeSave = useCallback((context?: { address?: string }) => {
    const addressToValidate = context?.address !== undefined ? context.address : propertyAddress;
    if (!addressToValidate || addressToValidate.trim().length < 3) {
      return { 
        isValid: false, 
        message: 'Property address must be at least 3 characters long' 
      };
    }
    return { isValid: true };
  }, [propertyAddress]);

  const saveFunction = useCallback(async (name: string, data: any, existingId?: string) => {
    return await saveFloorPlanToSupabase(name, {
      ...data,
      createdAt: existingId ? data.createdAt || new Date().toISOString() : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      name,
      version: '2.0.0'
    }, existingId, 'estate'); // Specify plan_type as 'estate' for Estate_floorplan
  }, []);

  // Add autosave functionality
  const autosave = useAutosave(
    {
      id: currentFloorPlanId,
      data: {
        floorPlans,
        currentFloor,
        elements,
        textBoxes,
        roomCustomLabels,
        roomLabelPositions,
        propertyAddress,
        propertyOrientation,
        windowCount,
        lightCount,
        siteNotesData,
      },
      name: propertyAddress || `plan-${Date.now()}`,
      hasUnsavedChanges: hasFloorPlanChanges || hasSiteNotesChanges, // Use dedicated change flags
    },
    {
      saveFunction,
      getDataToSave,
      getFileName,
      validateBeforeSave,
      onSaveSuccess,
      onSaveError,
    },
    {
      enabled: false, // Start disabled, enable after address is provided
      intervalMs: 45000, // 45 seconds for floor plans (they might be larger)
      debounceMs: 500,   // 500ms after last change (much faster for site notes)
      maxRetries: 3
    }
  );

  // EFFECT TO FIX IOS WEBVIEW HEIGHT AND ENSURE SCROLL RESTORATION
  useEffect(() => {
    const layoutElement = layoutRef.current;
    
    // Store original overflow styles on mount to restore on unmount
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    
    if (layoutElement) {
      const setHeight = () => {
        // Use window.innerHeight to get the actual viewport height
        layoutElement.style.height = `${window.innerHeight}px`;
      };

      setHeight(); // Set initial height

      // Adjust height on resize events (like orientation change)
      window.addEventListener('resize', setHeight);

      // Cleanup listener on component unmount and ALWAYS restore scrolling
      return () => {
        window.removeEventListener('resize', setHeight);
        // CRITICAL: Force restore scrolling on component unmount
        document.documentElement.style.overflow = originalHtmlOverflow || '';
        document.body.style.overflow = originalBodyOverflow || '';
      };
    }
    
    // Cleanup function even if layoutElement is not available
    return () => {
      // CRITICAL: Force restore scrolling on component unmount
      document.documentElement.style.overflow = originalHtmlOverflow || '';
      document.body.style.overflow = originalBodyOverflow || '';
    };
  }, []);

  // CRITICAL: Scroll restoration fail-safe with interval check
  useEffect(() => {
    const scrollCheckInterval = setInterval(() => {
      // Simple check - if overflow is set to hidden but component is still mounted,
      // and we're not intentionally in a fullscreen state, restore it
      const currentHtmlOverflow = document.documentElement.style.overflow;
      const currentBodyOverflow = document.body.style.overflow;
      
      if (currentHtmlOverflow === 'hidden' || currentBodyOverflow === 'hidden') {
        // Only log and restore if this seems unintentional (not in fullscreen)
        console.warn('[RetrofitFloorPlan] Detected overflow:hidden, ensuring scroll works...');
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }
    }, 3000); // Check every 3 seconds

    // Listen for page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // When page becomes hidden, ensure scrolling is restored
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    };

    const handleBeforeUnload = () => {
      // Always restore scrolling before page unload
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(scrollCheckInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Force restore scrolling when this effect cleans up
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []); // No dependencies to keep it simple and avoid linter issues

  // Enhanced iOS fullscreen effect with better error handling
  useEffect(() => {
    const layoutElement = layoutRef.current;
    if (!layoutElement) return;

    const isCurrentlyIOS = isIOSDevice();
    
    try {
      // Simple overflow management without complex state dependencies
      if (isCurrentlyIOS) {
        // Only apply iOS-specific handling when needed
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 
            'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
          );
        }
      }
    } catch (error) {
      console.error('[RetrofitFloorPlan] Error in iOS handling:', error);
      // On error, always restore scrolling
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    return () => {
      try {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 
            'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
          );
        }
        // Always restore scrolling on cleanup
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      } catch (error) {
        console.error('[RetrofitFloorPlan] Error in cleanup:', error);
        // Force restore scrolling even if other operations fail
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    };
  }, []); // No complex dependencies

  // Additional safety net for page events
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    };

    const handleBeforeUnload = () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };

    const handlePageShow = () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  useEffect(() => {
    selectedWallRef.current = selectedWall;
  }, [selectedWall]);

  const recordUndoState = useCallback(() => {
    const stateToSave: UndoRedoState = {
      floorPlans,
      elements,
      textBoxes,
      roomCustomLabels,
      propertyAddress,
      windowCount,
      lightCount,
      currentFloor,
    };
    setUndoStack(prev => [...prev, stateToSave]);
    setRedoStack([]);
    
    // Mark that floor plan has changes
    setHasFloorPlanChanges(true);
    
    // Trigger autosave after state change
    autosave.markAsChanged();
  }, [floorPlans, elements, textBoxes, roomCustomLabels, propertyAddress, windowCount, lightCount, currentFloor, setUndoStack, setRedoStack, autosave]);

  // Initialize AR scanning functionality
  const handleARWallsGenerated = useCallback((walls: Wall[], scanData: ARRoomScan) => {
    console.log(`[AR Integration] Received ${walls.length} walls from scan "${scanData.name}" for floor ${currentFloor}`);
    
    if (walls.length > 0) {
      // Calculate bounds and center for the scanned walls
      const xCoords = walls.flatMap(wall => [wall.start.x, wall.end.x]);
      const yCoords = walls.flatMap(wall => [wall.start.y, wall.end.y]);
      
      const minX = Math.min(...xCoords);
      const maxX = Math.max(...xCoords);
      const minY = Math.min(...yCoords);
      const maxY = Math.max(...yCoords);
      
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      
      // Store center point for rotation
      scannedWallsCenter.current = { x: centerX, y: centerY };
      
      // Store pending walls and scan data
      setPendingARWalls(walls);
      setPendingARScanData(scanData);
      
      // Show the AR orientation preview screen
      setShowAROrientationPreview(true);
      
      console.log('[AR Integration] Showing AR orientation preview - user can select top wall');
    }
  }, [currentFloor]);

  // Helper function to check if two room bounds overlap with a buffer
  const doRoomBoundsOverlap = (bounds1: { minX: number; maxX: number; minY: number; maxY: number }, bounds2: { minX: number; maxX: number; minY: number; maxY: number }, buffer: number = 50): boolean => {
    // Add buffer around both bounds
    const b1 = {
      minX: bounds1.minX - buffer,
      maxX: bounds1.maxX + buffer,
      minY: bounds1.minY - buffer,
      maxY: bounds1.maxY + buffer
    };
    const b2 = {
      minX: bounds2.minX - buffer,
      maxX: bounds2.maxX + buffer,
      minY: bounds2.minY - buffer,
      maxY: bounds2.maxY + buffer
    };
    
    return !(b1.maxX < b2.minX || b1.minX > b2.maxX || b1.maxY < b2.minY || b1.minY > b2.maxY);
  };
  
  // Helper function to find a safe placement position for new AR room that doesn't overlap existing rooms
  const findSafePlacementOffset = (newRoomWalls: Wall[], existingWalls: Wall[]): Point => {
    if (existingWalls.length === 0) {
      return { x: 0, y: 0 }; // No existing rooms, place at origin
    }
    
    // Calculate bounds of new room
    const newRoomCoords = newRoomWalls.flatMap(w => [w.start, w.end]);
    const newRoomBounds = {
      minX: Math.min(...newRoomCoords.map(p => p.x)),
      maxX: Math.max(...newRoomCoords.map(p => p.x)),
      minY: Math.min(...newRoomCoords.map(p => p.y)),
      maxY: Math.max(...newRoomCoords.map(p => p.y))
    };
    
    // Calculate bounds of all existing rooms
    const existingRooms = calculateRooms(existingWalls, roomCustomLabels, 'explicitRoomCreation', currentFloor);
    const existingBounds = existingRooms.map(room => getRoomBounds(room.walls));
    
    const newRoomWidth = newRoomBounds.maxX - newRoomBounds.minX;
    const newRoomHeight = newRoomBounds.maxY - newRoomBounds.minY;
    const spacing = 100; // Minimum spacing between rooms in pixels
    
    // Try placing the room to the right of existing rooms
    const maxExistingX = Math.max(...existingBounds.map(b => b.maxX));
    const offsetX = maxExistingX - newRoomBounds.minX + spacing;
    
    // Check if this placement overlaps
    const testBounds = {
      minX: newRoomBounds.minX + offsetX,
      maxX: newRoomBounds.maxX + offsetX,
      minY: newRoomBounds.minY,
      maxY: newRoomBounds.maxY
    };
    
    let hasOverlap = existingBounds.some(b => doRoomBoundsOverlap(testBounds, b, spacing));
    
    if (!hasOverlap) {
      console.log('[AR Placement] Placing room to the right with offset:', { offsetX, offsetY: 0 });
      return { x: offsetX, y: 0 };
    }
    
    // If right placement fails, try below existing rooms
    const maxExistingY = Math.max(...existingBounds.map(b => b.maxY));
    const offsetY = maxExistingY - newRoomBounds.minY + spacing;
    
    const testBounds2 = {
      minX: newRoomBounds.minX,
      maxX: newRoomBounds.maxX,
      minY: newRoomBounds.minY + offsetY,
      maxY: newRoomBounds.maxY + offsetY
    };
    
    hasOverlap = existingBounds.some(b => doRoomBoundsOverlap(testBounds2, b, spacing));
    
    if (!hasOverlap) {
      console.log('[AR Placement] Placing room below with offset:', { offsetX: 0, offsetY });
      return { x: 0, y: offsetY };
    }
    
    // If both fail, place diagonally (bottom-right)
    console.log('[AR Placement] Placing room diagonally with offset:', { offsetX, offsetY });
    return { x: offsetX, y: offsetY };
  };
  
  // Handle setting a wall as the "top" of the AR scan - rotates pending walls and adds to canvas
  const handleAROrientationComplete = useCallback((selectedWall: Wall | null) => {
    if (pendingARWalls.length === 0 || !scannedWallsCenter.current || !pendingARScanData) {
      return;
    }

    let wallsToAdd = pendingARWalls;

    // If user selected a wall, rotate all walls
    if (selectedWall) {
      console.log('[AR Orientation] Rotating walls - selected wall will be at top');
      
      // Calculate the angle of the selected wall
      const dx = selectedWall.end.x - selectedWall.start.x;
      const dy = selectedWall.end.y - selectedWall.start.y;
      const wallAngle = Math.atan2(dy, dx);
      
      // Calculate rotation needed to make this wall horizontal (0°)
      // Horizontal means parallel to the X-axis, which will place it at the "top" of the floor plan
      const targetAngle = 0; // 0° (horizontal, pointing right)
      const rotationAngle = targetAngle - wallAngle;
      
      console.log('[AR Orientation] Wall angle:', (wallAngle * 180 / Math.PI).toFixed(1) + '°');
      console.log('[AR Orientation] Rotation needed:', (rotationAngle * 180 / Math.PI).toFixed(1) + '°');
      
      const center = scannedWallsCenter.current;
      const cosAngle = Math.cos(rotationAngle);
      const sinAngle = Math.sin(rotationAngle);
      
      // Rotate a point around the center
      const rotatePoint = (point: Point): Point => {
        const x = point.x - center.x;
        const y = point.y - center.y;
        
        const rotatedX = x * cosAngle - y * sinAngle;
        const rotatedY = x * sinAngle + y * cosAngle;
        
        return {
          x: Math.round(rotatedX + center.x),
          y: Math.round(rotatedY + center.y)
        };
      };
      
      // Rotate all pending walls (explicitly preserve ID and all other properties)
      wallsToAdd = pendingARWalls.map(wall => {
        const rotated = {
          ...wall,
          id: wall.id, // Explicitly preserve ID
          start: rotatePoint(wall.start),
          end: rotatePoint(wall.end)
        };
        console.log('[AR Orientation] Rotated wall - ID preserved:', { id: rotated.id, hasId: !!rotated.id });
        return rotated;
      });
      
      console.log('[AR Orientation] ✅ Walls rotated - selected wall is now at the top');
    } else {
      console.log('[AR Orientation] Skipped rotation - using original orientation');
    }

    // 🆕 SAFE PLACEMENT: Calculate offset to avoid overlapping existing rooms
    const existingWalls = floorPlans[currentFloor] || [];
    const safeOffset = findSafePlacementOffset(wallsToAdd, existingWalls);
    
    // Apply offset to all walls
    if (safeOffset.x !== 0 || safeOffset.y !== 0) {
      console.log('[AR Placement] Applying safe placement offset:', safeOffset);
      wallsToAdd = wallsToAdd.map(wall => {
        const offsetWall = {
          ...wall,
          id: wall.id, // Explicitly preserve ID
          start: { x: wall.start.x + safeOffset.x, y: wall.start.y + safeOffset.y },
          end: { x: wall.end.x + safeOffset.x, y: wall.end.y + safeOffset.y }
        };
        console.log('[AR Placement] Applied offset to wall - ID preserved:', { id: offsetWall.id, hasId: !!offsetWall.id });
        return offsetWall;
      });
      
      // Update scanned walls center for proper zoom calculation
      if (scannedWallsCenter.current) {
        scannedWallsCenter.current = {
          x: scannedWallsCenter.current.x + safeOffset.x,
          y: scannedWallsCenter.current.y + safeOffset.y
        };
      }
    } else {
      console.log('[AR Placement] No offset needed - room placed at scan location');
    }

    // Add walls to the floor plan
    recordUndoState();
    
    // Debug: Verify all walls have IDs before adding
    console.log('[AR Integration] Walls being added to floor plan:', {
      count: wallsToAdd.length,
      wallIds: wallsToAdd.map(w => w.id),
      allHaveIds: wallsToAdd.every(w => w.id)
    });
    
    setFloorPlans(prev => ({
      ...prev,
      [currentFloor]: [...(prev[currentFloor] || []), ...wallsToAdd]
    }));

    // NEW WORKFLOW: After adding walls, calculate the new room and show label prompt
    console.log('[AR Integration] New single-room workflow - calculating room for labeling...');
    
    setTimeout(() => {
      const allFloorWalls = [...(floorPlans[currentFloor] || []), ...wallsToAdd];
      const rooms = calculateRooms(allFloorWalls, roomCustomLabels, 'explicitRoomCreation', currentFloor);
      
      // Find the newly scanned room (the one with walls from this scan)
      const newRoom = rooms.find(room => 
        room.walls.some((wall: any) => 
          wallsToAdd.some(addedWall => 
            wall.start.x === addedWall.start.x && 
            wall.start.y === addedWall.start.y &&
            wall.end.x === addedWall.end.x && 
            wall.end.y === addedWall.end.y
          )
        )
      );
      
      if (newRoom) {
        console.log('[AR Integration] New room detected:', {
          id: newRoom.stableId,
          area: newRoom.area,
          wallCount: newRoom.walls.length
        });
        
        // Show label prompt for the new room
        // Positioning mode will be activated in handleSaveRoomLabel after label is saved
        setRoomToLabel(newRoom);
        setIsRoomLabelPromptOpen(true);
        
        console.log('[AR Integration] ✅ Showing label prompt - positioning mode will activate after labeling', {
          roomId: newRoom.stableId,
          wallIds: newRoom.walls.map(w => w.id)
        });
      }
    }, 100);

    // Auto-zoom and center view on the new room only
    const allFloorWalls = [...(floorPlans[currentFloor] || []), ...wallsToAdd];
    
    if (allFloorWalls.length > 0) {
      const xCoords = allFloorWalls.flatMap(wall => [wall.start.x, wall.end.x]);
      const yCoords = allFloorWalls.flatMap(wall => [wall.start.y, wall.end.y]);
      
      const minX = Math.min(...xCoords);
      const maxX = Math.max(...xCoords);
      const minY = Math.min(...yCoords);
      const maxY = Math.max(...yCoords);
      
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      
      const containerWidth = canvasRef.current?.clientWidth || 750;
      const containerHeight = canvasRef.current?.clientHeight || 600;
      
      // Calculate zoom to fit all rooms with padding
      const roomWidth = maxX - minX;
      const roomHeight = maxY - minY;
      const padding = 100; // pixels of padding
      
      const scaleX = (containerWidth - padding) / roomWidth;
      const scaleY = (containerHeight - padding) / roomHeight;
      const newScale = Math.min(scaleX, scaleY, 2); // Cap at 2x zoom
      
      // Only update scale if it's significantly different (avoid tiny adjustments)
      if (Math.abs(newScale - scale) > 0.1) {
        setScale(newScale);
      }
      
      const newViewOffset = {
        x: centerX - containerWidth / (2 * (newScale || scale)),
        y: centerY - containerHeight / (2 * (newScale || scale))
      };
      
      setViewOffset(newViewOffset);
      
      console.log('[AR Integration] Auto-zoomed to fit all rooms:', {
        totalWalls: allFloorWalls.length,
        bounds: { minX, maxX, minY, maxY },
        scale: newScale || scale
      });
    }

    // Clean up
    setShowAROrientationPreview(false);
    setPendingARWalls([]);
    setPendingARScanData(null);
    scannedWallsCenter.current = null;
    
    console.log(`[AR Integration] Successfully added ${wallsToAdd.length} walls to floor ${currentFloor}`);
  }, [pendingARWalls, pendingARScanData, currentFloor, recordUndoState, scale, floorPlans, roomCustomLabels]);

  const arScanning = useARScanning({
    onWallsGenerated: handleARWallsGenerated,
    currentFloor,
    shouldAlignWallsTo90Degrees: shouldAlignWallsTo90Degrees
  });

  // Usage tracking for AR scans
  const { arScan } = useUsageTracking();
  const { isStandardPlan } = useSubscription();

  // Wrapper function to start AR scan - shows limit modal for standard users
  const handleStartARScan = useCallback(() => {
    if (isStandardPlan) {
      // Show usage modal for standard users
      setShowARScanLimitModal(true);
    } else {
      // Pro users go directly to scanner
      if (arScanning.openNativeARScanner) {
        arScanning.openNativeARScanner();
      }
    }
  }, [arScanning, isStandardPlan]);
  
  // Handle AR scan confirmation from modal - increments usage and opens scanner
  const handleARScanConfirm = useCallback(async () => {
    try {
      // Increment usage counter
      const usageTrackingModule = await import('../services/usageTrackingService');
      const UsageTrackingService = usageTrackingModule.default;
      const usageService = UsageTrackingService.getInstance();
      await usageService.incrementUsage('ar_scan');
      console.log('[AR Scanner] ✅ AR scan usage incremented from modal');
      
      // Open the scanner
      if (arScanning.openNativeARScanner) {
        arScanning.openNativeARScanner();
      }
    } catch (error) {
      console.error('[AR Scanner] Failed to increment AR scan usage:', error);
      // Still allow scan even if tracking fails
      if (arScanning.openNativeARScanner) {
        arScanning.openNativeARScanner();
      }
    }
  }, [arScanning]);

  const стенкаДляПеретаскиванияRef = useRef<Wall | null>(null); // Dragged wall ref (renamed from draggedWallRef for clarity if needed)
  const активноИзменяемаяИнформацияОСтенеRef = useRef<ActivelyResizingWallInfo | null>(null); // Actively resizing wall info ref
  const сегментПеретаскиванойСтеныRef = useRef<{ start: Point; end: Point } | null>(null); // Dragged wall segment ref
  const активноПеретаскиваемыеСтеныRef = useRef<{ originalWall: Wall; segments: Wall[] } | null>(null); // Actively resized walls ref (for split wall segments)

  const resizingRoomInfoRef = useRef<ResizingRoomInfo | null>(null); // Resizing room info ref
  const lastRawDraggedCornerPosRef = useRef<Point | null>(null); // To store the last raw position during corner drag

  const [isDraggingCorner, setIsDraggingCorner] = useState(false); // New state for corner drag
  const [hoveredCorner, setHoveredCorner] = useState<Point | null>(null); // Added for drawRoomCornerPoints
  const eraserPreviewPointRef = useRef<Point | null>(null); // Added for eraser preview
  const isMouseDownRef = useRef<boolean>(false); // Added for eraser preview condition
  
  // Performance optimization refs
  const lastDrawTimeRef = useRef(0);
  const drawTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (drawTimeoutRef.current) {
        clearTimeout(drawTimeoutRef.current);
      }
    };
  }, []);
  
  type ActiveCornerInfo = {
    originalCornerWorldPoint: Point;
    affectedWallsData: Array<{ tempId: string; isStartPointCorner: boolean }>; // Store more info
  };
  const activeCornerInfoRef = useRef<ActiveCornerInfo | null>(null); // Ref for active corner info

  const currentToolRef = useRef<ElementType>(currentTool);
  const isDraggingRoomRef = useRef(isDraggingRoom);
  const roomStartPointRef = useRef<Point | null>(roomStart);
  const lastOperationTypeRef = useRef<RoomCalculationContext>('geometryUpdate'); // Added ref for operation type
  const newlyAddedWallsRef = useRef<Wall[]>([]); // Track newly added walls for endpoint room creation
  const roomEndPointRef = useRef<Point | null>(roomEnd);
  const initialRoomPointsRef = useRef<Point[]>([]);
  const roomPreviewStartPointRef = useRef<Point | null>(null);
  const roomPreviewEndPointRef = useRef<Point | null>(null);

  const currentWalls = useMemo(() => {
    const wallsForFloor = floorPlans[currentFloor];
    
    // 🔍 DEBUG: Check what wall coordinates are in currentWalls computation
    console.log('[CURRENT_WALLS_USEMEMO] 🔍 Computing currentWalls:', {
      currentFloor,
      wallCount: wallsForFloor?.length || 0,
      firstWallCoords: wallsForFloor?.[0] ? `${wallsForFloor[0].start?.x},${wallsForFloor[0].start?.y} to ${wallsForFloor[0].end?.x},${wallsForFloor[0].end?.y}` : 'no walls',
      coordinateDriftExample: wallsForFloor?.[0] ? {
        startXHasDrift: wallsForFloor[0].start?.x % 0.1 !== 0,
        startYHasDrift: wallsForFloor[0].start?.y % 0.1 !== 0,
        endXHasDrift: wallsForFloor[0].end?.x % 0.1 !== 0,
        endYHasDrift: wallsForFloor[0].end?.y % 0.1 !== 0
      } : 'no walls to check'
    });
    if (!Array.isArray(wallsForFloor)) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`[FloorPlanCreator] currentWalls: floorPlans[${currentFloor}] is not an array! Got:`, wallsForFloor);
      }
      return [];
    }

    const walls = wallsForFloor.map((wall, index) => {
      // Enhanced check for valid wall structure
      if (!wall || typeof wall !== 'object' || 
          !wall.start || typeof wall.start.x !== 'number' || typeof wall.start.y !== 'number' ||
          !wall.end || typeof wall.end.x !== 'number' || typeof wall.end.y !== 'number'
      ) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[FloorPlanCreator] currentWalls: Invalid or incomplete wall data at index ${index} for floor ${currentFloor}. Skipping. Wall data:`, JSON.stringify(wall));
        }
        return null;
      }
      
      const result: WallWithTempId = {
        ...wall,
        // Use existing tempId if available (for old file compatibility), otherwise generate new one
        tempId: (wall as any).tempId || `wall-${currentFloor}-${index}`,
        // Preserve all extrusion properties and their related data
        ...(wall.isExtrusionConnector && { 
          isExtrusionConnector: true,
          parentWallId: (wall as any).parentWallId,
          parentRoomIds: (wall as any).parentRoomIds,
          connectedToSplitWallId: (wall as any).connectedToSplitWallId,
          extrusionGroupId: (wall as any).extrusionGroupId
        }),
        ...(wall.isMainExtrusionSegment && { 
          isMainExtrusionSegment: true,
          parentWallId: (wall as any).parentWallId,
          parentRoomIds: (wall as any).parentRoomIds
        }),
        ...(wall.isSplitOrigin && { 
          isSplitOrigin: true,
          parentWallId: (wall as any).parentWallId,
          parentRoomIds: (wall as any).parentRoomIds,
          requiresExtrusionOnMove: (wall as any).requiresExtrusionOnMove
        })
      };
      
      if (wall.isExtrusionConnector || wall.isMainExtrusionSegment) {
        // console.log('EXTRUSION_WALL_LOAD_DEBUG', { /* ... */ }); // Keep if needed
      }
      
      return result;
    }).filter(Boolean);
    
    return walls as WallWithTempId[];
  }, [floorPlans, currentFloor]);

  // 🚀 PERFORMANCE BOOST: Smart cached room calculation - SINGLE SOURCE OF TRUTH
  // This eliminates 22+ duplicate calculateRooms calls throughout the component
  const currentRooms = useMemo(() => {
    // IMPORTANT: Skip room calculations during wall splitting to prevent race conditions
    // 🔧 CRITICAL FIX: Allow room calculations during resize operations even when wall splitting
    // This prevents empty precalculatedRooms during resize, which forces canvas renderer fallback
    const isActiveResizeOperation = isResizing || !!resizingRoomInfo;
    if (isWallSplitting && !isActiveResizeOperation) {
      // 🔧 ROOM CREATION FIX: Allow room calculation after successful room creation even during wall splitting
      const hasNewRooms = newlyAddedWallsRef.current.length > 0 || lastOperationTypeRef?.current === 'explicitRoomCreation';
      
      if (hasNewRooms) {
        console.log('🔧 WALL SPLIT: Allowing room calculation after room creation despite wall splitting', {
          newWallsAdded: newlyAddedWallsRef.current.length,
          lastOperation: lastOperationTypeRef?.current
        });
      } else {
        console.log('🔧 WALL SPLIT: Skipping room calculation during wall splitting to prevent race conditions');
        return []; // Return empty array during wall splitting (but not during resize)
      }
    }
    
    if (isWallSplitting && isActiveResizeOperation) {
      console.log('🔧 RESIZE DURING SPLIT: Allowing room calculation during resize even with isWallSplitting=true');
    }
    
    // 🔧 CRITICAL FIX: Skip room calculations during label-only operations to prevent label override
    // This prevents the useMemo from triggering a recalculation when roomCustomLabels changes during label save
    // EXCEPTION: During positioning mode, we MUST recalculate to include the newly labeled room
    if (labelsOnlyOperation.current && !isPositioningRoom) {
      console.log('🔧 CURRENT ROOMS: Skipping room calculation during label-only operation to preserve user labels');
      // Return previous calculation or empty array to prevent interference
      return previousCurrentRoomsRef.current || [];
    }
    
    if (labelsOnlyOperation.current && isPositioningRoom) {
      console.log('🔧 POSITIONING MODE: Forcing room recalculation during label-only to include newly labeled room');
    }
    
    // 🔥 PERFORMANCE BOOST: Reduce calculation frequency during post-resize operations
    // This prevents excessive recalculations when multiple state updates happen in quick succession
    if (justResizedRef.current && isResizing === false) {
      // Use a slight delay to allow all post-resize state updates to settle
      console.log('🏠 POST-RESIZE: Throttling room calculation for post-resize stability');
    }
    
    const start = performance.now();
    
    // 🏷️ ROOM LABELING FIX: Use explicitRoomCreation context for labelRoom tool to show ALL rooms
    // geometryUpdate context filters out unlabeled rooms, but labelRoom needs to see all rooms
    
    // 🔧 CRITICAL FIX: Only use endpoint filtering for ACTUAL wall endpoint operations, not general explicitRoomCreation
    // The endpoint filtering should only apply when we're truly connecting walls to create enclosed rooms
    const hasNewlyAddedWalls = newlyAddedWallsRef.current.length > 0;
    const isActualEndpointRoomCreation = lastOperationTypeRef.current === 'explicitRoomCreation' && 
                                        hasNewlyAddedWalls && 
                                        currentTool === 'wall';
    
    // 🔧 RESIZE FIX: Use labelPreservation context during resize to prevent room merging and preserve labels
    const isResizeOperation = isResizing || justResizedRef.current || resizingRoomInfo !== null;
    
    // 🔧 ROOM MERGE FIX: More selective context determination to prevent inappropriate merging
    // Following memory guidance - make endpoint filtering specific to actual wall endpoint operations
    const hasExistingLabels = Object.keys(roomCustomLabels).length > 0;
    
    // 🔧 CRITICAL: More permissive context determination for better room creation detection
    // Allow explicitRoomCreation context for labelRoom tool and general room creation scenarios
    const isValidEndpointRoomCreation = isActualEndpointRoomCreation && 
                                       lastOperationTypeRef.current === 'explicitRoomCreation' && 
                                       hasNewlyAddedWalls && 
                                       currentTool === 'wall';
    
    const needsExplicitContext = currentTool === 'labelRoom' || // Always use explicit context for labelRoom tool
                                isValidEndpointRoomCreation ||  // ONLY for validated endpoint room creation
                                lastOperationTypeRef.current === 'explicitRoomCreation'; // Allow general room creation
    
    // 🔧 ADDITIONAL SAFEGUARD: Use explicitRoomCreation more liberally but still prevent during wall tool unless validated
    const safeContext: RoomCalculationContext = (currentTool === 'wall' && !isValidEndpointRoomCreation && lastOperationTypeRef.current !== 'explicitRoomCreation') ? 'geometryUpdate' : 
                       (isResizeOperation ? 'labelPreservation' : 
                        (needsExplicitContext ? 'explicitRoomCreation' : 'geometryUpdate'));
    
    const context: RoomCalculationContext = safeContext;
    
    // 🔧 CRITICAL FIX: Only pass endpoint context for ACTUAL endpoint room creation, not general operations
    const creationContext = isActualEndpointRoomCreation ? { 
      isFromEndpoint: true,
      originalRoomCount: Object.keys(roomCustomLabels).length, // Track how many rooms had labels before endpoint creation  
      newWalls: [...newlyAddedWallsRef.current] // Include newly added walls
    } : undefined;
    

    
    // 🚨 CRITICAL AREA CALCULATION BUG FIX: Use complete floor plan walls during resize AND endpoint room creation
    // During resize drag preview, currentWalls may only contain ~4 walls from tempWalls (resizing room)
    // During endpoint room creation, currentWalls may also contain incomplete wall set, causing room merging
    // labelPreservation context needs ALL walls for accurate room separation and area calculations
    // Without this fix, area calculations think there's only 1 room and sum areas incorrectly
    const isEndpointRoomCreation = context === 'explicitRoomCreation' || lastOperationTypeRef.current === 'explicitRoomCreation';
    let wallsForCalculation = (isResizeOperation || isEndpointRoomCreation)
      ? (floorPlans[currentFloor] || currentWalls) // Use complete floor plan during resize or endpoint room creation
      : currentWalls; // Use current/temp walls for normal operations
    
    // 🛡️ CRITICAL: Enforce wall-room membership before room calculation
    // This prevents walls from losing their room associations during operations
    // ⚡ SKIP DURING EXPLICIT ROOM CREATION: During room creation, walls are correctly assigned
    // to new room IDs that don't exist yet, so membership enforcement would fail incorrectly
    const skipMembershipEnforcement = isEndpointRoomCreation || 
      (context as RoomCalculationContext) === 'explicitRoomCreation' ||
      (context as RoomCalculationContext) === 'positioningRoomAdjustment' ||
      isPositioningRoom;
    
    if (!skipMembershipEnforcement) {
      try {
        const membershipResult = enforceWallRoomMembership(wallsForCalculation, previousCurrentRoomsRef.current || []);
        
        if (membershipResult.repairsApplied > 0) {
          console.log('🛡️ WALL_ROOM_MEMBERSHIP_REPAIRS_APPLIED', {
            repairsApplied: membershipResult.repairsApplied,
            totalIssues: membershipResult.issues.length,
            context: context || 'general',
            operationType: lastOperationTypeRef?.current,
            wallCount: wallsForCalculation.length
          });
          // Use the repaired walls for calculation
          wallsForCalculation = membershipResult.walls;
        }
      } catch (error) {
        console.warn('🚨 WALL_ROOM_MEMBERSHIP_ENFORCEMENT_FAILED', {
          error: error instanceof Error ? error.message : 'Unknown error',
          fallbackToOriginalWalls: true
        });
        // Continue with original walls if enforcement fails
      }
    } else {
      console.log('⚡ MEMBERSHIP_ENFORCEMENT_SKIPPED', {
        reason: 'explicit room creation in progress',
        context,
        operationType: lastOperationTypeRef?.current,
        wallCount: wallsForCalculation.length
      });
    }
    
    const result = memoizedCalculateRooms(wallsForCalculation, roomCustomLabels, context, currentFloor, labelsOnlyOperation, creationContext, isPositioningRoom);
    
    // 🔧 ENDPOINT ROOM CREATION FIX: Clear tracked walls after successful room calculation
    if (isActualEndpointRoomCreation && newlyAddedWallsRef.current.length > 0) {
      console.log('🔧 ENDPOINT WALL TRACKING: Clearing tracked walls after room calculation', {
        clearedWallsCount: newlyAddedWallsRef.current.length,
        resultRoomCount: result.length
      });
      newlyAddedWallsRef.current = [];
    }
    

    const end = performance.now();
    const calculationTime = end - start;
    
    // OPTIMIZATION: Only log if calculation is expensive to reduce console spam
    if (calculationTime > 10) {
      console.log(`🏠 Smart Room Cache: ${calculationTime.toFixed(1)}ms (${result.length} rooms, context: ${context})`);
    }
    
    if (calculationTime > 500) {
      console.warn(`⚠️ PERFORMANCE: Room calculation took ${calculationTime.toFixed(1)}ms - consider reducing wall complexity or using background processing`);
    }
    
    // 🔧 CRITICAL FIX: Store the result for use during label-only operations
    previousCurrentRoomsRef.current = result;
    
    return result;
  }, [currentWalls, roomCustomLabels, isWallSplitting, currentFloor, currentTool, lastOperationTypeRef.current, isPositioningRoom]);

  // 🔗 PERFORMANCE BOOST: Smart cached shared wall system - PREVENT EXCESSIVE RECALCULATION
  // This prevents the shared wall system from being recalculated on every render
  const memoizedSharedWallSystem = useMemo(() => {
    // Skip shared wall analysis during wall splitting or if no rooms/walls
    if (isWallSplitting || currentWalls.length === 0 || currentRooms.length === 0) {
      return { sharedSegments: [], connections: [], independentSegments: [], roomConnectivity: new Map() };
    }
    
    // 🔥 PERFORMANCE BOOST: During active resize operations, use less frequent updates
    // This prevents the shared wall system from being recalculated excessively during drag operations
    if (isResizing && tempWalls) {
      console.log('🔗 RESIZE MODE: Using stable shared wall system during active resize to prevent excessive recalculation');
      // Return a stable version during resize to prevent constant recalculation
      // The shared wall system will be updated when the resize operation completes
      return { sharedSegments: [], connections: [], independentSegments: [], roomConnectivity: new Map() };
    }
    
    const start = performance.now();
    const plainWalls = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
    const result = analyzeSharedWallSystem(plainWalls, currentRooms);
    const end = performance.now();
    const calculationTime = end - start;
    
    // OPTIMIZATION: Only log if calculation is expensive to reduce console spam
    if (calculationTime > 5) {
      console.log(`🔗 Shared Wall Cache: ${calculationTime.toFixed(1)}ms (${result.sharedSegments.length} segments)`);
    }
    
    return result;
  }, [currentWalls, currentRooms, isWallSplitting, currentFloor, isResizing, tempWalls]);

  // 🔗 PERFORMANCE BOOST: Debounced shared wall system for post-resize operations
  // This prevents excessive recalculations when multiple state updates happen in quick succession
  const [debouncedSharedWallSystem, setDebouncedSharedWallSystem] = useState(memoizedSharedWallSystem);
  
  useEffect(() => {
    // Only debounce if we just completed a resize operation
    if (justResizedRef.current) {
      const debounceTimer = setTimeout(() => {
        setDebouncedSharedWallSystem(memoizedSharedWallSystem);
        console.log('🔗 DEBOUNCED: Updated shared wall system after resize completion');
        justResizedRef.current = false; // Reset the flag after debounced update
      }, 150); // 150ms debounce for post-resize operations
      
      return () => clearTimeout(debounceTimer);
    } else {
      // For non-resize operations, update immediately
      setDebouncedSharedWallSystem(memoizedSharedWallSystem);
    }
  }, [memoizedSharedWallSystem]);

  // PERFORMANCE OPTIMIZATION: Throttled room label state updates to prevent cascading recalculations
  const throttledRoomLabelUpdate = useCallback(
    debounce((updateFunction: (prev: Record<string, string>) => Record<string, string>) => {
      setRoomCustomLabels(updateFunction);
    }, 100), // 100ms throttle to batch updates
    []
  );

  // PERFORMANCE OPTIMIZATION: Batch multiple room label updates
  const batchRoomLabelUpdates = useCallback((updates: Array<{roomId: string, label: string}>) => {
    recordUndoState(); // Single undo state for batch
    
    throttledRoomLabelUpdate(prev => {
      const newLabels = { ...prev };
      updates.forEach(({roomId, label}) => {
        newLabels[roomId] = label;
      });
      return newLabels;
    });
  }, [throttledRoomLabelUpdate]);

  // Define onDebug callback AFTER currentWalls is defined
  const onDebug = useCallback((event: string, data: any) => {
    // 🚪 DOOR EDITING: Handle door selection events and trigger editing modal
    if (event === 'DOOR_SELECTED_FOR_EDITING' && data.doorElement) {
      console.log('🚪 Door selected for editing:', data);
      setDoorToEdit(data.doorElement as DoorElement);
      setIsDoorPromptOpen(true);
    }
    // Debug logging disabled for other events
  }, []);

  useEffect(() => {
    const loadFloorPlan = async () => {
      setIsFileLoading(true);
      const loadingStartTime = Date.now();
      const MIN_LOADING_TIME = 1500; // 1.5 seconds minimum display
      try {
        if (floorPlanId) {
          // console.log(`[FloorPlanCreator] Attempting to load floor plan with ID (filename): ${floorPlanId}`);
          let loadedData;
          
          // Try loading from Supabase first
          try {
            loadedData = await loadFloorPlanFromSupabase(floorPlanId);
          } catch (supabaseError) {
            console.warn('Failed to load from Supabase, trying local file:', supabaseError);
            // Fallback to local file if Supabase fails
            loadedData = await loadFloorPlanFromFile(floorPlanId);
          }

          if (loadedData) {
            // console.log("[FloorPlanCreator] Successfully loaded data:", loadedData);
            
            // 🔧 AUTO-MIGRATION: Apply data migration to bring old files up to current standards
            console.log('[AUTO-MIGRATION] Checking if file needs migration...');
            const { migrateFloorPlanData, needsMigration } = await import('../utils/dataMigration');
            
            let finalData = loadedData;
            if (needsMigration(loadedData)) {
              console.log('[AUTO-MIGRATION] Migrating file to latest format...');
              const migrationResult = migrateFloorPlanData(loadedData, true); // Preserve original metadata
              
              if (migrationResult.success) {
                finalData = migrationResult.migratedData;
                setHasFloorPlanChanges(true); // Mark as changed to trigger autosave with migrated data
                
                console.log(`[AUTO-MIGRATION] Successfully migrated from v${migrationResult.originalVersion} to v${migrationResult.newVersion}`, {
                  issuesFixed: migrationResult.repairsSummary.totalIssuesFixed,
                  wallsRepaired: migrationResult.repairsSummary.wallsRepaired,
                  elementsRepaired: migrationResult.repairsSummary.elementsRepaired,
                });
              } else {
                console.warn('[AUTO-MIGRATION] Migration failed, using original data:', migrationResult.migrationLog);
              }
            } else {
              console.log('[AUTO-MIGRATION] File is already up to date');
            }
            
            // 🚫 AUTO REPAIR CONTROL: Set to false to disable all auto repair phases 1-5
            const ENABLE_AUTO_REPAIR = false;
            
            if (!ENABLE_AUTO_REPAIR) {
              console.log('[AUTO-REPAIR-DISABLED] Auto repair phases 1-5 are disabled. Files will load without automatic repairs.');
              console.log('[AUTO-REPAIR-DISABLED] To enable auto repair, set ENABLE_AUTO_REPAIR = true in FloorPlanCreator.tsx');
            } else {
              console.log('[AUTO-REPAIR-ENABLED] Auto repair phases 1-5 are enabled. Running automatic repairs...');
            }
            
            if (ENABLE_AUTO_REPAIR) {
              // 🔗 PHASE 1 LEGACY REPAIR: Auto-repair extrusion metadata corruption in old files
            console.log('[LEGACY-EXTRUSION-REPAIR] Checking for extrusion metadata corruption...');
            const { shouldApplyLegacyRepair, repairLegacyExtrusionData } = await import('../utils/legacyExtrusionRepair');
            
            const repairCheck = shouldApplyLegacyRepair(finalData, true); // Force corruption check
            if (repairCheck.shouldRepair) {
              console.log(`[LEGACY-EXTRUSION-REPAIR] ${repairCheck.reason} - applying repair...`);
              
              // Apply repair to all floors
              let extrusionRepairedFloorPlans = { ...finalData.floorPlans };
              let totalCorruptionFixed = 0;
              let totalExtrusionsRepaired = 0;
              
              for (const [floorNumStr, walls] of Object.entries(extrusionRepairedFloorPlans)) {
                const floorNum = parseInt(floorNumStr);
                console.log(`[LEGACY-EXTRUSION-REPAIR] Checking floor ${floorNum} with ${walls.length} walls...`);
                
                const repairResult = repairLegacyExtrusionData(walls);
                if (repairResult.repairApplied) {
                  extrusionRepairedFloorPlans[floorNum] = repairResult.repairedWalls;
                  totalCorruptionFixed += repairResult.repairSummary.corruptedExtrusions;
                  totalExtrusionsRepaired += repairResult.repairSummary.repairedExtrusions;
                  
                  console.log(`[LEGACY-EXTRUSION-REPAIR] Floor ${floorNum}: Repaired ${repairResult.repairSummary.repairedExtrusions}/${repairResult.repairSummary.totalExtrusions} extrusions`);
                }
              }
              
              if (totalExtrusionsRepaired > 0) {
                finalData.floorPlans = extrusionRepairedFloorPlans;
                setHasFloorPlanChanges(true); // Mark as changed to trigger autosave with repaired data
                
                // 🔍 DEBUG: Verify repair was applied to finalData
                console.log(`[LEGACY-EXTRUSION-REPAIR] ✅ Repair completed: Fixed ${totalExtrusionsRepaired} extrusions across ${Object.keys(extrusionRepairedFloorPlans).length} floors`);
                console.log('[LEGACY-EXTRUSION-REPAIR] 🔍 DEBUG: Verifying repair application...');
                
                // Quick validation check on finalData
                for (const [floorNumStr, walls] of Object.entries(finalData.floorPlans)) {
                  const { analyzeExtrusionRelationships } = await import('../utils/extrusionMetadataPreservation');
                  const relationships = analyzeExtrusionRelationships(walls);
                  const brokenRelationships = relationships.filter(rel => !rel.isValid);
                  console.log(`[LEGACY-EXTRUSION-REPAIR] 🔍 Floor ${floorNumStr}: ${brokenRelationships.length}/${relationships.length} relationships still broken in finalData`);
                }
                
                // Show user notification about the repair
                onDebug('LEGACY_EXTRUSION_REPAIR_APPLIED', {
                  totalExtrusionsRepaired,
                  totalCorruptionFixed,
                  floorsAffected: Object.keys(extrusionRepairedFloorPlans).length,
                  reason: repairCheck.reason
                });
              } else {
                console.log('[LEGACY-EXTRUSION-REPAIR] No corruption found or repair not needed');
              }
            } else {
              console.log(`[LEGACY-EXTRUSION-REPAIR] Skipped: ${repairCheck.reason}`);
            }
            
            // 🔗 PHASE 2 LEGACY REPAIR: Auto-repair connector duplication in all files
            console.log('[LEGACY-CONNECTOR-REPAIR] Checking for duplicate connector walls...');
            const { removeDuplicateConnectorWalls } = await import('../utils/floorPlanRepairSystem');
            
            let connectorRepairedFloorPlans = { ...finalData.floorPlans };
            let totalConnectorDuplicatesRemoved = 0;
            let floorsWithConnectorRepairs = 0;
            
            for (const [floorNumStr, walls] of Object.entries(connectorRepairedFloorPlans)) {
              const floorNum = parseInt(floorNumStr);
              console.log(`[LEGACY-CONNECTOR-REPAIR] Checking floor ${floorNum} with ${walls.length} walls...`);
              
              const repairResult = removeDuplicateConnectorWalls(walls as any);
              if (repairResult.operations.length > 0) {
                connectorRepairedFloorPlans[floorNum] = repairResult.walls;
                totalConnectorDuplicatesRemoved += repairResult.operations.length;
                floorsWithConnectorRepairs++;
                
                console.log(`[LEGACY-CONNECTOR-REPAIR] Floor ${floorNum}: Removed ${repairResult.operations.length} duplicate connector walls`);
              }
            }
            
            if (totalConnectorDuplicatesRemoved > 0) {
              finalData.floorPlans = connectorRepairedFloorPlans;
              setHasFloorPlanChanges(true); // Mark as changed to trigger autosave with repaired data
              
              console.log(`[LEGACY-CONNECTOR-REPAIR] ✅ Repair completed: Removed ${totalConnectorDuplicatesRemoved} duplicate connectors across ${floorsWithConnectorRepairs} floors`);
              
              // Show user notification about the repair
              onDebug('LEGACY_CONNECTOR_REPAIR_APPLIED', {
                totalDuplicatesRemoved: totalConnectorDuplicatesRemoved,
                floorsAffected: floorsWithConnectorRepairs,
                reason: 'Phase 2 - Automatic connector duplication cleanup'
              });
            } else {
              console.log('[LEGACY-CONNECTOR-REPAIR] No duplicate connector walls found');
            }
            
            // 🔗 PHASE 3 LEGACY REPAIR: Auto-repair room-wall relationship corruption in all files
            console.log('[LEGACY-ROOM-WALL-REPAIR] Checking for room-wall relationship corruption...');
            const { repairRoomWallRelationships } = await import('../utils/roomWallRelationshipSystem');
            
            let roomWallRepairedFloorPlans = { ...finalData.floorPlans };
            let totalRoomWallIssuesRepaired = 0;
            let floorsWithRoomWallRepairs = 0;
            
            for (const [floorNumStr, walls] of Object.entries(roomWallRepairedFloorPlans)) {
              const floorNum = parseInt(floorNumStr);
              console.log(`[LEGACY-ROOM-WALL-REPAIR] Checking floor ${floorNum} with ${walls.length} walls...`);
              
              // Get rooms from stored data first to detect corruption in stored relationships
              const { calculateRooms } = await import('../utils/floorPlanUtils');
              const storedRooms = calculateRooms(walls as any);
              
              // 🔧 CRITICAL FIX: Use the SAME analysis method as diagnostics to detect corruption  
              const { diagnoseFloorPlan } = await import('../utils/floorPlanDiagnostics');
              const diagnosticAnalysis = diagnoseFloorPlan(walls as any, storedRooms, {}, floorNum);
              
              console.log(`[LEGACY-ROOM-WALL-REPAIR] Floor ${floorNum} diagnostic analysis: ${diagnosticAnalysis.totalIssues} total issues (${diagnosticAnalysis.criticalIssues} critical)`, {
                roomIssues: diagnosticAnalysis.results.filter(r => r.category === 'rooms').length,
                summary: diagnosticAnalysis.summary
              });
              
              // Also run repair-specific analysis for detailed issue types
              const { analyzeRoomWallRelationships } = await import('../utils/roomWallRelationshipSystem');
              const repairAnalysis = analyzeRoomWallRelationships(walls as any, storedRooms);
              
              console.log(`[LEGACY-ROOM-WALL-REPAIR] Floor ${floorNum} repair analysis: ${repairAnalysis.issues.length} repair-specific issues found`, {
                issueTypes: repairAnalysis.issues.reduce((acc, issue) => {
                  acc[issue.type] = (acc[issue.type] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>)
              });
              
              // Run repair to clean up wall metadata corruption
              const repairResult = repairRoomWallRelationships(walls as any, storedRooms);
              if (repairResult.success && repairResult.issuesRepaired > 0) {
                roomWallRepairedFloorPlans[floorNum] = repairResult.repairedWalls;
                totalRoomWallIssuesRepaired += repairResult.issuesRepaired;
                floorsWithRoomWallRepairs++;
                
                console.log(`[LEGACY-ROOM-WALL-REPAIR] Floor ${floorNum}: Repaired ${repairResult.issuesRepaired} room-wall relationship issues`);
                console.log(`[LEGACY-ROOM-WALL-REPAIR] Floor ${floorNum} operations:`, repairResult.operations);
              }
            }
            
            if (totalRoomWallIssuesRepaired > 0) {
              finalData.floorPlans = roomWallRepairedFloorPlans;
              setHasFloorPlanChanges(true); // Mark as changed to trigger autosave with repaired data
              
              console.log(`[LEGACY-ROOM-WALL-REPAIR] ✅ Repair completed: Fixed ${totalRoomWallIssuesRepaired} room-wall issues across ${floorsWithRoomWallRepairs} floors`);
              
              // Show user notification about the repair
              onDebug('LEGACY_ROOM_WALL_REPAIR_APPLIED', {
                totalIssuesRepaired: totalRoomWallIssuesRepaired,
                floorsAffected: floorsWithRoomWallRepairs,
                reason: 'Phase 3 - Automatic room-wall relationship cleanup'
              });
            } else {
              console.log('[LEGACY-ROOM-WALL-REPAIR] No room-wall relationship issues found');
            }
            
            // 🆔 PHASE 4 LEGACY REPAIR: Auto-repair wall identity corruption in all files
            console.log('[LEGACY-WALL-IDENTITY-REPAIR] Checking for wall identity corruption...');
            const { fixWallIdentityIssues } = await import('../utils/floorPlanRepairSystem');
            
            let wallIdentityRepairedFloorPlans = { ...finalData.floorPlans };
            let totalWallIdentityIssuesRepaired = 0;
            let floorsWithWallIdentityRepairs = 0;
            
            for (const [floorNumStr, walls] of Object.entries(wallIdentityRepairedFloorPlans)) {
              const floorNum = parseInt(floorNumStr);
              console.log(`[LEGACY-WALL-IDENTITY-REPAIR] Checking floor ${floorNum} with ${walls.length} walls...`);
              
              const identityRepairResult = fixWallIdentityIssues(walls as any);
              if (identityRepairResult.operations.length > 0) {
                wallIdentityRepairedFloorPlans[floorNum] = identityRepairResult.walls;
                totalWallIdentityIssuesRepaired += identityRepairResult.operations.length;
                floorsWithWallIdentityRepairs++;
                
                console.log(`[LEGACY-WALL-IDENTITY-REPAIR] Floor ${floorNum}: Repaired ${identityRepairResult.operations.length} wall identity issues`);
                console.log(`[LEGACY-WALL-IDENTITY-REPAIR] Floor ${floorNum} operations:`, identityRepairResult.operations.map((op: any) => op.description));
              }
            }
            
            if (totalWallIdentityIssuesRepaired > 0) {
              finalData.floorPlans = wallIdentityRepairedFloorPlans;
              setHasFloorPlanChanges(true); // Mark as changed to trigger autosave with repaired data
              
              console.log(`[LEGACY-WALL-IDENTITY-REPAIR] ✅ Repair completed: Fixed ${totalWallIdentityIssuesRepaired} wall identity issues across ${floorsWithWallIdentityRepairs} floors`);
              
              // Show user notification about the repair
              onDebug('LEGACY_WALL_IDENTITY_REPAIR_APPLIED', {
                totalIssuesRepaired: totalWallIdentityIssuesRepaired,
                floorsAffected: floorsWithWallIdentityRepairs,
                reason: 'Phase 4 - Automatic wall identity repair and tempId generation'
              });
            } else {
              console.log('[LEGACY-WALL-IDENTITY-REPAIR] No wall identity issues found');
            }
            
            // 🎯 PHASE 5 LEGACY REPAIR: Auto-repair coordinate precision drift in all files
            console.log('[LEGACY-COORDINATE-REPAIR] Checking for coordinate precision drift...');
            const { sanitizeWallCoordinates } = await import('../utils/geometryTolerance');
            
            let coordinateRepairedFloorPlans = { ...finalData.floorPlans };
            let totalCoordinateIssuesRepaired = 0;
            let floorsWithCoordinateRepairs = 0;
            
            for (const [floorNumStr, walls] of Object.entries(coordinateRepairedFloorPlans)) {
              const floorNum = parseInt(floorNumStr);
              console.log(`[LEGACY-COORDINATE-REPAIR] Checking floor ${floorNum} with ${walls.length} walls...`);
              
              const coordinateRepairResult = sanitizeWallCoordinates(walls as any, {
                precision: 0.1,
                removeZeroLength: true,
                minLength: 0.1
              });
              
              if (coordinateRepairResult.wallsModified > 0 || coordinateRepairResult.zeroLengthWallsRemoved > 0) {
                coordinateRepairedFloorPlans[floorNum] = coordinateRepairResult.sanitizedWalls;
                totalCoordinateIssuesRepaired += coordinateRepairResult.coordinatesDriftFixed + coordinateRepairResult.zeroLengthWallsRemoved;
                floorsWithCoordinateRepairs++;
                
                console.log(`[LEGACY-COORDINATE-REPAIR] Floor ${floorNum}: Fixed ${coordinateRepairResult.coordinatesDriftFixed} coordinate drift + removed ${coordinateRepairResult.zeroLengthWallsRemoved} zero-length walls`);
                console.log(`[LEGACY-COORDINATE-REPAIR] Floor ${floorNum} operations:`, coordinateRepairResult.operations);
              }
            }
            
            if (totalCoordinateIssuesRepaired > 0) {
              finalData.floorPlans = coordinateRepairedFloorPlans;
              setHasFloorPlanChanges(true); // Mark as changed to trigger autosave with repaired data
              
              console.log(`[LEGACY-COORDINATE-REPAIR] ✅ Repair completed: Fixed ${totalCoordinateIssuesRepaired} coordinate precision issues across ${floorsWithCoordinateRepairs} floors`);
              
              // Show user notification about the repair
              onDebug('LEGACY_COORDINATE_REPAIR_APPLIED', {
                totalIssuesRepaired: totalCoordinateIssuesRepaired,
                floorsAffected: floorsWithCoordinateRepairs,
                reason: 'Phase 5 - Automatic coordinate precision drift repair and zero-length wall removal'
              });
              
              // 🔥 CRITICAL: Immediately save repaired data to prevent double-load from reading unrepaired file
              console.log('[LEGACY-COORDINATE-REPAIR] 💾 Immediately saving repaired data to prevent double-load issues...');
              try {
                const { saveFloorPlanToSupabase } = await import('../utils/supabaseStorage');
                await saveFloorPlanToSupabase(finalData.propertyAddress || 'Repaired Floor Plan', {
                  ...finalData,
                  floorPlans: coordinateRepairedFloorPlans
                }, floorPlanId, 'estate');
                console.log('[LEGACY-COORDINATE-REPAIR] ✅ Repaired data saved successfully');
              } catch (saveError) {
                console.warn('[LEGACY-COORDINATE-REPAIR] ⚠️ Failed to save repaired data:', saveError);
              }
            } else {
              console.log('[LEGACY-COORDINATE-REPAIR] No coordinate precision issues found');
            }
            
            // 🎯 PHASE 5B LEGACY REPAIR: Auto-repair coordinate precision drift in elements
            console.log('[LEGACY-ELEMENT-COORDINATE-REPAIR] Checking for element coordinate precision drift...');
            const { sanitizeElementCoordinates } = await import('../utils/geometryTolerance');
            
            let coordinateRepairedElements = finalData.elements || [];
            let totalElementCoordinateIssuesRepaired = 0;
            
            if (coordinateRepairedElements.length > 0) {
              const elementRepairResult = sanitizeElementCoordinates(coordinateRepairedElements, {
                precision: 0.1
              });
              
              if (elementRepairResult.elementsModified > 0) {
                finalData.elements = elementRepairResult.sanitizedElements;
                totalElementCoordinateIssuesRepaired = elementRepairResult.coordinatesDriftFixed;
                setHasFloorPlanChanges(true); // Mark as changed to trigger autosave with repaired data
                
                console.log(`[LEGACY-ELEMENT-COORDINATE-REPAIR] ✅ Element repair completed: Fixed ${totalElementCoordinateIssuesRepaired} coordinate precision issues in ${elementRepairResult.elementsModified} elements`);
                console.log('[LEGACY-ELEMENT-COORDINATE-REPAIR] Element operations:', elementRepairResult.operations);
                
                // Show user notification about the element repair
                onDebug('LEGACY_ELEMENT_COORDINATE_REPAIR_APPLIED', {
                  totalIssuesRepaired: totalElementCoordinateIssuesRepaired,
                  elementsAffected: elementRepairResult.elementsModified,
                  reason: 'Phase 5B - Automatic element coordinate precision drift repair'
                });
                
                // 🔥 CRITICAL: Immediately save repaired element data
                console.log('[LEGACY-ELEMENT-COORDINATE-REPAIR] 💾 Immediately saving repaired element data...');
                try {
                  const { saveFloorPlanToSupabase } = await import('../utils/supabaseStorage');
                  await saveFloorPlanToSupabase(finalData.propertyAddress || 'Repaired Floor Plan', finalData, floorPlanId, 'estate');
                  console.log('[LEGACY-ELEMENT-COORDINATE-REPAIR] ✅ Repaired element data saved successfully');
                } catch (saveError) {
                  console.warn('[LEGACY-ELEMENT-COORDINATE-REPAIR] ⚠️ Failed to save repaired element data:', saveError);
                }
              } else {
                console.log('[LEGACY-ELEMENT-COORDINATE-REPAIR] No element coordinate precision issues found');
              }
            } else {
              console.log('[LEGACY-ELEMENT-COORDINATE-REPAIR] No elements found to repair');
            }
            
            // 🎯 PHASE 5C LEGACY REPAIR: Auto-repair coordinate precision drift in room label positions
            console.log('[LEGACY-ROOM-LABEL-COORDINATE-REPAIR] Checking for room label coordinate precision drift...');
            
            let coordinateRepairedRoomLabelPositions = finalData.roomLabelPositions || {};
            let totalRoomLabelCoordinateIssuesRepaired = 0;
            
            if (Object.keys(coordinateRepairedRoomLabelPositions).length > 0) {
              const { snapPointToGrid, hasPointDrift } = await import('../utils/geometryTolerance');
              
              let roomLabelPositionsModified = 0;
              const roomLabelOperations: string[] = [];
              
              // Repair each room label position
              Object.entries(coordinateRepairedRoomLabelPositions).forEach(([roomId, position]) => {
                if (position && typeof position === 'object' && 'x' in position && 'y' in position) {
                  if (hasPointDrift(position, 0.1)) {
                    const originalPosition = { ...position };
                    const snappedPosition = snapPointToGrid(position, 0.1);
                    
                    coordinateRepairedRoomLabelPositions[roomId] = snappedPosition;
                    totalRoomLabelCoordinateIssuesRepaired++;
                    roomLabelPositionsModified++;
                    
                    console.log(`🎯 ROOM_LABEL_COORDINATE_DRIFT_FIXED ${roomId}`, {
                      roomId,
                      originalPosition,
                      snappedPosition
                    });
                    
                    roomLabelOperations.push(`Fixed coordinate drift for room label ${roomId}`);
                  }
                }
              });
              
              if (roomLabelPositionsModified > 0) {
                finalData.roomLabelPositions = coordinateRepairedRoomLabelPositions;
                setHasFloorPlanChanges(true); // Mark as changed to trigger autosave with repaired data
                
                console.log(`[LEGACY-ROOM-LABEL-COORDINATE-REPAIR] ✅ Room label repair completed: Fixed ${totalRoomLabelCoordinateIssuesRepaired} coordinate precision issues in ${roomLabelPositionsModified} room labels`);
                console.log('[LEGACY-ROOM-LABEL-COORDINATE-REPAIR] Room label operations:', roomLabelOperations);
                
                // Show user notification about the room label repair
                onDebug('LEGACY_ROOM_LABEL_COORDINATE_REPAIR_APPLIED', {
                  totalIssuesRepaired: totalRoomLabelCoordinateIssuesRepaired,
                  roomLabelsAffected: roomLabelPositionsModified,
                  reason: 'Phase 5C - Automatic room label coordinate precision drift repair'
                });
                
                // 🔥 CRITICAL: Immediately save repaired room label data
                console.log('[LEGACY-ROOM-LABEL-COORDINATE-REPAIR] 💾 Immediately saving repaired room label data...');
                try {
                  const { saveFloorPlanToSupabase } = await import('../utils/supabaseStorage');
                  await saveFloorPlanToSupabase(finalData.propertyAddress || 'Repaired Floor Plan', finalData, floorPlanId, 'estate');
                  console.log('[LEGACY-ROOM-LABEL-COORDINATE-REPAIR] ✅ Repaired room label data saved successfully');
                } catch (saveError) {
                  console.warn('[LEGACY-ROOM-LABEL-COORDINATE-REPAIR] ⚠️ Failed to save repaired room label data:', saveError);
                }
              } else {
                console.log('[LEGACY-ROOM-LABEL-COORDINATE-REPAIR] No room label coordinate precision issues found');
              }
            } else {
              console.log('[LEGACY-ROOM-LABEL-COORDINATE-REPAIR] No room label positions found to repair');
            }
            
            } // End of ENABLE_AUTO_REPAIR conditional block
            
            // 🔧 ROOM OWNERSHIP MIGRATION: Auto-assign ownership to old files
            console.log('[ROOM-OWNERSHIP-MIGRATION] Checking for old files that need room ownership migration...');
            let migratedFloorPlans = finalData.floorPlans || { 1: [] };
            const hasRoomLabels = Object.keys(finalData.roomCustomLabels || {}).length > 0;
            
            if (hasRoomLabels) {
              // Check each floor for unowned walls
              for (const [floorNumStr, walls] of Object.entries(migratedFloorPlans)) {
                const floorNum = parseInt(floorNumStr);
                const unownedWalls = walls.filter((w: any) => !w.originatingRoomId && !w.assignedRoomIds?.length);
                
                if (unownedWalls.length > 0) {
                  console.log(`[ROOM-OWNERSHIP-MIGRATION] Floor ${floorNum}: Found ${unownedWalls.length} unowned walls, auto-assigning ownership...`);
                  
                  try {
                    // Import room calculation utilities
                    const { calculateRooms, getPersistentRoomId } = await import('../utils/floorPlanUtils');
                    
                    // Calculate rooms to get geometric room boundaries
                    const detectedRooms = calculateRooms(walls, {}, 'explicitRoomCreation', floorNum);
                    console.log(`[ROOM-OWNERSHIP-MIGRATION] Floor ${floorNum}: Detected ${detectedRooms.length} geometric rooms`);
                    
                    // Create mapping from old room labels to canonical stable IDs
                    const roomLabelToStableId = new Map<string, string>();
                    
                    // Find matching room labels for detected rooms
                    detectedRooms.forEach(room => {
                      // Look for existing labels that might match this room
                      const matchingLabels = Object.entries(finalData.roomCustomLabels || {}).filter(([labelKey, labelValue]) => {
                        // Check if label value matches room name or if key contains room identifiers
                        return labelValue === room.name || labelKey.includes(room.name) || 
                               (typeof labelValue === 'string' && room.name.includes(labelValue));
                      });
                      
                      if (matchingLabels.length > 0 && room.stableId) {
                        const [, labelValue] = matchingLabels[0];
                        roomLabelToStableId.set(labelValue, room.stableId);
                        console.log(`[ROOM-OWNERSHIP-MIGRATION] Floor ${floorNum}: Mapped label "${labelValue}" to stable ID "${room.stableId.substring(0, 30)}..."`);
                      }
                    });
                    
                    // Assign ownership to unowned walls based on geometric containment
                    const updatedWalls = walls.map((wall: any) => {
                      if (wall.originatingRoomId || wall.assignedRoomIds?.length) {
                        return wall; // Already has ownership
                      }
                      
                      // Find which detected room contains this wall
                      const containingRoom = detectedRooms.find(room => 
                        room.walls.some(roomWall => 
                          Math.abs(roomWall.start.x - wall.start.x) < 0.1 &&
                          Math.abs(roomWall.start.y - wall.start.y) < 0.1 &&
                          Math.abs(roomWall.end.x - wall.end.x) < 0.1 &&
                          Math.abs(roomWall.end.y - wall.end.y) < 0.1
                        )
                      );
                      
                      if (containingRoom?.stableId) {
                        console.log(`[ROOM-OWNERSHIP-MIGRATION] Floor ${floorNum}: Assigning wall (${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)}) to room "${containingRoom.stableId.substring(0, 30)}..."`);
                        return {
                          ...wall,
                          originatingRoomId: containingRoom.stableId,
                          assignedRoomIds: [containingRoom.stableId]
                        };
                      }
                      
                      return wall; // Couldn't assign ownership
                    });
                    
                    // Update the floor plan with walls that now have ownership
                    migratedFloorPlans[floorNum] = updatedWalls;
                    const assignedCount = updatedWalls.filter((w: any) => w.originatingRoomId).length;
                    console.log(`[ROOM-OWNERSHIP-MIGRATION] Floor ${floorNum}: Successfully assigned ownership to ${assignedCount}/${walls.length} walls`);
                    
                  } catch (error) {
                    console.warn(`[ROOM-OWNERSHIP-MIGRATION] Floor ${floorNum}: Migration failed:`, error);
                  }
                } else {
                  console.log(`[ROOM-OWNERSHIP-MIGRATION] Floor ${floorNum}: All walls already have ownership, skipping`);
                }
              }
            } else {
              console.log('[ROOM-OWNERSHIP-MIGRATION] No room labels found, skipping ownership migration');
            }
            
            setFloorPlans(finalData.floorPlans);
            
            // 🔍 DEBUG: Verify repaired data is being set into state
            console.log('[LEGACY-COORDINATE-REPAIR] 🔍 Setting repaired floorPlans into state:', {
              totalFloors: Object.keys(finalData.floorPlans).length,
              sampleWallCoords: Object.entries(finalData.floorPlans).map(([floor, walls]) => ({
                floor,
                wallCount: walls.length,
                firstWallCoords: walls[0] ? `${walls[0].start.x},${walls[0].start.y} to ${walls[0].end.x},${walls[0].end.y}` : 'no walls'
              }))
            });
            setCurrentFloor(finalData.currentFloor || 1);
            setElements(finalData.elements || []);
            setTextBoxes(finalData.textBoxes || []);
            // Reset migration flag so window IDs are assigned to loaded elements
            setWindowIdsMigrated(false);
            // Migrate old room labels to new floor-specific format
            const migratedRoomLabels = migrateOldRoomLabelsToFloorFormat(
              finalData.roomCustomLabels || {}, 
              finalData.floorPlans,
              finalData.currentFloor || 1
            );
            setRoomCustomLabels(migratedRoomLabels);
            setRoomLabelPositions(finalData.roomLabelPositions || {}); // Load room label positions
            const loadedAddress = finalData.propertyAddress || '';
            console.log('📍 Loading property address from saved data:', {
              loadedAddress,
              willEnableAutosave: !!loadedAddress
            });
            setPropertyAddress(loadedAddress);
            setPropertyOrientation(finalData.propertyOrientation || 0);
            setOrientationHasBeenSet(true); // Existing plans have already gone through orientation setup
            setWindowCount(finalData.windowCount || { single: 0, double: 0 });
            setLightCount(finalData.lightCount || { nonLed: 0, led: 0 });
            // Fix: Only use fallback if siteNotesData is truly missing (null/undefined), not if it's an object with empty values
            // Also preserve any existing selectedPCDBSystem and heatingSystems data
            let siteNotesToLoad;
            if (finalData.siteNotesData !== null && finalData.siteNotesData !== undefined) {
              // Create default form data structure
              const defaultFormData = {
                address: '',
                propertyType: '',
                detachment: '',
                ageBand: '',
                groundLevel: '',
                roofTypeRdSAP: '',
                wallType: '',
                partyWall: '',
                wallThickness: '',
                roofType: '',
                roofInsulation: '',
                floorType: '',
                // Habitable rooms fields
                totalHabitableRooms: '',
                totalHeatedHabitableRooms: '',
                heatingType: '',
                heatingSystemType: '',
                heatingFuelType: '',
                heatingBrand: '',
                heatingModel: '',
                heatingSerialNumber: '',
                totalRads: '0',
                trvs: '0',
                tRad: '',
                heatingSourceLocation: '',
                tankLocation: '',
                totalWindows: '0',
                glazingType: '',
                windowType: '',
                windowFrameType: '',
                glazingThickness: '',
                windowsMissingTrickleVents: '0',
                missingTrickleVentsLocation: '',
                mevsKitchen: '',
                mevsBathroom: '',
                mevsEnSuite: '',
                mevsWC: '',
                mevsUtility: '',
                totalDoors: '0',
                doorsWithoutUndercuts: '0',
                doorsWithoutUndercutsLocation: '',
                // Additional ventilation fields
                openChimneys: '0',
                openFlues: '0',
                openChimneysFluesAttachedToClose: '0',
                fluesAttachedToSolidFuelBoiler: '0',
                openFluesAttachedToOtherHeater: '0',
                blockedChimneys: '0',
                intermittentExtractFans: '0',
                passiveVents: '0',
                fluelessGasFires: '0',
                fixedSpaceCooling: '',
                draughtLobby: '',
                gmLocation: '',
                emLocations: '',
                stapLocation: '',
                additionalNotes: '',
                totalLightsX: '',
                totalLightsO: '',
                totalLEL: '',
                wallInsulationType: '',
                wallInsulationElevationsCount: '',
                wallInsulationElevations: [],
              };
              
              // Merge defaults with saved data (saved data takes precedence)
              siteNotesToLoad = {
                ...finalData.siteNotesData,
                formData: {
                  ...defaultFormData,
                  ...finalData.siteNotesData.formData
                }
              };
            } else {
              siteNotesToLoad = {
                formData: {
                    address: {
                      houseNumber: '',
                      street: '',
                      town: '',
                      postcode: '',
                    },
                    propertyType: '',
                    detachment: '',
                    ageBand: '',
                    groundLevel: '',
                    roofTypeRdSAP: '',
                    wallType: '',
                    partyWall: '',
                    wallThickness: '',
                    roofType: '',
                    roofInsulation: '',
                    floorType: '',
                    // Habitable rooms fields
                    totalHabitableRooms: '',
                    totalHeatedHabitableRooms: '',
                    heatingType: '',
                    heatingSystemType: '',
                    heatingFuelType: '',
                    heatingBrand: '',
                    heatingModel: '',
                    heatingSerialNumber: '',
                    totalRads: '0',
                    trvs: '0',
                    tRad: '',
                    heatingSourceLocation: '',
                    tankLocation: '',
                    totalWindows: '0',
                    glazingType: '',
                    windowType: '',
                    windowFrameType: '',
                    glazingThickness: '',
                    windowsMissingTrickleVents: '0',
                    missingTrickleVentsLocation: '',
                    mevsKitchen: '',
                    mevsBathroom: '',
                    mevsEnSuite: '',
                    mevsWC: '',
                    mevsUtility: '',
                    totalDoors: '0',
                    doorsWithoutUndercuts: '0',
                    doorsWithoutUndercutsLocation: '',
                    // Additional ventilation fields
                    openChimneys: '0',
                    openFlues: '0',
                    openChimneysFluesAttachedToClose: '0',
                    fluesAttachedToSolidFuelBoiler: '0',
                    openFluesAttachedToOtherHeater: '0',
                    blockedChimneys: '0',
                    intermittentExtractFans: '0',
                    passiveVents: '0',
                    fluelessGasFires: '0',
                    fixedSpaceCooling: '',
                    draughtLobby: '',
                    gmLocation: '',
                    emLocations: '',
                    stapLocation: '',
                    additionalNotes: '',
                    totalLightsX: '',
                    totalLightsO: '',
                    totalLEL: '',
                    wallInsulationType: '',
                    wallInsulationElevationsCount: '',
                    wallInsulationElevations: [],
                  },
                  floors: [
                    {
                      id: 1,
                      floorLevel: 'ground floor',
                      buildingType: 'main building',
                      height: '',
                      floorArea: '',
                      hlp: '',
                      pw: '',
                      rooms: [
                        {
                          id: 1,
                          length: '',
                          width: '',
                          height: '',
                          lowEnergyLights: '0',
                          nonLowEnergyLights: '0',
                          radiatorWidth: '',
                          radiatorHeight: '',
                          radiatorType: '',
                          windowWidth: '',
                          windowHeight: '',
                
                          windowTrickleVents: '',
                          windows: [
                            {
                              id: 1,
                              width: '',
                              height: '',
                              orientation: ''
                            }
                          ]
                        }
                      ]
                    }
                  ]
                };
              }
            console.log('📥 Loading site notes data from saved plan:', { 
              fromFile: loadedData.siteNotesData, 
              finalData: siteNotesToLoad,
              hasSelectedPCDBSystem: !!siteNotesToLoad.selectedPCDBSystem,
              hasHeatingSystems: !!(siteNotesToLoad.heatingSystems && siteNotesToLoad.heatingSystems.length > 0),
              pcdbSystemData: siteNotesToLoad.selectedPCDBSystem ? {
                id: siteNotesToLoad.selectedPCDBSystem.id,
                brand: siteNotesToLoad.selectedPCDBSystem.brand,
                model: siteNotesToLoad.selectedPCDBSystem.model
              } : null,
              heatingSystemsCount: siteNotesToLoad.heatingSystems?.length || 0
            });
            console.log('📂 FULL FILE DATA loaded:', {
              floorPlans: loadedData.floorPlans,
              currentFloor: loadedData.currentFloor,
              elements: loadedData.elements?.length || 0,
              textBoxes: loadedData.textBoxes?.length || 0,
              roomCustomLabels: Object.keys(loadedData.roomCustomLabels || {}).length,
              propertyAddress: loadedData.propertyAddress,
              windowCount: loadedData.windowCount,
              lightCount: loadedData.lightCount,
              hasFloorPlanData: Object.keys(loadedData.floorPlans || {}).length > 0,
              dataKeys: Object.keys(loadedData)
            });
            setSiteNotesData(siteNotesToLoad);
            setSiteNotesDataReady(true); // Mark site notes data as ready
            console.log('✅ Site notes data loaded and ready for use');
            
            // 🎯 AUTO-CENTER: Center the canvas on floor plan content when loading
            // Use saved view settings if they exist, otherwise auto-center the content
            const shouldUseAutoCentering = !loadedData.viewOffset || !loadedData.scale;
            
            if (shouldUseAutoCentering) {
              console.log('🎯 Auto-centering canvas on floor plan content...');
              
              // Get the canvas container dimensions for centering calculation
              const getContainerDimensions = () => {
                if (canvasRef.current && canvasRef.current.parentElement) {
                  const container = canvasRef.current.parentElement;
                  return {
                    width: container.clientWidth || 750,
                    height: container.clientHeight || 600
                  };
                }
                return { width: 750, height: 600 }; // Default fallback
              };
              
              // Use a small delay to ensure DOM is ready for dimension calculation
              setTimeout(() => {
                const containerDims = getContainerDimensions();
                const currentFloorNumber = finalData.currentFloor || 1;
                const currentFloorWalls = finalData.floorPlans[currentFloorNumber] || [];
                const currentFloorElements = (finalData.elements || []).filter((el: any) => (el.floor === currentFloorNumber));
                const currentFloorTextBoxes = (finalData.textBoxes || []).filter((tb: any) => (tb.floor === currentFloorNumber));
                
                console.log('🎯 Centering calculation data:', {
                  floor: currentFloorNumber,
                  walls: currentFloorWalls.length,
                  elements: currentFloorElements.length,
                  textBoxes: currentFloorTextBoxes.length,
                  containerDims
                });
                
                // Import and use the centering utility
                import('../utils/viewUtils').then(({ centerFloorPlanContent }) => {
                  const centeringResult = centerFloorPlanContent(
                    currentFloorWalls,
                    currentFloorElements,
                    currentFloorTextBoxes,
                    containerDims.width,
                    containerDims.height,
                    0.85 // Use 85% of container space with 15% padding
                  );
                  
                  if (centeringResult) {
                    console.log('🎯 Applying auto-centering:', {
                      scale: centeringResult.scale.toFixed(2),
                      viewOffset: {
                        x: centeringResult.viewOffset.x.toFixed(1),
                        y: centeringResult.viewOffset.y.toFixed(1)
                      }
                    });
                    
                    setScale(centeringResult.scale);
                    setViewOffset(centeringResult.viewOffset);
                  } else {
                    console.log('🎯 No content to center, using default view settings');
                    setScale(INITIAL_SCALE);
                    setViewOffset({ x: 0, y: 0 });
                  }
                }).catch(error => {
                  console.warn('🎯 Failed to import centering utilities:', error);
                  // Fallback to default settings
                  setScale(INITIAL_SCALE);
                  setViewOffset({ x: 0, y: 0 });
                });
              }, 100); // Small delay to ensure DOM is ready
            } else {
              // Use saved view settings
              console.log('📍 Using saved view settings from file');
              setViewOffset(loadedData.viewOffset!);
              setScale(loadedData.scale!);
            }
            setUndoStack([]);
            setRedoStack([]);
            setCurrentFloorPlanId(floorPlanId);
            setShowOnboardingModal(false); // Don't show modal for existing plans
            
            // **FIX: Enable autosave if an address is loaded**
            if (loadedData.propertyAddress) {
              console.log('🎯 Enabling autosave for existing file with address:', loadedData.propertyAddress);
              autosave.enableAutosave({ address: loadedData.propertyAddress });
            }
            
            // 📐 Initial floor dimension sync after data is loaded (delayed to ensure roomSync is available)
            setTimeout(() => {
              if (roomSync) {
                console.log('📐 Auto-calculating initial floor dimensions for all floors...');
                Object.keys(loadedData.floorPlans || {}).forEach(floorNum => {
                  const floorNumber = parseInt(floorNum);
                  const walls = loadedData.floorPlans[floorNumber] || [];
                  if (walls.length > 0) {
                    try {
                      const rooms = calculateRooms(walls, loadedData.roomCustomLabels || {}, 'explicitRoomCreation', floorNumber);
                      // Removed: roomSync.syncFloorDimensions(rooms, walls, floorNumber, true);
                      // console.log(`  📐 Calculated initial dimensions for floor ${floorNumber}`);
                    } catch (error) {
                      console.warn(`Failed to calculate initial dimensions for floor ${floorNumber}:`, error);
                    }
                  }
                });
              }
            }, 100); // Small delay to ensure all state is settled
            // console.log("[FloorPlanCreator] States updated from loaded file.");
          } else {
            // console.warn(`[FloorPlanCreator] No data returned or file empty/malformed for ID: ${floorPlanId}. Starting fresh.`);
            // 🔄 Reset room ID system for new file so room numbering starts at room_1
            const { resetRoomIdSystem } = await import('../utils/floorPlanUtils');
            resetRoomIdSystem();
            setFloorPlans({ 1: [] });
            setCurrentFloor(1);
            setElements([]);
            setTextBoxes([]);
            setRoomCustomLabels({});
            setPropertyAddress('');
            // Reset migration flag for new projects
            setWindowIdsMigrated(false);
            setPropertyOrientation(0);
            setWindowCount({ single: 0, double: 0 });
            setLightCount({ nonLed: 0, led: 0 });
            setUndoStack([]);
            setRedoStack([]);
            setViewOffset({ x: 0, y: 0 });
            setScale(INITIAL_SCALE);
            setCurrentFloorPlanId(null);
            setSiteNotesDataReady(true); // Default site notes data is ready
            setShowOnboardingModal(true); // Show modal for failed loads of new plans
          }
        } else {
          // console.log("[FloorPlanCreator] No floorPlanId provided. Ensuring default new plan state.");
          // 🔄 Reset room ID system for new file so room numbering starts at room_1
          const { resetRoomIdSystem } = await import('../utils/floorPlanUtils');
          resetRoomIdSystem();
          setFloorPlans({ 1: [] });
          setCurrentFloor(1);
          setElements([]);
          setTextBoxes([]);
          setRoomCustomLabels({});
          setPropertyAddress('');
          setPropertyOrientation(0);
          setWindowCount({ single: 0, double: 0 });
          setLightCount({ nonLed: 0, led: 0 });
          setUndoStack([]);
          setRedoStack([]);
          setViewOffset({ x: 0, y: 0 });
          setScale(INITIAL_SCALE);
          setCurrentFloorPlanId(null);
          setSiteNotesDataReady(true); // Default site notes data is ready
          setShowOnboardingModal(true); // Show modal for new plans
        }
      } catch (error) {
        // console.error(`[FloorPlanCreator] Error loading floor plan ${floorPlanId}:`, error);
        setFloorPlans({ 1: [] });
        setCurrentFloor(1);
        setElements([]);
        setTextBoxes([]);
        setRoomCustomLabels({});
        setPropertyAddress('');
        setPropertyOrientation(0);
        setWindowCount({ single: 0, double: 0 });
        setLightCount({ nonLed: 0, led: 0 });
        setUndoStack([]);
        setRedoStack([]);
        setViewOffset({ x: 0, y: 0 });
        setScale(INITIAL_SCALE);
        setCurrentFloorPlanId(null);
        setSiteNotesDataReady(true); // Default site notes data is ready
        setShowOnboardingModal(true); // Show modal on error for new plans
      } finally {
        // Ensure loading screen shows for minimum time
        const elapsedTime = Date.now() - loadingStartTime;
        const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
        setTimeout(() => {
          setIsFileLoading(false); // Set loading state to false
        }, remainingTime);
      }
    };

    loadFloorPlan();
  }, [floorPlanId]);

  // Auto-enable autosave whenever there's a valid property address (but not for existing files)
  useEffect(() => {
    // Skip this auto-enable if we already have a floor plan ID (existing file)
    if (currentFloorPlanId) {
      console.log('🎯 Skipping auto-enable for existing file:', currentFloorPlanId);
      return;
    }
    
    if (propertyAddress && propertyAddress.trim().length >= 3) {
      console.log('🎯 Auto-enabling autosave due to valid address (new file):', propertyAddress);
      autosave.enableAutosave({ address: propertyAddress });
    } else if (!propertyAddress || propertyAddress.trim().length < 3) {
      autosave.disableAutosave();
    }
  }, [propertyAddress, currentFloorPlanId]); // Added currentFloorPlanId to prevent interference with existing files

  // Monitor floor plan and element changes for autosave
  useEffect(() => {
    // Skip autosave trigger during initial load to prevent saving empty states
    if (!hasFloorPlanChanges) return;
    
    // Skip if autosave is not enabled
    if (!autosave.isAutosaveEnabled) return;
    
    const autosaveStart = performance.now();
    autosave.markAsChanged();
    const autosaveEnd = performance.now();
    if (autosaveEnd - autosaveStart > 5) {
      console.log(`⚡ Autosave markAsChanged: ${(autosaveEnd - autosaveStart).toFixed(1)}ms`);
    }
  }, [floorPlans, elements, hasFloorPlanChanges, autosave]);

  // Function to save the current floor plan
  const handleSaveFloorPlan = async () => {
    // Validate property address
    if (!propertyAddress || propertyAddress.trim().length < 3) {
      alert("Please enter a property address with at least 3 characters before saving.");
      return;
    }

    try {
      await autosave.triggerManualSave();
      alert(`Floor plan "${propertyAddress}" saved successfully to cloud!`);
    } catch (error) {
      console.error("Error saving floor plan:", error);
      alert("Failed to save floor plan to cloud. Please check your internet connection and try again.");
    }
  };

  // Helper function to transform a point during corner resize
  const transformPointForCornerResize = (point: Point, originalCorner: Point, oppositeCorner: Point, newCornerPos: Point): Point => {
    // Calculate scale factors based on how much the corner moved
    const originalWidth = Math.abs(oppositeCorner.x - originalCorner.x);
    const originalHeight = Math.abs(oppositeCorner.y - originalCorner.y);
    const newWidth = Math.abs(oppositeCorner.x - newCornerPos.x);
    const newHeight = Math.abs(oppositeCorner.y - newCornerPos.y);
    
    const scaleX = originalWidth > 0 ? newWidth / originalWidth : 1;
    const scaleY = originalHeight > 0 ? newHeight / originalHeight : 1;
    
    // Transform point relative to opposite corner (fixed point)
    const relativeX = point.x - oppositeCorner.x;
    const relativeY = point.y - oppositeCorner.y;
    
    return {
      x: oppositeCorner.x + relativeX * scaleX,
      y: oppositeCorner.y + relativeY * scaleY
    };
  };

  // Use the fullscreen hook
  const { isFullscreen, toggleFullscreen, isSupported: isNativeFullscreenSupported } = useFullscreen(layoutRef); // Target the main layout div

  // State for CSS-based fullscreen on iOS
  const [isCssFullscreen, setIsCssFullscreen] = useState(false);

  // State to preserve view when entering/exiting fullscreen
  const [preFullscreenViewState, setPreFullscreenViewState] = useState<{
    viewOffset: Point;
    scale: number;
  } | null>(null);

  const labelsForRender = useMemo(() => {
    // Skip expensive label calculations during split tool hover to improve performance
    if (currentTool === 'split' && !selectedWall && !splitPoint) {
      return roomCustomLabels;
    }
    
    if (isResizing && resizingRoomInfo && activelyResizedWalls && selectedRoom && selectedRoom.walls.length > 0) {
      const singleLabelToMigrate: Record<string, string> = {
        [resizingRoomInfo.originalStableId]: resizingRoomInfo.label
      };
      
      
      // 🔧 CRITICAL ROOM MERGE FIX: Use complete floor plan walls for label migration (not just resizing room's walls)
      // During resize, selectedRoom.walls/activelyResizedWalls only contain walls from the resizing room (~4 walls)
      // but labelPreservation context in migrateRoomLabels needs ALL floor plan walls to maintain accurate room counts
      const completeOriginalWalls = floorPlans[currentFloor] || []; // Complete floor plan (before resize)
      const completeNewWalls = floorPlans[currentFloor] || []; // Complete floor plan (current state)
      
      const migrationResult = migrateRoomLabels( // Assign to a variable first
        completeOriginalWalls.map(({ tempId, ...wall }) => wall as Wall), // Complete floor plan walls (before)
        completeNewWalls.map(({ tempId, ...wall }) => wall as Wall),     // Complete floor plan walls (current)
        singleLabelToMigrate,
        currentFloor
      );
      
      // Use migrationResult.labels
      return { ...roomCustomLabels, ...migrationResult.labels }; // MODIFIED
    }
    return roomCustomLabels;
  }, [isResizing, resizingRoomInfo, activelyResizedWalls, selectedRoom, roomCustomLabels, currentTool, selectedWall, splitPoint]);

  // Memoize current walls to prevent unnecessary recalculations
  const memoizedCurrentWalls = useMemo(() => {
    return currentWalls;
  }, [currentWalls]);

  // Memoize elements to prevent unnecessary recalculations
  const memoizedElements = useMemo(() => {
    return liveUpdatedElements || elements;
  }, [liveUpdatedElements, elements]);

  // Enhanced coordinate handling with better touch device support
  // Simple coordinate conversion that works correctly across all devices
  const getCanvasCoordinates = useCallback((clientX: number, clientY: number): Point => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };

    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;
    
    const x = (relativeX / scale) + viewOffset.x; 
    const y = (relativeY / scale) + viewOffset.y;

    return { x, y };
  }, [scale, viewOffset, canvasRef]);

  // Initialize the touch gestures hook
  const { 
    isMultiTouching, 
    processTouchStart, 
    processTouchMove, 
    processTouchEnd 
  } = useTouchGestures({
    scale, setScale, viewOffset, setViewOffset, canvasRef, getCanvasCoordinates,
  });



  // Initialize the new interactions hook
  const {
    handleMouseDown: hookHandleMouseDown,
    handleCanvasMouseMove: hookHandleCanvasMouseMove,
    handleMouseUp: hookHandleMouseUp,
    handleMouseLeave,
    handleCanvasDoubleClick: hookHandleCanvasDoubleClick,
    handleTouchStart: hookHandleTouchStart,
    handleTouchMove: hookHandleTouchMove,
    handleTouchEndEvent: hookHandleTouchEndEvent,
    handleWheel,
    deleteSelectedRoom, // Added room deletion function
    // New enhanced mouse event handlers for element dragging
    handleCanvasMouseDown: hookHandleCanvasMouseDown,
    handleCanvasMouseUp: hookHandleCanvasMouseUp,
    updateElementPosition,
    findElementAtPointInternal,
  } = useFloorPlanInteractions({
    // States
    currentWalls,
    elements,
    scale,
    viewOffset,
    currentTool, // Pass the state (for hook's callback dependencies)
    selectedRoom,
    selectedElement, // Pass selectedElement here
    tempWalls,
    resizeHandle,
    isResizing,
    isDragging, // Make sure this line is present
    isDraggingRoom,
    roomStart,
    roomEnd,
    selectedWall,
    splitPoint,
    floorPlans,
    currentFloor,
    undoStack,
    redoStack,
    textBoxes,
    selectedTextBox,
    roomCustomLabels,
    resizingRoomInfo,
    resizingRoomLiveDimensions,
    activelyResizedWalls, // Pass these new props
    splitPreviewDimensions, // Pass the new split dimensions state
    cornerDragPreview, // Add corner drag preview
    isPanning,
    panStart,
    touchStartPos,
    isLongPress,
    longPressTimer,
    touchMoved,
    isDraggingCorner,
    hoveredCorner,
    isMultiTouching, // Ensure this is passed if it's in UseFloorPlanInteractionsProps
    isWallSplitting, // Added for performance optimization

    // Setters
    setScale,
    setViewOffset,
    setFloorPlans,
    setElements,
    setUndoStack,
    setRedoStack,
    setSelectedWall,
    setCurrentTool,
    setCurrentPoint,
    setSelectedElement,
    setIsDragging,
    setIsPanning,
    setPanStart,
    setIsDraggingRoom,
    setRoomStart,
    setRoomEnd,
    setSelectedRoom,
    setResizeHandle,
    setTempWalls,
    setSplitPoint,
    setTextBoxes,
    setSelectedTextBox,
    setRoomCustomLabels,
    setResizingRoomInfo,
    setActivelyResizedWalls,
    setTouchStartPos,
    setIsLongPress,
    setLongPressTimer,
    setTouchMoved,
    setIsResizing,
    setHoveredCorner,
    setIsDraggingCorner,
    setResizingRoomLiveDimensions, // Pass setter for live dimensions
    setSplitPreviewDimensions, // Pass setter for split preview dimensions
    setCornerDragPreview,
    setIsWallSplitting, // Pass setter for wall splitting flag
    setCurrentSnapResult, // 🆕 ENHANCED: Visual feedback for wall snapping

    // Refs
    canvasRef,
    isResizingRef,
    activeCornerInfoRef,
    lastRawDraggedCornerPosRef,
    draggedWallSegmentRef,
    isMouseDownRef,
    eraserPreviewPointRef,
    justResizedRef,
    roomMigrationMapRef,
    selectedWallRef,

    // Callbacks/Utilities
    getCanvasCoordinates: (event: MouseEvent | TouchEvent | Touch | { clientX: number, clientY: number }) => {
      let clientX: number, clientY: number;
      
      // Handle React MouseEvent
      if (event && typeof event === 'object' && ('clientX' in event) && ('clientY' in event)) {
        clientX = Number(event.clientX);
        clientY = Number(event.clientY);
      }
      // Handle TouchEvent with touches array
      else if (event && typeof event === 'object' && ('touches' in event) && event.touches && event.touches.length > 0) {
        const touch = event.touches[0];
        clientX = Number(touch.clientX);
        clientY = Number(touch.clientY);
      }
      // Handle TouchEvent with changedTouches array
      else if (event && typeof event === 'object' && ('changedTouches' in event) && event.changedTouches && event.changedTouches.length > 0) {
        const touch = event.changedTouches[0];
        clientX = Number(touch.clientX);
        clientY = Number(touch.clientY);
      }
      // Fallback for invalid/undefined events
      else {
        console.warn('[FloorPlanCreator getCanvasCoordinates] Invalid event object:', event);
        clientX = 0;
        clientY = 0;
      }
      
      // Validate extracted coordinates
      if (isNaN(clientX) || isNaN(clientY)) {
        console.error('[FloorPlanCreator getCanvasCoordinates] NaN coordinates detected:', { clientX, clientY, event });
        return { x: 0, y: 0 };
      }
      
      return getCanvasCoordinates(clientX, clientY);
    }, // Robust coordinate wrapper preventing NaN issues
    onDebug,
    currentToolInternalRef, 
    lastOperationTypeRef, // Pass the ref here
    newlyAddedWallsRef, // Pass newly added walls ref for endpoint room creation tracking
    recordUndoState, // Pass recordUndoState to the hook
    roomSync: roomSync as any, // Fix interface mismatch - cast to any to match expected signature
    triggerAutosave: () => autosave.markAsChanged(), // Add direct autosave trigger
    

  });

  // 🔧 WALL SEGMENTATION: Memoize wall conversion to prevent excessive re-computation
  const wallsForSegmentation = useMemo(() => {
    return currentWalls.map(({ tempId, stableId, ...wall }) => wall);
  }, [currentWalls]);

  // 🔧 WALL SEGMENTATION: Initialize wall segmentation hook for visual feedback
  const {
    segmentationResult,
    segmentHighlights,
    hoveredSegmentId,
    isWallTypeMode
  } = useWallSegmentation(
    wallsForSegmentation,
    currentTool,
    currentPoint, // Use currentPoint as mouse position
    scale
  );

  // ENHANCEMENT: Wrapper for double-click that handles text boxes, elements, and rooms
  const handleCanvasDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // First, let the hook handle text box double-clicks
    hookHandleCanvasDoubleClick(e);
    
    // Handle element and room double-click for select tool
    if (currentTool === 'select') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      
      // Check for garden label double-click first
      const gardenAtPoint = elements.find(el => {
        if (el.type !== 'utility') return false;
        const garden = el as UtilityElement;
        if (garden.utilityType !== 'garden') return false;
        return isPointOnGardenLabel(point, garden, 30);
      });
      
      if (gardenAtPoint) {
        const garden = gardenAtPoint as UtilityElement;
        setGardenToEdit(garden);
        setGardenLabelInput(garden.customLabel || 'GARDEN');
        setGardenAreaInput(garden.customArea || (garden.area ? garden.area.toFixed(2) : '0.00'));
        setIsGardenEditPromptOpen(true);
        console.log('🌳 Garden label double-clicked for editing');
        return;
      }
      
      // Check for elements (windows, doors, radiators, etc.)
      const elementAtClickPoint = findElementAtPointInternal(point, elements);
      
      if (elementAtClickPoint) {
        // Double-click on element should trigger edit
        console.log('DOUBLE_CLICK_EDIT: Triggering edit for element', {
          elementId: (elementAtClickPoint as any).id,
          elementType: elementAtClickPoint.type
        });
        
        // Handle editing different element types inline
        switch (elementAtClickPoint.type) {
          case 'radiator':
            // 🏢 ESTATE MODE: Skip radiator modal
            console.log('🏢 ESTATE: Radiator modals disabled for estate floorplan');
            break;
          case 'boiler':
            // 🏢 ESTATE MODE: Skip boiler modal
            console.log('🏢 ESTATE: Boiler modals disabled for estate floorplan');
            break;
          case 'window':
            // 🏢 ESTATE MODE: Skip window modal
            console.log('🏢 ESTATE: Window modals disabled for estate floorplan');
            break;
          case 'door':
            setDoorToEdit(elementAtClickPoint as DoorElement);
            setIsDoorPromptOpen(true);
            break;
          default:
            console.log('Edit not implemented for element type:', elementAtClickPoint.type);
            break;
        }
        return; // Don't continue to room handling
      }
      
      // Then handle room double-click for easy renaming
      const roomAtClickPoint = findRoomAtPoint(point, currentRooms);
      
      if (roomAtClickPoint && roomAtClickPoint.stableId) {
        const hasCustomLabel = roomCustomLabels[roomAtClickPoint.stableId] && 
                              !roomCustomLabels[roomAtClickPoint.stableId].startsWith('Room ');
        
        if (hasCustomLabel) {
          // Double-click on labeled room should trigger rename
          console.log('DOUBLE_CLICK_RENAME: Triggering rename for room', {
            roomId: roomAtClickPoint.stableId,
            currentLabel: roomCustomLabels[roomAtClickPoint.stableId]
          });
          
          setRoomToRename(roomAtClickPoint);
          setIsRoomRenameModalOpen(true);
        } else {
          // Double-click on unlabeled room should trigger labeling
          setRoomToLabel(roomAtClickPoint);
          setIsRoomLabelPromptOpen(true);
        }
      }
    }
  }, [
    hookHandleCanvasDoubleClick,
    currentTool,
    getCanvasCoordinates,
    currentRooms,
    roomCustomLabels,
    findRoomAtPoint,
    findElementAtPointInternal,
    elements,
    currentFloor,
    isPointOnGardenLabel
  ]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const point = getCanvasCoordinates(e.clientX, e.clientY);
    
    if (e.button === 0) {
      // Check if text tool is active and clicking on garden label
      if (currentTool === 'text') {
        const gardens = elements.filter(el => el.type === 'utility' && (el as UtilityElement).utilityType === 'garden' && el.floor === currentFloor) as UtilityElement[];
        for (const garden of gardens) {
          if (isPointOnGardenLabel(point, garden, 30)) {
            setGardenToEdit(garden);
            setGardenLabelInput(garden.customLabel || 'GARDEN');
            setGardenAreaInput(garden.customArea || `${(garden.area || 0).toFixed(2)} m²`);
            setIsGardenEditPromptOpen(true);
            console.log('🌳 Text tool: Garden label clicked for editing');
            return;
          }
        }
      }
      
      // NEW: Check if in room positioning mode (AR scanned room being positioned)
      if (isPositioningRoom && positioningRoomId) {
        const room = currentRooms.find(r => r.stableId === positioningRoomId);
        if (room) {
          const bounds = getRoomBounds(room.walls);
          
          // Check if clicked on center drag handle
          const centerX = (bounds.minX + bounds.maxX) / 2;
          const centerY = (bounds.minY + bounds.maxY) / 2;
          const handleRadius = 20 / scale;
          const distanceToCenter = Math.hypot(point.x - centerX, point.y - centerY);
          
          if (distanceToCenter <= handleRadius) {
            // Clicked on center handle - start dragging
            setRoomDragStart(point);
            console.log('[Room Positioning] Started dragging room via center handle:', positioningRoomId);
            return;
          }
          
          // Check if clicked on corner handle for angle adjustment
          const handle = getResizeHandleUtil(point, bounds);
          if (handle) {
            console.log('[Room Positioning] Started corner dragging for angle adjustment:', handle);
            setResizeHandle(handle);
            setRoomStart(point);
            return;
          }
        }
        // During positioning mode, block all other interactions including room/wall creation
        console.log('[Room Positioning] Blocked interaction during positioning mode');
        return;
      }
      
      // Check for counter rotation/move/resize when counter tool is active
      if ((currentTool as string) === 'counter') {
        const countersOnFloor = elements.filter(el => el.type === 'counter' && el.floor === currentFloor) as CounterElement[];
        for (const counter of countersOnFloor) {
          // Check rotation control first (only for free-floating counters)
          if (isPointOnRotationControl(point, counter, 15 / scale)) {
            setSelectedCounterId(counter.id);
            setIsRotatingCounter(true);
            // Calculate initial angle from counter center to mouse
            const centerX = (counter.start.x + counter.end.x) / 2;
            const centerY = (counter.start.y + counter.end.y) / 2;
            const startAngle = Math.atan2(point.y - centerY, point.x - centerX);
            setRotationStartAngle(startAngle);
            return;
          }
          
          // Check move control (only for free-floating counters)
          if (isPointOnMoveControl(point, counter, 15 / scale)) {
            setSelectedCounterId(counter.id);
            setIsDraggingCounter(true);
            setCounterDragStart(point);
            return;
          }
          
          // Check resize handles
          const handle = getCounterResizeHandle(point, counter, 15 / scale);
          if (handle) {
            setSelectedCounterId(counter.id);
            setCounterResizeHandle(handle);
            setIsResizingCounter(true);
            return;
          }
        }
      }
      
      
      if (currentTool === 'roomResize') {
        // CRITICAL: During positioning mode, wall resize is handled above in the positioning block
        // Block general room resize operations to prevent conflicts
        if (isPositioningRoom) {
          console.log('[Room Positioning] Blocked general room resize - positioning mode active');
          return;
        }
        
        const roomAtPoint = findRoomAtPoint(point, currentRooms);
        if (roomAtPoint) {
          const bounds = getRoomBounds(roomAtPoint.walls);
          const handle = getResizeHandleUtil(point, bounds);
          if (handle) {
            hookHandleMouseDown(e);
          } else {
            setIsDraggingRoom(true);
            setSelectedRoom(roomAtPoint);
            setRoomStart(point);
          }
          return;
        }
      }
      
      // Check for garden label dragging when using select tool
      if (currentTool === 'select') {
        // Check garden labels first
        const gardenAtPoint = elements.find(el => {
          if (el.type !== 'utility') return false;
          const garden = el as UtilityElement;
          if (garden.utilityType !== 'garden') return false;
          return isPointOnGardenLabel(point, garden, 30);
        });
        
        if (gardenAtPoint) {
          const garden = gardenAtPoint as UtilityElement;
          setSelectedGardenId(garden.id);
          setIsDraggingGardenLabel(true);
          setGardenLabelDragStart(point);
          return;
        }
        
        // Then check room labels
        const roomLabelAtPoint = currentRooms.find(room => {
          const roomKey = room.stableId || room.id;
          const labelPosition = roomLabelPositions[roomKey];
          if (!labelPosition) return false;
          const distance = Math.hypot(point.x - labelPosition.x, point.y - labelPosition.y);
          return distance < 20;
        });
        if (roomLabelAtPoint) {
          setIsDraggingRoomLabel(true);
          setDraggedRoomLabelId(roomLabelAtPoint.id);
          return;
        }
      }
    }
    
    // Rotation buttons are now always visible on sinks - no click detection needed
    
    if (e.button === 0 && (currentTool === 'select' || currentTool === 'labelRoom')) {
      
      const roomAtPoint = findRoomAtPoint(point, currentRooms);
      
      if (roomAtPoint) {
        if (isRoomLabelPromptOpen && roomToLabel?.stableId === roomAtPoint.stableId) {
          return;
        }
        
        if (currentTool === 'labelRoom') {
          setRoomToLabel(roomAtPoint);
          setIsRoomLabelPromptOpen(true);
          // 🔧 FIX: Don't add to promptedRoomIds for manual labelRoom tool clicks
          // promptedRoomIds should only prevent automatic prompts, not manual user interactions
          return;
        } else if (currentTool === 'select') {
          const hasCustomLabel = roomAtPoint.stableId && 
                                roomCustomLabels[roomAtPoint.stableId] && 
                                roomCustomLabels[roomAtPoint.stableId].trim() !== '' && 
                                !roomCustomLabels[roomAtPoint.stableId].startsWith('Room ');
          
          // 🔧 FIX: Allow select tool to work with labeled rooms for selection and movement
          // Instead of immediately opening context menu, select the room and allow interaction
          if (hasCustomLabel) {
            // Use handleRoomSelect to support both normal selection and merge tool
            handleRoomSelect(roomAtPoint);
            setSelectedElement(null); // Clear element selection when room is selected
            
            // 🔧 ENHANCEMENT: Show a brief indicator that the room is selected and can be renamed
            console.log('🔧 SELECT_TOOL: Selected labeled room for interaction', {
              roomId: roomAtPoint.stableId,
              roomLabel: roomAtPoint.stableId ? roomCustomLabels[roomAtPoint.stableId] : 'unknown',
              hint: 'Right-click or double-click to rename'
            });
            return;
          } else {
            // For unlabeled rooms, open the labeling prompt
            setRoomToLabel(roomAtPoint);
            setIsRoomLabelPromptOpen(true);
            // 🔧 FIX: Don't add to promptedRoomIds for manual select tool clicks on unlabeled rooms
            // promptedRoomIds should only prevent automatic prompts, not manual user interactions
            return;
          }
        }
      }
    }

    if (e.button === 2) {
      const roomAtPoint = findRoomAtPoint(point, currentRooms);
      
      if (roomAtPoint) {
        e.preventDefault();
        handleOpenRoomContextMenu(roomAtPoint, e.clientX, e.clientY);
        return;
      }
    }

    if (isRoomContextMenuOpen) {
      setIsRoomContextMenuOpen(false);
    }

    if (UTILITY_TOOLS.includes(currentTool as UtilityType)) {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
      
      // Garden rectangle drag creation
      if ((currentTool as string) === 'garden') {
        const gardensOnFloor = elements.filter(el => el.type === 'utility' && (el as any).utilityType === 'garden' && el.floor === currentFloor) as UtilityElement[];
        
        // Check if clicking on existing garden area text to edit it
        for (const garden of gardensOnFloor) {
          const size = 27;
          const labelOffsetX = garden.labelOffset?.x || 0;
          // Garden editing now handled via double-click on label
          
          // Check if clicking on corner resize handle
          if (garden.width && garden.height) {
            const gardenResizeHandle = getGardenResizeHandle(point, garden, 15);
            if (gardenResizeHandle) {
              setSelectedGardenId((garden as any).id);
              setGardenResizeHandle(gardenResizeHandle);
              setIsResizingGarden(true);
              console.log('🌳 Started resizing garden corner:', gardenResizeHandle);
              return;
            }
          }
        }
        
        // Start dragging new garden rectangle
        setIsDraggingGarden(true);
        setGardenDragStart(point);
        setGardenPreview({ start: point, end: point });
        console.log('🌳 Started garden rectangle drag at', point);
        return;
      }
      
      // Sinks require wall proximity (like radiators)
      const isSink = (currentTool as string) === 'single-sink' || (currentTool as string) === 'double-sink';
      const isBathroomFixture = (currentTool as string) === 'bathtub' || (currentTool as string) === 'shower' || (currentTool as string) === 'toilet';
      
      if (isSink) {
        const wall = findWallForSink(point, wallsWithStableIds);
        if (wall) {
          setIsPlacingUtility(true);
          setWallForSink(wall);
          setUtilityPreview(createSinkElementOnWall(
            point,
            wall,
            currentTool as 'single-sink' | 'double-sink',
            currentFloor
          ));
        } else {
          // Fallback: place sink without wall constraint if no wall nearby
          setIsPlacingUtility(true);
          setUtilityPreview(createUtilityElement(point, currentTool as UtilityType, currentFloor, utilityRotation, wallsWithStableIds));
        }
      } else if (isBathroomFixture) {
        // Bathtub/shower/toilet require wall proximity (like sinks)
        const wall = findWallForSink(point, wallsWithStableIds);
        if (wall) {
          setIsPlacingUtility(true);
          setWallForSink(wall);
          setUtilityPreview(createBathroomFixtureOnWall(
            point,
            wall,
            currentTool as 'bathtub' | 'shower' | 'toilet',
            currentFloor
          ));
        } else {
          // Fallback: place without wall constraint if no wall nearby
          setIsPlacingUtility(true);
          setUtilityPreview(createUtilityElement(point, currentTool as UtilityType, currentFloor, utilityRotation, wallsWithStableIds));
        }
      } else {
        // Other utilities can be placed anywhere
        setIsPlacingUtility(true);
        setUtilityPreview(createUtilityElement(point, currentTool as UtilityType, currentFloor, utilityRotation, wallsWithStableIds));
      }
    } else if ((currentTool as string) === 'light') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      setIsPlacingLight(true);
      setLightPreview(createLightElement(point, 'low-energy', currentFloor));
    } else if ((currentTool as string) === 'smoke-alarm') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      setIsPlacingSmokeAlarm(true);
      setSmokeAlarmPreview(createSmokeAlarmElement(point, currentFloor));
    } else if ((currentTool as string) === 'emergency-light' || (currentTool as string) === 'fire-call-point' || (currentTool as string) === 'fire-panel' ||
               (currentTool as string) === 'heat-alarm' || (currentTool as string) === 'alarm-sounder' || (currentTool as string) === 'wc-call-point' || (currentTool as string) === 'ac-unit' ||
               (currentTool as string) === 'fire-alarm' || (currentTool as string) === 'ice-machine' || (currentTool as string) === 'coffee-machine') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      setIsPlacingFireSafety(true);
      setFireSafetyPreview(createFireSafetyElement(point, currentTool as FireSafetyType, currentFloor));
    } else if ((currentTool as string) === 'loft-hatch') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      setIsPlacingLoftHatch(true);
      setLoftHatchPreview(createLoftHatch(point, currentFloor));
    } else if ((currentTool as string) === 'compass') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      setIsPlacingCompass(true);
      setCompassPreview(createCompass(point));
    } else if ((currentTool as string) === 'door') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
      const wall = findWallForDoor(point, wallsWithStableIds);
      if (wall) {
        setIsPlacingDoor(true);
        setWallForDoor(wall);
        const newDoorPreview = createDoorElement(point, wall, currentFloor, true);
        setDoorPreview(newDoorPreview as DoorElement | null);
      }
    } else if ((currentTool as string) === 'window') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      
      if (selectedWindowType === 'wall') {
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const wall = findWallForWindow(point, wallsWithStableIds);
        if (wall) {
          setIsPlacingWindow(true);
          setWallForWindow(wall);
          const newWindowPreview = createWindowElement(point, wall as Wall & { tempId: string; stableId: string }, currentFloor, true);
          if (newWindowPreview) {
            (newWindowPreview as any).windowInstallationType = 'wall';
          }
          setWindowPreview(newWindowPreview as WindowElement | null);
        }
      } else if (selectedWindowType === 'roof') {
        const newRoofWindow = createRoofWindowElement(point, currentFloor);
        setIsPlacingWindow(true);
        setWindowPreview(newRoofWindow);
      } else if (selectedWindowType === 'canted') {
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const wall = findWallForWindow(point, wallsWithStableIds);
        if (wall) {
          setIsPlacingWindow(true);
          setWallForWindow(wall);
          const newCantedWindow = createCantedWindowElement(point, wall, currentFloor, true);
          setWindowPreview(newCantedWindow as WindowElement | null);
        }
      }
    } else if ((currentTool as string) === 'radiator') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
      const wall = findWallForRadiator(point, wallsWithStableIds);
      if (wall) {
        setIsPlacingRadiator(true);
        setWallForRadiator(wall);
        const newRadiatorPreview = createRadiatorElement(point, wall, currentFloor, currentWalls, roomCustomLabels);
        setRadiatorPreview(newRadiatorPreview);
      }
    } else if ((currentTool as string) === 'boiler') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
      const wall = findWallForBoiler(point, wallsWithStableIds);
      if (wall) {
        setIsPlacingBoiler(true);
        setWallForBoiler(wall);
        const newBoilerPreview = createBoilerElement(point, wall, currentFloor);
        setBoilerPreview(newBoilerPreview);
      }
    } else if ((currentTool as string) === 'counter' && !isResizingCounter) {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
      
      // Check if clicking on a resize handle first
      const countersOnFloor = elements.filter(el => el.type === 'counter' && el.floor === currentFloor) as CounterElement[];
      let clickedHandle = false;
      for (const counter of countersOnFloor) {
        const handle = getCounterResizeHandle(point, counter, 15 / scale);
        if (handle) {
          clickedHandle = true;
          break;
        }
      }
      
      // Only start placing if not clicking on a resize handle
      if (!clickedHandle) {
        setIsPlacingCounter(true);
        const newCounterPreview = createCounterWithAutoSnap(point, wallsWithStableIds, currentFloor);
        setCounterPreview(newCounterPreview);
        
        // Store wall reference if snapped (for later use in resizing)
        const wall = findWallForCounter(point, wallsWithStableIds);
        setWallForCounter(wall);
      }
    } else if ((currentTool as string) === 'stairs' && !isResizingStairs && !isRotatingStairs && !isDraggingStairs) {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
      
      // Check for rotation/move/resize controls
      const stairsOnFloor = elements.filter(el => el.type === 'stairs' && el.floor === currentFloor) as StairsElement[];
      let clickedControl = false;
      for (const stairs of stairsOnFloor) {
        // Check rotation control first (only for free-floating stairs)
        if (isPointOnStairsRotationControl(point, stairs, 15 / scale)) {
          clickedControl = true;
          setSelectedStairsId(stairs.id);
          setIsRotatingStairs(true);
          // Calculate initial angle from stairs center to mouse
          const centerX = (stairs.start.x + stairs.end.x) / 2;
          const centerY = (stairs.start.y + stairs.end.y) / 2;
          const startAngle = Math.atan2(point.y - centerY, point.x - centerX);
          setRotationStartAngle(startAngle);
          break;
        }
        
        // Check move control
        if (isPointOnStairsMoveControl(point, stairs, 15 / scale)) {
          clickedControl = true;
          setSelectedStairsId(stairs.id);
          setIsDraggingStairs(true);
          setCounterDragStart(point); // Reuse counter drag start for simplicity
          break;
        }
        
        // Check resize handles
        const handle = getStairsResizeHandle(point, stairs, 20 / scale);
        if (handle) {
          clickedControl = true;
          setSelectedStairsId(stairs.id);
          setStairsResizeHandle(handle);
          setIsResizingStairs(true);
          break;
        }
      }
      
      // Only start placing if not clicking on any control
      if (!clickedControl) {
        setIsPlacingStairs(true);
        const newStairsPreview = createStairsWithAutoSnap(point, wallsWithStableIds, currentFloor, undefined, undefined, stairsDirection);
        setStairsPreview(newStairsPreview);
        
        // Store wall reference if snapped (for later use in resizing)
        const wall = findWallForStairs(point, wallsWithStableIds);
        setWallForStairs(wall);
      }
    } else if ((currentTool as string) === 'compass' && !isRotatingCompass) {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      
      // Check if clicking on existing compass rotation control
      const compassesOnFloor = elements.filter(el => el.type === 'compass' && (el as any).floor === currentFloor) as CompassType[];
      let clickedControl = false;
      
      for (const compass of compassesOnFloor) {
        if (isPointOnCompassRotationControl(point, compass, 15 / scale)) {
          clickedControl = true;
          setSelectedCompassId((compass as any).id);
          setIsRotatingCompass(true);
          // Calculate initial angle from compass center to mouse
          const angle = Math.atan2(point.y - compass.position.y, point.x - compass.position.x) * 180 / Math.PI;
          setCompassRotationStartAngle(angle);
          break;
        }
      }
      
      if (!clickedControl) {
        // Check if compass already exists (limit to 1)
        if (compassesOnFloor.length > 0) {
          console.log('⚠️ Compass already exists, only one compass allowed');
          return;
        }
        
        const newCompass: CompassType = {
          type: 'compass',
          position: point,
          rotation: 0, // Default to north
          size: 40
        };
        setCompassPreview(newCompass);
        setIsPlacingCompass(true);
      }
    } else if ((currentTool as string) === 'highlight') {
      const point = getCanvasCoordinates(e.clientX, e.clientY);
      setIsHighlighting(true);
      setHighlightStart(point);
      // Use wall snapping for highlight preview
      const wallsForSnapping = currentWalls.map(({tempId, ...w}) => w);
      const roomsForSnapping = currentRooms || [];
      
      // Calculate area snap for visual feedback
      const areaSnap = snapHighlightAreaToWalls(point, point, wallsForSnapping, roomsForSnapping, currentFloor);
      setHighlightSnap(areaSnap);
      
      setHighlightPreview(createHighlightPreviewWithWallSnapping(point, point, wallsForSnapping, roomsForSnapping, currentFloor, highlightColor));
    } else {
      if (currentTool === 'select') {
        hookHandleCanvasMouseDown(e);
      } else {
        hookHandleMouseDown(e);
      }
    }
  };

  // Create optimized mouse move handler with throttling
  const optimizedMouseMoveHandler = useMemo(() => {
    let lastExecuted = 0;
    const throttleMs = 16; // 60fps throttling
    
    return (e: React.MouseEvent<HTMLCanvasElement>) => {
      const now = Date.now();
      if (now - lastExecuted < throttleMs) return;
      lastExecuted = now;
      
      if (isDraggingGardenLabel && selectedGardenId && gardenLabelDragStart) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        const garden = elements.find(el => el.type === 'utility' && (el as any).id === selectedGardenId) as UtilityElement | undefined;
        
        if (garden) {
          const dx = point.x - gardenLabelDragStart.x;
          const dy = point.y - gardenLabelDragStart.y;
          
          const currentOffsetX = garden.labelOffset?.x || 0;
          const currentOffsetY = garden.labelOffset?.y || 0;
          
          const updatedGarden = {
            ...garden,
            labelOffset: {
              x: currentOffsetX + dx,
              y: currentOffsetY + dy
            }
          };
          
          setElements(elements.map(el => (el.type === 'utility' && (el as any).id === selectedGardenId) ? updatedGarden : el));
          setGardenLabelDragStart(point);
        }
      } else if (isDraggingRoomLabel && draggedRoomLabelId) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        setRoomLabelPositions(prev => ({
          ...prev,
          [draggedRoomLabelId]: point
        }));
      } else if (!isPlacingUtility && !isPlacingLight && !isPlacingSmokeAlarm && !isPlacingFireSafety && !isPlacingLoftHatch && !isPlacingDoor && !isPlacingWindow && !isPlacingRadiator && !isPlacingBoiler && !isPlacingCounter && !isPlacingStairs) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        
        // Check if hovering over a room label to change cursor (only for select tool)
        if (currentTool === 'select') {
          const roomLabelAtPoint = currentRooms.find(room => {
          const roomKey = room.stableId || room.id;
          const labelPosition = roomLabelPositions[roomKey];
          if (!labelPosition) return false;
          const distance = Math.hypot(point.x - labelPosition.x, point.y - labelPosition.y);
          return distance < 20;
        });
          if (roomLabelAtPoint) {
            e.currentTarget.style.cursor = 'move';
          } else {
            e.currentTarget.style.cursor = 'default';
          }
        } else if (currentTool === 'labelRoom') {
          // Check if hovering over a room when using labelRoom tool
          const roomAtPoint = findRoomAtPoint(point, currentRooms);
          if (roomAtPoint) {
            e.currentTarget.style.cursor = 'pointer';
          } else {
            e.currentTarget.style.cursor = 'default';
          }
        } else {
          e.currentTarget.style.cursor = 'default';
        }
      }
      
      if (isPlacingUtility) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        if (utilityPreview) {
          // For sinks, constrain to the detected wall (like radiators)
          const isSink = utilityPreview.utilityType === 'single-sink' || utilityPreview.utilityType === 'double-sink';
          const isBathroomFixture = utilityPreview.utilityType === 'bathtub' || utilityPreview.utilityType === 'shower' || utilityPreview.utilityType === 'toilet';
          
          if (isSink && wallForSink?.stableId) {
            setUtilityPreview(createSinkElementOnWall(
              point, 
              wallForSink as Wall & { tempId: string, stableId: string },
              utilityPreview.utilityType as 'single-sink' | 'double-sink',
              currentFloor
            ));
          } else if (isBathroomFixture && wallForSink?.stableId) {
            // Bathtub/shower/toilet constrained to wall
            setUtilityPreview(createBathroomFixtureOnWall(
              point,
              wallForSink as Wall & { tempId: string, stableId: string },
              utilityPreview.utilityType as 'bathtub' | 'shower' | 'toilet',
              currentFloor
            ));
          } else if (!isSink && !isBathroomFixture) {
            // Other utilities can move freely
            setUtilityPreview({ ...utilityPreview, position: point });
          }
        }
      } else if (isPlacingLight) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        if (lightPreview) {
          setLightPreview({ ...lightPreview, position: point });
        }
      } else if (isPlacingSmokeAlarm) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        if (smokeAlarmPreview) {
          setSmokeAlarmPreview({ ...smokeAlarmPreview, position: point });
        }
      } else if (isPlacingFireSafety) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        if (fireSafetyPreview) {
          setFireSafetyPreview({ ...fireSafetyPreview, position: point });
        }
      } else if (isPlacingLoftHatch && loftHatchPreview) {
        // Update loft hatch preview position
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        const newLoftHatchPreview: LoftHatchElement = {
          ...loftHatchPreview,
          position: point
        };
        setLoftHatchPreview(newLoftHatchPreview);
      } else if (isPlacingCompass && compassPreview) {
        // Update compass preview position
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        setCompassPreview({
          ...compassPreview,
          position: point
        });
      } else if ((currentTool as string) === 'compass' && !isPlacingCompass && !isRotatingCompass) {
        // Show compass preview on hover before clicking
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        setCompassPreview({
          type: 'compass',
          position: point,
          rotation: 0,
          size: 40
        });
      } else if (isPlacingDoor && wallForDoor?.stableId) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        const newDoorPreview = createDoorElement(point, wallForDoor as Wall & { tempId: string; stableId: string; }, currentFloor);
        setDoorPreview(newDoorPreview as DoorElement | null);
      } else if (isPlacingWindow) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        
        if (wallForWindow?.stableId) {
          // Create preview based on selected window type
          if (selectedWindowType === 'solid_box') {
            const newSolidBoxPreview = createSolidBoxWindowElement(point, wallForWindow as Wall & { tempId: string; stableId: string; }, currentFloor);
            setWindowPreview(newSolidBoxPreview as WindowElement | null);
          } else if (selectedWindowType === 'canted') {
            const newCantedPreview = createCantedWindowElement(point, wallForWindow as Wall & { tempId: string; stableId: string; }, currentFloor, true);
            setWindowPreview(newCantedPreview as WindowElement | null);
          } else {
            // Regular wall window preview
            const newWindowPreview = createWindowElement(point, wallForWindow as Wall & { tempId: string; stableId: string; }, currentFloor);
            if (newWindowPreview) {
              (newWindowPreview as any).windowInstallationType = 'wall';
            }
            setWindowPreview(newWindowPreview as WindowElement | null);
          }
        } else if (windowPreview && !wallForWindow) {
          // Roof window preview - just update position
          setWindowPreview({ ...windowPreview, position: point });
        }
      } else if (isPlacingRadiator && wallForRadiator?.stableId) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        const newRadiatorPreview = createRadiatorElement(point, wallForRadiator as Wall & { tempId: string; stableId: string; }, currentFloor, currentWalls, roomCustomLabels);
        setRadiatorPreview(newRadiatorPreview);
      } else if (isPlacingBoiler && wallForBoiler?.stableId) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        const newBoilerPreview = createBoilerElement(point, wallForBoiler as Wall & { tempId: string; stableId: string; }, currentFloor);
        setBoilerPreview(newBoilerPreview);
      } else if (isPlacingCounter) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const newCounterPreview = createCounterWithAutoSnap(point, wallsWithStableIds, currentFloor);
        setCounterPreview(newCounterPreview);
        
        // Update wall reference if snapping changed
        const wall = findWallForCounter(point, wallsWithStableIds);
        setWallForCounter(wall);
      } else if (isPlacingStairs) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const newStairsPreview = createStairsWithAutoSnap(point, wallsWithStableIds, currentFloor, undefined, undefined, stairsDirection);
        setStairsPreview(newStairsPreview);
        
        // Update wall reference if snapping changed
        const wall = findWallForStairs(point, wallsWithStableIds);
        setWallForStairs(wall);
      } else if (isHighlighting && highlightStart) {
        const point = getCanvasCoordinates(e.clientX, e.clientY);
        // Use wall snapping for highlight preview
        const wallsForSnapping = currentWalls.map(({tempId, ...w}) => w);
        const roomsForSnapping = currentRooms || [];
        
        // Calculate area snap for visual feedback
        const areaSnap = snapHighlightAreaToWalls(highlightStart, point, wallsForSnapping, roomsForSnapping, currentFloor);
        setHighlightSnap(areaSnap);
        
        setHighlightPreview(createHighlightPreviewWithWallSnapping(highlightStart, point, wallsForSnapping, roomsForSnapping, currentFloor, highlightColor));
      } else {
        // Use enhanced mouse move handler for select tool element dragging
        hookHandleCanvasMouseMove(e);
      }
    };
  }, [isDraggingGardenLabel, selectedGardenId, gardenLabelDragStart, isDraggingRoomLabel, draggedRoomLabelId, isPlacingUtility, utilityPreview, isPlacingLight, lightPreview, isPlacingSmokeAlarm, smokeAlarmPreview, 
     isPlacingFireSafety, fireSafetyPreview, isPlacingLoftHatch, loftHatchPreview, isPlacingCompass, compassPreview, isPlacingDoor, wallForDoor, isPlacingWindow, wallForWindow, isPlacingRadiator, wallForRadiator, 
     isPlacingBoiler, wallForBoiler, currentFloor, currentWalls, roomCustomLabels, hookHandleCanvasMouseMove, calculateRooms, elements]);

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const mousePoint = getCanvasCoordinates(e.clientX, e.clientY);
    
    // NEW: Handle room positioning mode - free movement for center drag with room-to-room snapping
    if (isPositioningRoom && positioningRoomId && roomDragStart && !resizeHandle) {
      let deltaX = mousePoint.x - roomDragStart.x;
      let deltaY = mousePoint.y - roomDragStart.y;
      
      // Only update if there's actual movement
      if (Math.abs(deltaX) > 0.1 || Math.abs(deltaY) > 0.1) {
        // Get walls by ID (more reliable than coordinate matching)
        const roomWallIdSet = new Set(positioningRoomWallIds);
        const positioningWalls = currentWalls.filter(w => w.id && roomWallIdSet.has(w.id));
        
        if (positioningWalls.length > 0) {
          console.log('[Room Positioning] Moving room with', positioningWalls.length, 'walls');
          
          // 🧲 ROOM-TO-ROOM SNAPPING: Snap wall endpoints to nearby existing room walls
          const SNAP_THRESHOLD = 30; // pixels
          const otherRooms = currentRooms.filter(r => r.stableId !== positioningRoomId);
          
          // Calculate proposed new positions for positioning room walls
          const proposedWalls = positioningWalls.map(w => ({
            start: { x: w.start.x + deltaX, y: w.start.y + deltaY },
            end: { x: w.end.x + deltaX, y: w.end.y + deltaY }
          }));
          
          // Check for snap opportunities
          let snapOffsetX = 0;
          let snapOffsetY = 0;
          let foundSnap = false;
          
          for (const proposedWall of proposedWalls) {
            if (foundSnap) break;
            
            for (const otherRoom of otherRooms) {
              if (foundSnap) break;
              
              for (const otherWall of otherRoom.walls) {
                // Check if proposed wall endpoints are close to other wall endpoints
                const snapPoints = [
                  { proposed: proposedWall.start, target: otherWall.start },
                  { proposed: proposedWall.start, target: otherWall.end },
                  { proposed: proposedWall.end, target: otherWall.start },
                  { proposed: proposedWall.end, target: otherWall.end }
                ];
                
                for (const { proposed, target } of snapPoints) {
                  const dx = target.x - proposed.x;
                  const dy = target.y - proposed.y;
                  const dist = Math.sqrt(dx * dx + dy * dy);
                  
                  if (dist < SNAP_THRESHOLD) {
                    // Found a snap target - adjust delta to snap to this point
                    snapOffsetX = dx;
                    snapOffsetY = dy;
                    foundSnap = true;
                    console.log('[Room Snapping] Snapping to nearby room wall:', { distance: dist, snapOffset: { x: snapOffsetX, y: snapOffsetY } });
                    break;
                  }
                }
                if (foundSnap) break;
              }
            }
          }
          
          // Apply snap offset if found
          if (foundSnap) {
            deltaX += snapOffsetX;
            deltaY += snapOffsetY;
          }
          
          // Actually move the walls using wall IDs
          const updatedWalls = currentWalls.map(wall => {
            if (wall.id && roomWallIdSet.has(wall.id)) {
              return {
                ...wall,
                start: { x: wall.start.x + deltaX, y: wall.start.y + deltaY },
                end: { x: wall.end.x + deltaX, y: wall.end.y + deltaY }
              };
            }
            return wall;
          });
          
          setFloorPlans(prev => ({ ...prev, [currentFloor]: updatedWalls }));
          setRoomDragStart({ x: roomDragStart.x + deltaX, y: roomDragStart.y + deltaY }); // Update for next delta
        }
      }
      return;
    }
    
    // NEW: Handle room positioning - corner dragging with EXACT resize tool behavior
    // Includes: angle snapping, tempWalls preview, corner angle display
    if (isPositioningRoom && positioningRoomId && positioningRoomWallIds.length > 0 && resizeHandle && roomStart) {
      const rawPoint = mousePoint;
      
      // Find actual corner junction (AR rooms aren't perfect rectangles)
      const positioningRoom = currentRooms.find(r => r.stableId === positioningRoomId);
      if (!positioningRoom) return;
      
      const roomWalls = positioningRoom.walls;
      const bounds = {
        minX: Math.min(...roomWalls.map(w => Math.min(w.start.x, w.end.x))),
        maxX: Math.max(...roomWalls.map(w => Math.max(w.start.x, w.end.x))),
        minY: Math.min(...roomWalls.map(w => Math.min(w.start.y, w.end.y))),
        maxY: Math.max(...roomWalls.map(w => Math.max(w.start.y, w.end.y)))
      };
      
      let theoreticalCorner: Point;
      switch (resizeHandle) {
        case 'nw': theoreticalCorner = { x: bounds.minX, y: bounds.minY }; break;
        case 'ne': theoreticalCorner = { x: bounds.maxX, y: bounds.minY }; break;
        case 'sw': theoreticalCorner = { x: bounds.minX, y: bounds.maxY }; break;
        case 'se': theoreticalCorner = { x: bounds.maxX, y: bounds.maxY }; break;
        default: return;
      }
      
      const roomWallIdSet = new Set(positioningRoomWallIds);
      const allEndpoints: Point[] = [];
      currentWalls.forEach(wall => {
        if (wall.id && roomWallIdSet.has(wall.id)) {
          allEndpoints.push(wall.start, wall.end);
        }
      });
      
      let actualCorner: Point | null = null;
      let minDistance = Infinity;
      allEndpoints.forEach(endpoint => {
        const dist = Math.hypot(endpoint.x - theoreticalCorner.x, endpoint.y - theoreticalCorner.y);
        if (dist < minDistance) {
          minDistance = dist;
          actualCorner = endpoint;
        }
      });
      
      if (!actualCorner) return;
      
      // Find the two walls connected to this corner
      const junctionTolerance = 2;
      const affectedWallsData: Array<{ wall: Wall; isStartCorner: boolean }> = [];
      
      currentWalls.forEach(wall => {
        if (!wall.id || !roomWallIdSet.has(wall.id)) return;
        
        const startIsJunction = 
          Math.abs(wall.start.x - actualCorner!.x) < junctionTolerance &&
          Math.abs(wall.start.y - actualCorner!.y) < junctionTolerance;
        
        const endIsJunction = 
          Math.abs(wall.end.x - actualCorner!.x) < junctionTolerance &&
          Math.abs(wall.end.y - actualCorner!.y) < junctionTolerance;
        
        if (startIsJunction) affectedWallsData.push({ wall, isStartCorner: true });
        if (endIsJunction) affectedWallsData.push({ wall, isStartCorner: false });
      });
      
      // 🎯 EXACT RESIZE TOOL ANGLE SNAPPING (from useFloorPlanInteractions:8783-8796)
      let snappedForAnglePoint = { ...rawPoint };
      
      if (affectedWallsData.length === 2) {
        const entry1 = affectedWallsData[0];
        const entry2 = affectedWallsData[1];
        const fixedP1 = entry1.isStartCorner ? entry1.wall.end : entry1.wall.start;
        const fixedP2 = entry2.isStartCorner ? entry2.wall.end : entry2.wall.start;
        const toleranceWorld = 10 / scale; // SNAP_ANGLE_TOLERANCE_PX / scale
        
        // Check all angle snapping conditions (exact resize tool logic)
        if (Math.abs(rawPoint.y - fixedP1.y) < toleranceWorld && Math.abs(rawPoint.x - fixedP2.x) < toleranceWorld)
          snappedForAnglePoint = { x: fixedP2.x, y: fixedP1.y };
        else if (Math.abs(rawPoint.x - fixedP1.x) < toleranceWorld && Math.abs(rawPoint.y - fixedP2.y) < toleranceWorld)
          snappedForAnglePoint = { x: fixedP1.x, y: fixedP2.y };
        else if (Math.abs(rawPoint.y - fixedP1.y) < toleranceWorld)
          snappedForAnglePoint = { x: rawPoint.x, y: fixedP1.y };
        else if (Math.abs(rawPoint.x - fixedP1.x) < toleranceWorld)
          snappedForAnglePoint = { x: fixedP1.x, y: rawPoint.y };
        else if (Math.abs(rawPoint.y - fixedP2.y) < toleranceWorld)
          snappedForAnglePoint = { x: rawPoint.x, y: fixedP2.y };
        else if (Math.abs(rawPoint.x - fixedP2.x) < toleranceWorld)
          snappedForAnglePoint = { x: fixedP2.x, y: rawPoint.y };
      }
      
      // Update tempWalls for preview (not floorPlans directly)
      const updatedTempWalls = currentWalls.map(wall => {
        const affection = affectedWallsData.find(a => a.wall.id === wall.id);
        if (affection) {
          return {
            ...wall,
            start: affection.isStartCorner ? snappedForAnglePoint : wall.start,
            end: affection.isStartCorner ? wall.end : snappedForAnglePoint
          };
        }
        return wall;
      });
      
      setTempWalls(updatedTempWalls);
      
      // Calculate and display corner drag preview (orange lines + angle measurements)
      if (affectedWallsData.length === 2) {
        const previewWalls = affectedWallsData.map(aff => {
          const fixedPoint = aff.isStartCorner ? aff.wall.end : aff.wall.start;
          const originalLength = Math.sqrt(
            Math.pow(aff.wall.end.x - aff.wall.start.x, 2) + 
            Math.pow(aff.wall.end.y - aff.wall.start.y, 2)
          ) / PIXELS_PER_METER;
          const newLength = Math.sqrt(
            Math.pow(fixedPoint.x - snappedForAnglePoint.x, 2) + 
            Math.pow(fixedPoint.y - snappedForAnglePoint.y, 2)
          ) / PIXELS_PER_METER;
          const angle = Math.atan2(
            fixedPoint.y - snappedForAnglePoint.y,
            fixedPoint.x - snappedForAnglePoint.x
          ) * 180 / Math.PI;
          
          return {
            wallId: aff.wall.id!,
            newLength,
            angle: angle >= 0 ? angle : angle + 360,
            originalLength,
            lengthChange: newLength - originalLength
          };
        });
        
        const angleDiff = Math.abs(previewWalls[1].angle - previewWalls[0].angle);
        const cornerAngle = angleDiff > 180 ? 360 - angleDiff : angleDiff;
        
        setCornerDragPreview({
          cornerPosition: snappedForAnglePoint,
          affectedWalls: previewWalls,
          cornerAngle
        });
      }
      
      return;
    }
    
    // Handle counter rotation
    if (isRotatingCounter && selectedCounterId) {
      const counter = elements.find(el => el.type === 'counter' && (el as any).id === selectedCounterId) as CounterElement | undefined;
      if (counter && !counter.wallId) {
        // Calculate angle from counter center to current mouse position
        const centerX = (counter.start.x + counter.end.x) / 2;
        const centerY = (counter.start.y + counter.end.y) / 2;
        const currentAngle = Math.atan2(mousePoint.y - centerY, mousePoint.x - centerX);
        const angleDelta = currentAngle - rotationStartAngle;
        
        // Get current counter direction
        const dx = counter.end.x - counter.start.x;
        const dy = counter.end.y - counter.start.y;
        const currentCounterAngle = Math.atan2(dy, dx);
        
        // Calculate new angle
        const newAngle = currentCounterAngle + angleDelta;
        const length = counter.length;
        
        // Rotate counter around its center
        const newStart = {
          x: centerX - Math.cos(newAngle) * length / 2,
          y: centerY - Math.sin(newAngle) * length / 2,
        };
        const newEnd = {
          x: centerX + Math.cos(newAngle) * length / 2,
          y: centerY + Math.sin(newAngle) * length / 2,
        };
        
        const rotatedCounter = { ...counter, start: newStart, end: newEnd };
        setElements(elements.map(el => (el.type === 'counter' && (el as any).id === selectedCounterId) ? rotatedCounter : el));
        
        // Update rotation start angle for continuous rotation
        setRotationStartAngle(currentAngle);
      }
      return;
    }
    
    // Handle garden rectangle drag preview
    if (isDraggingGarden && gardenDragStart) {
      setGardenPreview({ start: gardenDragStart, end: mousePoint });
      return;
    }
    
    // Handle garden corner resizing
    if (isResizingGarden && selectedGardenId && gardenResizeHandle) {
      const garden = elements.find(el => el.type === 'utility' && (el as any).utilityType === 'garden' && (el as any).id === selectedGardenId) as UtilityElement | undefined;
      if (garden && garden.width && garden.height) {
        const resizedGarden = resizeGarden(garden, gardenResizeHandle, mousePoint);
        setElements(elements.map(el => (el.type === 'utility' && (el as any).id === selectedGardenId) ? resizedGarden : el));
      }
      return;
    }
    
    // Handle counter dragging
    if (isDraggingCounter && selectedCounterId && counterDragStart) {
      const counter = elements.find(el => el.type === 'counter' && (el as any).id === selectedCounterId) as CounterElement | undefined;
      if (counter && !counter.wallId) {
        // Free-floating counter: drag freely in any direction
        const deltaX = mousePoint.x - counterDragStart.x;
        const deltaY = mousePoint.y - counterDragStart.y;
        
        const draggedCounter = {
          ...counter,
          start: { x: counter.start.x + deltaX, y: counter.start.y + deltaY },
          end: { x: counter.end.x + deltaX, y: counter.end.y + deltaY },
        };
        
        setElements(elements.map(el => (el.type === 'counter' && (el as any).id === selectedCounterId) ? draggedCounter : el));
        setCounterDragStart(mousePoint);
      } else if (counter && counter.wallId) {
        // Wall-snapped counter: drag along the wall
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const wall = wallsWithStableIds.find(w => w.stableId === counter.wallId);
        if (wall) {
          const draggedCounter = dragCounterAlongWall(counter, mousePoint, wall);
          setElements(elements.map(el => (el.type === 'counter' && (el as any).id === selectedCounterId) ? draggedCounter : el));
        }
      }
      return;
    }
    
    // Handle counter resizing
    if (isResizingCounter && selectedCounterId && counterResizeHandle) {
      const counter = elements.find(el => el.type === 'counter' && (el as any).id === selectedCounterId) as CounterElement | undefined;
      if (counter && counter.wallId) {
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const wall = wallsWithStableIds.find(w => w.stableId === counter.wallId);
        if (wall) {
          const resizedCounter = resizeCounter(counter, counterResizeHandle, mousePoint, wall);
          // Update the counter in elements
          setElements(elements.map(el => (el.type === 'counter' && (el as any).id === selectedCounterId) ? resizedCounter : el));
        }
      } else if (counter) {
        // Free-floating counter - update dimensions based on handle
        // Maintain current orientation (don't allow rotation during resize)
        const resizedCounter = { ...counter };
        
        if (counterResizeHandle === 'length-start') {
          // Move start point along current counter direction, keep end fixed
          const dx = counter.end.x - counter.start.x;
          const dy = counter.end.y - counter.start.y;
          const len = Math.hypot(dx, dy);
          const dirX = dx / len;
          const dirY = dy / len;
          
          // Project cursor onto counter line from end point
          const toMouse = { x: mousePoint.x - counter.end.x, y: mousePoint.y - counter.end.y };
          const projection = toMouse.x * dirX + toMouse.y * dirY;
          
          // New length is absolute value of projection (negative means opposite direction)
          const newLength = Math.max(50, Math.abs(projection));
          resizedCounter.length = newLength;
          
          // Move start along counter direction from end
          resizedCounter.start = {
            x: counter.end.x - dirX * newLength,
            y: counter.end.y - dirY * newLength,
          };
        } else if (counterResizeHandle === 'length-end') {
          // Move end point along current counter direction, keep start fixed
          const dx = counter.end.x - counter.start.x;
          const dy = counter.end.y - counter.start.y;
          const len = Math.hypot(dx, dy);
          const dirX = dx / len;
          const dirY = dy / len;
          
          // Project cursor onto counter line from start point
          const toMouse = { x: mousePoint.x - counter.start.x, y: mousePoint.y - counter.start.y };
          const projection = toMouse.x * dirX + toMouse.y * dirY;
          
          // New length is absolute value of projection
          const newLength = Math.max(50, Math.abs(projection));
          resizedCounter.length = newLength;
          
          // Move end along counter direction from start
          resizedCounter.end = {
            x: counter.start.x + dirX * newLength,
            y: counter.start.y + dirY * newLength,
          };
        } else if (counterResizeHandle === 'width-start' || counterResizeHandle === 'width-end') {
          // Calculate perpendicular vector from counter direction
          const dx = counter.end.x - counter.start.x;
          const dy = counter.end.y - counter.start.y;
          const perpX = -dy;
          const perpY = dx;
          const perpLen = Math.hypot(perpX, perpY);
          const perpNormX = perpX / perpLen;
          const perpNormY = perpY / perpLen;
          
          // Reference point is the corresponding length corner
          const refPoint = counterResizeHandle === 'width-start' ? counter.start : counter.end;
          
          // Calculate perpendicular distance from reference to cursor
          const toPoint = { x: mousePoint.x - refPoint.x, y: mousePoint.y - refPoint.y };
          const perpDist = toPoint.x * perpNormX + toPoint.y * perpNormY;
          
          resizedCounter.width = Math.max(20, Math.abs(perpDist));
        }
        setElements(elements.map(el => (el.type === 'counter' && (el as any).id === selectedCounterId) ? resizedCounter : el));
      }
      return;
    }
    
    // Handle compass rotation
    if (isRotatingCompass && selectedCompassId) {
      const compass = elements.find(el => el.type === 'compass' && (el as any).id === selectedCompassId) as CompassType | undefined;
      if (compass) {
        const currentAngle = Math.atan2(mousePoint.y - compass.position.y, mousePoint.x - compass.position.x) * 180 / Math.PI;
        const angleDelta = currentAngle - compassRotationStartAngle;
        const newRotation = (compass.rotation || 0) + angleDelta;
        
        const rotatedCompass = { ...compass, rotation: newRotation };
        setElements(elements.map(el => (el.type === 'compass' && (el as any).id === selectedCompassId) ? rotatedCompass : el));
        setCompassRotationStartAngle(currentAngle);
      }
      return;
    }
    
    // Handle stairs rotation
    if (isRotatingStairs && selectedStairsId) {
      const stairs = elements.find(el => el.type === 'stairs' && (el as any).id === selectedStairsId) as StairsElement | undefined;
      if (stairs && !stairs.wallId) {
        // Calculate angle from stairs center to current mouse position
        const centerX = (stairs.start.x + stairs.end.x) / 2;
        const centerY = (stairs.start.y + stairs.end.y) / 2;
        const currentAngle = Math.atan2(mousePoint.y - centerY, mousePoint.x - centerX);
        
        // Calculate rotation delta
        const deltaAngle = currentAngle - rotationStartAngle;
        const newAngle = stairs.rotation + deltaAngle;
        
        // Rotate stairs around center
        const length = stairs.length;
        const newStart = {
          x: centerX - Math.cos(newAngle) * length / 2,
          y: centerY - Math.sin(newAngle) * length / 2,
        };
        const newEnd = {
          x: centerX + Math.cos(newAngle) * length / 2,
          y: centerY + Math.sin(newAngle) * length / 2,
        };
        
        const rotatedStairs = { ...stairs, start: newStart, end: newEnd, rotation: newAngle };
        setElements(elements.map(el => (el.type === 'stairs' && (el as any).id === selectedStairsId) ? rotatedStairs : el));
        setRotationStartAngle(currentAngle);
      }
      return;
    }
    
    // Handle stairs dragging
    if (isDraggingStairs && selectedStairsId && counterDragStart) {
      const stairs = elements.find(el => el.type === 'stairs' && (el as any).id === selectedStairsId) as StairsElement | undefined;
      if (stairs && !stairs.wallId) {
        // Free-floating stairs: drag freely
        const deltaX = mousePoint.x - counterDragStart.x;
        const deltaY = mousePoint.y - counterDragStart.y;
        
        const draggedStairs = {
          ...stairs,
          start: { x: stairs.start.x + deltaX, y: stairs.start.y + deltaY },
          end: { x: stairs.end.x + deltaX, y: stairs.end.y + deltaY },
        };
        
        setElements(elements.map(el => (el.type === 'stairs' && (el as any).id === selectedStairsId) ? draggedStairs : el));
        setCounterDragStart(mousePoint);
      } else if (stairs && stairs.wallId) {
        // Wall-snapped stairs: drag along the wall
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const wall = wallsWithStableIds.find(w => w.stableId === stairs.wallId);
        if (wall) {
          const draggedStairs = dragStairsAlongWall(stairs, mousePoint, wall);
          setElements(elements.map(el => (el.type === 'stairs' && (el as any).id === selectedStairsId) ? draggedStairs : el));
        }
      }
      return;
    }
    
    // Handle stairs resizing
    if (isResizingStairs && selectedStairsId && stairsResizeHandle) {
      const stairs = elements.find(el => el.type === 'stairs' && (el as any).id === selectedStairsId) as StairsElement | undefined;
      if (stairs && stairs.wallId) {
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const wall = wallsWithStableIds.find(w => w.stableId === stairs.wallId);
        if (wall) {
          const resizedStairs = resizeStairs(stairs, stairsResizeHandle as any, mousePoint, wall);
          setElements(elements.map(el => (el.type === 'stairs' && (el as any).id === selectedStairsId) ? resizedStairs : el));
        }
      } else if (stairs) {
        // Free-floating stairs - resize length or width
        const resizedStairs = { ...stairs };
        
        if (stairsResizeHandle === 'length-start') {
          // Move start point along current stairs direction, keep end fixed
          const dx = stairs.end.x - stairs.start.x;
          const dy = stairs.end.y - stairs.start.y;
          const len = Math.hypot(dx, dy);
          const dirX = dx / len;
          const dirY = dy / len;
          
          // Project cursor onto stairs line from end point
          const toMouse = { x: mousePoint.x - stairs.end.x, y: mousePoint.y - stairs.end.y };
          const projection = toMouse.x * dirX + toMouse.y * dirY;
          
          // New length is absolute value of projection (negative means opposite direction)
          const newLength = Math.max(50, Math.abs(projection));
          resizedStairs.length = newLength;
          
          // Move start along stairs direction from end
          resizedStairs.start = {
            x: stairs.end.x - dirX * newLength,
            y: stairs.end.y - dirY * newLength,
          };
        } else if (stairsResizeHandle === 'length-end') {
          // Move end point along current stairs direction, keep start fixed
          const dx = stairs.end.x - stairs.start.x;
          const dy = stairs.end.y - stairs.start.y;
          const len = Math.hypot(dx, dy);
          const dirX = dx / len;
          const dirY = dy / len;
          
          // Project cursor onto stairs line from start point
          const toMouse = { x: mousePoint.x - stairs.start.x, y: mousePoint.y - stairs.start.y };
          const projection = toMouse.x * dirX + toMouse.y * dirY;
          
          // New length is absolute value of projection
          const newLength = Math.max(50, Math.abs(projection));
          resizedStairs.length = newLength;
          
          // Move end along stairs direction from start
          resizedStairs.end = {
            x: stairs.start.x + dirX * newLength,
            y: stairs.start.y + dirY * newLength,
          };
        } else if (stairsResizeHandle === 'width-start' || stairsResizeHandle === 'width-end') {
          // Adjust width perpendicular to stairs direction
          const dx = stairs.end.x - stairs.start.x;
          const dy = stairs.end.y - stairs.start.y;
          const perpX = -dy;
          const perpY = dx;
          const perpLen = Math.hypot(perpX, perpY);
          const perpNormX = perpX / perpLen;
          const perpNormY = perpY / perpLen;
          
          const refPoint = stairsResizeHandle === 'width-start' ? stairs.start : stairs.end;
          const toPoint = { x: mousePoint.x - refPoint.x, y: mousePoint.y - refPoint.y };
          const perpDist = toPoint.x * perpNormX + toPoint.y * perpNormY;
          
          resizedStairs.width = Math.max(20, Math.abs(perpDist));
        }
        setElements(elements.map(el => (el.type === 'stairs' && (el as any).id === selectedStairsId) ? resizedStairs : el));
      }
      return;
    }
    
    // Update cursor position for gap fill prompt
    setCursorPosition({ x: e.clientX, y: e.clientY });
    
    // 🎯 GAP DETECTION: Check for gaps during room resize
    if (isResizing && selectedRoom && detectedGaps.length > 0) {
      const suggestion = findGapForResize(selectedRoom, mousePoint, detectedGaps, 150);
      setGapFillSuggestion(suggestion);
    } else if (gapFillSuggestion) {
      setGapFillSuggestion(null);
    }

    // SIMPLIFIED: Room dragging with simple wall snapping
    if (isDraggingRoom && selectedRoom && roomStart) {
      const deltaX = mousePoint.x - roomStart.x;
      const deltaY = mousePoint.y - roomStart.y;

      // Simple wall snapping - just check if the mouse is near any wall endpoints
      const targetWalls = currentWalls.filter(wall => 
        !selectedRoom.walls.some(roomWall => roomWall.tempId === wall.tempId)
      );
      
      const snapResult = snapToWalls(mousePoint, targetWalls);
      
      if (snapResult.type !== 'none') {
        // Use snapped position
        const adjustedDelta = {
          x: snapResult.point.x - roomStart.x,
          y: snapResult.point.y - roomStart.y
        };
        
        const movedWalls = selectedRoom.walls.map(wall => ({
          ...wall,
          start: { x: wall.start.x + adjustedDelta.x, y: wall.start.y + adjustedDelta.y },
          end: { x: wall.end.x + adjustedDelta.x, y: wall.end.y + adjustedDelta.y },
        }));
        setTempWalls(movedWalls);
        setCurrentSnapResult(snapResult);
      } else {
        // Regular movement
        const movedWalls = selectedRoom.walls.map(wall => ({
          ...wall,
          start: { x: wall.start.x + deltaX, y: wall.start.y + deltaY },
          end: { x: wall.end.x + deltaX, y: wall.end.y + deltaY },
        }));
        setTempWalls(movedWalls);
        setCurrentSnapResult(null);
      }
      return;
    }

    // Continue with regular mouse move handling
    optimizedMouseMoveHandler(e);
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // NEW: Handle room positioning mode drag end
    if (isPositioningRoom && roomDragStart) {
      setRoomDragStart(null);
      console.log('[Room Positioning] Center drag ended - user can adjust or confirm position');
      return;
    }
    
    // NEW: Handle room positioning corner drag end - EXACT RESIZE TOOL COMMIT LOGIC
    if (isPositioningRoom && resizeHandle && tempWalls) {
      // 🎯 EXACT RESIZE TOOL COMMIT (from useFloorPlanInteractions:11066-11070)
      // Apply CM grid snapping to the final position from tempWalls
      const roomWallIdSet = new Set(positioningRoomWallIds);
      
      const committedWalls = tempWalls.map(wall => {
        if (!wall.id || !roomWallIdSet.has(wall.id)) {
          return wall;
        }
        
        // Apply CM grid snapping (same as resize tool)
        return {
          ...wall,
          start: {
            x: Math.round(wall.start.x / CM_SNAP_SIZE) * CM_SNAP_SIZE,
            y: Math.round(wall.start.y / CM_SNAP_SIZE) * CM_SNAP_SIZE
          },
          end: {
            x: Math.round(wall.end.x / CM_SNAP_SIZE) * CM_SNAP_SIZE,
            y: Math.round(wall.end.y / CM_SNAP_SIZE) * CM_SNAP_SIZE
          }
        };
      });
      
      // CRITICAL: Set operation type to prevent room merging during positioning
      lastOperationTypeRef.current = 'positioningRoomAdjustment';
      
      // Commit to floorPlans
      setFloorPlans(prev => ({ ...prev, [currentFloor]: committedWalls }));
      
      // Clear states
      setResizeHandle(null);
      setRoomStart(null);
      setTempWalls(null);
      setCornerDragPreview(null);
      
      console.log('[Room Positioning] ✅ Corner drag committed with CM grid snapping - room merging prevented');
      return;
    }
    
    // Finish garden rectangle drag and create garden
    if (isDraggingGarden && gardenPreview) {
      const width = Math.abs(gardenPreview.end.x - gardenPreview.start.x);
      const height = Math.abs(gardenPreview.end.y - gardenPreview.start.y);
      
      // Only create if drag is meaningful (minimum size)
      if (width > 50 && height > 50) {
        recordUndoState();
        const PIXELS_PER_METER = 100;
        const area = (width * height) / (PIXELS_PER_METER * PIXELS_PER_METER);
        const center = {
          x: (gardenPreview.start.x + gardenPreview.end.x) / 2,
          y: (gardenPreview.start.y + gardenPreview.end.y) / 2
        };
        
        const newGarden: UtilityElement = {
          id: `garden-${Date.now()}`,
          type: 'utility',
          utilityType: 'garden',
          position: center,
          floor: currentFloor,
          width,
          height,
          area,
          snappedWalls: []
        };
        
        setElements([...elements, newGarden]);
        console.log('🌳 Garden created:', width.toFixed(0), 'x', height.toFixed(0), 'px, area:', area.toFixed(2), 'm²');
      }
      
      setIsDraggingGarden(false);
      setGardenDragStart(null);
      setGardenPreview(null);
      return;
    }
    
    // Finish garden corner resizing
    if (isResizingGarden) {
      recordUndoState();
      setIsResizingGarden(false);
      setSelectedGardenId(null);
      setGardenResizeHandle(null);
      return;
    }
    
    // Finish counter rotation
    if (isRotatingCounter) {
      recordUndoState();
      setIsRotatingCounter(false);
      setSelectedCounterId(null);
      setRotationStartAngle(0);
      return;
    }
    
    // Finish compass rotation
    if (isRotatingCompass) {
      recordUndoState();
      setIsRotatingCompass(false);
      setSelectedCompassId(null);
      setCompassRotationStartAngle(0);
      return;
    }
    
    // Finish counter dragging
    if (isDraggingCounter) {
      recordUndoState();
      setIsDraggingCounter(false);
      setSelectedCounterId(null);
      setCounterDragStart(null);
      return;
    }
    
    // Finish counter resizing
    if (isResizingCounter) {
      recordUndoState();
      setIsResizingCounter(false);
      setSelectedCounterId(null);
      setCounterResizeHandle(null);
      return;
    }
    
    // Skip room drag handling if we're using wall tool - let hook handle it
    if (isDraggingRoom && currentTool !== 'wall') {
      if (tempWalls) {
        recordUndoState();
        const newWalls = currentWalls.filter(wall => !selectedRoom?.walls.some(selectedWall => selectedWall.tempId === wall.tempId));
        
        // 🆕 ENHANCED: Auto-connect room to nearby walls if snap occurred
        let finalWalls = tempWalls;
        if (currentSnapResult && currentSnapResult.type === 'endpoint' && selectedRoom) {
          try {
            // Simple room connection logic without complex auto-connect
            console.log('✅ Room endpoint snap detected - keeping simple behavior');
          } catch (error) {
            console.warn('⚠️ Room auto-connection failed:', error);
          }
        }
        
        setFloorPlans(prev => ({
          ...prev,
          [currentFloor]: [...newWalls, ...finalWalls]
        }));
      }
      
      // 🆕 ENHANCED: Clear snap feedback
      setIsDraggingRoom(false);
      setSelectedRoom(null);
      setRoomStart(null);
      setTempWalls(null);
      setCurrentSnapResult(null);
      return;
    }

    if (isDraggingGardenLabel) {
      setIsDraggingGardenLabel(false);
      setSelectedGardenId(null);
      setGardenLabelDragStart(null);
      return;
    }

    if (isDraggingRoomLabel) {
      setIsDraggingRoomLabel(false);
      setDraggedRoomLabelId(null);
      return;
    }

    
    if (e.nativeEvent.detail === 0 && 'ontouchstart' in window) {
      return;
    }

    if (isPlacingLight && lightPreview) {
      recordUndoState();
      const newLight = { ...lightPreview, id: `light-${Date.now()}` } as unknown as FloorPlanElement;
      const updatedElements = [...elements, newLight];
      setElements(updatedElements);
      
      setTimeout(() => {
        const roomContainingLight = findRoomAtPoint(lightPreview.position, currentRooms);
        
        if (roomContainingLight && roomContainingLight.name) {
          roomSync.updateLightCount(roomContainingLight.name, lightPreview.lightType, 1, currentFloor);
          
          roomSync.syncAllFloorsLightsToAdditionalInfo();
          
          setSyncedRoomName(roomContainingLight.name);
          setShowRoomSyncIndicator(true);
          
          console.log(`💡 Light placed in room "${roomContainingLight.name}" - updated ${lightPreview.lightType} light count`);
        }
      }, 0);
    }

    if (isPlacingSmokeAlarm && smokeAlarmPreview) {
      recordUndoState();
      setElements([...elements, { ...smokeAlarmPreview, id: `smoke-alarm-${Date.now()}` } as unknown as FloorPlanElement]);
    }

    if (isPlacingFireSafety && fireSafetyPreview) {
      recordUndoState();
      setElements([...elements, { ...fireSafetyPreview, id: `${fireSafetyPreview.type}-${Date.now()}` } as unknown as FloorPlanElement]);
    }

    if (isPlacingLoftHatch && loftHatchPreview) {
      recordUndoState();
      setElements([...elements, { ...loftHatchPreview, id: `loft-hatch-${Date.now()}` } as unknown as FloorPlanElement]);
      onDebug('LOFT_HATCH_PLACED', { loftHatch: loftHatchPreview });
    }

    if (isHighlighting && highlightPreview && highlightStart) {
      const currentPoint = getCanvasCoordinates(e.clientX, e.clientY);
      // Only create highlight if there's significant drag distance
      const dragDistance = Math.sqrt(
        Math.pow(currentPoint.x - highlightStart.x, 2) + 
        Math.pow(currentPoint.y - highlightStart.y, 2)
      );
      
      if (dragDistance > 5) { // Minimum drag distance to create highlight
        recordUndoState();
        // Use wall snapping for final highlight creation
        const wallsForSnapping = currentWalls.map(({tempId, ...w}) => w);
        const roomsForSnapping = currentRooms || [];
        const finalHighlight = createHighlightAreaWithWallSnapping(
          highlightStart, 
          currentPoint, 
          wallsForSnapping, 
          roomsForSnapping, 
          currentFloor, 
          highlightColor
        );
        setElements([...elements, finalHighlight]);
        console.log(`🎨 Highlight created with wall snapping from (${highlightStart.x}, ${highlightStart.y}) to (${currentPoint.x}, ${currentPoint.y})`);
        
        // Calculate metrics for the highlighted area (keeping full calculations for shared popup component)
        const calculations = calculateHighlightMetrics(finalHighlight, wallsForSnapping, roomsForSnapping);
        
        // Show popup with calculations
        setHighlightPopupData({
          highlight: finalHighlight,
          calculations,
          position: { 
            x: e.clientX + 20, // Position popup near the mouse cursor
            y: e.clientY + 20 
          }
        });
        setShowHighlightPopup(true);
      }
      
      setIsHighlighting(false);
      setHighlightStart(null);
      setHighlightPreview(null);
      setHighlightSnap(null);
    }

    if (isPlacingDoor && doorPreview) {
      recordUndoState();
      const finalDoor = { ...doorPreview, id: `door-${Date.now()}` };
      const updatedElements = [...elements, finalDoor as unknown as FloorPlanElement];
      setElements(updatedElements);
      
      if (roomSync) {
        const allDoors = updatedElements.filter(el => el.type === 'door') as unknown as DoorElement[];
        const currentFloorWalls = floorPlans[currentFloor] || [];
        roomSync.syncDoorUndercuts(allDoors, currentRooms, currentFloor, currentFloorWalls);
        
        const allFloorsDoors = elements.filter(el => el.type === 'door') as unknown as DoorElement[];
        roomSync.syncAllFloorsDoorUndercuts(allFloorsDoors, floorPlans, roomCustomLabels);
        
        console.log(`🚪 New door placed (immediate) - updated door count: ${allDoors.length}, total across all floors: ${allFloorsDoors.length}`);
      }
      
      setDoorToEdit(finalDoor as DoorElement);
      setIsDoorPromptOpen(true);
    }

    if (isPlacingWindow && windowPreview) {
      recordUndoState();
      const finalWindow = { 
        ...windowPreview, 
        id: `window-${Date.now()}`,
        displayId: generateNextWindowId(windowCounter)
      };
      
      const finalWindowWithOrientation = calculateWindowOrientationIfReady(finalWindow as WindowElement);
      
      const updatedElements = [...elements, finalWindowWithOrientation as unknown as FloorPlanElement];
      setElements(updatedElements);
      
      // Increment window counter for next window
      setWindowCounter(prev => prev + 1);
      
      // Trigger window count update immediately after placement
      if (roomSync) {
        // Use setTimeout to ensure state update has completed before sync
        setTimeout(() => {
          const allWindows = updatedElements.filter(el => el.type === 'window') as unknown as WindowElement[];
          
          // Sync room-specific windows for current floor
          roomSync.smartSyncWindows(allWindows, currentRooms, currentFloor);
          
          // Sync overall window counts across ALL floors for site notes
          roomSync.syncAllFloorsWindows(allWindows, floorPlans, roomCustomLabels);
          
          console.log(`🪟 Window placement sync completed - Total windows: ${allWindows.length}`);
        }, 50);
      }
      
      setWindowToEdit(finalWindowWithOrientation as WindowElement);
      setIsWindowPromptOpen(true);
    }

    if (isPlacingRadiator && radiatorPreview) {
      recordUndoState();
      
      const finalPoint = getCanvasCoordinates(e.clientX, e.clientY);
      const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
      const finalWall = findWallForRadiator(finalPoint, wallsWithStableIds);
      
      if (finalWall) {
        const finalRadiatorElement = createRadiatorElement(finalPoint, finalWall, currentFloor, currentWalls, roomCustomLabels);
        
        if (finalRadiatorElement) {
          const finalRadiator: RadiatorElement = {
            ...finalRadiatorElement,
            id: `radiator-${Date.now()}`,
          };
          setElements([...elements, finalRadiator]);
          // 🏢 ESTATE MODE: Skip radiator modal on placement
          console.log('🏢 ESTATE: Radiator placed, modal disabled for estate floorplan');
          
        } else {
          console.warn('🔥 Radiator placement failed at final position - not inside any room');
        }
      } else {
        const finalRadiator: RadiatorElement = {
          ...radiatorPreview,
          id: `radiator-${Date.now()}`,
        };
        setElements([...elements, finalRadiator]);
        // 🏢 ESTATE MODE: Skip radiator modal on placement
        console.log('🏢 ESTATE: Radiator placed, modal disabled for estate floorplan');
        
      }
    }

    if (isPlacingBoiler && boilerPreview) {
      recordUndoState();
      
      const finalPoint = getCanvasCoordinates(e.clientX, e.clientY);
      const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
      const finalWall = findWallForBoiler(finalPoint, wallsWithStableIds);
      
      if (finalWall) {
        const finalBoilerElement = createBoilerElement(finalPoint, finalWall, currentFloor);
        
        if (finalBoilerElement) {
          const finalBoiler: BoilerElement = {
            ...finalBoilerElement,
            id: `boiler-${Date.now()}`,
          };
          setElements([...elements, finalBoiler]);
          // 🏢 ESTATE MODE: Skip boiler modal on placement
          console.log('🏢 ESTATE: Boiler placed, modal disabled for estate floorplan');
          
          if (roomSync && Object.values(currentRooms).length > 0) {
            roomSync.syncBoilerLocations([finalBoiler], Object.values(currentRooms));
          }
          
          console.log(`🔥 Boiler placed at final position: (${finalBoiler.position.x}, ${finalBoiler.position.y})`);
        } else {
          console.warn('🔥 Boiler placement failed at final position');
        }
      } else {
        const finalBoiler: BoilerElement = {
          ...boilerPreview,
          id: `boiler-${Date.now()}`,
        };
        setElements([...elements, finalBoiler]);
        // 🏢 ESTATE MODE: Skip boiler modal on placement
        console.log('🏢 ESTATE: Boiler placed, modal disabled for estate floorplan');
        
        if (roomSync && Object.values(currentRooms).length > 0) {
          roomSync.syncBoilerLocations([finalBoiler], Object.values(currentRooms));
        }
        
        console.log(`🔥 Boiler placed at preview position (fallback): (${finalBoiler.position.x}, ${finalBoiler.position.y})`);
      }
    }

    if (isPlacingCounter && counterPreview) {
      recordUndoState();
      
      const finalPoint = getCanvasCoordinates(e.clientX, e.clientY);
      const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
      const finalCounter = createCounterWithAutoSnap(finalPoint, wallsWithStableIds, currentFloor);
      const updatedElements = [...elements, finalCounter as unknown as FloorPlanElement];
      setElements(updatedElements);
      setIsPlacingCounter(false);
      setCounterPreview(null);
      setWallForCounter(null);
      
      // Deselect all other counters and select the new one
      setSelectedCounterId(finalCounter.id);
      
      // Log counter placement for sync tracking
      console.log('✅ Counter placed:', {
        id: finalCounter.id,
        floor: currentFloor,
        wallId: finalCounter.wallId || 'free-floating',
        position: `(${finalCounter.start.x.toFixed(1)}, ${finalCounter.start.y.toFixed(1)})`,
        length: `${finalCounter.length.toFixed(1)}px`,
        width: `${finalCounter.width.toFixed(1)}px`
      });
    }

    if (isPlacingStairs && stairsPreview) {
      recordUndoState();
      
      const finalPoint = getCanvasCoordinates(e.clientX, e.clientY);
      const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
      const finalStairs = createStairsWithAutoSnap(finalPoint, wallsWithStableIds, currentFloor, undefined, undefined, stairsDirection);
      const updatedElements = [...elements, finalStairs as unknown as FloorPlanElement];
      setElements(updatedElements);
      setIsPlacingStairs(false);
      setStairsPreview(null);
      setWallForStairs(null);
      
      // Deselect all other stairs and select the new one
      setSelectedStairsId(finalStairs.id);
      
      // Log stairs placement for sync tracking
      console.log('✅ Stairs placed:', {
        id: finalStairs.id,
        floor: currentFloor,
        wallId: finalStairs.wallId || 'free-floating',
        position: `(${finalStairs.start.x.toFixed(1)}, ${finalStairs.start.y.toFixed(1)})`,
        length: `${finalStairs.length.toFixed(1)}px`,
        width: `${finalStairs.width.toFixed(1)}px`,
        direction: finalStairs.direction
      });
    }

    if (isPlacingLight && lightPreview) {
      recordUndoState();
      const finalLight = { ...lightPreview, id: `light-${Date.now()}` };
      const updatedElements = [...elements, finalLight as unknown as FloorPlanElement];
      setElements(updatedElements);
      setIsPlacingLight(false);
      setLightPreview(null);
    }

    if (isPlacingSmokeAlarm && smokeAlarmPreview) {
      recordUndoState();
      const finalSmokeAlarm = { ...smokeAlarmPreview, id: `smoke-alarm-${Date.now()}` };
      const updatedElements = [...elements, finalSmokeAlarm as unknown as FloorPlanElement];
      setElements(updatedElements);
      setIsPlacingSmokeAlarm(false);
      setSmokeAlarmPreview(null);
    }

    if (isPlacingFireSafety && fireSafetyPreview) {
      recordUndoState();
      const finalFireSafety = { ...fireSafetyPreview, id: `fire-safety-${Date.now()}` };
      const updatedElements = [...elements, finalFireSafety as unknown as FloorPlanElement];
      setElements(updatedElements);
      setIsPlacingFireSafety(false);
      setFireSafetyPreview(null);
    }

    if (isPlacingUtility && utilityPreview) {
      recordUndoState();
      
      // Apply wall snapping for garden elements
      let finalUtility = { ...utilityPreview, id: `utility-${Date.now()}` };
      if (utilityPreview.utilityType === 'garden') {
        const snapResult = snapGardenToWalls(
          utilityPreview.position,
          currentWalls,
          currentRooms || [],
          { width: utilityPreview.width || 300, height: utilityPreview.height || 200 }
        );
        finalUtility = {
          ...finalUtility,
          width: snapResult.width,
          height: snapResult.height,
          area: snapResult.area,
          snappedWalls: snapResult.snappedWalls
        };
      }
      
      const updatedElements = [...elements, finalUtility as unknown as FloorPlanElement];
      setElements(updatedElements);
      setIsPlacingUtility(false);
      setUtilityPreview(null);
      setWallForSink(null);
    }

    if (isPlacingLoftHatch && loftHatchPreview) {
      recordUndoState();
      const finalLoftHatch = { ...loftHatchPreview, id: `loft-hatch-${Date.now()}` };
      const updatedElements = [...elements, finalLoftHatch as unknown as FloorPlanElement];
      setElements(updatedElements);
      setIsPlacingLoftHatch(false);
      setLoftHatchPreview(null);
    }

    if (isPlacingCompass && compassPreview) {
      recordUndoState();
      const finalCompass = { ...compassPreview, id: `compass-${Date.now()}`, floor: currentFloor } as any;
      const updatedElements = [...elements, finalCompass as unknown as FloorPlanElement];
      setElements(updatedElements);
      // Keep compass tool active for continuous placement
      setCompassPreview(null);
    }

    setIsPlacingDoor(false);
    setDoorPreview(null);
    setWallForDoor(null);
    setIsPlacingWindow(false);
    setWindowPreview(null);
    setWallForWindow(null);
    setIsPlacingRadiator(false);
    setRadiatorPreview(null);
    setWallForRadiator(null);
    setIsPlacingBoiler(false);
    setBoilerPreview(null);
    setWallForBoiler(null);
    setIsPlacingCounter(false);
    setCounterPreview(null);
    setWallForCounter(null);
    setIsPlacingStairs(false);
    setStairsPreview(null);
    setWallForStairs(null);
    setIsPlacingLight(false);
    setLightPreview(null);
    setIsPlacingSmokeAlarm(false);
    setSmokeAlarmPreview(null);
    setIsPlacingFireSafety(false);
    setFireSafetyPreview(null);
    setIsPlacingUtility(false);
    setUtilityPreview(null);
    setWallForSink(null);
    setIsPlacingLoftHatch(false);
    setLoftHatchPreview(null);
    setIsHighlighting(false);
    setHighlightStart(null);
    setHighlightPreview(null);
    setHighlightSnap(null);

    if (currentTool === 'select') {
      hookHandleCanvasMouseUp(e);
    } else {
      hookHandleMouseUp(e);
    }
  };

  const activeFullscreenState = isNativeFullscreenSupported ? isFullscreen : isCssFullscreen;

  const handleToggleFullscreen = () => {
    const isCurrentlyIOS = isIOSDevice();

    if (isNativeFullscreenSupported && !isCurrentlyIOS) {
      // For native fullscreen, save/restore view state based on current state
      if (!isFullscreen) {
        // Entering fullscreen - save current view state
        setPreFullscreenViewState({
          viewOffset: { ...viewOffset },
          scale: scale
        });
      }
      toggleFullscreen();
    } else if (isCurrentlyIOS || !isNativeFullscreenSupported) {
      setIsCssFullscreen(prev => {
        const newValue = !prev;
        
        if (newValue) {
          // Entering fullscreen - save current view state
          setPreFullscreenViewState({
            viewOffset: { ...viewOffset },
            scale: scale
          });
          // Trigger canvas resize after entering fullscreen
          setTimeout(() => {
            const canvas = canvasRef.current;
            const container = containerRef.current;
            if (canvas && container) {
              const resizeEvent = new Event('resize');
              window.dispatchEvent(resizeEvent);
            }
          }, 400);
        } else {
          // Exiting fullscreen - restore view state
          if (preFullscreenViewState) {
            setTimeout(() => {
              setViewOffset(preFullscreenViewState.viewOffset);
              setScale(preFullscreenViewState.scale);
              setPreFullscreenViewState(null);
              // Trigger canvas resize after exiting fullscreen
              const canvas = canvasRef.current;
              const container = containerRef.current;
              if (canvas && container) {
                const resizeEvent = new Event('resize');
                window.dispatchEvent(resizeEvent);
              }
            }, 200); // Small delay to allow layout to settle
          }
        }
        
        // Hide address bar on iOS when entering fullscreen
        if (newValue && isCurrentlyIOS) {
          setTimeout(() => {
            window.scrollTo(0, 1);
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 100);
          }, 100);
        }
        return newValue;
      });
    } else {
      toggleFullscreen();
    }
  };

  // CRITICAL: Scroll restoration fail-safe to prevent app from becoming unscrollable
  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    
    const scrollCheckInterval = setInterval(() => {
      if (!activeFullscreenState && !isCssFullscreen) {
        const currentHtmlOverflow = document.documentElement.style.overflow;
        const currentBodyOverflow = document.body.style.overflow;
        
        if (currentHtmlOverflow === 'hidden' || currentBodyOverflow === 'hidden') {
          console.warn('[RetrofitFloorPlan] Detected stuck scroll state, restoring...');
          document.documentElement.style.overflow = '';
          document.body.style.overflow = '';
        }
      }
    }, 2000);

    return () => {
      clearInterval(scrollCheckInterval);
      document.documentElement.style.overflow = originalHtmlOverflow || '';
      document.body.style.overflow = originalBodyOverflow || '';
    };
  }, [activeFullscreenState, isCssFullscreen]);

  // Enhanced iOS fullscreen effect with better error handling
  useEffect(() => {
    const layoutElement = layoutRef.current;
    if (!layoutElement) return;

    const isCurrentlyIOS = isIOSDevice();
    
    try {
    if (isCssFullscreen && isCurrentlyIOS) {
      layoutElement.classList.add('ios-fullscreen');
      
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover, minimal-ui'
        );
      }
      
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
    } else {
      layoutElement.classList.remove('ios-fullscreen');
      
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        );
      }
      
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    } catch (error) {
      console.error('[RetrofitFloorPlan] Error in fullscreen effect:', error);
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    return () => {
      try {
      layoutElement.classList.remove('ios-fullscreen');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
        
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 
            'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
          );
        }
      } catch (error) {
        console.error('[RetrofitFloorPlan] Error in fullscreen cleanup:', error);
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    };
  }, [isCssFullscreen]);

  // Keyboard event handler for room deletion
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input field or SurveyorSiteNotes is open
      const isTypingInInput = (e.target as HTMLElement)?.tagName === 'INPUT' || 
                             (e.target as HTMLElement)?.tagName === 'TEXTAREA' ||
                             (e.target as HTMLElement)?.contentEditable === 'true';
      
      if (isTypingInInput || showSiteNotes) {
        return; // Don't handle keyboard shortcuts when typing or in site notes
      }
      
      // Only handle Delete key when a room is selected and no modal is open
      if (e.key === 'Delete' && selectedRoom && !isRoomLabelPromptOpen && !isRoomRenameModalOpen && !isRoomContextMenuOpen) {
        e.preventDefault();
        deleteSelectedRoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedRoom, deleteSelectedRoom, isRoomLabelPromptOpen, isRoomRenameModalOpen, isRoomContextMenuOpen, showSiteNotes]);

  // Keyboard event handler for ESC key to close popups and Space key for gap fill
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input field or SurveyorSiteNotes is open
      const isTypingInInput = (e.target as HTMLElement)?.tagName === 'INPUT' || 
                             (e.target as HTMLElement)?.tagName === 'TEXTAREA' ||
                             (e.target as HTMLElement)?.isContentEditable ||
                             showSiteNotes;
      
      if (isTypingInInput) return;

      if (e.key === 'Escape') {
        if (showHighlightPopup) {
          setGapFillSuggestion(null);
          return;
        }
        
        if (showHighlightPopup && highlightPopupData) {
          // Keep the highlight on canvas with orange border (don't remove it)
          // The highlight will persist and show the orange border to distinguish extensions
          setShowHighlightPopup(false);
          setHighlightPopupData(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showHighlightPopup, highlightPopupData, showSiteNotes, gapFillSuggestion, currentWalls, currentFloor, setFloorPlans, recordUndoState]);

  // Keyboard event handler for R key to rotate sinks
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input field or SurveyorSiteNotes is open
      const isTypingInInput = (e.target as HTMLElement)?.tagName === 'INPUT' || 
                             (e.target as HTMLElement)?.tagName === 'TEXTAREA' ||
                             (e.target as HTMLElement)?.isContentEditable ||
                             showSiteNotes;
      
      if (isTypingInInput) return;

      // Note: Sinks now automatically orient toward nearest wall
      // Manual rotation with R key has been removed
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPlacingUtility, utilityPreview, utilityRotation, showSiteNotes, elements]);

  // Keyboard event handler for ESC key to cancel garden drawing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isTypingInInput = (e.target as HTMLElement)?.tagName === 'INPUT' || 
                             (e.target as HTMLElement)?.tagName === 'TEXTAREA' ||
                             (e.target as HTMLElement)?.isContentEditable;
      if (isTypingInInput || showSiteNotes) return;
      
      // Cancel garden drag with ESC key
      if (e.key === 'Escape' && isDraggingGarden) {
        console.log('🌳 Garden drag cancelled by ESC key');
        setIsDraggingGarden(false);
        setGardenDragStart(null);
        setGardenPreview(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDraggingGarden, showSiteNotes]);

  // Add the drawRoomPreview function
  const drawRoomPreview = (ctx: CanvasRenderingContext2D, start: Point, end: Point) => {
    // Intentionally left blank to prevent drawing the room preview
    return;
  };

  // Effect for ResizeObserver on the canvas container
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (!canvas || !container) return;

    let animationFrameId: number;
    let resizeTimeout: ReturnType<typeof setTimeout>;

    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          const roundedWidth = Math.round(width);
          const roundedHeight = Math.round(height);
          if (canvas.width !== roundedWidth || canvas.height !== roundedHeight) {
            canvas.width = roundedWidth;
            canvas.height = roundedHeight;
            setCanvasRenderDimensions({ width: canvas.width, height: canvas.height });
            // console.log(`[ResizeObserver] Canvas attributes set by observer: ${canvas.width}x${canvas.height}`);
          }
        }
      }
    });

    observer.observe(container);

    // Enhanced function to set canvas size based on container with fullscreen considerations
    const syncCanvasSizeToContainer = () => {
      if (container && canvas) { // Re-check refs as this runs asynchronously
        let availableWidth = container.clientWidth;
        let availableHeight = container.clientHeight;
        
        
        // For fullscreen mode, account for device-specific viewport issues
        if (activeFullscreenState) {
          // Use visual viewport if available (better mobile support)
          if (window.visualViewport) {
            const visualViewport = window.visualViewport;
            availableWidth = Math.min(availableWidth, visualViewport.width);
            availableHeight = Math.min(availableHeight, visualViewport.height);
          }
          
          // Account for mobile browser UI variations
          const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const isAndroid = /Android/.test(navigator.userAgent);
          
          if (isIOSDevice || isAndroid) {
            // Use window dimensions for mobile devices as they're more reliable in fullscreen
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            
            // For mobile devices in fullscreen, use viewport dimensions directly
            availableWidth = viewportWidth;
            availableHeight = viewportHeight;
            
            // Apply device-specific safe area adjustments
            if (isIOSDevice) {
              // Account for iOS safe areas (notch, home indicator)
              const safeAreaTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sat')) || 0;
              const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sab')) || 0;
              const safeAreaLeft = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sal')) || 0;
              const safeAreaRight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sar')) || 0;
              
              availableWidth = Math.max(0, availableWidth - safeAreaLeft - safeAreaRight);
              availableHeight = Math.max(0, availableHeight - safeAreaTop - safeAreaBottom);
            }
          }
        } else {
          // For non-fullscreen mode, ensure minimum responsive dimensions
          const minWidth = Math.min(400, window.innerWidth - 40);
          const minHeight = Math.min(300, window.innerHeight - 200);
          
          availableWidth = Math.max(availableWidth, minWidth);
          availableHeight = Math.max(availableHeight, minHeight);
          
          // For smaller screens, maximize available space
          if (window.innerWidth < 768) {
            availableHeight = Math.min(availableHeight, window.innerHeight - 150);
          }
        }
        
        // Apply device pixel ratio for crisp rendering on high-DPI displays
        const devicePixelRatio = window.devicePixelRatio || 1;
        const displayWidth = availableWidth;
        const displayHeight = availableHeight;
        const canvasWidth = displayWidth * devicePixelRatio;
        const canvasHeight = displayHeight * devicePixelRatio;
        
        if (availableWidth > 0 && availableHeight > 0) {
          if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
            // Set the actual canvas size in memory (scaled for device pixel ratio)
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            
            // Scale the canvas back down using CSS to the display size
            canvas.style.width = displayWidth + 'px';
            canvas.style.height = displayHeight + 'px';
            
            // Scale the drawing context to match the device pixel ratio
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.scale(devicePixelRatio, devicePixelRatio);
            }
            
            setCanvasRenderDimensions({ width: displayWidth, height: displayHeight });
            console.log(`[ResizeObserver] Canvas resized: ${displayWidth}x${displayHeight} (actual: ${canvasWidth}x${canvasHeight}, DPR: ${devicePixelRatio}, fullscreen: ${activeFullscreenState})`);
          }
        }
      }
    };
    
    // Initial size setting with immediate call for proper startup sizing
    // First, try immediate sizing
    requestAnimationFrame(syncCanvasSizeToContainer);
    
    // Then follow up with delayed sizing for fullscreen transitions
    const initialDelay = activeFullscreenState ? 300 : 100;
    resizeTimeout = setTimeout(() => {
      animationFrameId = requestAnimationFrame(syncCanvasSizeToContainer);
    }, initialDelay);

    // Enhanced window resize handler for fullscreen scenarios
    const handleWindowResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        syncCanvasSizeToContainer();
      }, activeFullscreenState ? 200 : 100);
    };

    // Additional event listeners for better fullscreen support
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', handleWindowResize);
    
    // Listen for visual viewport changes (better mobile support)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleWindowResize);
    }

    // Listen for fullscreen change events
    const handleFullscreenChange = () => {
      // Delay canvas resize to allow layout to settle after fullscreen change
      setTimeout(() => {
        syncCanvasSizeToContainer();
      }, 250);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      observer.unobserve(container);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleWindowResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleWindowResize);
      }
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [activeFullscreenState]); // Keep activeFullscreenState as a dependency

  // Initial canvas sizing fix - runs once on mount to prevent grey background
  useEffect(() => {
    const initializeCanvasSize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      
      if (canvas && container) {
        // Multiple attempts to ensure proper sizing
        const triggerResize = () => {
          const resizeEvent = new Event('resize');
          window.dispatchEvent(resizeEvent);
        };
        
        // Immediate attempt
        triggerResize();
        
        // Short delay for DOM settling
        setTimeout(triggerResize, 10);
        
        // Medium delay for layout completion
        setTimeout(triggerResize, 100);
        
        // Longer delay as final fallback
        setTimeout(triggerResize, 250);
        
        // Also try direct canvas sizing as backup
        setTimeout(() => {
          if (container.clientWidth > 0 && container.clientHeight > 0) {
            canvas.width = container.clientWidth;
            canvas.height = container.clientHeight;
            canvas.style.width = container.clientWidth + 'px';
            canvas.style.height = container.clientHeight + 'px';
          }
        }, 50);
      }
    };
    
    // Run immediately
    initializeCanvasSize();
  }, []); // Run once on mount

  // Screen size monitoring for responsive canvas
  useEffect(() => {
    const handleScreenSizeChange = () => {
      // Force canvas resize when screen dimensions change
      const container = containerRef.current;
      const canvas = canvasRef.current;
      
      if (container && canvas) {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          const event = new Event('resize');
          window.dispatchEvent(event);
        });
      }
    };

    // Listen for orientation changes on mobile devices
    const handleOrientationChange = () => {
      // Delay to allow browser UI to settle
      setTimeout(handleScreenSizeChange, 200);
    };

    // Monitor screen size changes
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Monitor visual viewport changes (mobile browser UI changes)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleScreenSizeChange);
    }

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleScreenSizeChange);
      }
    };
  }, []);

  // Performance-optimized drawCanvas with minimal dependencies
  const drawCanvas = useCallback(() => {
    const drawStart = performance.now();
    const canvas = canvasRef.current;
    if (!canvas || !canvasRenderDimensions) {
        return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const startTime = PerformanceMonitor.startMeasurement('drawCanvas');

    // Early exit for performance during certain operations
    const isUsingSplitTool = currentTool === 'split';
    const shouldSkipExpensiveOperations = isUsingSplitTool && !selectedWall && !splitPoint;
    
    if (shouldSkipExpensiveOperations) {
      PerformanceMonitor.endMeasurement('drawCanvas', startTime);
      return;
    }

    let touchFeedbackPointToDraw: Point | null = null;
    if (touchStartPos && (isLongPress || (Date.now() - lastTap < 200))) {
      touchFeedbackPointToDraw = touchStartPos;
    }

    const selectedWallId = selectedWall?.tempId || null;

    // Get current values from refs to avoid dependency issues
    const currentLabelsForRender = labelsForRender;
    const currentRoomLabelPositions = roomLabelPositions;

    // Use viewport culling for walls before passing to renderer
    const wallsToRender = tempWalls || memoizedCurrentWalls;
    
    // Debug tempWalls usage
    if (tempWalls) {
      console.log('🎨 RENDER: Using tempWalls for rendering', {
        tempWallsCount: tempWalls.length,
        firstTempWall: tempWalls[0] ? `${tempWalls[0].start.x},${tempWalls[0].start.y} -> ${tempWalls[0].end.x},${tempWalls[0].end.y}` : 'none',
        currentWallsCount: memoizedCurrentWalls.length
      });
    }
    
    const culledWalls = getVisibleWalls(wallsToRender, viewOffset, scale, canvas.width, canvas.height);
    
    // Only use culled walls if the savings are significant (> 30% reduction)
    const useWallCulling = wallsToRender.length > 20 && culledWalls.length < wallsToRender.length * 0.7;
    const optimizedWalls = useWallCulling ? culledWalls : wallsToRender;

    // 🔗 PERFORMANCE OPTIMIZED: Use debounced shared wall system for better post-resize performance
    // This prevents excessive recalculation during and after resize operations
    const sharedWallSystem = debouncedSharedWallSystem;

    const elementsToRender = memoizedElements;

    renderCanvasView(ctx, {
      canvas,
      viewOffset,
      scale,
      currentWalls: optimizedWalls as (Wall & { tempId: string; stableId: string })[], 
      tempWalls: tempWalls ? getVisibleWalls(tempWalls, viewOffset, scale, canvas.width, canvas.height) : null,
      selectedWallId: selectedWallId,
      elements: elementsToRender,
      selectedElement: selectedElement, // Added for element selection highlighting
      currentTool,
      currentFloor,
      isDraggingRoom,
      roomStartPoint: roomStart,
      roomEndPoint: roomEnd,
      selectedRoomForResize: selectedRoom,
      hoveredResizeHandle: hoveredHandle,
      splitPointToDraw: splitPoint,
      touchFeedbackPoint: touchFeedbackPointToDraw,
      eraserPreviewPoint: eraserPreviewPointRef.current,
      isEraserActive: !!(currentTool === 'eraser' && eraserPreviewPointRef.current && isMouseDownRef.current),
      hoveredCornerPoint: hoveredCorner,
      textBoxesToDraw: textBoxes,
      selectedTextBoxToHighlight: selectedTextBox,
      currentInteractingPoint: currentPoint, 
      snapThreshold: SNAP_THRESHOLD,
      roomCustomLabels: currentLabelsForRender,
              roomLabelPositions: currentRoomLabelPositions,
        // 🔧 CRITICAL FIX: Pass pre-calculated rooms to prevent canvas renderer from overriding user labels
        precalculatedRooms: currentRooms,
      isDraggingRoomLabel,
      draggedRoomLabelId,
      resizingRoomInfo,
      resizingRoomLiveDimensions,
      activelyResizedWalls,
      splitPreviewDimensions,
      cornerDragPreview,
      showMeasurements: showMeasurements || isPositioningRoom, // Always show during positioning
      // NEW: Room positioning mode state
      isPositioningRoom,
      positioningRoomId,
      roomDragOffset,
      positioningTransformedWalls,
      // Element previews
      doorPreview,
      windowPreview,
      radiatorPreview,
      boilerPreview,
      lightPreview,
      smokeAlarmPreview,
      fireSafetyPreview,
      utilityPreview,
      loftHatchPreview,
      counterPreview,
      stairsPreview,
      highlightPreview,
      highlightSnap,
      // Garden rectangle drag state
      gardenPreview,
      // 🆕 ADD: Professional wall snapping visual feedback
      currentSnapResult,
      // 🔗 ADD: Shared wall system for visual differentiation
      sharedWallSystem,
      // 🔧 ADD: Wall segment highlights for visual feedback
      wallSegmentHighlights: segmentHighlights,
      // 🎯 GAP DETECTION: Gap fill suggestion for visual feedback
      gapFillSuggestion,
      // 🏢 ESTATE MODE: Enable estate-specific room label rendering (no box, full names, dimensions)
      isEstate: true,
    });

    // Additional performance logging for debugging
    const drawEnd = performance.now();
    if (drawEnd - drawStart > 16) {
      console.log(`⚡ drawCanvas TOTAL: ${(drawEnd - drawStart).toFixed(1)}ms`);
    }
    
    PerformanceMonitor.endMeasurement('drawCanvas', startTime);
  }, [
    // Minimal dependency array for optimal performance
    canvasRenderDimensions, 
    viewOffset, 
    scale, 
    memoizedCurrentWalls, 
    tempWalls, 
    selectedWall, 
    currentTool,
    currentFloor,
    // Essential interaction state
    isDraggingRoom,
    roomStart,
    roomEnd,
    selectedRoom,
    hoveredHandle,
    splitPoint,
    hoveredCorner,
    currentPoint,
    // Memoized computed values
    labelsForRender,
    roomLabelPositions,
    memoizedElements,
    // Essential UI state
    isDraggingRoomLabel,
    draggedRoomLabelId,
    // Measurement display state
    showMeasurements,
    // Touch feedback state (simplified)
    touchStartPos,
    isLongPress,
    lastTap,
    // Text boxes
    textBoxes,
    selectedTextBox,
    // Live resize data
    resizingRoomInfo,
    resizingRoomLiveDimensions,
    activelyResizedWalls,
    splitPreviewDimensions,
    cornerDragPreview,
    // Element previews
    doorPreview,
    windowPreview,
    radiatorPreview,
    boilerPreview,
    lightPreview,
    smokeAlarmPreview,
    fireSafetyPreview,
    utilityPreview,
    loftHatchPreview,
    compassPreview,
    counterPreview,
    stairsPreview,
    highlightPreview,
    highlightSnap,
    // Garden preview state
    gardenPreview,
    // 🆕 ADD: Wall snapping feedback state
    currentSnapResult,
    // 🔧 ADD: Wall segmentation feedback state
    segmentHighlights,
    // 🎯 GAP DETECTION: Gap fill suggestion state
    gapFillSuggestion,
  ]);

  // Smart throttled canvas update with frame skipping
  const throttledDrawCanvas = useMemo(() => {
    return createThrottledDraw(drawCanvas, 60); // 60 FPS target
  }, [drawCanvas]);

  // Optimized drawing scheduler
  const scheduleCanvasDraw = useCallback((highPriority: boolean = false) => {
    RenderScheduler.scheduleRender(throttledDrawCanvas, highPriority);
  }, [throttledDrawCanvas]);

  // useEffect that calls drawCanvas with optimization
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // useEffect for wall stableId assignment
  useEffect(() => {
    if (isFileLoading) { 
        const canvas = canvasRef.current;
        if (canvas && canvas.getContext('2d')) { 
            const ctx = canvas.getContext('2d')!; 
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#e0e0e0'; 
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#000000';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = '24px Arial';
            ctx.fillText('Loading floor plan...', canvas.width / 2, canvas.height / 2);
        }
      return;
    }
    
    const currentWallsOnFloor = floorPlans[currentFloor] || [];
    let wallsModified = false;
    const newWallsForFloor = currentWallsOnFloor.map(wall => {
      if (!(wall as any).stableId) {
        wallsModified = true;
        // Use coordinate-based stable ID instead of timestamp-based
        const stableId = `wall_${wall.start.x.toFixed(2)}_${wall.start.y.toFixed(2)}_${wall.end.x.toFixed(2)}_${wall.end.y.toFixed(2)}`;
        return { ...wall, stableId };
      }
      return wall;
    });

    if (wallsModified) {
      setFloorPlans(prev => ({
        ...prev,
        [currentFloor]: newWallsForFloor
      }));
    }
  }, [floorPlans, currentFloor, isFileLoading, setFloorPlans]);

  useEffect(() => {
    if (isResizing && activelyResizedWalls && activelyResizedWalls.length > 0) {
      const resizedWallIds = new Set(activelyResizedWalls.map(w => (w as any).stableId).filter(Boolean));
      if (resizedWallIds.size === 0) {
        setLiveUpdatedElements(null);
        return;
      }

      const wallsMap = new Map(activelyResizedWalls.map(w => [(w as any).stableId, w]));

      const staticElements = elements.filter(el => {
        const item = el as any;
        return !(item.type === 'door' && resizedWallIds.has(item.wallId)) && 
               !(item.type === 'window' && resizedWallIds.has(item.wallId)) &&
               !(item.type === 'radiator' && resizedWallIds.has(item.wallId)) &&
               !(item.type === 'boiler' && resizedWallIds.has(item.wallId));
      });

      const itemsToUpdate = elements.filter(el => {
        const item = el as any;
        return (item.type === 'door' || item.type === 'window' || item.type === 'radiator' || item.type === 'boiler') && resizedWallIds.has(item.wallId);
      });

      const updatedItemPreviews = itemsToUpdate.map(item => {
        const wall = wallsMap.get((item as any).wallId);
        if (wall) {
          const newPosition = {
            x: wall.start.x + (wall.end.x - wall.start.x) * (item as any).positionOnWall,
            y: wall.start.y + (wall.end.y - wall.start.y) * (item as any).positionOnWall,
          };
          return { ...item, position: newPosition };
        }
        return item;
      });

      setLiveUpdatedElements([...staticElements, ...updatedItemPreviews as unknown as FloorPlanElement[]]);
    } else {
      setLiveUpdatedElements(null);
    }
  }, [isResizing, activelyResizedWalls, elements]);

  useEffect(() => {
    if (isFileLoading || !currentWalls.length) return;

    const wallsMap = new Map(currentWalls.map(w => [(w as any).stableId, w]));

    
    let elementsModified = false;
    const newlyUpdatedElements = elements.map(element => {
        const anyElement = element as any;
        if (anyElement.type === 'door' || anyElement.type === 'window' || anyElement.type === 'radiator' || anyElement.type === 'boiler') {
            const item = anyElement as DoorElement | WindowElement | RadiatorElement | BoilerElement;
            if (!item.wallId) return element;
            
            // 🔧 FIX: Only process elements that belong to the current floor
            // This prevents elements from other floors from being removed when their walls aren't found
            if (item.floor !== currentFloor) {
                return element; // Keep elements from other floors unchanged
            }
            
            let wall = wallsMap.get(item.wallId);
            
            // 🔄 MIGRATION: If wall not found and wallId looks like timestamp-based, try to find by position
            if (!wall && item.wallId.includes('_') && item.position) {
                // Check if this looks like a timestamp-based wall ID
                const wallIdParts = item.wallId.split('_');
                if (wallIdParts.length >= 2 && !isNaN(parseInt(wallIdParts[1]))) {
                    // Find the closest wall to this element's position
                    let closestWall = null;
                    let minDistance = Infinity;

                    for (const [stableId, candidateWall] of wallsMap) {
                        // Calculate distance from element to wall
                        const wallStart = candidateWall.start;
                        const wallEnd = candidateWall.end;
                        
                        // Distance from point to line segment
                        const A = item.position.x - wallStart.x;
                        const B = item.position.y - wallStart.y;
                        const C = wallEnd.x - wallStart.x;
                        const D = wallEnd.y - wallStart.y;
                        
                        const dot = A * C + B * D;
                        const lenSq = C * C + D * D;
                        
                        let param = -1;
                        if (lenSq !== 0) param = dot / lenSq;
                        
                        let xx, yy;
                        if (param < 0) {
                            xx = wallStart.x;
                            yy = wallStart.y;
                        } else if (param > 1) {
                            xx = wallEnd.x;
                            yy = wallEnd.y;
                        } else {
                            xx = wallStart.x + param * C;
                            yy = wallStart.y + param * D;
                        }
                        
                        const dx = item.position.x - xx;
                        const dy = item.position.y - yy;
                        const distance = Math.sqrt(dx * dx + dy * dy);
                        
                        if (distance < minDistance) {
                            minDistance = distance;
                            closestWall = candidateWall;
                        }
                    }

                    // If we found a close wall (within 50 pixels), migrate the element
                    if (closestWall && minDistance < 50) {
                        console.log(`🔄 Migrating element ${item.id} from wall ID "${item.wallId}" to "${(closestWall as any).stableId}"`);
                        elementsModified = true;
                        wall = closestWall;
                        // Update the element with the new wall ID
                        item.wallId = (closestWall as any).stableId;
                    }
                }
            }
            
            if (wall) {
                let newPosition: Point;

                if (item.type === 'radiator' || item.type === 'boiler') {
                    // Radiator and Boiler positions include an offset from the wall
                    const wallDx = wall.end.x - wall.start.x;
                    const wallDy = wall.end.y - wall.start.y;
                    const wallAngle = Math.atan2(wallDy, wallDx);
                    const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
                    
                    const pointOnWall = {
                      x: wall.start.x + wallDx * item.positionOnWall,
                      y: wall.start.y + wallDy * item.positionOnWall,
                    };

                    // Use default radiator height for consistent positioning, or a constant for boilers.
                    const itemHeight = item.type === 'radiator' ? 10 : 0; // Always use default radiator height (10)
                    const offsetConstant = item.type === 'radiator' ? RADIATOR_WALL_OFFSET : BOILER_WALL_OFFSET;
                    
                    const totalOffset = (WALL_THICKNESS / 2 + offsetConstant + itemHeight / 2) * item.side;

                    newPosition = {
                      x: pointOnWall.x + perpVector.x * totalOffset,
                      y: pointOnWall.y + perpVector.y * totalOffset,
                    };

                } else {
                    // Doors and windows are positioned directly on the wall line
                    newPosition = {
                        x: wall.start.x + (wall.end.x - wall.start.x) * item.positionOnWall,
                        y: wall.start.y + (wall.end.y - wall.start.y) * item.positionOnWall,
                    };
                }

                if (!item.position || newPosition.x !== item.position.x || newPosition.y !== item.position.y) {
                    elementsModified = true;
                    return { ...item, position: newPosition };
                }
            } else {
                // 🔧 FIX: Only log and remove elements from current floor
                // Elements from other floors should be preserved even if their walls aren't in currentWalls

                elementsModified = true;
                return null;
            }
        }
        return element;
    }).filter(Boolean); // remove nulls

    if (elementsModified) {

        setElements(newlyUpdatedElements as FloorPlanElement[]);
    }

  }, [currentWalls, isFileLoading, elements, setElements, currentFloor]);

  // 🔥 DEBUG: Manual radiator debug function (call from console: window.debugRadiators())
  useEffect(() => {
    (window as any).debugRadiators = () => {
      if (roomSync) {
        const radiators = elements.filter(el => el.type === 'radiator') as unknown as RadiatorElement[];
        const currentRooms = calculateRooms(currentWalls, roomCustomLabels, 'geometryUpdate');
        console.log(`🔥 MANUAL_DEBUG: Analyzing ${radiators.length} radiators in ${currentRooms.length} rooms`);
        roomSync.smartSyncRadiators(radiators, currentRooms, currentFloor, true);
      }
    };
  }, [elements, currentWalls, roomCustomLabels, currentFloor, roomSync]);

  // Debounced room calculation to prevent performance issues with multiple rooms
  const debouncedRoomCalculation = useMemo(() => {
    return createSmartDebounce(() => {
      // Perform expensive room calculations here if needed
      // This will only run after 100ms of no changes
    }, 100);
  }, []);

  // 🎯 GAP DETECTION: Detect enclosed gaps when walls change
  useEffect(() => {
    if (isFileLoading || !currentWalls.length || currentTool !== 'roomResize') return;
    
    try {
      const gaps = detectEnclosedGaps(currentWalls, currentFloor, 2); // Min 2m² gaps
      setDetectedGaps(gaps);
      
      if (gaps.length > 0) {
        console.log(`🎯 [Gap Detection] Found ${gaps.length} enclosed gaps:`, 
          gaps.map(g => ({ area: g.area.toFixed(1) + 'm²', confidence: g.confidence }))
        );
      }
    } catch (error) {
      console.warn('⚠️ Gap detection failed:', error);
      setDetectedGaps([]);
    }
  }, [currentWalls, currentFloor, isFileLoading, currentTool]);

  // Throttled room calculation to prevent performance issues during split tool usage
  useEffect(() => {
    // Skip expensive room calculations during split tool hover
    if (currentTool === 'split' && !selectedWall && !splitPoint) {
      return;
    }
    
    // Throttle room calculations during split tool usage
    if (currentTool === 'split') {
      const now = Date.now();
      if (now - roomCalculationThrottle < 250) { // Throttle to max 4 times per second
        return;
      }
      setRoomCalculationThrottle(now);
    }
    
    // Use debounced calculation for better performance
    debouncedRoomCalculation();
  }, [currentTool, selectedWall, splitPoint, roomCalculationThrottle, debouncedRoomCalculation]);

  // Effect to prompt for new room labels when currentWalls changes
  useEffect(() => {
    console.log('🔧 USEEFFECT_TRIGGERED:', {
      labelsOnlyOperation: labelsOnlyOperation.current,
      isWallSplitting,
      isFileLoading,
      currentWallsLength: currentWalls.length,
      roomCustomLabelsCount: Object.keys(roomCustomLabels).length,
      currentFloor
    });
    
    // Skip expensive operations during wall splitting for performance
    if (isWallSplitting) {
      previousCurrentWallsRef.current = currentWalls;
      return;
    }

    // Get current operation type early (needed for multiple checks below)
    const currentOperationType = lastOperationTypeRef.current;
    const isExplicitRoomCreation = currentOperationType === 'explicitRoomCreation';

    // 🔥 PERFORMANCE BOOST: Throttle post-resize calculations to prevent excessive recalculations
    // This prevents room multiplication when multiple state updates happen in quick succession
    // ⚠️ EXCEPTION: Don't throttle during explicit room creation - we need the prompt to fire
    if (justResizedRef.current && !isExplicitRoomCreation) {
      console.log('🏠 POST-RESIZE THROTTLE: Scheduling delayed post-resize calculations');
      
      // 🔧 FIX: Use a more reliable approach that doesn't block subsequent operations
      const throttleTimer = setTimeout(() => {
        console.log('🏠 POST-RESIZE THROTTLE: Processing delayed post-resize calculations');
        justResizedRef.current = false; // Reset flag after throttled processing
        
        // 🔧 FIX: Force a re-run of this effect after throttling completes
        // This ensures room detection isn't permanently blocked
        setRoomCalculationThrottle(prev => prev + 1);
      }, 200); // 200ms throttle for post-resize operations
      
      // 🔧 FIX: Don't return early cleanup - let the effect continue
      // Just schedule the reset for later
      return;
    } else if (justResizedRef.current && isExplicitRoomCreation) {
      console.log('🏠 ROOM CREATION: Allowing prompt to fire despite throttle flag');
      // Don't return - let the effect continue to trigger room label prompt
    }

    if (undoRedoInProgressRef.current) {
      onDebug("[FPC useEffect currentWalls] Undo/Redo in progress. Resetting flag.", { 
        selectedRoomStableId: selectedRoom?.stableId,
        isResizingState: isResizing 
      });
      previousCurrentWallsRef.current = currentWalls; 
      undoRedoInProgressRef.current = false;          
      return;
    }

    if (isFileLoading) {
      previousCurrentWallsRef.current = currentWalls; 
      return;
    }

    // 🔧 PERFORMANCE FIX: Skip all expensive operations during label-only operations
    // ⚠️ EXCEPTION: Don't skip during explicit room creation - we need the prompt to fire
    if (labelsOnlyOperation.current && currentOperationType !== 'explicitRoomCreation') {
      console.log('🔧 LABEL_ONLY_OPERATION: Skipping entire useEffect during label-only operation');
      previousCurrentWallsRef.current = currentWalls;
      return;
    } else if (labelsOnlyOperation.current && currentOperationType === 'explicitRoomCreation') {
      console.log('🏠 ROOM CREATION: Allowing prompt to fire despite labelsOnlyOperation flag');
      // Don't return - let the effect continue to trigger room label prompt
    }

    if (!previousCurrentWallsRef.current) {
      previousCurrentWallsRef.current = currentWalls;
      return;
    }

    if (currentWalls === previousCurrentWallsRef.current && !isFileLoading) { 
        return;
    }

    // 🔄 Detect floor switches early to handle them properly
    const isFloorSwitch = previousFloorRef.current !== null && previousFloorRef.current !== currentFloor;
    
    const prevWalls = previousCurrentWallsRef.current || [];
    const wasRecentResizeActivity = justResizedRef.current; // Capture before potential reset

        let migrationResult: { labels: Record<string, string>; migrationMap: Record<string, string> };
    let labelsChanged = false;
    let effectiveRoomCustomLabels = roomCustomLabels;

    if (isFloorSwitch) {
      // For floor switches, skip migration and preserve existing labels
      migrationResult = { labels: roomCustomLabels, migrationMap: {} };
      effectiveRoomCustomLabels = roomCustomLabels;
      // console.log("[FPC useEffect] Floor switch detected - skipping migration, preserving existing labels");
    } else {
      // Normal migration for geometry changes
      // 🔧 EXTRUSION FIX: Only migrate labels if walls actually changed significantly
      // Skip migration for minor wall adjustments that don't affect room structure
      const wallsActuallyChanged = prevWalls.length !== currentWalls.length || 
        !prevWalls.every((prevWall, index) => {
          const currentWall = currentWalls[index];
          return currentWall && 
            pointsAreEqual(prevWall.start, currentWall.start, 0.1) && 
            pointsAreEqual(prevWall.end, currentWall.end, 0.1) &&
            prevWall.type === currentWall.type;
        });
      
      // 🔧 ROOM LABEL PROMPT FIX: Skip migration during explicit room creation
      // This prevents auto-labeling new rooms before the user can name them
      const currentOperationType = lastOperationTypeRef.current;
      const isExplicitRoomCreation = currentOperationType === 'explicitRoomCreation';
      
      if (!wallsActuallyChanged || isExplicitRoomCreation) {
        // Walls haven't changed meaningfully OR explicit room creation, preserve existing labels
        migrationResult = { labels: roomCustomLabels, migrationMap: {} };
        effectiveRoomCustomLabels = roomCustomLabels;
        const skipReason = isExplicitRoomCreation ? 'explicit room creation (allow prompt)' : 'walls unchanged';
        console.log(`🔧 LABEL MIGRATION: Skipping migration - ${skipReason}, preserving existing labels`);
        
        // 🔧 EXTRUSION FIX: Check for stable ID mismatches in extrusion rooms and fix them
        // 🔧 ROOM LABEL PROMPT FIX: Skip this entirely during explicit room creation
        const currentRoomsForMismatchCheck = (labelsOnlyOperation.current || isExplicitRoomCreation) ? [] : calculateRooms(currentWalls, roomCustomLabels, 'explicitRoomCreation', currentFloor);
        let labelsNeedUpdate = false;
        const updatedLabels = { ...roomCustomLabels };
        
        if (!isExplicitRoomCreation) {
          currentRoomsForMismatchCheck.forEach(room => {
            if (room.stableId && !roomCustomLabels[room.stableId]) {
              // This room has no label - check if there's a label with a similar but different stable ID
              const potentialStableId = getPersistentRoomId(room.walls, currentFloor);
              if (potentialStableId !== room.stableId && roomCustomLabels[potentialStableId]) {
                console.log('🔧 EXTRUSION FIX: Migrating label due to stable ID mismatch:', {
                  from: potentialStableId,
                  to: room.stableId,
                  label: roomCustomLabels[potentialStableId]
                });
                updatedLabels[room.stableId] = roomCustomLabels[potentialStableId];
                delete updatedLabels[potentialStableId];
                labelsNeedUpdate = true;
              } else {
                // 🔧 EXTRUSION FIX: Try fuzzy matching for orphaned rooms
                // ONLY allow fuzzy migration when it's actually needed (orphaned labels)
                // Prevent label theft from recently created or stable rooms
                const currentOperationType = lastOperationTypeRef.current;
                const isExplicitRoomCreation = currentOperationType === 'explicitRoomCreation';
                
                // 🔧 ENHANCED PROTECTION: Be much more conservative about fuzzy migration
                // Only allow it for genuinely orphaned rooms, not for room creation scenarios
                const shouldAllowFuzzyMigration = !isExplicitRoomCreation && 
                // Don't migrate if the room already has a label (it's not orphaned)
                !roomCustomLabels[room.stableId] &&
                // Only migrate if there are rooms that exist in labels but not in current rooms
                // This ensures we're actually fixing orphaned labels, not stealing from active rooms
                Object.keys(roomCustomLabels).some(labelId => {
                  const labelFloorPrefix = `floor_${currentFloor}_`;
                  return labelId.startsWith(labelFloorPrefix) && 
                         !currentRoomsForMismatchCheck.some(r => r.stableId === labelId);
                });
              
              if (shouldAllowFuzzyMigration) {
              const currentFloorPrefix = `floor_${currentFloor}_`;
              const coordinateMatches = room.stableId.match(/(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)/g) || [];
              const currentCoords = new Set(coordinateMatches);
              
              // Find labels with similar coordinates on the same floor
                // BUT only from rooms that no longer exist (truly orphaned labels)
              const candidateLabels = Object.entries(roomCustomLabels).filter(([id, label]) => {
                if (!id.startsWith(currentFloorPrefix)) return false;
                if (updatedLabels[id] !== label) return false; // Skip if already migrated
                  
                  // 🔧 CRITICAL: Only consider labels from rooms that no longer exist
                  const labelRoomExists = currentRoomsForMismatchCheck.some(r => r.stableId === id);
                  if (labelRoomExists) return false; // Don't steal from existing rooms
                
                const candidateMatches = id.match(/(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)/g) || [];
                const candidateCoords = new Set(candidateMatches);
                
                  // Check for coordinate overlap (at least 3 coordinates should match for safety)
                const intersection = [...currentCoords].filter(coord => candidateCoords.has(coord));
                  return intersection.length >= 3; // Increased threshold for safety
              });
              
              if (candidateLabels.length > 0) {
                const [matchedId, matchedLabel] = candidateLabels[0];
                  console.log('🔧 EXTRUSION FIX: Fuzzy migrating ORPHANED label due to coordinate similarity:', {
                  from: matchedId,
                  to: room.stableId,
                  label: matchedLabel,
                    coordinateOverlap: [...currentCoords].filter(coord => matchedId.includes(coord)),
                    operationType: currentOperationType,
                    sourceRoomExists: false
                });
                updatedLabels[room.stableId] = matchedLabel;
                delete updatedLabels[matchedId];
                labelsNeedUpdate = true;
                }
              } else {
                console.log('🔧 EXTRUSION FIX: Skipping fuzzy matching to prevent label theft:', {
                  roomStableId: room.stableId,
                  operationType: currentOperationType,
                  roomHasLabel: !!roomCustomLabels[room.stableId],
                  reason: isExplicitRoomCreation ? 'explicit_room_creation' : 
                          roomCustomLabels[room.stableId] ? 'room_already_labeled' : 'no_orphaned_labels'
                });
              }
            }
          }
          });
        }
        
        if (labelsNeedUpdate) {
          setRoomCustomLabels(updatedLabels);
          effectiveRoomCustomLabels = updatedLabels;
        }
      } else {
        // console.log(`[FPC useEffect] Attempting label migration. Prev wall count: ${prevWalls.length}, New wall count: ${currentWalls.length}`);
        
        // 🔧 LABEL PRESERVATION FIX: If this is a label-only operation, use conservative migration
        const isLabelOnlyOperation = labelsOnlyOperation.current;
        
        // 🐛 DEBUG: Track label state before migration
        console.log('🔍 LABEL PRESERVATION DEBUG - BEFORE MIGRATION:', {
          isLabelOnlyOperation,
          currentLabelsCount: Object.keys(roomCustomLabels).length,
          currentLabels: Object.entries(roomCustomLabels).map(([id, label]) => `${id.substring(0, 20)}...=${label}`),
          prevWallsCount: prevWalls.length,
          currentWallsCount: currentWalls.length,
          currentFloor
        });
        
        const rawMigrationResult = isLabelOnlyOperation 
          ? { labels: roomCustomLabels, migrationMap: {} } // Preserve all existing labels
          : migrateRoomLabels(prevWalls, currentWalls, roomCustomLabels, currentFloor);
        
        // 🐛 DEBUG: Track migration results
        console.log('🔍 LABEL PRESERVATION DEBUG - AFTER MIGRATION:', {
          rawMigrationLabelsCount: Object.keys(rawMigrationResult.labels).length,
          rawMigrationLabels: Object.entries(rawMigrationResult.labels).map(([id, label]) => `${id.substring(0, 20)}...=${label}`),
          migrationMapSize: Object.keys(rawMigrationResult.migrationMap).length
        });
        
        // 🔄 Preserve labels from other floors that aren't affected by current migration
        // Skip expensive room calculations during wall splitting
        const currentFloorRoomIds = isWallSplitting ? new Set() : new Set(
          Object.values(currentRooms).map(room => room.stableId).filter(Boolean)
        );
        
        const preservedLabelsFromOtherFloors: Record<string, string> = {};
        Object.entries(roomCustomLabels).forEach(([roomId, label]) => {
          if (!currentFloorRoomIds.has(roomId)) {
            // This label belongs to a room on another floor - preserve it
            preservedLabelsFromOtherFloors[roomId] = label;
          }
        });
        
        // 🔧 EXTRUSION FIX: If migration failed (returned empty labels), preserve original labels
        const migrationFailed = Object.keys(rawMigrationResult.labels).length === 0 && Object.keys(roomCustomLabels).length > 0;
        
        if (migrationFailed) {
          console.log('🔧 EXTRUSION FIX: Migration failed, preserving original labels');
          migrationResult = { labels: roomCustomLabels, migrationMap: {} };
          effectiveRoomCustomLabels = roomCustomLabels;
        } else {
          // Merge migration results with preserved labels from other floors
          migrationResult = {
            labels: { ...preservedLabelsFromOtherFloors, ...rawMigrationResult.labels },
            migrationMap: rawMigrationResult.migrationMap
          };
          
          // 🔧 CRITICAL FIX: Detect changes based on both label content AND room ID migrations
          // When room geometries change, stableIds change even if label values stay the same
          const labelsContentChanged = JSON.stringify(migrationResult.labels) !== JSON.stringify(roomCustomLabels);
          const roomIdsMigrated = Object.keys(rawMigrationResult.migrationMap).length > 0;
          labelsChanged = labelsContentChanged || roomIdsMigrated;
          
          // 🐛 DEBUG: Track change detection logic
          console.log('🔍 LABEL PRESERVATION DEBUG - CHANGE DETECTION:', {
            labelsContentChanged,
            roomIdsMigrated,
            migrationMapSize: Object.keys(rawMigrationResult.migrationMap).length,
            finalLabelsChanged: labelsChanged,
            oldLabelsKeys: Object.keys(roomCustomLabels).map(k => k.substring(0, 20) + '...'),
            newLabelsKeys: Object.keys(migrationResult.labels).map(k => k.substring(0, 20) + '...')
          });

          if (labelsChanged) {
            // console.log("[FPC useEffect] Labels were migrated and merged with other floors.");
            console.log(`🔄 Room migration completed:`, {
              currentFloorRooms: currentFloorRoomIds.size,
              preservedFromOtherFloors: Object.keys(preservedLabelsFromOtherFloors).length,
              totalLabelsAfterMigration: Object.keys(migrationResult.labels).length,
              migrationMap: rawMigrationResult.migrationMap
            });
            
            // 🐛 DEBUG: Track final state before setting labels
            console.log('🔍 LABEL PRESERVATION DEBUG - SETTING FINAL LABELS:', {
              finalLabelsCount: Object.keys(migrationResult.labels).length,
              finalLabels: Object.entries(migrationResult.labels).map(([id, label]) => `${id.substring(0, 20)}...=${label}`),
              previousLabelsCount: Object.keys(roomCustomLabels).length
            });
            
            // 🔧 CRITICAL FIX: Migrate room label positions alongside labels
            // When room IDs change, update positions to match new IDs
            if (Object.keys(rawMigrationResult.migrationMap).length > 0) {
              setRoomLabelPositions(prevPositions => {
                const updatedPositions = { ...prevPositions };
                
                // Migrate positions from old room IDs to new room IDs
                Object.entries(rawMigrationResult.migrationMap).forEach(([oldId, newId]) => {
                  if (prevPositions[oldId] && !updatedPositions[newId]) {
                    updatedPositions[newId] = prevPositions[oldId];
                    delete updatedPositions[oldId]; // Remove old position
                    console.log('🔍 MIGRATED LABEL POSITION:', {
                      from: oldId.substring(0, 20) + '...',
                      to: newId.substring(0, 20) + '...',
                      position: prevPositions[oldId]
                    });
                  }
                });
                
                return updatedPositions;
              });
            }
            
            setRoomCustomLabels(migrationResult.labels); 
            effectiveRoomCustomLabels = migrationResult.labels; 
          } else {
             console.log('🔍 LABEL PRESERVATION DEBUG - NO CHANGES:', {
               reason: 'Migration resulted in no label changes',
               currentLabelsCount: Object.keys(roomCustomLabels).length
             });
          }
        }
      }
    }
    
    roomMigrationMapRef.current = migrationResult.migrationMap; 
    // console.log('[FPC useEffect] Updated roomMigrationMapRef.current:', roomMigrationMapRef.current);

    if (wasRecentResizeActivity) {
      justResizedRef.current = false; 
      onDebug('[FPC useEffect] Post-resize activity detected.', { 
        resizingRoomInfo,
        migrationMapKeys: Object.keys(roomMigrationMapRef.current),
        migrationMapValues: Object.values(roomMigrationMapRef.current),
        fullMigrationMap: roomMigrationMapRef.current
      });
      
      if (resizingRoomInfo && resizingRoomInfo.originalStableId) {
        const originalId = resizingRoomInfo.originalStableId;
        const newStableIdForResizedRoom = roomMigrationMapRef.current[originalId];
        
        onDebug('[FPC useEffect] Room ID migration lookup:', { 
          originalId, 
          newId: newStableIdForResizedRoom, 
          map: roomMigrationMapRef.current,
          allMigrationKeys: Object.keys(roomMigrationMapRef.current)
        });
        
        // Calculate rooms with current state (skip during wall splitting)
        const currentRoomsArrayAfterMigration = isWallSplitting ? [] : Object.values(currentRooms);
        
        // Convert Room[] to Record<string, Room>
        const currentRoomsRecordAfterMigration = currentRoomsArrayAfterMigration.reduce<Record<string, Room>>((acc: Record<string, Room>, room: Room) => {
          if (room.stableId) {
            acc[room.stableId] = room;
          }
          return acc;
        }, {});
        
        onDebug('[FPC useEffect] Rooms after migration (Record):', {
          totalRooms: Object.keys(currentRoomsRecordAfterMigration).length,
          roomIds: Object.values(currentRoomsRecordAfterMigration).map(r => ({ id: r.stableId, name: r.name })),
          lookingForId: newStableIdForResizedRoom || originalId
        });

        if (newStableIdForResizedRoom || originalId) {
          const foundRoom = findRoomAfterResize(originalId, newStableIdForResizedRoom, currentRoomsRecordAfterMigration, selectedRoom, onDebug);
          setSelectedRoom(foundRoom);
        } else {
          onDebug('[FPC useEffect] Post-resize activity, but no resizingRoomInfo or originalStableId.', {
            hasResizingRoomInfo: !!resizingRoomInfo,
            resizingRoomInfoContent: resizingRoomInfo
          });
        }
        // This line should remain - it clears resizingRoomInfo AFTER processing
        setResizingRoomInfo(null);
      }
    }
    
    // Skip expensive room calculations for prompts during wall splitting
    // Use cached rooms for prompt comparison (only new rooms need full calculation)
    // PERFORMANCE: Only use expensive room calculation when we actually need to detect new rooms
    // Skip entirely during wall splitting, floor switches, resizes, wall type modifications, and room positioning
    // Note: currentOperationType and isExplicitRoomCreation already declared earlier in scope
    const shouldDetectNewRooms = !isWallSplitting && !isFloorSwitch && !wasRecentResizeActivity && currentOperationType !== 'wallTypeModification' && currentOperationType !== 'positioningRoomAdjustment' && !isPositioningRoom;
    
    // 🔧 DEBUG: Log all conditions for shouldDetectNewRooms
    console.log('🔧 ROOM PROMPT DEBUG: shouldDetectNewRooms conditions', {
      isWallSplitting,
      isFloorSwitch,
      wasRecentResizeActivity,
      currentOperationType,
      shouldDetectNewRooms,
      currentWallsLength: currentWalls.length,
      previousWallsLength: (previousCurrentWallsRef.current || []).length
    });
    
    // 🔧 ENDPOINT ROOM CREATION FIX: Only pass endpoint context for ACTUAL endpoint-based room creation
    // NOT for normal explicit room creation (which just means user-initiated room creation)
    // Keep isEndpointRoomCreation for debugging/logging, but don't use it for context
    const isEndpointRoomCreation = currentOperationType === 'explicitRoomCreation';
    const promptCreationContext = undefined; // Normal room creation should use standard logic, not endpoint context
    
    const oldRoomsForPrompt: Room[] = shouldDetectNewRooms ? memoizedCalculateRooms(previousCurrentWallsRef.current || [], effectiveRoomCustomLabels, 'explicitRoomCreation', currentFloor, labelsOnlyOperation, promptCreationContext) : [];
    const newRoomsForPrompt: Room[] = shouldDetectNewRooms ? memoizedCalculateRooms(currentWalls, effectiveRoomCustomLabels, 'explicitRoomCreation', currentFloor, labelsOnlyOperation, promptCreationContext) : [];
    
    // 🔧 DEBUG: Always log room calculation results
    console.log('🔧 ROOM PROMPT DEBUG: Room calculation results', {
      shouldDetectNewRooms,
      oldRoomsCount: oldRoomsForPrompt.length,
      newRoomsCount: newRoomsForPrompt.length,
      oldRooms: oldRoomsForPrompt.map(r => ({ name: r.name, stableId: r.stableId?.substring(0, 30) + '...' })),
      newRooms: newRoomsForPrompt.map(r => ({ name: r.name, stableId: r.stableId?.substring(0, 30) + '...' })),
      currentOperationType,
      isEndpointRoomCreation
    });
    
    // DEBUG: Simplified logging for performance (only when actually detecting rooms)
    if (shouldDetectNewRooms && (newRoomsForPrompt.length > 0 || oldRoomsForPrompt.length > 0)) {
      console.log('[FPC RoomLabelEffect] ROOM DETECTION:', {
        newRoomsCalculated: newRoomsForPrompt.length,
        oldRoomsCalculated: oldRoomsForPrompt.length,
        currentOperationType,
        shouldDetectNewRooms
      });
    }
    // onDebug('[FPC RoomLabelEffect] Operation Context:', { 
    //   type: currentOperationType,
    //   oldRoomCount: oldRoomsForPrompt.length,
    //   newRoomCount: newRoomsForPrompt.length,
    //   labelsChanged,
    //   wasRecentResizeActivity,
    //   isFloorSwitch,
    //   previousFloor: previousFloorRef.current,
    //   currentFloor
    // });

    if (currentOperationType === 'explicitRoomCreation' || currentOperationType === 'wallTypeModification') {
      // 🔧 ENDPOINT ROOM CREATION FIX: Don't reset operation type during room label migration
      // The operation type should only be reset when starting a new operation, not during the current one
      if (currentOperationType === 'wallTypeModification') {
        lastOperationTypeRef.current = 'geometryUpdate';
      }
      // For explicitRoomCreation, keep the operation type until room calculation is complete
      // and we've had a chance to apply the hybrid endpoint logic
      if (currentOperationType === 'explicitRoomCreation') {
        console.log('🔧 ENDPOINT ROOM CREATION: Preserving explicitRoomCreation operation type', {
          currentOperationType,
          newWallsTracked: newlyAddedWallsRef.current.length,
          shouldTriggerHybridLogic: newlyAddedWallsRef.current.length > 0
        });
        // Don't reset the operation type yet - let the room calculation detect it first
      }
      // onDebug('[FPC RoomLabelEffect] Reset lastOperationTypeRef to geometryUpdate from:', { prevType: currentOperationType });
    }

    // 🔧 RESIZE FIX: Comprehensive resize detection for prompt suppression
    const isCurrentlyResizing = isResizing || resizingRoomInfo !== null || activelyResizedWalls !== null;
    const isRecentlyResized = wasRecentResizeActivity || justResizedRef.current;
    const isAnyResizeActivity = isCurrentlyResizing || isRecentlyResized;
    
    // 🔧 ROOM CREATION FIX: Allow prompt after successful room creation even during wall splitting or resize activity
    const wasSuccessfulRoomCreation = lastOperationTypeRef?.current === 'explicitRoomCreation' && 
                                      newRoomsForPrompt.length > 0;
    
    // Suppress prompt if it was a resize, wall type modification, or floor switch
    // BUT allow prompt after successful room creation even during wall splitting or resize flags
    const shouldSuppressPrompt = (isAnyResizeActivity && !isExplicitRoomCreation) || 
                                currentOperationType === 'wallTypeModification' || 
                                isFloorSwitch ||
                                (isWallSplitting && !wasSuccessfulRoomCreation);
    
    if (!shouldSuppressPrompt) {
      // Only log when we actually have rooms to consider
      if (newRoomsForPrompt.length > 0) {
        console.log('[FPC RoomLabelEffect] Evaluating room label prompt:', {currentOperationType, oldRoomsCount: oldRoomsForPrompt.length, newRoomsCount: newRoomsForPrompt.length});
      }
      
      // 🔧 EXPLICIT ROOM CREATION FIX: For mouse/touch room creation, directly find any unlabeled room
      // Don't rely on comparison logic since previousCurrentWallsRef may already be updated
      let newlyFormedAndUnlabeledRoom: Room | undefined;
      
      if (isExplicitRoomCreation && newRoomsForPrompt.length > 0) {
        // For explicit room creation: find ANY unlabeled room that hasn't been prompted yet
        newlyFormedAndUnlabeledRoom = newRoomsForPrompt.find(
          (nr: Room) => nr.stableId &&
                (!effectiveRoomCustomLabels[nr.stableId] || nr.name?.startsWith('Room ')) &&
                !promptedRoomIds.has(nr.stableId)
        );
        console.log('[FPC RoomLabelEffect] EXPLICIT ROOM CREATION: Found unlabeled room:', { 
          room: newlyFormedAndUnlabeledRoom ? newlyFormedAndUnlabeledRoom.stableId : null,
          totalRooms: newRoomsForPrompt.length,
          reason: 'explicit_room_creation_direct_search'
        });
      } else if (oldRoomsForPrompt.length === 0 && newRoomsForPrompt.length > 0 && isEndpointRoomCreation) {
        // Special case: endpoint creation where old algorithm returns no rooms but new one does
        // In this case, any unlabeled room in the new results is a candidate for prompting
        newlyFormedAndUnlabeledRoom = newRoomsForPrompt.find(
          (nr: Room) => nr.stableId &&
                !effectiveRoomCustomLabels[nr.stableId]
        );
        console.log('[FPC RoomLabelEffect] ENDPOINT CREATION: Found unlabeled room (algorithm mismatch scenario):', { 
          room: newlyFormedAndUnlabeledRoom ? newlyFormedAndUnlabeledRoom.stableId : null,
          reason: 'oldRooms_empty_newRooms_present'
        });
      } else {
        // Normal case: compare old vs new rooms by stable ID
        newlyFormedAndUnlabeledRoom = newRoomsForPrompt.find(
          (nr: Room) => nr.stableId &&
                !oldRoomsForPrompt.some((or: Room) => or.stableId === nr.stableId) &&
                !effectiveRoomCustomLabels[nr.stableId]
        );
        console.log('[FPC RoomLabelEffect] Newly Formed & Unlabeled Room for Prompt:', { 
          room: newlyFormedAndUnlabeledRoom ? newlyFormedAndUnlabeledRoom.stableId : null,
          reason: 'normal_comparison'
        });
      }

      if (newlyFormedAndUnlabeledRoom && newlyFormedAndUnlabeledRoom.stableId) {
        // Prevent duplicate prompts for the same room
        if (!isRoomLabelPromptOpen || roomToLabel?.stableId !== newlyFormedAndUnlabeledRoom.stableId) {
          console.log('[FPC RoomLabelEffect] Triggering prompt for newlyFormedAndUnlabeledRoom.', { stableId: newlyFormedAndUnlabeledRoom.stableId });
          console.log('🏷️ NEW ROOM DEBUG: Room being prompted for labeling:', {
            id: newlyFormedAndUnlabeledRoom.id,
            stableId: newlyFormedAndUnlabeledRoom.stableId,
            name: newlyFormedAndUnlabeledRoom.name,
            wallsCount: newlyFormedAndUnlabeledRoom.walls?.length,
            center: newlyFormedAndUnlabeledRoom.center,
            area: newlyFormedAndUnlabeledRoom.area,
            currentLabels: Object.keys(roomCustomLabels)
          });
          setRoomToLabel(newlyFormedAndUnlabeledRoom);
          setIsRoomLabelPromptOpen(true);
          
          // 🔧 FIX: Force cache invalidation when new room is detected
          invalidateRoomCalculationCache('new room detected for labeling');
          
          // Force immediate canvas update to show the new room
          setTimeout(() => {
            console.log('🔧 FIX: Forcing canvas update for newly detected room');
            setScale(prevScale => prevScale); // Trigger re-render
          }, 0);
          
          // Track that we've prompted for this room
          setPromptedRoomIds(prev => new Set(prev).add(newlyFormedAndUnlabeledRoom.stableId!));
          
          // 🔄 Prompt for room height after label is set
          // Height prompt will be triggered after label prompt is completed
        }
      } else if ((newRoomsForPrompt.length > oldRoomsForPrompt.length && !labelsChanged) || 
                 (oldRoomsForPrompt.length === 0 && newRoomsForPrompt.length > 0 && isEndpointRoomCreation && !labelsChanged)) { 
        console.log('[FPC RoomLabelEffect] Condition (newRoomCount > oldRoomCount && !labelsChanged) MET for prompt.', { 
          newL: newRoomsForPrompt.length, 
          oldL: oldRoomsForPrompt.length, 
          labelsChanged,
          isEndpointRoomCreation,
          scenario: oldRoomsForPrompt.length === 0 ? 'algorithm_mismatch' : 'normal_room_increase'
        });
        
        // 🔧 ENDPOINT ROOM CREATION FIX: Handle both normal and algorithm mismatch scenarios
        let firstNewByCountAndUnlabeled: Room | undefined;
        
        if (oldRoomsForPrompt.length === 0 && isEndpointRoomCreation) {
          // Algorithm mismatch scenario: any unlabeled room is a candidate
          firstNewByCountAndUnlabeled = newRoomsForPrompt.find(
              (nr: Room) => nr.stableId &&
                    !effectiveRoomCustomLabels[nr.stableId]
          );
        } else {
          // Normal scenario: compare stable IDs
          firstNewByCountAndUnlabeled = newRoomsForPrompt.find(
              (nr: Room) => nr.stableId &&
                    !oldRoomsForPrompt.some((or: Room) => or.stableId === nr.stableId) &&
                    !effectiveRoomCustomLabels[nr.stableId]
          );
        }
        
        console.log('[FPC RoomLabelEffect] First new by count & unlabeled for prompt:', { 
          room: firstNewByCountAndUnlabeled ? firstNewByCountAndUnlabeled.stableId : null,
          scenario: oldRoomsForPrompt.length === 0 ? 'algorithm_mismatch' : 'normal_comparison'
        });
        if (firstNewByCountAndUnlabeled) {
          // Prevent duplicate prompts for the same room
          if (!isRoomLabelPromptOpen || roomToLabel?.stableId !== firstNewByCountAndUnlabeled.stableId) {
            console.log('[FPC RoomLabelEffect] Triggering prompt for firstNewByCountAndUnlabeled.', { stableId: firstNewByCountAndUnlabeled.stableId });
            setRoomToLabel(firstNewByCountAndUnlabeled);
            setIsRoomLabelPromptOpen(true);
            
            // Track that we've prompted for this room (but allow re-prompting if room changes)
            setPromptedRoomIds(prev => new Set(prev).add(firstNewByCountAndUnlabeled.stableId!));
            
            // 🔄 Prompt for room height after label is set
            // Height prompt will be triggered after label prompt is completed
          }
        }
      } else {
        console.log('[FPC RoomLabelEffect] No conditions met to show prompt within the main IF for prompting.', {
          hasNewlyFormedRoom: !!newlyFormedAndUnlabeledRoom,
          roomCountIncreased: newRoomsForPrompt.length > oldRoomsForPrompt.length,
          labelsChanged
        });
      }
          } else {
        console.log('[FPC RoomLabelEffect] Condition to show prompt NOT MET - suppressing due to resize or other operation.', {
          currentOperationType, 
          isAnyResizeActivity,
          isCurrentlyResizing,
          isRecentlyResized,
          wasRecentResizeActivity, 
          isFloorSwitch, 
          isWallSplitting,
          resizingRoomInfo: !!resizingRoomInfo,
          activelyResizedWalls: !!activelyResizedWalls
        });
        
        if (isAnyResizeActivity) {
          console.log('🏠 RESIZE SUPPRESSION: Room label prompts suppressed due to resize activity', {
            isCurrentlyResizing,
            isRecentlyResized,
            resizingRoomLabel: resizingRoomInfo?.label,
            resizingRoomId: resizingRoomInfo?.originalStableId?.substring(0, 50) + '...'
          });
        }
        
        if (isFloorSwitch) {
          console.log(`🏢 Floor switch detected: ${previousFloorRef.current} → ${currentFloor}. Suppressing room creation prompts.`);
          console.log(`🔄 Room migration for floor switch:`, {
            previousFloor: previousFloorRef.current,
            currentFloor,
            roomsOnCurrentFloor: newRoomsForPrompt.length,
            labelsPreserved: Object.keys(effectiveRoomCustomLabels).length,
            migrationMap: roomMigrationMapRef.current
          });
        }
      }
    
    // 🔄 Also sync when rooms are resized/modified
    if (wasRecentResizeActivity && resizingRoomInfo) {
      // Find the room that was resized and sync its updated dimensions AFTER state update
      // Use setTimeout to ensure this runs after React has processed the state update
      setTimeout(() => {
        // Skip expensive room calculations during wall splitting
        if (!isWallSplitting) {
          const allCurrentRooms = Object.values(currentRooms);
          const resizedRoom = allCurrentRooms.find(room => room.stableId === resizingRoomInfo.originalStableId);
          if (resizedRoom) {
            roomSync.updateRoomDimensions(resizedRoom, currentFloor);
            
            // Note: Floor dimensions are now calculated manually via "Get Calculations" button in site notes
            // roomSync.syncFloorDimensions(allCurrentRooms, currentWalls, currentFloor, true);
          }
        }
      }, 0);
    }
    
    previousCurrentWallsRef.current = currentWalls;
    previousFloorRef.current = currentFloor; // Track floor changes
    
    // 🔧 CANVAS RE-RENDER FIX: Force canvas re-render after room creation completes
    // This ensures that preserved room labels are displayed immediately after room creation
    // without requiring user interaction (like switching tools)
    if (currentOperationType === 'explicitRoomCreation' || labelsChanged || 
        (currentRooms && Object.keys(currentRooms).length > 0)) {
      setTimeout(() => {
        console.log('🔧 CANVAS SYNC: Forcing canvas re-render after room creation/label changes');
        // Trigger canvas re-render by updating scale state
        setScale(prevScale => prevScale);
        // Also trigger a canvas redraw if we have the canvas reference
        if (canvasRef.current) {
          // Force a redraw by dispatching a resize event
          window.dispatchEvent(new Event('resize'));
        }
      }, 50); // Small delay to ensure all state updates are complete
    }

  }, [currentWalls, roomCustomLabels, isFileLoading, lastOperationTypeRef, resizingRoomInfo, selectedRoom, currentFloor, isWallSplitting]); // ADDED currentFloor to detect floor switches and isWallSplitting for performance

  // 🔧 ROOM PROMPT FIX: Clear promptedRoomIds when major geometry changes occur
  // This ensures that room labeling prompts can be triggered again after modifications
  useEffect(() => {
    const currentWallCount = currentWalls.length;
    const previousWallCount = previousCurrentWallsRef.current?.length || 0;
    
    // If walls were deleted or significantly modified, clear the prompted room IDs
    // to allow re-prompting for rooms that might have changed
    if (currentWallCount < previousWallCount) {
      console.log('🔧 PROMPT_RESET: Clearing promptedRoomIds due to wall deletion', {
        previousCount: previousWallCount,
        currentCount: currentWallCount
      });
      setPromptedRoomIds(new Set());
    }
  }, [currentWalls.length]);

  // Clear promptedRoomIds when switching floors
  useEffect(() => {
    if (currentFloor !== undefined) {
      setPromptedRoomIds(new Set());
    }
  }, [currentFloor]);

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    // 🎯 GAP DETECTION: Track touch position for gap fill prompt
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setCursorPosition({ x: touch.clientX, y: touch.clientY });
      
      // Check for gaps during room resize
      if (isResizing && selectedRoom && detectedGaps.length > 0) {
        const touchPoint = getCanvasCoordinates(touch.clientX, touch.clientY);
        const suggestion = findGapForResize(selectedRoom, touchPoint, detectedGaps, 150);
        setGapFillSuggestion(suggestion);
      } else if (gapFillSuggestion) {
        setGapFillSuggestion(null);
      }
    }
    
    if (processTouchMove(e)) { 
      console.log('🔥 HANDLE_TOUCH_MOVE: Handled by processTouchMove (multi-touch)');
      return; 
    }
  
  // 🔥 DEBUG: Log all touch move events for roomResize tool
  if (currentTool === 'roomResize') {
    console.log('🔥 HANDLE_TOUCH_MOVE: roomResize tool detected', { 
      isDraggingRoom,
      selectedRoom: !!selectedRoom,
      roomStart: !!roomStart,
      isResizing,
      isDragging,
      touchCount: e.touches.length,
      resizeHandle
    });
  }
  
  // 🔥 DEBUG: Log all touch move events for roomResize tool
    if (currentTool === 'roomResize') {
      console.log('🔥 HANDLE_TOUCH_MOVE: roomResize tool detected', { 
        isDraggingRoom,
        selectedRoom: !!selectedRoom,
        roomStart: !!roomStart,
        isResizing,
        isDragging,
        touchCount: e.touches.length,
        resizeHandle
      });
    }
    
    if (isDraggingRoom && selectedRoom && roomStart && e.touches.length === 1 && currentTool !== 'roomResize') {
      console.log('🚪 ROOM DRAG DETECTED:', {
        currentTool,
        isDraggingRoom,
        selectedRoom: !!selectedRoom,
        roomStart: !!roomStart,
        elementsCount: elements.length
      });
      
      const touch = e.touches[0];
      const point = getCanvasCoordinates(touch.clientX, touch.clientY);
      const deltaX = point.x - roomStart.x;
      const deltaY = point.y - roomStart.y;

      const movedWalls = selectedRoom.walls.map(wall => ({
        ...wall,
        start: { x: wall.start.x + deltaX, y: wall.start.y + deltaY },
        end: { x: wall.end.x + deltaX, y: wall.end.y + deltaY },
      }));
      setTempWalls(movedWalls);
      
      console.log('🚪 WALLS MOVED:', {
        originalWallsCount: selectedRoom.walls.length,
        movedWallsCount: movedWalls.length,
        deltaX,
        deltaY
      });
      
      // 🔧 FIX: Update element positions (doors, windows, radiators, etc.) when walls move
      // Create a map of original wall ID to new wall position
      const wallIdToNewWall = new Map();
      movedWalls.forEach((newWall) => {
        const wallId = (newWall as any).tempId || (newWall as any).stableId;
        if (wallId) {
          wallIdToNewWall.set(wallId, newWall);
        }
      });
      
      // Also map by stableId if available
      selectedRoom.walls.forEach((originalWall, index) => {
        const stableId = (originalWall as any).stableId;
        if (stableId && movedWalls[index]) {
          wallIdToNewWall.set(stableId, movedWalls[index]);
        }
      });
      
      console.log('🚪 WALL ID MAP CREATED:', {
        mapSize: wallIdToNewWall.size,
        wallIds: Array.from(wallIdToNewWall.keys())
      });
      
      // Find wall-attached elements
      const wallAttachedElements = elements.filter(el => 'wallId' in el);
      console.log('🚪 WALL-ATTACHED ELEMENTS:', {
        total: wallAttachedElements.length,
        types: wallAttachedElements.map(el => ({
          type: el.type,
          id: (el as any).id,
          wallId: (el as any).wallId,
          positionOnWall: (el as any).positionOnWall,
          position: (el as any).position
        }))
      });
      
      // Update elements attached to moved walls
      const updatedElements = elements.map(element => {
        // Only process wall-attached elements
        if (!('wallId' in element)) return element;
        
        const elementWallId = (element as any).wallId;
        const newWall = wallIdToNewWall.get(elementWallId);
        
        console.log(`🚪 CHECKING ELEMENT ${element.type}:`, {
          elementWallId,
          hasNewWall: !!newWall,
          positionOnWall: (element as any).positionOnWall
        });
        
        // If this element's wall didn't move, leave it unchanged
        if (!newWall) return element;
        
        // Recalculate position based on positionOnWall ratio
        const positionOnWall = (element as any).positionOnWall;
        if (typeof positionOnWall === 'number') {
          const oldPosition = (element as any).position;
          const newPosition = {
            x: newWall.start.x + (newWall.end.x - newWall.start.x) * positionOnWall,
            y: newWall.start.y + (newWall.end.y - newWall.start.y) * positionOnWall,
          };
          
          console.log(`🚪 UPDATING ${element.type} POSITION:`, {
            oldPosition,
            newPosition,
            positionOnWall,
            wallStart: newWall.start,
            wallEnd: newWall.end
          });
          
          // For radiators and boilers, maintain perpendicular offset from wall
          if (element.type === 'radiator' || element.type === 'boiler') {
            const wallDx = newWall.end.x - newWall.start.x;
            const wallDy = newWall.end.y - newWall.start.y;
            const wallAngle = Math.atan2(wallDy, wallDx);
            const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
            
            const elementSide = (element as any).side || 1;
            const itemHeight = element.type === 'radiator' ? ((element as any).height || 10) : 0;
            const offsetConstant = element.type === 'radiator' ? 15 : 20;
            const wallThickness = 10;
            
            const totalOffset = (wallThickness / 2 + offsetConstant + itemHeight / 2) * elementSide;
            
            newPosition.x += perpVector.x * totalOffset;
            newPosition.y += perpVector.y * totalOffset;
            
            console.log(`🚪 RADIATOR/BOILER OFFSET APPLIED:`, {
              finalPosition: newPosition,
              offset: totalOffset
            });
          }
          
          return {
            ...element,
            position: newPosition
          };
        }
        
        console.log(`🚪 ELEMENT ${element.type} HAS NO VALID positionOnWall`);
        return element;
      });
      
      console.log('🚪 SETTING UPDATED ELEMENTS:', {
        originalCount: elements.length,
        updatedCount: updatedElements.length,
        changed: elements !== updatedElements
      });
      
      setElements(updatedElements);
      
      return;
    }

    if (isDraggingGardenLabel && selectedGardenId && gardenLabelDragStart && e.touches.length === 1) {
      const touch = e.touches[0];
      const point = getCanvasCoordinates(touch.clientX, touch.clientY);
      const garden = elements.find(el => el.type === 'utility' && (el as any).id === selectedGardenId) as UtilityElement | undefined;
      
      if (garden) {
        const dx = point.x - gardenLabelDragStart.x;
        const dy = point.y - gardenLabelDragStart.y;
        
        const currentOffsetX = garden.labelOffset?.x || 0;
        const currentOffsetY = garden.labelOffset?.y || 0;
        
        const updatedGarden = {
          ...garden,
          labelOffset: {
            x: currentOffsetX + dx,
            y: currentOffsetY + dy
          }
        };
        
        setElements(elements.map(el => (el.type === 'utility' && (el as any).id === selectedGardenId) ? updatedGarden : el));
        setGardenLabelDragStart(point);
      }
      return;
    }

    if (isDraggingGardenLabel && selectedGardenId && gardenLabelDragStart && e.touches.length === 1) {
      const touch = e.touches[0];
      const point = getCanvasCoordinates(touch.clientX, touch.clientY);
      const delta = { x: point.x - gardenLabelDragStart.x, y: point.y - gardenLabelDragStart.y };
      const garden = elements.find(el => el.type === 'utility' && (el as any).id === selectedGardenId) as UtilityElement | undefined;
      if (garden) {
        const currentOffset = garden.labelOffset || { x: 0, y: 0 };
        const updatedGarden = { ...garden, labelOffset: { x: currentOffset.x + delta.x, y: currentOffset.y + delta.y } };
        setElements(elements.map(el => (el.type === 'utility' && (el as any).id === selectedGardenId) ? updatedGarden : el));
        setGardenLabelDragStart(point);
      }
      return;
    }
    
    if (isDraggingRoomLabel && draggedRoomLabelId && e.touches.length === 1) {
      const touch = e.touches[0];
      const point = getCanvasCoordinates(touch.clientX, touch.clientY);
      setRoomLabelPositions(prev => ({
        ...prev,
        [draggedRoomLabelId]: point
      }));
      return;
    }
    
    if (e.touches.length === 1 && !isMultiTouching) {
      const touch = e.touches[0];
      
      // Handle garden rectangle drag preview (CRITICAL: CHECK FIRST)
      if (isDraggingGarden && gardenDragStart) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        setGardenPreview({ start: gardenDragStart, end: point });
        return;
      }
      
      // Handle garden corner resizing
      if (isResizingGarden && selectedGardenId && gardenResizeHandle) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const garden = elements.find(el => el.type === 'utility' && (el as any).id === selectedGardenId) as UtilityElement | undefined;
        if (garden) {
          const resizedGarden = resizeGarden(garden, gardenResizeHandle as any, point);
          setElements(elements.map(el => (el.type === 'utility' && (el as any).id === selectedGardenId) ? resizedGarden : el));
        }
        return;
      }
      
      // NEW: Handle room positioning - free movement for center drag with room-to-room snapping
      if (isPositioningRoom && positioningRoomId && roomDragStart && !resizeHandle) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        let deltaX = point.x - roomDragStart.x;
        let deltaY = point.y - roomDragStart.y;
        
        // Only update if there's actual movement
        if (Math.abs(deltaX) > 0.1 || Math.abs(deltaY) > 0.1) {
          // Get walls by ID (more reliable than coordinate matching)
          const roomWallIdSet = new Set(positioningRoomWallIds);
          const positioningWalls = currentWalls.filter(w => w.id && roomWallIdSet.has(w.id));
          
          if (positioningWalls.length > 0) {
            console.log('[Room Positioning] Moving room with', positioningWalls.length, 'walls (touch)');
            
            // 🧲 ROOM-TO-ROOM SNAPPING: Snap wall endpoints to nearby existing room walls
            const SNAP_THRESHOLD = 30; // pixels
            const otherRooms = currentRooms.filter(r => r.stableId !== positioningRoomId);
            
            // Calculate proposed new positions for positioning room walls
            const proposedWalls = positioningWalls.map(w => ({
              start: { x: w.start.x + deltaX, y: w.start.y + deltaY },
              end: { x: w.end.x + deltaX, y: w.end.y + deltaY }
            }));
            
            // Check for snap opportunities
            let snapOffsetX = 0;
            let snapOffsetY = 0;
            let foundSnap = false;
            
            for (const proposedWall of proposedWalls) {
              if (foundSnap) break;
              
              for (const otherRoom of otherRooms) {
                if (foundSnap) break;
                
                for (const otherWall of otherRoom.walls) {
                  // Check if proposed wall endpoints are close to other wall endpoints
                  const snapPoints = [
                    { proposed: proposedWall.start, target: otherWall.start },
                    { proposed: proposedWall.start, target: otherWall.end },
                    { proposed: proposedWall.end, target: otherWall.start },
                    { proposed: proposedWall.end, target: otherWall.end }
                  ];
                  
                  for (const { proposed, target } of snapPoints) {
                    const dx = target.x - proposed.x;
                    const dy = target.y - proposed.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    
                    if (dist < SNAP_THRESHOLD) {
                      // Found a snap target - adjust delta to snap to this point
                      snapOffsetX = dx;
                      snapOffsetY = dy;
                      foundSnap = true;
                      console.log('[Room Snapping] Snapping to nearby room wall (touch):', { distance: dist, snapOffset: { x: snapOffsetX, y: snapOffsetY } });
                      break;
                    }
                  }
                  if (foundSnap) break;
                }
              }
            }
            
            // Apply snap offset if found
            if (foundSnap) {
              deltaX += snapOffsetX;
              deltaY += snapOffsetY;
            }
            
            // Actually move the walls using wall IDs
            const updatedWalls = currentWalls.map(wall => {
              if (wall.id && roomWallIdSet.has(wall.id)) {
                return {
                  ...wall,
                  start: { x: wall.start.x + deltaX, y: wall.start.y + deltaY },
                  end: { x: wall.end.x + deltaX, y: wall.end.y + deltaY }
                };
              }
              return wall;
            });
            
            setFloorPlans(prev => ({ ...prev, [currentFloor]: updatedWalls }));
          setRoomDragStart({ x: roomDragStart.x + deltaX, y: roomDragStart.y + deltaY }); // Update for next delta
          }
        }
        return;
      }
      
      // NEW: Handle room positioning - corner dragging with EXACT resize tool behavior
      // Includes: angle snapping, tempWalls preview, corner angle display
      if (isPositioningRoom && positioningRoomId && positioningRoomWallIds.length > 0 && resizeHandle && roomStart) {
        const rawPoint = getCanvasCoordinates(touch.clientX, touch.clientY);
        
        // Find actual corner junction (AR rooms aren't perfect rectangles)
        const positioningRoom = currentRooms.find(r => r.stableId === positioningRoomId);
        if (!positioningRoom) return;
        
        const roomWalls = positioningRoom.walls;
        const bounds = {
          minX: Math.min(...roomWalls.map(w => Math.min(w.start.x, w.end.x))),
          maxX: Math.max(...roomWalls.map(w => Math.max(w.start.x, w.end.x))),
          minY: Math.min(...roomWalls.map(w => Math.min(w.start.y, w.end.y))),
          maxY: Math.max(...roomWalls.map(w => Math.max(w.start.y, w.end.y)))
        };
        
        let theoreticalCorner: Point;
        switch (resizeHandle) {
          case 'nw': theoreticalCorner = { x: bounds.minX, y: bounds.minY }; break;
          case 'ne': theoreticalCorner = { x: bounds.maxX, y: bounds.minY }; break;
          case 'sw': theoreticalCorner = { x: bounds.minX, y: bounds.maxY }; break;
          case 'se': theoreticalCorner = { x: bounds.maxX, y: bounds.maxY }; break;
          default: return;
        }
        
        const roomWallIdSet = new Set(positioningRoomWallIds);
        const allEndpoints: Point[] = [];
        currentWalls.forEach(wall => {
          if (wall.id && roomWallIdSet.has(wall.id)) {
            allEndpoints.push(wall.start, wall.end);
          }
        });
        
        let actualCorner: Point | null = null;
        let minDistance = Infinity;
        allEndpoints.forEach(endpoint => {
          const dist = Math.hypot(endpoint.x - theoreticalCorner.x, endpoint.y - theoreticalCorner.y);
          if (dist < minDistance) {
            minDistance = dist;
            actualCorner = endpoint;
          }
        });
        
        if (!actualCorner) return;
        
        // Find the two walls connected to this corner
        const junctionTolerance = 2;
        const affectedWallsData: Array<{ wall: Wall; isStartCorner: boolean }> = [];
        
        currentWalls.forEach(wall => {
          if (!wall.id || !roomWallIdSet.has(wall.id)) return;
          
          const startIsJunction = 
            Math.abs(wall.start.x - actualCorner!.x) < junctionTolerance &&
            Math.abs(wall.start.y - actualCorner!.y) < junctionTolerance;
          
          const endIsJunction = 
            Math.abs(wall.end.x - actualCorner!.x) < junctionTolerance &&
            Math.abs(wall.end.y - actualCorner!.y) < junctionTolerance;
          
          if (startIsJunction) affectedWallsData.push({ wall, isStartCorner: true });
          if (endIsJunction) affectedWallsData.push({ wall, isStartCorner: false });
        });
        
        // 🎯 EXACT RESIZE TOOL ANGLE SNAPPING (from useFloorPlanInteractions:8783-8796)
        let snappedForAnglePoint = { ...rawPoint };
        
        if (affectedWallsData.length === 2) {
          const entry1 = affectedWallsData[0];
          const entry2 = affectedWallsData[1];
          const fixedP1 = entry1.isStartCorner ? entry1.wall.end : entry1.wall.start;
          const fixedP2 = entry2.isStartCorner ? entry2.wall.end : entry2.wall.start;
          const toleranceWorld = 10 / scale; // SNAP_ANGLE_TOLERANCE_PX / scale
          
          // Check all angle snapping conditions (exact resize tool logic)
          if (Math.abs(rawPoint.y - fixedP1.y) < toleranceWorld && Math.abs(rawPoint.x - fixedP2.x) < toleranceWorld)
            snappedForAnglePoint = { x: fixedP2.x, y: fixedP1.y };
          else if (Math.abs(rawPoint.x - fixedP1.x) < toleranceWorld && Math.abs(rawPoint.y - fixedP2.y) < toleranceWorld)
            snappedForAnglePoint = { x: fixedP1.x, y: fixedP2.y };
          else if (Math.abs(rawPoint.y - fixedP1.y) < toleranceWorld)
            snappedForAnglePoint = { x: rawPoint.x, y: fixedP1.y };
          else if (Math.abs(rawPoint.x - fixedP1.x) < toleranceWorld)
            snappedForAnglePoint = { x: fixedP1.x, y: rawPoint.y };
          else if (Math.abs(rawPoint.y - fixedP2.y) < toleranceWorld)
            snappedForAnglePoint = { x: rawPoint.x, y: fixedP2.y };
          else if (Math.abs(rawPoint.x - fixedP2.x) < toleranceWorld)
            snappedForAnglePoint = { x: fixedP2.x, y: rawPoint.y };
        }
        
        // Update tempWalls for preview (not floorPlans directly)
        const updatedTempWalls = currentWalls.map(wall => {
          const affection = affectedWallsData.find(a => a.wall.id === wall.id);
          if (affection) {
            return {
              ...wall,
              start: affection.isStartCorner ? snappedForAnglePoint : wall.start,
              end: affection.isStartCorner ? wall.end : snappedForAnglePoint
            };
          }
          return wall;
        });
        
        setTempWalls(updatedTempWalls);
        
        // Calculate and display corner drag preview (orange lines + angle measurements)
        if (affectedWallsData.length === 2) {
          const previewWalls = affectedWallsData.map(aff => {
            const fixedPoint = aff.isStartCorner ? aff.wall.end : aff.wall.start;
            const originalLength = Math.sqrt(
              Math.pow(aff.wall.end.x - aff.wall.start.x, 2) + 
              Math.pow(aff.wall.end.y - aff.wall.start.y, 2)
            ) / PIXELS_PER_METER;
            const newLength = Math.sqrt(
              Math.pow(fixedPoint.x - snappedForAnglePoint.x, 2) + 
              Math.pow(fixedPoint.y - snappedForAnglePoint.y, 2)
            ) / PIXELS_PER_METER;
            const angle = Math.atan2(
              fixedPoint.y - snappedForAnglePoint.y,
              fixedPoint.x - snappedForAnglePoint.x
            ) * 180 / Math.PI;
            
            return {
              wallId: aff.wall.id!,
              newLength,
              angle: angle >= 0 ? angle : angle + 360,
              originalLength,
              lengthChange: newLength - originalLength
            };
          });
          
          const angleDiff = Math.abs(previewWalls[1].angle - previewWalls[0].angle);
          const cornerAngle = angleDiff > 180 ? 360 - angleDiff : angleDiff;
          
          setCornerDragPreview({
            cornerPosition: snappedForAnglePoint,
            affectedWalls: previewWalls,
            cornerAngle
          });
        }
        
        return;
      }
      
      // 🚨 CRITICAL FIX: Handle roomResize tool specifically to ensure drag events reach the hook
      if (currentTool === 'roomResize') {
        console.log('🔥 ROUTING: Calling hookHandleTouchMove for roomResize tool');
        hookHandleTouchMove(e);
      // 🆕 ENHANCED: Prioritize wall tool to ensure snapping works
      } else if (currentTool === 'wall') {
        hookHandleTouchMove(e);
      } else if (isPlacingLight) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        if (lightPreview) {
          setLightPreview({ ...lightPreview, position: point });
        }
      } else if (isPlacingSmokeAlarm) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        if (smokeAlarmPreview) {
          setSmokeAlarmPreview({ ...smokeAlarmPreview, position: point });
        }
      } else if (isPlacingFireSafety) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        if (fireSafetyPreview) {
          setFireSafetyPreview({ ...fireSafetyPreview, position: point });
        }
      } else if (isResizingGarden && selectedGardenId && gardenResizeHandle) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const garden = elements.find(el => el.type === 'utility' && (el as any).id === selectedGardenId) as UtilityElement | undefined;
        if (garden) {
          const resizedGarden = resizeGarden(garden, gardenResizeHandle as any, point);
          setElements(elements.map(el => (el.type === 'utility' && (el as any).id === selectedGardenId) ? resizedGarden : el));
        }
      } else if (isPlacingUtility) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        if (utilityPreview) {
          // For sinks, constrain to the detected wall (like radiators)
          const isSink = utilityPreview.utilityType === 'single-sink' || utilityPreview.utilityType === 'double-sink';
          const isBathroomFixture = utilityPreview.utilityType === 'bathtub' || utilityPreview.utilityType === 'shower' || utilityPreview.utilityType === 'toilet';
          
          if (isSink && wallForSink?.stableId) {
            setUtilityPreview(createSinkElementOnWall(
              point, 
              wallForSink as Wall & { tempId: string, stableId: string },
              utilityPreview.utilityType as 'single-sink' | 'double-sink',
              currentFloor
            ));
          } else if (isBathroomFixture && wallForSink?.stableId) {
            // Bathtub/shower/toilet constrained to wall
            setUtilityPreview(createBathroomFixtureOnWall(
              point,
              wallForSink as Wall & { tempId: string, stableId: string },
              utilityPreview.utilityType as 'bathtub' | 'shower' | 'toilet',
              currentFloor
            ));
          } else if (!isSink && !isBathroomFixture) {
            // Other utilities can move freely
            setUtilityPreview({ ...utilityPreview, position: point });
          }
        }
      } else if (isPlacingLoftHatch) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        if (loftHatchPreview) {
          setLoftHatchPreview({ ...loftHatchPreview, position: point });
        }
      } else if (isRotatingCompass && selectedCompassId) {
        console.log('🧭 TOUCH MOVE: Rotating compass');
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const compass = elements.find(el => el.type === 'compass' && (el as any).id === selectedCompassId) as CompassType | undefined;
        if (compass) {
          const currentAngle = Math.atan2(point.y - compass.position.y, point.x - compass.position.x) * 180 / Math.PI;
          const angleDelta = currentAngle - compassRotationStartAngle;
          const newRotation = (compass.rotation || 0) + angleDelta;
          
          const rotatedCompass = { ...compass, rotation: newRotation };
          setElements(elements.map(el => (el.type === 'compass' && (el as any).id === selectedCompassId) ? rotatedCompass : el));
          setCompassRotationStartAngle(currentAngle);
          console.log('🧭 TOUCH MOVE: Compass rotated to', newRotation);
        } else {
          console.log('🧭 TOUCH MOVE: Compass NOT found in elements!');
        }
      } else if (isPlacingCompass && compassPreview) {
        console.log('🧭 TOUCH MOVE: Moving compass preview');
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        setCompassPreview({ ...compassPreview, position: point });
      } else if (isPlacingUtility && utilityPreview) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        // For sinks, constrain to the detected wall (like radiators)
        const isSink = utilityPreview.utilityType === 'single-sink' || utilityPreview.utilityType === 'double-sink';
        const isBathroomFixture = utilityPreview.utilityType === 'bathtub' || utilityPreview.utilityType === 'shower' || utilityPreview.utilityType === 'toilet';
        
        if (isSink && wallForSink?.stableId) {
          setUtilityPreview(createSinkElementOnWall(
            point, 
            wallForSink as Wall & { tempId: string, stableId: string },
            utilityPreview.utilityType as 'single-sink' | 'double-sink',
            currentFloor
          ));
        } else if (isBathroomFixture && wallForSink?.stableId) {
          // Bathtub/shower/toilet constrained to wall
          setUtilityPreview(createBathroomFixtureOnWall(
            point,
            wallForSink as Wall & { tempId: string, stableId: string },
            utilityPreview.utilityType as 'bathtub' | 'shower' | 'toilet',
            currentFloor
          ));
        } else if (!isSink && !isBathroomFixture) {
          // Other utilities can move freely
          setUtilityPreview({ ...utilityPreview, position: point });
        }
      } else if (isPlacingLoftHatch && loftHatchPreview) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        setLoftHatchPreview({ ...loftHatchPreview, position: point });
      } else if (isPlacingDoor && wallForDoor?.stableId) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const newDoorPreview = createDoorElement(point, wallForDoor as Wall & { tempId: string; stableId: string; }, currentFloor);
        setDoorPreview(newDoorPreview as DoorElement | null);
      } else if (isPlacingWindow) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        
        if (wallForWindow?.stableId) {
          // Wall window - recreate at new position along wall
          const windowType = windowPreview?.windowInstallationType;
          let newWindowPreview: WindowElement | null = null;
          
          if (windowType === 'solid_box') {
            newWindowPreview = createSolidBoxWindowElement(point, wallForWindow as Wall & { tempId: string; stableId: string; }, currentFloor);
          } else if (windowType === 'canted') {
            newWindowPreview = createCantedWindowElement(point, wallForWindow as Wall & { tempId: string; stableId: string; }, currentFloor, true);
          } else {
            newWindowPreview = createWindowElement(point, wallForWindow as Wall & { tempId: string; stableId: string; }, currentFloor);
            if (newWindowPreview) {
              (newWindowPreview as any).windowInstallationType = 'wall';
            }
          }
          setWindowPreview(newWindowPreview);
        } else if (windowPreview?.windowInstallationType === 'roof') {
          // Roof window preview - just update position
          setWindowPreview({ ...windowPreview, position: point });
        }
      } else if (isPlacingRadiator && wallForRadiator?.stableId) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const newRadiatorPreview = createRadiatorElement(point, wallForRadiator as Wall & { tempId: string; stableId: string; }, currentFloor);
        setRadiatorPreview(newRadiatorPreview);
      } else if (isPlacingBoiler && wallForBoiler?.stableId) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const newBoilerPreview = createBoilerElement(point, wallForBoiler as Wall & { tempId: string; stableId: string; }, currentFloor);
        setBoilerPreview(newBoilerPreview);
      } else if (isRotatingCounter && selectedCounterId) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const counter = elements.find(el => el.type === 'counter' && (el as any).id === selectedCounterId) as CounterElement | undefined;
        if (counter && !counter.wallId) {
          // Calculate angle from counter center to current touch position
          const centerX = (counter.start.x + counter.end.x) / 2;
          const centerY = (counter.start.y + counter.end.y) / 2;
          const currentAngle = Math.atan2(point.y - centerY, point.x - centerX);
          const angleDelta = currentAngle - rotationStartAngle;
          
          // Get current counter direction
          const dx = counter.end.x - counter.start.x;
          const dy = counter.end.y - counter.start.y;
          const currentCounterAngle = Math.atan2(dy, dx);
          
          // Calculate new angle
          const newAngle = currentCounterAngle + angleDelta;
          const length = counter.length;
          
          // Rotate counter around its center
          const newStart = {
            x: centerX - Math.cos(newAngle) * length / 2,
            y: centerY - Math.sin(newAngle) * length / 2,
          };
          const newEnd = {
            x: centerX + Math.cos(newAngle) * length / 2,
            y: centerY + Math.sin(newAngle) * length / 2,
          };
          
          const rotatedCounter = { ...counter, start: newStart, end: newEnd };
          setElements(elements.map(el => (el.type === 'counter' && (el as any).id === selectedCounterId) ? rotatedCounter : el));
          
          // Update rotation start angle for continuous rotation
          setRotationStartAngle(currentAngle);
        }
      } else if (isDraggingCounter && selectedCounterId && counterDragStart) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const counter = elements.find(el => el.type === 'counter' && (el as any).id === selectedCounterId) as CounterElement | undefined;
        if (counter && !counter.wallId) {
          // Free-floating counter: drag freely in any direction
          const deltaX = point.x - counterDragStart.x;
          const deltaY = point.y - counterDragStart.y;
          
          const draggedCounter = {
            ...counter,
            start: { x: counter.start.x + deltaX, y: counter.start.y + deltaY },
            end: { x: counter.end.x + deltaX, y: counter.end.y + deltaY },
          };
          
          setElements(elements.map(el => (el.type === 'counter' && (el as any).id === selectedCounterId) ? draggedCounter : el));
          setCounterDragStart(point);
        } else if (counter && counter.wallId) {
          // Wall-snapped counter: drag along the wall
          const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
          const wall = wallsWithStableIds.find(w => w.stableId === counter.wallId);
          if (wall) {
            const draggedCounter = dragCounterAlongWall(counter, point, wall);
            setElements(elements.map(el => (el.type === 'counter' && (el as any).id === selectedCounterId) ? draggedCounter : el));
          }
        }
      } else if (isResizingCounter && selectedCounterId && counterResizeHandle) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const counter = elements.find(el => el.type === 'counter' && (el as any).id === selectedCounterId) as CounterElement | undefined;
        if (counter && counter.wallId) {
          const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
          const wall = wallsWithStableIds.find(w => w.stableId === counter.wallId);
          if (wall) {
            const resizedCounter = resizeCounter(counter, counterResizeHandle, point, wall);
            setElements(elements.map(el => (el.type === 'counter' && (el as any).id === selectedCounterId) ? resizedCounter : el));
          }
        } else if (counter) {
          // Free-floating counter - update dimensions based on handle
          // Maintain current orientation (don't allow rotation during resize)
          const resizedCounter = { ...counter };
          
          if (counterResizeHandle === 'length-start') {
            // Move start point along current counter direction, keep end fixed
            const dx = counter.end.x - counter.start.x;
            const dy = counter.end.y - counter.start.y;
            const len = Math.hypot(dx, dy);
            const dirX = dx / len;
            const dirY = dy / len;
            
            // Project cursor onto counter line from end point
            const toMouse = { x: point.x - counter.end.x, y: point.y - counter.end.y };
            const projection = toMouse.x * dirX + toMouse.y * dirY;
            
            // New length is absolute value of projection (negative means opposite direction)
            const newLength = Math.max(50, Math.abs(projection));
            resizedCounter.length = newLength;
            
            // Move start along counter direction from end
            resizedCounter.start = {
              x: counter.end.x - dirX * newLength,
              y: counter.end.y - dirY * newLength,
            };
          } else if (counterResizeHandle === 'length-end') {
            // Move end point along current counter direction, keep start fixed
            const dx = counter.end.x - counter.start.x;
            const dy = counter.end.y - counter.start.y;
            const len = Math.hypot(dx, dy);
            const dirX = dx / len;
            const dirY = dy / len;
            
            // Project cursor onto counter line from start point
            const toMouse = { x: point.x - counter.start.x, y: point.y - counter.start.y };
            const projection = toMouse.x * dirX + toMouse.y * dirY;
            
            // New length is absolute value of projection
            const newLength = Math.max(50, Math.abs(projection));
            resizedCounter.length = newLength;
            
            // Move end along counter direction from start
            resizedCounter.end = {
              x: counter.start.x + dirX * newLength,
              y: counter.start.y + dirY * newLength,
            };
          } else if (counterResizeHandle === 'width-start' || counterResizeHandle === 'width-end') {
            // Calculate perpendicular vector from counter direction
            const dx = counter.end.x - counter.start.x;
            const dy = counter.end.y - counter.start.y;
            const perpX = -dy;
            const perpY = dx;
            const perpLen = Math.hypot(perpX, perpY);
            const perpNormX = perpX / perpLen;
            const perpNormY = perpY / perpLen;
            
            // Reference point is the corresponding length corner
            const refPoint = counterResizeHandle === 'width-start' ? counter.start : counter.end;
            
            // Calculate perpendicular distance from reference to cursor
            const toPoint = { x: point.x - refPoint.x, y: point.y - refPoint.y };
            const perpDist = toPoint.x * perpNormX + toPoint.y * perpNormY;
            
            resizedCounter.width = Math.max(20, Math.abs(perpDist));
          }
          setElements(elements.map(el => (el.type === 'counter' && (el as any).id === selectedCounterId) ? resizedCounter : el));
        }
      } else if (isRotatingStairs && selectedStairsId) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const stairs = elements.find(el => el.type === 'stairs' && (el as any).id === selectedStairsId) as StairsElement | undefined;
        if (stairs && !stairs.wallId) {
          // Calculate angle from stairs center to current touch position
          const centerX = (stairs.start.x + stairs.end.x) / 2;
          const centerY = (stairs.start.y + stairs.end.y) / 2;
          const currentAngle = Math.atan2(point.y - centerY, point.x - centerX);
          
          // Calculate rotation delta
          const deltaAngle = currentAngle - rotationStartAngle;
          const newAngle = stairs.rotation + deltaAngle;
          
          // Rotate stairs around center
          const length = stairs.length;
          const newStart = {
            x: centerX - Math.cos(newAngle) * length / 2,
            y: centerY - Math.sin(newAngle) * length / 2,
          };
          const newEnd = {
            x: centerX + Math.cos(newAngle) * length / 2,
            y: centerY + Math.sin(newAngle) * length / 2,
          };
          
          const rotatedStairs = { ...stairs, start: newStart, end: newEnd, rotation: newAngle };
          setElements(elements.map(el => (el.type === 'stairs' && (el as any).id === selectedStairsId) ? rotatedStairs : el));
          setRotationStartAngle(currentAngle);
        }
      } else if (isDraggingStairs && selectedStairsId && counterDragStart) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const stairs = elements.find(el => el.type === 'stairs' && (el as any).id === selectedStairsId) as StairsElement | undefined;
        if (stairs && !stairs.wallId) {
          // Free-floating stairs: drag freely
          const deltaX = point.x - counterDragStart.x;
          const deltaY = point.y - counterDragStart.y;
          
          const draggedStairs = {
            ...stairs,
            start: { x: stairs.start.x + deltaX, y: stairs.start.y + deltaY },
            end: { x: stairs.end.x + deltaX, y: stairs.end.y + deltaY },
          };
          
          setElements(elements.map(el => (el.type === 'stairs' && (el as any).id === selectedStairsId) ? draggedStairs : el));
          setCounterDragStart(point);
        }
      } else if (isResizingStairs && selectedStairsId && stairsResizeHandle) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const stairs = elements.find(el => el.type === 'stairs' && (el as any).id === selectedStairsId) as StairsElement | undefined;
        if (stairs && stairs.wallId) {
          const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
          const wall = wallsWithStableIds.find(w => w.stableId === stairs.wallId);
          if (wall) {
            const resizedStairs = resizeStairs(stairs, stairsResizeHandle as any, point, wall);
            setElements(elements.map(el => (el.type === 'stairs' && (el as any).id === selectedStairsId) ? resizedStairs : el));
          }
        } else if (stairs) {
          // Free-floating stairs - resize length or width
          const resizedStairs = { ...stairs };
          
          if (stairsResizeHandle === 'length-start') {
            const dx = stairs.end.x - stairs.start.x;
            const dy = stairs.end.y - stairs.start.y;
            const len = Math.hypot(dx, dy);
            const dirX = dx / len;
            const dirY = dy / len;
            
            const toMouse = { x: point.x - stairs.end.x, y: point.y - stairs.end.y };
            const projection = toMouse.x * dirX + toMouse.y * dirY;
            const newLength = Math.max(50, Math.abs(projection));
            resizedStairs.length = newLength;
            
            resizedStairs.start = {
              x: stairs.end.x - dirX * newLength,
              y: stairs.end.y - dirY * newLength,
            };
          } else if (stairsResizeHandle === 'length-end') {
            const dx = stairs.end.x - stairs.start.x;
            const dy = stairs.end.y - stairs.start.y;
            const len = Math.hypot(dx, dy);
            const dirX = dx / len;
            const dirY = dy / len;
            
            const toMouse = { x: point.x - stairs.start.x, y: point.y - stairs.start.y };
            const projection = toMouse.x * dirX + toMouse.y * dirY;
            const newLength = Math.max(50, Math.abs(projection));
            resizedStairs.length = newLength;
            
            resizedStairs.end = {
              x: stairs.start.x + dirX * newLength,
              y: stairs.start.y + dirY * newLength,
            };
          } else if (stairsResizeHandle === 'width-start' || stairsResizeHandle === 'width-end') {
            const dx = stairs.end.x - stairs.start.x;
            const dy = stairs.end.y - stairs.start.y;
            const perpX = -dy;
            const perpY = dx;
            const perpLen = Math.hypot(perpX, perpY);
            const perpNormX = perpX / perpLen;
            const perpNormY = perpY / perpLen;
            
            const refPoint = stairsResizeHandle === 'width-start' ? stairs.start : stairs.end;
            const toPoint = { x: point.x - refPoint.x, y: point.y - refPoint.y };
            const perpDist = toPoint.x * perpNormX + toPoint.y * perpNormY;
            
            resizedStairs.width = Math.max(20, Math.abs(perpDist));
          }
          setElements(elements.map(el => (el.type === 'stairs' && (el as any).id === selectedStairsId) ? resizedStairs : el));
        }
      } else if (isPlacingCounter) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const newCounterPreview = createCounterWithAutoSnap(point, wallsWithStableIds, currentFloor);
        setCounterPreview(newCounterPreview);
        
        // Update wall reference if snapping changed
        const wall = findWallForCounter(point, wallsWithStableIds);
        setWallForCounter(wall);
      } else if (isPlacingStairs) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const newStairsPreview = createStairsWithAutoSnap(point, wallsWithStableIds, currentFloor, undefined, undefined, stairsDirection);
        setStairsPreview(newStairsPreview);
        
        // Update wall reference if snapping changed
        const wall = findWallForStairs(point, wallsWithStableIds);
        setWallForStairs(wall);
      } else if (isHighlighting && highlightStart) {
        const point = getCanvasCoordinates(touch.clientX, touch.clientY);
        // Use wall snapping for highlight preview during drag
        const wallsForSnapping = currentWalls.map(({tempId, ...w}) => w);
        const roomsForSnapping = currentRooms || [];
        
        // Calculate area snap for visual feedback
        const areaSnap = snapHighlightAreaToWalls(highlightStart, point, wallsForSnapping, roomsForSnapping, currentFloor);
        setHighlightSnap(areaSnap);
        
        setHighlightPreview(createHighlightPreviewWithWallSnapping(highlightStart, point, wallsForSnapping, roomsForSnapping, currentFloor, highlightColor));
    } else {
        hookHandleTouchMove(e);
      }

      if (!isResizingRef.current && !isDraggingCorner) {
        const rawPoint = getCanvasCoordinates(touch.clientX, touch.clientY);
        
        if (currentTool === 'eraser') {
          eraserPreviewPointRef.current = rawPoint;
        }
        if (currentTool === 'cornerAdjust' || currentTool === 'roomResize') {
          const uniqueCorners = getUniqueCornerPoints(currentWalls.map(({tempId, ...w}) => w)); 
          let foundHoveredCorner: Point | null = null;
          // EXTRA LARGE corner detection area
          for (const corner of uniqueCorners) {
              if (isNearPoint(rawPoint, corner, SNAP_THRESHOLD * 12 / scale)) { 
                  foundHoveredCorner = corner;
                  break;
              }
          }
          setHoveredCorner(foundHoveredCorner);
        }
      }
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const perfStart = performance.now();
    console.log('🔍 TOUCH START EVENT:', {
      currentTool,
      touchCount: e.touches.length
    });
    
    const isMultiTouchStartEvent = processTouchStart(e);

    if (isRoomContextMenuOpen) setIsRoomContextMenuOpen(false);
    if (showFloatingActions) setShowFloatingActions(false);

    if (isMultiTouchStartEvent) {
      if (isDraggingRoom) {
        setIsDraggingRoom(false);
        setRoomStart(null);
        setRoomEnd(null);
        setCurrentPoint(null);
      }
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      
      const point = getCanvasCoordinates(touch.clientX, touch.clientY);
      // Coordinate conversion - debug logs removed to reduce console noise

      // Check if text tool is active and touching garden label
      if (currentTool === 'text') {
        const gardens = elements.filter(el => el.type === 'utility' && (el as UtilityElement).utilityType === 'garden' && el.floor === currentFloor) as UtilityElement[];
        for (const garden of gardens) {
          if (isPointOnGardenLabel(point, garden, 30)) {
            setGardenToEdit(garden);
            setGardenLabelInput(garden.customLabel || 'GARDEN');
            setGardenAreaInput(garden.customArea || `${(garden.area || 0).toFixed(2)} m²`);
            setIsGardenEditPromptOpen(true);
            console.log('🌳 Text tool (touch): Garden label touched for editing');
            return;
          }
        }
      }

      // NEW: Check if in room positioning mode (AR scanned room being positioned)
      if (isPositioningRoom && positioningRoomId) {
        console.log('[Room Positioning] 🔍 TOUCH: Positioning mode active:', {
          positioningRoomId,
          currentTool,
          totalRooms: currentRooms.length,
          roomIds: currentRooms.map(r => ({ id: r.stableId, name: r.name }))
        });
        
        const room = currentRooms.find(r => r.stableId === positioningRoomId);
        if (room) {
          console.log('[Room Positioning] 🔍 TOUCH: Found target room:', {
            roomId: room.stableId,
            roomName: room.name,
            wallCount: room.walls.length,
            wallIdsPresent: room.walls.map(w => ({ hasId: !!w.id, id: w.id }))
          });
          
          const bounds = getRoomBounds(room.walls);
          console.log('[Room Positioning] 🔍 TOUCH: Bounding box:', {
            bounds,
            width: bounds.maxX - bounds.minX,
            height: bounds.maxY - bounds.minY,
            centerX: (bounds.minX + bounds.maxX) / 2,
            centerY: (bounds.minY + bounds.maxY) / 2
          });
          
          // Check if touched on center drag handle
          const centerX = (bounds.minX + bounds.maxX) / 2;
          const centerY = (bounds.minY + bounds.maxY) / 2;
          const handleRadius = 20 / scale;
          const distanceToCenter = Math.hypot(point.x - centerX, point.y - centerY);
          
          console.log('[Room Positioning] 🔍 TOUCH: Center handle check:', {
            touchPoint: point,
            centerPoint: { x: centerX, y: centerY },
            distanceToCenter,
            handleRadius,
            willActivate: distanceToCenter <= handleRadius
          });
          
          if (distanceToCenter <= handleRadius) {
            // Touched on center handle - start dragging
            setRoomDragStart(point);
            console.log('[Room Positioning] Started dragging room via center handle (touch):', positioningRoomId);
            return;
          }
          
          // Check if touched on corner handle for angle adjustment
          // Use larger detection radius for positioning mode (scale-aware like center handle)
          const cornerHandleRadius = 30 / scale; // Larger than standard 12px for easier touch on mobile
          
          // Check each corner
          const corners = [
            { id: 'nw', pos: { x: bounds.minX, y: bounds.minY } },
            { id: 'ne', pos: { x: bounds.maxX, y: bounds.minY } },
            { id: 'sw', pos: { x: bounds.minX, y: bounds.maxY } },
            { id: 'se', pos: { x: bounds.maxX, y: bounds.maxY } }
          ];
          
          let detectedHandle = null;
          let minCornerDistance = cornerHandleRadius;
          
          for (const corner of corners) {
            const dist = Math.hypot(point.x - corner.pos.x, point.y - corner.pos.y);
            if (dist < minCornerDistance) {
              minCornerDistance = dist;
              detectedHandle = corner.id;
            }
          }
          
          console.log('[Room Positioning] Corner detection check:', {
            touchPoint: point,
            bounds,
            detectedHandle,
            minCornerDistance,
            cornerHandleRadius,
            scale
          });
          
          if (detectedHandle) {
            console.log('[Room Positioning] ✅ Started corner dragging for angle adjustment (touch):', detectedHandle);
            setResizeHandle(detectedHandle);
            setRoomStart(point);
            return;
          }
        }
        // During positioning mode, if no handle was touched, allow roomResize tool to handle it
        // This allows the positioning room to be interacted with via the roomResize logic
        if (currentTool !== 'roomResize') {
          console.log('[Room Positioning] Blocked non-roomResize interaction during positioning mode');
          return;
        }
        console.log('[Room Positioning] Allowing roomResize tool during positioning mode');
      }

      if (currentTool === 'roomResize') {
        // CRITICAL: During positioning mode, wall resize is handled above in the positioning block
        // Block general room resize operations to prevent conflicts
        if (isPositioningRoom) {
          console.log('[Room Positioning] Blocked general room resize (touch) - positioning mode active');
          return;
        }
        
        // Always let the hook handle roomResize tool - it has its own wall detection logic
        hookHandleTouchStart(e);
        return;
      }
      
      // Check for garden and room label dragging when using select tool
      if (currentTool === 'select') {
        // Check garden labels first
        const gardenAtPoint = elements.find(el => {
          if (el.type !== 'utility') return false;
          const garden = el as UtilityElement;
          if (garden.utilityType !== 'garden') return false;
          return isPointOnGardenLabel(point, garden, 30);
        });
        
        if (gardenAtPoint) {
          const garden = gardenAtPoint as UtilityElement;
          setSelectedGardenId(garden.id);
          setIsDraggingGardenLabel(true);
          setGardenLabelDragStart(point);
          return;
        }
        
        // Then check room labels
        const roomLabelAtPoint = currentRooms.find(room => {
          const roomKey = room.stableId || room.id;
          const labelPosition = roomLabelPositions[roomKey];
          if (!labelPosition) return false;
          const distance = Math.hypot(point.x - labelPosition.x, point.y - labelPosition.y);
          return distance < 20;
        });
        if (roomLabelAtPoint) {
          setIsDraggingRoomLabel(true);
          setDraggedRoomLabelId(roomLabelAtPoint.id);
          return;
        }
        
        // 🚪 DOOR EDITING: Check for door elements before checking for rooms
        const doorAtPoint = findElementAtPoint(point);
        if (doorAtPoint && doorAtPoint.type === 'door') {
          console.log('🚪 Door element found on touch:', doorAtPoint);
          setDoorToEdit(doorAtPoint as DoorElement);
          setIsDoorPromptOpen(true);
          return;
        }
      }

      // Rotation buttons are now always visible on sinks - no touch detection needed
      
      if (currentTool === 'select' || currentTool === 'labelRoom') {
        
        const roomAtPoint = findRoomAtPoint(point, currentRooms);
        console.log('🔍 TOUCH START - ROOM_DETECTION:', {
          tool: currentTool,
          touchPoint: point,
          roomAtPoint: roomAtPoint ? { id: roomAtPoint.stableId, name: roomAtPoint.name } : null,
          totalRooms: currentRooms.length,
          willCallHandleTouchRoomInteraction: !!roomAtPoint
        });
        if (roomAtPoint) {
          console.log('🔍 CALLING handleTouchRoomInteraction with room:', roomAtPoint.name);
          handleTouchRoomInteraction(roomAtPoint, point, 'start');
          return;
        } else {
          console.log('🔍 NO ROOM FOUND AT TOUCH POINT');
        }
      }
      
      if (UTILITY_TOOLS.includes(currentTool as UtilityType)) {
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        
        // Garden rectangle drag creation
        if ((currentTool as string) === 'garden') {
          const gardensOnFloor = elements.filter(el => el.type === 'utility' && (el as any).utilityType === 'garden' && el.floor === currentFloor) as UtilityElement[];
          
          // Check if clicking on existing garden area text to edit it
          for (const garden of gardensOnFloor) {
            const size = 27;
            const labelOffsetX = garden.labelOffset?.x || 0;
            const labelOffsetY = garden.labelOffset?.y || 0;
            const areaTextY = garden.position.y + labelOffsetY + size * 2;
            // Garden editing now handled via double-click on label
            
            // Check if clicking on corner resize handle
            if (garden.width && garden.height) {
              const gardenResizeHandle = getGardenResizeHandle(point, garden, 30);
              if (gardenResizeHandle) {
                setSelectedGardenId((garden as any).id);
                setGardenResizeHandle(gardenResizeHandle);
                setIsResizingGarden(true);
                console.log('🌳 Started resizing garden corner:', gardenResizeHandle);
                return;
              }
            }
          }
          
          // Start dragging new garden rectangle
          setIsDraggingGarden(true);
          setGardenDragStart(point);
          setGardenPreview({ start: point, end: point });
          console.log('🌳 Started garden rectangle drag at', point);
          return;
        }
        
        // Sinks require wall proximity (like radiators)
        const isSink = (currentTool as string) === 'single-sink' || (currentTool as string) === 'double-sink';
        const isBathroomFixture = (currentTool as string) === 'bathtub' || (currentTool as string) === 'shower' || (currentTool as string) === 'toilet';
        
        if (isSink) {
          const wall = findWallForSink(point, wallsWithStableIds);
          if (wall) {
            setIsPlacingUtility(true);
            setWallForSink(wall);
            setUtilityPreview(createSinkElementOnWall(
              point,
              wall,
              currentTool as 'single-sink' | 'double-sink',
              currentFloor
            ));
          } else {
            // Fallback: place sink without wall constraint if no wall nearby
            setIsPlacingUtility(true);
            setUtilityPreview(createUtilityElement(point, currentTool as UtilityType, currentFloor, utilityRotation, wallsWithStableIds));
          }
        } else if (isBathroomFixture) {
          // Bathtub/shower/toilet require wall proximity (like sinks)
          const wall = findWallForSink(point, wallsWithStableIds);
          if (wall) {
            setIsPlacingUtility(true);
            setWallForSink(wall);
            setUtilityPreview(createBathroomFixtureOnWall(
              point,
              wall,
              currentTool as 'bathtub' | 'shower' | 'toilet',
              currentFloor
            ));
          } else {
            // Fallback: place without wall constraint if no wall nearby
            setIsPlacingUtility(true);
            setUtilityPreview(createUtilityElement(point, currentTool as UtilityType, currentFloor, utilityRotation, wallsWithStableIds));
          }
        } else {
          // Other utilities can be placed anywhere
          setIsPlacingUtility(true);
          setUtilityPreview(createUtilityElement(point, currentTool as UtilityType, currentFloor, utilityRotation, wallsWithStableIds));
        }
      } else if ((currentTool as string) === 'light') {
        setIsPlacingLight(true);
        setLightPreview(createLightElement(point, 'low-energy', currentFloor));
      } else if ((currentTool as string) === 'smoke-alarm') {
        setIsPlacingSmokeAlarm(true);
        setSmokeAlarmPreview(createSmokeAlarmElement(point, currentFloor));
      } else if ((currentTool as string) === 'emergency-light' || (currentTool as string) === 'fire-call-point' || (currentTool as string) === 'fire-panel' ||
                 (currentTool as string) === 'heat-alarm' || (currentTool as string) === 'alarm-sounder' || (currentTool as string) === 'wc-call-point' || (currentTool as string) === 'ac-unit' ||
                 (currentTool as string) === 'fire-alarm' || (currentTool as string) === 'ice-machine' || (currentTool as string) === 'coffee-machine') {
        setIsPlacingFireSafety(true);
        setFireSafetyPreview(createFireSafetyElement(point, currentTool as FireSafetyType, currentFloor));
      } else if ((currentTool as string) === 'loft-hatch') {
        setIsPlacingLoftHatch(true);
        setLoftHatchPreview(createLoftHatch(point, currentFloor));
      } else if ((currentTool as string) === 'compass' && !isRotatingCompass) {
        console.log('🧭 TOUCH START: Compass tool active (mouse handler)');
        // Check for rotation control on existing compass
        const compassesOnFloor = elements.filter(el => el.type === 'compass' && (el as any).floor === currentFloor) as CompassType[];
        console.log(`🧭 TOUCH START: Found ${compassesOnFloor.length} compasses on floor ${currentFloor}`);
        let clickedControl = false;
        
        for (const compass of compassesOnFloor) {
          const threshold = 15 / scale;
          if (isPointOnCompassRotationControl(point, compass, threshold)) {
            clickedControl = true;
            setSelectedCompassId((compass as any).id);
            setIsRotatingCompass(true);
            console.log('✅ TOUCH START: Rotation control clicked (mouse), isRotatingCompass set to TRUE');
            const angle = Math.atan2(point.y - compass.position.y, point.x - compass.position.x) * 180 / Math.PI;
            setCompassRotationStartAngle(angle);
            break;
          }
        }
        
        // Only start placing if not clicking on rotation control
        if (!clickedControl) {
          // Check if compass already exists (limit to 1)
          if (compassesOnFloor.length > 0) {
            console.log('⚠️ Compass already exists, only one compass allowed');
            return;
          }
          
          console.log('🧭 TOUCH START: Creating compass preview (mouse)');
          setIsPlacingCompass(true);
          setCompassPreview(createCompass(point));
        }
      } else if ((currentTool as string) === 'highlight') {
        setIsHighlighting(true);
        setHighlightStart(point);
        // Use wall snapping for highlight preview
        const wallsForSnapping = currentWalls.map(({tempId, ...w}) => w);
        const roomsForSnapping = currentRooms || [];
        
        // Calculate area snap for visual feedback
        const areaSnap = snapHighlightAreaToWalls(point, point, wallsForSnapping, roomsForSnapping, currentFloor);
        setHighlightSnap(areaSnap);
        
        setHighlightPreview(createHighlightPreviewWithWallSnapping(point, point, wallsForSnapping, roomsForSnapping, currentFloor, highlightColor));
      } else if ((currentTool as string) === 'door') {
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const wall = findWallForDoor(point, wallsWithStableIds);
        if (wall) {
          setIsPlacingDoor(true);
          setWallForDoor(wall);
          const newDoorPreview = createDoorElement(point, wall, currentFloor, true);
          setDoorPreview(newDoorPreview as DoorElement | null);
        }
      } else if ((currentTool as string) === 'window') {
        
        if (selectedWindowType === 'wall') {
          // Wall window placement - find wall and create wall-attached window
          const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
          const wall = findWallForWindow(point, wallsWithStableIds);
          if (wall) {
            setIsPlacingWindow(true);
            setWallForWindow(wall);
            const newWindowPreview = createWindowElement(point, wall as Wall & { tempId: string; stableId: string }, currentFloor, true);
            if (newWindowPreview) {
              (newWindowPreview as any).windowInstallationType = 'wall';
            }
            setWindowPreview(newWindowPreview as WindowElement | null);
          }
        } else if (selectedWindowType === 'roof') {
          // Roof window placement - create draggable canvas element
          const newRoofWindow = createRoofWindowElement(point, currentFloor, true);
          setIsPlacingWindow(true);
          setWindowPreview(newRoofWindow);
          return;
        } else if (selectedWindowType === 'solid_box') {
          // Solid box window placement - needs wall attachment like regular windows
          const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
          const clickedWall = findWallForWindow(point, wallsWithStableIds);
          if (clickedWall) {
            const newSolidBoxWindow = createSolidBoxWindowElement(point, clickedWall, currentFloor, true);
            if (newSolidBoxWindow) {
              setWindowPreview(newSolidBoxWindow);
              setWallForWindow(clickedWall);
              setIsPlacingWindow(true);
            }
          }
          return;
        } else if (selectedWindowType === 'canted') {
          // Canted window placement - needs wall attachment
          const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
          const clickedWall = findWallForWindow(point, wallsWithStableIds);
          if (clickedWall) {
            const newCantedWindow = createCantedWindowElement(point, clickedWall, currentFloor);
            if (newCantedWindow) {
              setWindowPreview(newCantedWindow);
              setWallForWindow(clickedWall);
              setIsPlacingWindow(true);
            }
          }
          return;
        }
      } else if ((currentTool as string) === 'radiator') {
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const wall = findWallForRadiator(point, wallsWithStableIds);
        if (wall) {
          setIsPlacingRadiator(true);
          setWallForRadiator(wall);
          const newRadiatorPreview = createRadiatorElement(point, wall, currentFloor, currentWalls, roomCustomLabels);
          setRadiatorPreview(newRadiatorPreview);
        }
      } else if ((currentTool as string) === 'boiler') {
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const wall = findWallForBoiler(point, wallsWithStableIds);
        if (wall) {
          setIsPlacingBoiler(true);
          setWallForBoiler(wall);
          const newBoilerPreview = createBoilerElement(point, wall, currentFloor);
          setBoilerPreview(newBoilerPreview);
        }
      } else if ((currentTool as string) === 'counter' && !isResizingCounter && !isRotatingCounter && !isDraggingCounter) {
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        
        // Check for rotation/move/resize controls
        const countersOnFloor = elements.filter(el => el.type === 'counter' && el.floor === currentFloor) as CounterElement[];
        let clickedControl = false;
        for (const counter of countersOnFloor) {
          // Check rotation control first (only for free-floating counters)
          if (isPointOnRotationControl(point, counter, 15 / scale)) {
            clickedControl = true;
            setSelectedCounterId(counter.id);
            setIsRotatingCounter(true);
            // Calculate initial angle from counter center to touch
            const centerX = (counter.start.x + counter.end.x) / 2;
            const centerY = (counter.start.y + counter.end.y) / 2;
            const startAngle = Math.atan2(point.y - centerY, point.x - centerX);
            setRotationStartAngle(startAngle);
            break;
          }
          
          // Check move control (only for free-floating counters)
          if (isPointOnMoveControl(point, counter, 15 / scale)) {
            clickedControl = true;
            setSelectedCounterId(counter.id);
            setIsDraggingCounter(true);
            setCounterDragStart(point);
            break;
          }
          
          // Check resize handles
          const handle = getCounterResizeHandle(point, counter, 15 / scale);
          if (handle) {
            clickedControl = true;
            setSelectedCounterId(counter.id);
            setCounterResizeHandle(handle);
            setIsResizingCounter(true);
            break;
          }
        }
        
        // Only start placing if not clicking on any control
        if (!clickedControl) {
          setIsPlacingCounter(true);
          const newCounterPreview = createCounterWithAutoSnap(point, wallsWithStableIds, currentFloor);
          setCounterPreview(newCounterPreview);
          
          // Store wall reference if snapped (for later use in resizing)
          const wall = findWallForCounter(point, wallsWithStableIds);
          setWallForCounter(wall);
        }
      } else if ((currentTool as string) === 'stairs' && !isResizingStairs && !isRotatingStairs && !isDraggingStairs) {
        console.log('🪜 TOUCH START: Stairs tool active');
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        
        // Check for rotation/move/resize controls
        const stairsOnFloor = elements.filter(el => el.type === 'stairs' && el.floor === currentFloor) as StairsElement[];
        let clickedControl = false;
        for (const stairs of stairsOnFloor) {
          // Check rotation control first (only for free-floating stairs)
          if (isPointOnStairsRotationControl(point, stairs, 15 / scale)) {
            clickedControl = true;
            setSelectedStairsId(stairs.id);
            setIsRotatingStairs(true);
            // Calculate initial angle from stairs center to touch
            const centerX = (stairs.start.x + stairs.end.x) / 2;
            const centerY = (stairs.start.y + stairs.end.y) / 2;
            const startAngle = Math.atan2(point.y - centerY, point.x - centerX);
            setRotationStartAngle(startAngle);
            break;
          }
          
          // Check move control
          if (isPointOnStairsMoveControl(point, stairs, 15 / scale)) {
            clickedControl = true;
            setSelectedStairsId(stairs.id);
            setIsDraggingStairs(true);
            setCounterDragStart(point); // Reuse counter drag start for simplicity
            break;
          }
          
          // Check resize handles
          const handle = getStairsResizeHandle(point, stairs, 20 / scale);
          if (handle) {
            clickedControl = true;
            setSelectedStairsId(stairs.id);
            setStairsResizeHandle(handle);
            setIsResizingStairs(true);
            break;
          }
        }
        
        // Only start placing if not clicking on any control
        if (!clickedControl) {
          setIsPlacingStairs(true);
          const newStairsPreview = createStairsWithAutoSnap(point, wallsWithStableIds, currentFloor, undefined, undefined, stairsDirection);
          setStairsPreview(newStairsPreview);
          
          // Store wall reference if snapped (for later use in resizing)
          const wall = findWallForStairs(point, wallsWithStableIds);
          setWallForStairs(wall);
        }
      } else if ((currentTool as string) === 'compass' && !isRotatingCompass) {
        console.log('🧭 TOUCH START: Compass tool active');
        // Check for rotation control on existing compass
        const compassesOnFloor = elements.filter(el => el.type === 'compass' && (el as any).floor === currentFloor) as CompassType[];
        console.log(`🧭 TOUCH START: Found ${compassesOnFloor.length} compasses on floor ${currentFloor}`);
        let clickedControl = false;
        
        for (const compass of compassesOnFloor) {
          const threshold = 15 / scale;
          const controlX = compass.position.x + compass.size * 1.3;
          const controlY = compass.position.y;
          const dist = Math.hypot(point.x - controlX, point.y - controlY);
          const handleRadius = compass.size * 0.35;
          const maxThreshold = Math.max(handleRadius, threshold);
          
          console.log(`🧭 ROTATION CHECK: Touch:(${point.x.toFixed(1)},${point.y.toFixed(1)}) Control:(${controlX.toFixed(1)},${controlY.toFixed(1)}) Distance:${dist.toFixed(1)} Threshold:${maxThreshold.toFixed(1)} Scale:${scale.toFixed(2)}`);
          
          if (isPointOnCompassRotationControl(point, compass, threshold)) {
            clickedControl = true;
            setSelectedCompassId((compass as any).id);
            setIsRotatingCompass(true);
            console.log('✅ TOUCH START: Rotation control clicked, isRotatingCompass set to TRUE');
            // Calculate initial angle from compass center to touch
            const angle = Math.atan2(point.y - compass.position.y, point.x - compass.position.x) * 180 / Math.PI;
            setCompassRotationStartAngle(angle);
            break;
          } else {
            console.log(`❌ ROTATION CHECK: Distance ${dist.toFixed(1)} > Threshold ${maxThreshold.toFixed(1)} - NOT on rotation control`);
          }
        }
        
        // Only start placing if not clicking on rotation control
        if (!clickedControl) {
          // Check if compass already exists (limit to 1)
          if (compassesOnFloor.length > 0) {
            console.log('⚠️ Compass already exists, only one compass allowed');
            return;
          }
          
          console.log('🧭 TOUCH START: Creating compass preview, isPlacingCompass set to TRUE');
          const newCompass: CompassType = {
            type: 'compass',
            position: point,
            rotation: 0,
            size: 40
          };
          setCompassPreview(newCompass);
          setIsPlacingCompass(true);
        }
      } else {
        console.log(`⚠️ TOUCH START: No specific handler matched, falling through to hook. Tool: "${currentTool}"`);
        hookHandleTouchStart(e);
      }
    }

const perfEnd = performance.now();
console.log(`⚡ TouchStart TOTAL: ${(perfEnd - perfStart).toFixed(1)}ms`);
};

const handleTouchEndEvent = (e: React.TouchEvent<HTMLCanvasElement>) => {
const perfStart = performance.now();
console.log('⚡ TouchEnd BEGIN');
e.preventDefault();

    // NEW: Handle room positioning mode drag end
    if (isPositioningRoom && roomDragStart) {
      setRoomDragStart(null);
      console.log('[Room Positioning] Center drag ended (touch) - user can adjust or confirm position');
      return;
    }
    
    // Finish garden rectangle drag and create garden (MUST BE BEFORE HOOK HANDLER)
    if (isDraggingGarden && gardenPreview) {
      const width = Math.abs(gardenPreview.end.x - gardenPreview.start.x);
      const height = Math.abs(gardenPreview.end.y - gardenPreview.start.y);
      
      console.log('🌳 Garden drag end:', { width, height, isDraggingGarden, hasPreview: !!gardenPreview });
      
      // Only create if drag is meaningful (minimum size)
      if (width > 50 && height > 50) {
        recordUndoState();
        const PIXELS_PER_METER = 100;
        const area = (width * height) / (PIXELS_PER_METER * PIXELS_PER_METER);
        const center = {
          x: (gardenPreview.start.x + gardenPreview.end.x) / 2,
          y: (gardenPreview.start.y + gardenPreview.end.y) / 2
        };
        
        const newGarden: UtilityElement = {
          id: `garden-${Date.now()}`,
          type: 'utility',
          utilityType: 'garden',
          position: center,
          floor: currentFloor,
          width,
          height,
          area,
          snappedWalls: []
        };
        
        setElements([...elements, newGarden]);
        console.log('🌳 Garden created:', width.toFixed(0), 'x', height.toFixed(0), 'px, area:', area.toFixed(2), 'm²');
      } else {
        console.log('🌳 Garden too small, not creating:', width.toFixed(0), 'x', height.toFixed(0), 'px');
      }
      
      setIsDraggingGarden(false);
      setGardenDragStart(null);
      setGardenPreview(null);
      return;
    }
    
    // Finish garden corner resizing
    if (isResizingGarden) {
      recordUndoState();
      setIsResizingGarden(false);
      setSelectedGardenId(null);
      setGardenResizeHandle(null);
      return;
    }
    
    // NEW: Handle room positioning corner drag end - EXACT RESIZE TOOL COMMIT LOGIC
    if (isPositioningRoom && resizeHandle && tempWalls) {
      // 🎯 EXACT RESIZE TOOL COMMIT (from useFloorPlanInteractions:11066-11070)
      // Apply CM grid snapping to the final position from tempWalls
      const roomWallIdSet = new Set(positioningRoomWallIds);
      
      const committedWalls = tempWalls.map(wall => {
        if (!wall.id || !roomWallIdSet.has(wall.id)) {
          return wall;
        }
        
        // Apply CM grid snapping (same as resize tool)
        return {
          ...wall,
          start: {
            x: Math.round(wall.start.x / CM_SNAP_SIZE) * CM_SNAP_SIZE,
            y: Math.round(wall.start.y / CM_SNAP_SIZE) * CM_SNAP_SIZE
          },
          end: {
            x: Math.round(wall.end.x / CM_SNAP_SIZE) * CM_SNAP_SIZE,
            y: Math.round(wall.end.y / CM_SNAP_SIZE) * CM_SNAP_SIZE
          }
        };
      });
      
      // CRITICAL: Set operation type to prevent room merging during positioning
      lastOperationTypeRef.current = 'positioningRoomAdjustment';
      
      // Commit to floorPlans
      setFloorPlans(prev => ({ ...prev, [currentFloor]: committedWalls }));
      
      // Clear states
      setResizeHandle(null);
      setRoomStart(null);
      setTempWalls(null);
      setCornerDragPreview(null);
      
      console.log('[Room Positioning] ✅ Corner drag committed with CM grid snapping - room merging prevented');
      return;
    }
    
    // Finish counter rotation
    if (isRotatingCounter) {
      recordUndoState();
      setIsRotatingCounter(false);
      setSelectedCounterId(null);
      setRotationStartAngle(0);
      return;
    }
    
    // Finish counter dragging
    if (isDraggingCounter) {
      recordUndoState();
      setIsDraggingCounter(false);
      setSelectedCounterId(null);
      setCounterDragStart(null);
      return;
    }

    // Finish counter resizing
    if (isResizingCounter) {
      recordUndoState();
      setIsResizingCounter(false);
      setSelectedCounterId(null);
      setCounterResizeHandle(null);
      return;
    }

    // Finish compass rotation
    if (isRotatingCompass) {
      recordUndoState();
      setIsRotatingCompass(false);
      // Keep compass selected after rotation
      return;
    }
    
    // Finish stairs rotation
    if (isRotatingStairs) {
      recordUndoState();
      setIsRotatingStairs(false);
      setSelectedStairsId(null);
      setRotationStartAngle(0);
      return;
    }
    
    // Finish stairs dragging

    if (isDraggingRoomLabel) {
      setIsDraggingRoomLabel(false);
      setDraggedRoomLabelId(null);
      return;
    }

    if (touchRoomInteraction && touchRoomInteraction.room && e.changedTouches.length === 1) {
      const touch = e.changedTouches[0];
      const point = getCanvasCoordinates(touch.clientX, touch.clientY);
      handleTouchRoomInteraction(touchRoomInteraction.room, point, 'end');
      return;
    }


    
    if (isPlacingLight && lightPreview) {
      recordUndoState();
      const newLight = { ...lightPreview, id: `light-${Date.now()}` } as unknown as FloorPlanElement;
      const updatedElements = [...elements, newLight];
      setElements(updatedElements);
      
      const deferredLightSync = () => {
        const roomContainingLight = findRoomAtPoint(lightPreview.position, currentRooms);
        
        if (roomContainingLight && roomContainingLight.name) {
          roomSync.updateLightCount(roomContainingLight.name, lightPreview.lightType, 1, currentFloor);
          roomSync.syncAllFloorsLightsToAdditionalInfo();
          setSyncedRoomName(roomContainingLight.name);
          setShowRoomSyncIndicator(true);
          console.log(`💡 Light placed in room "${roomContainingLight.name}" - updated ${lightPreview.lightType} light count`);
        }
      };
      
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(deferredLightSync, { timeout: 100 });
      } else {
        setTimeout(deferredLightSync, 16);
      }
      
      setIsPlacingLight(false);
      setLightPreview(null);
    }

    if (isPlacingSmokeAlarm && smokeAlarmPreview) {
      recordUndoState();
      setElements([...elements, { ...smokeAlarmPreview, id: `smoke-alarm-${Date.now()}` } as unknown as FloorPlanElement]);
      
      setIsPlacingSmokeAlarm(false);
      setSmokeAlarmPreview(null);
    }

    if (isPlacingFireSafety && fireSafetyPreview) {
      recordUndoState();
      setElements([...elements, { ...fireSafetyPreview, id: `${fireSafetyPreview.type}-${Date.now()}` } as unknown as FloorPlanElement]);
      
      setIsPlacingFireSafety(false);
      setFireSafetyPreview(null);
    }

    if (isPlacingUtility && utilityPreview) {
      recordUndoState();
      
      // Apply wall snapping for garden elements
      let finalUtility = { ...utilityPreview, id: `utility-${Date.now()}` };
      if (utilityPreview.utilityType === 'garden') {
        const snapResult = snapGardenToWalls(
          utilityPreview.position,
          currentWalls,
          currentRooms || [],
          { width: utilityPreview.width || 300, height: utilityPreview.height || 200 }
        );
        finalUtility = {
          ...finalUtility,
          width: snapResult.width,
          height: snapResult.height,
          area: snapResult.area,
          snappedWalls: snapResult.snappedWalls
        };
      }
      
      setElements([...elements, finalUtility as unknown as FloorPlanElement]);
      
      setIsPlacingUtility(false);
      setUtilityPreview(null);
      setWallForSink(null);
      setUtilityRotation(0); // Reset rotation for next placement
    }

    if (isPlacingLoftHatch && loftHatchPreview) {
      recordUndoState();
      setElements([...elements, { ...loftHatchPreview, id: `loft-hatch-${Date.now()}` } as unknown as FloorPlanElement]);
      onDebug('LOFT_HATCH_PLACED', { loftHatch: loftHatchPreview });
      
      setIsPlacingLoftHatch(false);
      setLoftHatchPreview(null);
    }

    if (isPlacingDoor && doorPreview) {
      recordUndoState();
      const finalDoor = { ...doorPreview, id: `door-${Date.now()}` };
      const updatedElements = [...elements, finalDoor as unknown as FloorPlanElement];
      setElements(updatedElements);
      
      const deferredDoorSync = () => {
        if (roomSync) {
          const allDoors = updatedElements.filter(el => el.type === 'door') as unknown as DoorElement[];
          const currentFloorWalls = floorPlans[currentFloor] || [];
          roomSync.syncDoorUndercuts(allDoors, currentRooms, currentFloor, currentFloorWalls);
          
          const allFloorsDoors = elements.filter(el => el.type === 'door') as unknown as DoorElement[];
          roomSync.syncAllFloorsDoorUndercuts(allFloorsDoors, floorPlans, roomCustomLabels);
          
          console.log(`🚪 Door sync completed - count: ${allDoors.length}, total floors: ${allFloorsDoors.length}`);
        }
      };
      
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(deferredDoorSync, { timeout: 100 });
      } else {
        setTimeout(deferredDoorSync, 16);
      }
      
      setDoorToEdit(finalDoor as DoorElement);
      setIsDoorPromptOpen(true);
    }

    if (isPlacingWindow && windowPreview) {
      recordUndoState();
      const finalWindow = { 
        ...windowPreview, 
        id: `window-${Date.now()}`,
        displayId: generateNextWindowId(windowCounter)
      };
      
      const finalWindowWithOrientation = calculateWindowOrientationIfReady(finalWindow as WindowElement);
      
      const updatedElements = [...elements, finalWindowWithOrientation as unknown as FloorPlanElement];
      setElements(updatedElements);
      
      // Increment window counter for next window
      setWindowCounter(prev => prev + 1);
      
      // Trigger window count update immediately after placement
      if (roomSync) {
        // Use setTimeout to ensure state update has completed before sync
        setTimeout(() => {
          const allWindows = updatedElements.filter(el => el.type === 'window') as unknown as WindowElement[];
          
          // Sync room-specific windows for current floor
          roomSync.smartSyncWindows(allWindows, currentRooms, currentFloor);
          
          // Sync overall window counts across ALL floors for site notes
          roomSync.syncAllFloorsWindows(allWindows, floorPlans, roomCustomLabels);
          
          console.log(`🪟 Window placement sync completed - Total windows: ${allWindows.length}`);
        }, 50);
      }
      
      setIsPlacingWindow(false);
      setWindowPreview(null);
      setWallForWindow(null);

      // 🏢 ESTATE MODE: Skip window modal on touch placement
      console.log('🏢 ESTATE: Window placed via touch, modal disabled for estate floorplan');
    }

    if (isPlacingRadiator && radiatorPreview) {
      recordUndoState();
      
      const finalRadiator: RadiatorElement = {
        ...radiatorPreview,
        id: `radiator-${Date.now()}`,
      };
      setElements([...elements, finalRadiator]);
      // 🏢 ESTATE MODE: Skip radiator modal on touch placement
      console.log('🏢 ESTATE: Radiator placed via touch, modal disabled for estate floorplan');
      
      const deferredRadiatorOptimization = () => {
        const touch = e.changedTouches[0];
        const finalPoint = getCanvasCoordinates(touch.clientX, touch.clientY);
        const wallsWithStableIds = currentWalls.filter(w => (w as any).stableId) as (Wall & { tempId: string, stableId: string })[];
        const finalWall = findWallForRadiator(finalPoint, wallsWithStableIds);
        
        if (finalWall) {
          const optimizedRadiatorElement = createRadiatorElement(finalPoint, finalWall, currentFloor, currentWalls, roomCustomLabels);
          if (optimizedRadiatorElement) {
            const distance = Math.sqrt(
              Math.pow(optimizedRadiatorElement.position.x - finalRadiator.position.x, 2) +
              Math.pow(optimizedRadiatorElement.position.y - finalRadiator.position.y, 2)
            );
            if (distance > 10) {
              setElements(prev => prev.map(el => 
                'id' in el && el.id === finalRadiator.id 
                  ? { ...el, position: optimizedRadiatorElement.position }
                  : el
              ));
            }
          }
        }
      };
      
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(deferredRadiatorOptimization, { timeout: 200 });
      } else {
        setTimeout(deferredRadiatorOptimization, 50);
      }
    }

    if (isPlacingBoiler && boilerPreview) {
      recordUndoState();
      
      const finalBoiler: BoilerElement = {
        ...boilerPreview,
        id: `boiler-${Date.now()}`,
      };
      setElements([...elements, finalBoiler]);
      // 🏢 ESTATE MODE: Skip boiler modal on touch placement
      console.log('🏢 ESTATE: Boiler placed via touch, modal disabled for estate floorplan');
      
      if (roomSync && Object.values(currentRooms).length > 0) {
        roomSync.syncBoilerLocations([finalBoiler], Object.values(currentRooms));
      }
      
      console.log(`🔥 Boiler placed at position: (${finalBoiler.position.x}, ${finalBoiler.position.y})`);
    }

    if (isPlacingCounter && counterPreview) {
      recordUndoState();
      
      const finalCounter: CounterElement = {
        ...counterPreview,
        id: `counter-${Date.now()}`,
      };
      setElements([...elements, finalCounter]);
      console.log(`📐 Counter placed: (${finalCounter.start.x}, ${finalCounter.start.y}) to (${finalCounter.end.x}, ${finalCounter.end.y})`);
    }

    if (isPlacingStairs && stairsPreview) {
      recordUndoState();
      
      const finalStairs: StairsElement = {
        ...stairsPreview,
        id: `stairs-${Date.now()}`,
      };
      setElements([...elements, finalStairs]);
      console.log(`🪜 Stairs placed: (${finalStairs.start.x}, ${finalStairs.start.y}) to (${finalStairs.end.x}, ${finalStairs.end.y}), direction: ${finalStairs.direction}`);
    }

    if (isPlacingCompass && compassPreview) {
      recordUndoState();
      const finalCompass: any = {
        ...compassPreview,
        id: `compass-${Date.now()}`,
        floor: currentFloor,
      };
      setElements([...elements, finalCompass]);
      // Keep compass tool active for continuous placement
      setCompassPreview(null);
      console.log('🧭 ESTATE: Compass placed via touch');
    }

    // 🚨 DEBUG: Check highlight conditions before processing
    console.log('🚨 TOUCHEND: HIGHLIGHT CONDITIONS CHECK', {
      isHighlighting,
      hasHighlightPreview: !!highlightPreview,
      hasHighlightStart: !!highlightStart,
      changedTouchesLength: e.changedTouches.length,
      currentTool,
      timestamp: Date.now()
    });

    if (isHighlighting && highlightPreview && highlightStart && e.changedTouches.length > 0) {
      console.log('🎯 TOUCHEND: HIGHLIGHT CONDITIONS MET - Processing highlight');
      
      try {
        const touch = e.changedTouches[0];
        const currentPoint = getCanvasCoordinates(touch.clientX, touch.clientY);
        
        // Only create highlight if there's significant drag distance
        const dragDistance = Math.sqrt(
          Math.pow(currentPoint.x - highlightStart.x, 2) + 
          Math.pow(currentPoint.y - highlightStart.y, 2)
        );
        
        console.log('🎯 TOUCHEND: DRAG DISTANCE CHECK', {
          highlightStart,
          currentPoint,
          dragDistance,
          minRequired: 5,
          willCreateHighlight: dragDistance > 5
        });
        
        if (dragDistance > 5) { // Minimum drag distance to create highlight
          console.log('🎯 TOUCHEND: CREATING HIGHLIGHT - Drag distance sufficient');
          
          recordUndoState();
          // Use wall snapping for final highlight creation
          const wallsForSnapping = currentWalls.map(({tempId, ...w}) => w);
          const roomsForSnapping = currentRooms || [];
          
          console.log('🎯 TOUCHEND: HIGHLIGHT CREATION DATA', {
            wallsCount: wallsForSnapping.length,
            roomsCount: roomsForSnapping.length,
            currentFloor,
            highlightColor
          });
          
          const finalHighlight = createHighlightAreaWithWallSnapping(
            highlightStart, 
            currentPoint, 
            wallsForSnapping, 
            roomsForSnapping, 
            currentFloor, 
            highlightColor
          );
          
          console.log('🎯 TOUCHEND: HIGHLIGHT CREATED', {
            finalHighlight: {
              id: finalHighlight.id,
              type: finalHighlight.type,
              startPoint: finalHighlight.startPoint,
              endPoint: finalHighlight.endPoint,
              floor: finalHighlight.floor
            }
          });
          
          setElements([...elements, finalHighlight]);
          console.log(`🎨 Highlight created (touch) with wall snapping from (${highlightStart.x}, ${highlightStart.y}) to (${currentPoint.x}, ${currentPoint.y})`);
          
          // Calculate metrics for the highlighted area (keeping full calculations for shared popup component)
          console.log('🚨 TOUCHEND: ABOUT TO CALL calculateHighlightMetrics');
          
          try {
            const calculations = calculateHighlightMetrics(finalHighlight, wallsForSnapping, roomsForSnapping);
            
            console.log('🚨 TOUCHEND: CALCULATION RESULT', {
              calculations: {
                area: calculations.area,
                hlp: calculations.hlp,
                pw: calculations.pw,
                sw: calculations.sw
              }
            });
            
            // Show popup with calculations
            setHighlightPopupData({
              highlight: finalHighlight,
              calculations,
              position: { 
                x: touch.clientX + 20, // Position popup near the touch point
                y: touch.clientY + 20 
              }
            });
            setShowHighlightPopup(true);
            console.log(`📊 Highlight calculations popup shown - Area: ${calculations.area}m², HLP: ${calculations.hlp}m, PW: ${calculations.pw}m, SW: ${calculations.sw}m`);
          } catch (calcError) {
            console.error('🚨 TOUCHEND: ERROR IN calculateHighlightMetrics', calcError);
            if (calcError instanceof Error) {
              console.error('🚨 TOUCHEND: Error stack:', calcError.stack);
            }
          }
          
        } else {
          console.log('🎯 TOUCHEND: DRAG DISTANCE TOO SMALL - Not creating highlight');
        }
        
      } catch (error) {
        console.error('🚨 TOUCHEND: ERROR IN HIGHLIGHT PROCESSING', error);
        if (error instanceof Error) {
          console.error('🚨 TOUCHEND: Error stack:', error.stack);
        }
      }
      
      setIsHighlighting(false);
      setHighlightStart(null);
      setHighlightPreview(null);
      setHighlightSnap(null);
    } else {
      console.log('🚨 TOUCHEND: HIGHLIGHT CONDITIONS NOT MET - Skipping highlight processing');
    }
    
    const multiTouchHandled = processTouchEnd(e);
    console.log('🔄 TouchEnd GESTURE CHECK:', { 
      multiTouchHandled, 
      currentTool, 
      willCallHookHandler: !multiTouchHandled 
    });
    
    if (!multiTouchHandled) {
      console.log('✅ TouchEnd CALLING HOOK HANDLER');
      hookHandleTouchEndEvent(e);
    } else {
      console.log('❌ TouchEnd BLOCKED BY MULTITOUCH HANDLER');
    }
    
    if (isPlacingDoor) {
        setIsPlacingDoor(false);
        setDoorPreview(null);
        setWallForDoor(null);
    }
    if (isPlacingWindow) {
      setIsPlacingWindow(false);
      setWindowPreview(null);
      setWallForWindow(null);
    }
    if (isPlacingRadiator) {
      setIsPlacingRadiator(false);
      setRadiatorPreview(null);
      setWallForRadiator(null);
    }
    if (isPlacingBoiler) {
      setIsPlacingBoiler(false);
      setBoilerPreview(null);
      setWallForBoiler(null);
    }
    if (isPlacingCounter) {
      setIsPlacingCounter(false);
      setCounterPreview(null);
      setWallForCounter(null);
    }
    if (isPlacingStairs) {
      setIsPlacingStairs(false);
      setStairsPreview(null);
      setWallForStairs(null);
    }
    if (isPlacingLight) {
      setIsPlacingLight(false);
      setLightPreview(null);
    }
    if (isPlacingSmokeAlarm) {
      setIsPlacingSmokeAlarm(false);
      setSmokeAlarmPreview(null);
    }
    if (isPlacingFireSafety) {
      setIsPlacingFireSafety(false);
      setFireSafetyPreview(null);
    }
    // Finish garden rectangle drag and create garden
    if (isDraggingGarden && gardenPreview) {
      const width = Math.abs(gardenPreview.end.x - gardenPreview.start.x);
      const height = Math.abs(gardenPreview.end.y - gardenPreview.start.y);
      
      // Only create if drag is meaningful (minimum size)
      if (width > 50 && height > 50) {
        recordUndoState();
        const PIXELS_PER_METER = 100;
        const area = (width * height) / (PIXELS_PER_METER * PIXELS_PER_METER);
        const center = {
          x: (gardenPreview.start.x + gardenPreview.end.x) / 2,
          y: (gardenPreview.start.y + gardenPreview.end.y) / 2
        };
        
        const newGarden: UtilityElement = {
          id: `garden-${Date.now()}`,
          type: 'utility',
          utilityType: 'garden',
          position: center,
          floor: currentFloor,
          width,
          height,
          area,
          snappedWalls: []
        };
        
        setElements([...elements, newGarden]);
        console.log('🌳 Garden created:', width.toFixed(0), 'x', height.toFixed(0), 'px, area:', area.toFixed(2), 'm²');
      }
      
      setIsDraggingGarden(false);
      setGardenDragStart(null);
      setGardenPreview(null);
      return;
    }
    
    // Finish garden corner resizing
    if (isResizingGarden) {
      recordUndoState();
      setIsResizingGarden(false);
      setSelectedGardenId(null);
      setGardenResizeHandle(null);
      return;
    }
    
    if (isDraggingGardenLabel) {
      setIsDraggingGardenLabel(false);
      setGardenLabelDragStart(null);
      // Keep selectedGardenId for a moment to show selection
      setTimeout(() => setSelectedGardenId(null), 100);
    }
    if (isPlacingUtility) {
      setIsPlacingUtility(false);
      setUtilityPreview(null);
      setWallForSink(null);
      setUtilityRotation(0); // Reset rotation
    }
    if (isPlacingLoftHatch) {
      setIsPlacingLoftHatch(false);
      setLoftHatchPreview(null);
    }
    
    const perfEnd = performance.now();
    console.log(`⚡ TouchEnd TOTAL: ${(perfEnd - perfStart).toFixed(1)}ms`);
  };

  const handleWallPlacement = () => {
    onDebug('[FPC handleWallPlacement] Entry. lastOperationTypeRef.current:', {opType: lastOperationTypeRef.current});
    if (!roomStart || !roomEnd) return;

    // Calculate existing rooms for enhanced snapping
    const existingRooms = Object.values(currentRooms);
    
    // Use enhanced snapping for both start and end points
    const enhancedSnapping = snapRoomForAdjacentCreation(
      roomStart,
      roomEnd,
              currentWalls.map(({tempId, ...wall}) => ({
          ...wall,
          // Preserve split wall metadata
          ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
          ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
          ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
          ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
          ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
          ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector })
        })),
      existingRooms,
      GRID_SIZE,
      SNAP_THRESHOLD,
      scale
    );

    onDebug('[FPC handleWallPlacement] Enhanced snapping applied:', {
      originalStart: roomStart,
      originalEnd: roomEnd,
      snappedStart: enhancedSnapping.snappedStart,
      snappedEnd: enhancedSnapping.snappedEnd,
      isAdjacent: enhancedSnapping.isAdjacentToExisting
    });

    // Record undo state before making changes
    recordUndoState();

    // 🎯 ROOT CAUSE FIX: Apply precision control at creation time
    const precisionStart = snapPointToGrid(enhancedSnapping.snappedStart, GRID_SIZE);
    const precisionEnd = snapPointToGrid(enhancedSnapping.snappedEnd, GRID_SIZE);
    
    const newWall: any = {
      start: precisionStart,
      end: precisionEnd,
      type: 'None',
      floor: currentFloor,
      stableId: `wall_${precisionStart.x.toFixed(2)}_${precisionStart.y.toFixed(2)}_${precisionEnd.x.toFixed(2)}_${precisionEnd.y.toFixed(2)}`
    };
    
    // 🎯 ROOT CAUSE FIX: Check for duplicate walls with same coordinates
    const isDuplicateWall = currentWalls.some(existingWall => {
      const tolerance = 0.1;
      return (
        // Same direction
        (Math.abs(existingWall.start.x - precisionStart.x) < tolerance &&
         Math.abs(existingWall.start.y - precisionStart.y) < tolerance &&
         Math.abs(existingWall.end.x - precisionEnd.x) < tolerance &&
         Math.abs(existingWall.end.y - precisionEnd.y) < tolerance) ||
        // Opposite direction
        (Math.abs(existingWall.start.x - precisionEnd.x) < tolerance &&
         Math.abs(existingWall.start.y - precisionEnd.y) < tolerance &&
         Math.abs(existingWall.end.x - precisionStart.x) < tolerance &&
         Math.abs(existingWall.end.y - precisionStart.y) < tolerance)
      );
    });
    
    if (!doesWallIntersect(newWall, currentWalls) && !isDuplicateWall) {
      const updatedWalls = [...currentWalls, newWall];
      onDebug('[FPC handleWallPlacement] Updating walls:', {count: updatedWalls.length, opType: lastOperationTypeRef.current, isAdjacent: enhancedSnapping.isAdjacentToExisting});
      // Update floor plans and trigger room recalculation
      const newFloorPlansState = {
        ...floorPlans,
        [currentFloor]: updatedWalls
      };
      setFloorPlans(newFloorPlansState);
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    undoRedoInProgressRef.current = true; 

    const currentStateToPushToRedo: UndoRedoState = {
      floorPlans,
      elements,
      textBoxes,
      roomCustomLabels,
      propertyAddress,
      windowCount,
      lightCount,
      currentFloor,
    };
    setRedoStack(prev => [...prev, currentStateToPushToRedo]);

    const stateToRestore = undoStack[undoStack.length - 1];
    setFloorPlans(stateToRestore.floorPlans);
    setElements(stateToRestore.elements);
    setTextBoxes(stateToRestore.textBoxes);
    setRoomCustomLabels(stateToRestore.roomCustomLabels);
    setPropertyAddress(stateToRestore.propertyAddress);
    setWindowCount(stateToRestore.windowCount);
    setLightCount(stateToRestore.lightCount);
    setCurrentFloor(stateToRestore.currentFloor);
    
    setUndoStack(undoStack.slice(0, -1));

    // Reset interaction-specific states after undo
    setSelectedRoom(null);
    setActivelyResizedWalls(null);
    setResizingRoomInfo(null);
    setResizeHandle(null);
    setTempWalls(null);
    setIsResizing(false); // Explicitly reset resizing state
    isResizingRef.current = false; // And the corresponding ref used by the hook
    justResizedRef.current = false;
    roomMigrationMapRef.current = {}; 
    
    // Mark that floor plan has changes after undo
    setHasFloorPlanChanges(true);

    onDebug("HANDLE_UNDO_COMPLETE", { 
      undoStackSize: undoStack.length -1, 
      redoStackSize: redoStack.length + 1 
    });
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    undoRedoInProgressRef.current = true; 

    const currentStateToPushToUndo: UndoRedoState = {
      floorPlans,
      elements,
      textBoxes,
      roomCustomLabels,
      propertyAddress,
      windowCount,
      lightCount,
      currentFloor,
    };
    setUndoStack(prev => [...prev, currentStateToPushToUndo]);
    
    const stateToRestore = redoStack[redoStack.length - 1];
    setFloorPlans(stateToRestore.floorPlans);
    setElements(stateToRestore.elements);
    setTextBoxes(stateToRestore.textBoxes);
    setRoomCustomLabels(stateToRestore.roomCustomLabels);
    setPropertyAddress(stateToRestore.propertyAddress);
    setWindowCount(stateToRestore.windowCount);
    setLightCount(stateToRestore.lightCount);
    setCurrentFloor(stateToRestore.currentFloor);

    setRedoStack(redoStack.slice(0, -1));

    // Reset interaction-specific states after redo
    setSelectedRoom(null);
    setActivelyResizedWalls(null);
    setResizingRoomInfo(null);
    setResizeHandle(null);
    setTempWalls(null);
    setIsResizing(false); // Explicitly reset resizing state
    isResizingRef.current = false; // And the corresponding ref used by the hook
    justResizedRef.current = false;
    roomMigrationMapRef.current = {};

    // Mark that floor plan has changes after redo
    setHasFloorPlanChanges(true);

    onDebug("HANDLE_REDO_COMPLETE", { 
      undoStackSize: undoStack.length + 1, 
      redoStackSize: redoStack.length - 1 
    });
  };

  const addNewFloor = () => {
    recordUndoState(); // Record state before adding new floor

    const newFloor = Math.max(...Object.keys(floorPlans).map(Number)) + 1;
    setFloorPlans({
      ...floorPlans,
      [newFloor]: []
    });
    setCurrentFloor(newFloor);
  };

  // Floor recovery function to handle missing Floor 1 or gaps in floor sequence
  const recoverMissingFloors = () => {
    const existingFloors = Object.keys(floorPlans).map(Number).sort((a, b) => a - b);
    const missingFloors: number[] = [];
    
    console.log(`🏢 Current floor status:`, {
      currentFloor,
      existingFloors,
      floorPlansKeys: Object.keys(floorPlans),
      totalFloors: existingFloors.length
    });
    
    // Check for missing floors from 1 to the highest existing floor
    if (existingFloors.length > 0) {
      const highestFloor = Math.max(...existingFloors);
      for (let i = 1; i <= highestFloor; i++) {
        if (!existingFloors.includes(i)) {
          missingFloors.push(i);
        }
      }
    }
    
    // If there are missing floors, recover them
    if (missingFloors.length > 0) {
      recordUndoState(); // Record state before recovery
      
      const updatedFloorPlans = { ...floorPlans };
      missingFloors.forEach(floorNumber => {
        updatedFloorPlans[floorNumber] = []; // Create empty floor
      });
      
      setFloorPlans(updatedFloorPlans);
      
      console.log(`🔧 Recovered missing floors: ${missingFloors.join(', ')}`);
      alert(`Recovered missing floors: ${missingFloors.join(', ')}. You can now navigate between all floors.`);
      
      // If current floor is higher than 1 and Floor 1 was missing, optionally switch to Floor 1
      if (missingFloors.includes(1) && currentFloor > 1) {
        const shouldSwitchToFloor1 = window.confirm('Floor 1 has been recovered. Would you like to switch to Floor 1 now?');
        if (shouldSwitchToFloor1) {
          setCurrentFloor(1);
        }
      }
      
      return true; // Recovery performed
    } else {
      alert('No missing floors detected. All floors from 1 to ' + Math.max(...existingFloors) + ' are present.');
      return false; // No recovery needed
    }
  };

  // Debug function to check floor status (accessible from browser console)
  React.useEffect(() => {
    (window as any).debugFloorStatus = () => {
      const existingFloors = Object.keys(floorPlans).map(Number).sort((a, b) => a - b);
      console.log('🏢 Floor Plan Debug Info:', {
        currentFloor,
        existingFloors,
        floorPlansData: floorPlans,
        totalFloors: existingFloors.length,
        canNavigatePrev: existingFloors.indexOf(currentFloor) > 0,
        canNavigateNext: existingFloors.indexOf(currentFloor) < existingFloors.length - 1
      });
      return {
        currentFloor,
        existingFloors,
        totalFloors: existingFloors.length
      };
    };
    
    (window as any).recoverFloors = recoverMissingFloors;
    
    return () => {
      delete (window as any).debugFloorStatus;
      delete (window as any).recoverFloors;
    };
  }, [floorPlans, currentFloor, recoverMissingFloors]);

  const deleteFloor = (floorToDelete?: number) => {
    const targetFloor = floorToDelete || currentFloor;
    const floors = Object.keys(floorPlans).map(Number).sort((a, b) => a - b);
    
    // Prevent deleting the last remaining floor
    if (floors.length <= 1) {
      console.warn('Cannot delete the last remaining floor');
      return;
    }

    recordUndoState(); // Record state before deleting floor

    console.log(`🗑️ Starting floor deletion: target=${targetFloor}, existing floors=[${floors.join(',')}]`);

    // Step 1: Remove the target floor and renumber remaining floors sequentially
    const updatedFloorPlans: Record<number, Wall[]> = {};
    let newFloorNumber = 1;
    const floorMapping: Record<number, number> = {}; // old floor -> new floor

    floors.forEach(floorNum => {
      if (floorNum !== targetFloor) {
        updatedFloorPlans[newFloorNumber] = floorPlans[floorNum].map(wall => ({
          ...wall,
          floor: newFloorNumber // Update wall floor reference
        }));
        floorMapping[floorNum] = newFloorNumber;
        newFloorNumber++;
      }
    });

    // Step 2: Update elements to match new floor numbering
    const updatedElements = elements
      .filter(element => (element as any).floor !== targetFloor) // Remove elements from deleted floor
      .map(element => {
        const elementFloor = (element as any).floor;
        if (elementFloor && floorMapping[elementFloor]) {
          return { ...element, floor: floorMapping[elementFloor] };
        }
        return element;
      });

    // Step 3: Update room labels to match new floor numbering
    const updatedRoomLabels: Record<string, string> = {};
    Object.entries(roomCustomLabels).forEach(([roomId, label]) => {
      // Skip room labels from the deleted floor
      if (roomId.startsWith(`floor_${targetFloor}_`)) {
        return;
      }
      
      // Update room labels to match new floor numbering
      const match = roomId.match(/^floor_(\d+)_(.+)$/);
      if (match) {
        const oldFloorNum = parseInt(match[1]);
        const roomIdentifier = match[2];
        if (floorMapping[oldFloorNum]) {
          const newRoomId = `floor_${floorMapping[oldFloorNum]}_${roomIdentifier}`;
          updatedRoomLabels[newRoomId] = label;
        }
      } else {
        updatedRoomLabels[roomId] = label;
      }
    });

    // Step 4: Update current floor if necessary
    let newCurrentFloor = currentFloor;
    if (targetFloor === currentFloor) {
      // If we deleted the current floor, switch to the first available floor
      newCurrentFloor = 1;
    } else if (floorMapping[currentFloor]) {
      // If current floor was renumbered, update to new number
      newCurrentFloor = floorMapping[currentFloor];
    }

    // Step 5: Update state
    setFloorPlans(updatedFloorPlans);
    setElements(updatedElements);
    setRoomCustomLabels(updatedRoomLabels);
    setCurrentFloor(newCurrentFloor);

    // Step 6: Update site notes data to reflect new floor numbering
    if (siteNotesData) {
      const updatedSiteNotes = { ...siteNotesData };
      
      // Update floors array to match new numbering
      const updatedFloors = updatedSiteNotes.floors
        .filter(floor => floor.id !== targetFloor) // Remove deleted floor
        .map((floor, index) => ({
          ...floor,
          id: index + 1, // Renumber sequentially starting from 1
          floorLevel: index + 1 === 1 ? 'ground floor' : `floor ${index + 1}`
        }));
      
      updatedSiteNotes.floors = updatedFloors;
      
      setSiteNotesData(updatedSiteNotes);
    }

    // Mark that floor plan has changes
    setHasFloorPlanChanges(true);
    
    console.log(`✅ Floor deletion complete:`, {
      deletedFloor: targetFloor,
      floorMapping,
      newFloors: Object.keys(updatedFloorPlans).map(Number).sort((a, b) => a - b),
      newCurrentFloor,
      elementsUpdated: updatedElements.length,
      roomLabelsUpdated: Object.keys(updatedRoomLabels).length
    });
  };

  // 🎯 Helper function to center the canvas on a specific floor's content
  const centerFloorOnSwitch = async (targetFloorNumber: number) => {
    try {
      console.log(`🎯 Auto-centering canvas for floor ${targetFloorNumber}...`);
      
      // Get the canvas container dimensions
      const getContainerDimensions = () => {
        if (canvasRef.current && canvasRef.current.parentElement) {
          const container = canvasRef.current.parentElement;
          return {
            width: container.clientWidth || 750,
            height: container.clientHeight || 600
          };
        }
        return { width: 750, height: 600 }; // Default fallback
      };
      
      const containerDims = getContainerDimensions();
      const targetFloorWalls = floorPlans[targetFloorNumber] || [];
      const targetFloorElements = elements.filter((el: any) => (el.floor === targetFloorNumber));
      const targetFloorTextBoxes = textBoxes.filter((tb: any) => (tb.floor === targetFloorNumber));
      
      console.log('🎯 Floor switch centering data:', {
        floor: targetFloorNumber,
        walls: targetFloorWalls.length,
        elements: targetFloorElements.length,
        textBoxes: targetFloorTextBoxes.length,
        containerDims
      });
      
      // Import and use the centering utility
      const { centerFloorPlanContent } = await import('../utils/viewUtils');
      const centeringResult = centerFloorPlanContent(
        targetFloorWalls,
        targetFloorElements,
        targetFloorTextBoxes,
        containerDims.width,
        containerDims.height,
        0.85 // Use 85% of container space with 15% padding
      );
      
      if (centeringResult) {
        console.log('🎯 Applying floor switch centering:', {
          floor: targetFloorNumber,
          scale: centeringResult.scale.toFixed(2),
          viewOffset: {
            x: centeringResult.viewOffset.x.toFixed(1),
            y: centeringResult.viewOffset.y.toFixed(1)
          }
        });
        
        setScale(centeringResult.scale);
        setViewOffset(centeringResult.viewOffset);
      } else {
        console.log(`🎯 No content to center on floor ${targetFloorNumber}, keeping current view`);
        // Still trigger a re-render for state synchronization
        setScale(currentScale => currentScale);
      }
    } catch (error) {
      console.warn('🎯 Failed to center floor on switch:', error);
      // Fallback: just trigger re-render for state synchronization
      setScale(currentScale => currentScale);
    }
  };

  const switchFloor = (direction: 'prev' | 'next') => {
    const floors = Object.keys(floorPlans).map(Number).sort((a, b) => a - b);
    const currentIndex = floors.indexOf(currentFloor);
    
    // Switching floors might be considered an undoable action if it changes the "document view context"
    // For now, let's assume it's not, but if it should be, call recordUndoState() here.
    // recordUndoState(); // If switching floor should be undoable itself

    // Store the current floor before switching for proper floor change detection
    previousFloorRef.current = currentFloor;

    if (direction === 'prev' && currentIndex > 0) {
      // 🔧 FIX: Ensure scale/state sync during floor transitions to prevent pill badge oversizing
      const targetFloor = floors[currentIndex - 1];
      setCurrentFloor(targetFloor);
      
      // 🎯 AUTO-CENTER: Center the canvas on the new floor's content
      setTimeout(() => {
        centerFloorOnSwitch(targetFloor);
      }, 50); // Slightly longer delay to ensure floor data is updated
      
    } else if (direction === 'next' && currentIndex < floors.length - 1) {
      // 🔧 FIX: Ensure scale/state sync during floor transitions to prevent pill badge oversizing  
      const targetFloor = floors[currentIndex + 1];
      setCurrentFloor(targetFloor);
      
      // 🎯 AUTO-CENTER: Center the canvas on the new floor's content
      setTimeout(() => {
        centerFloorOnSwitch(targetFloor);
      }, 50); // Slightly longer delay to ensure floor data is updated
    }
  };


  // New handler for modal-based exports with loading UI
  const handleModalExport = async (exportType: 'comprehensive' | 'floorplan-only', format: 'pdf' | 'png', options?: { logoDataUrl?: string; headerColor?: string; companyName?: string; companyWebsite?: string; companyPhone?: string }) => {
    console.log('🖼️ EXPORT DEBUG: handleModalExport called with options:', {
      exportType,
      format,
      hasLogo: !!options?.logoDataUrl,
      logoDataLength: options?.logoDataUrl?.length || 0,
      headerColor: options?.headerColor,
      companyName: options?.companyName
    });
    // 🔧 MULTI-FLOOR FIX: Gather rooms from ALL floors for comprehensive export
    const getAllFloorsRooms = () => {
      const allRooms: Room[] = [];
      const floorNumbers = Object.keys(floorPlans).map(Number).sort();
      
      console.log(`🏠 GATHERING ROOMS: Processing ${floorNumbers.length} floors:`, floorNumbers);
      
      for (const floorNum of floorNumbers) {
        const floorWalls = floorPlans[floorNum] || [];
        if (floorWalls.length > 0) {
          try {
            const floorRooms = calculateRooms(floorWalls, roomCustomLabels, 'explicitRoomCreation', floorNum);
            console.log(`🏠 Floor ${floorNum}: Found ${floorRooms.length} rooms:`, floorRooms.map(r => r.name));
            allRooms.push(...floorRooms);
          } catch (error) {
            console.warn(`Failed to calculate rooms for floor ${floorNum}:`, error);
          }
        }
      }
      
      console.log(`🏠 TOTAL ROOMS FOR EXPORT: ${allRooms.length} rooms from ${floorNumbers.length} floors`);
      return allRooms;
    };
    
    const roomsForExport = getAllFloorsRooms();
    const { hlpLength, pwLength, totalArea } = getDisplayMeasurements();
    const exteriorMeasurements = getExteriorMeasurements();

    if (exportType === 'comprehensive') {
      setShowExportModal(false);
      
      // Initialize export steps (🏢 ESTATE MODE: No XML export needed for estate agency PDFs)
      const steps = [
        {
          id: 'preparing',
          label: 'Preparing Export Data',
          icon: Database,
          status: 'pending' as const,
          description: 'Collecting floor plan data and calculations'
        },
        {
          id: 'pdf',
          label: 'Generating PDF Report',
          icon: FileText,
          status: 'pending' as const,
          description: 'Creating professional estate PDF with floor plans'
        }
      ];

      setExportSteps(steps);
      setShowExportLoadingModal(true);

      try {
        // Step 1: Preparing data
        setCurrentExportStep('preparing');
        setExportSteps(prev => prev.map(step => 
          step.id === 'preparing' 
            ? { ...step, status: 'in-progress' as const }
            : step
        ));

        // 🚨 DEBUG: Export data preparation analysis
        console.log('🔍 EXPORT DEBUG - ROOM LABEL DATA ANALYSIS:', {
          roomCustomLabelsCount: Object.keys(roomCustomLabels).length,
          roomLabelPositionsCount: Object.keys(roomLabelPositions).length,
          roomsForExportCount: roomsForExport.length,
          currentFloor: currentFloor,
          roomCustomLabelsKeys: Object.keys(roomCustomLabels),
          roomLabelPositionsKeys: Object.keys(roomLabelPositions),
          roomsForExportKeys: roomsForExport.map(r => r.stableId || r.id),
          sampleRoomLabelPositions: Object.entries(roomLabelPositions).slice(0, 3).map(([key, pos]) => ({
            key,
            x: pos.x?.toFixed(1),
            y: pos.y?.toFixed(1)
          }))
        });

        const exportData = {
          currentWalls: currentWalls.map(({ tempId, ...wall }) => ({
            ...wall,
            // Preserve split wall metadata
            ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
            ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
            ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
            ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
            ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
            ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector })
          })), 
          elements,
          textBoxes,
          rooms: roomsForExport, 
          roomCustomLabels,
          propertyAddress,
          currentFloor,
          hlpLength,
          pwLength,
          totalArea,
          propertyOrientation,
          siteNotesData,
          roomLabelPositions,
          exteriorMeasurements,
          allFloorPlans: floorPlans,
        };

        // 🚨 DEBUG: Verify export data structure before sending to PDF generator
        console.log('🔍 EXPORT DEBUG - FINAL EXPORT DATA STRUCTURE:', {
          hasRoomLabelPositions: !!exportData.roomLabelPositions,
          roomLabelPositionsType: typeof exportData.roomLabelPositions,
          roomLabelPositionsCount: Object.keys(exportData.roomLabelPositions || {}).length,
          exportDataKeys: Object.keys(exportData),
          firstTwoRoomLabelPositions: exportData.roomLabelPositions ? 
            Object.entries(exportData.roomLabelPositions).slice(0, 2).map(([key, pos]) => ({
              key: key.substring(0, 20) + '...',
              x: (pos as any)?.x?.toFixed(1),
              y: (pos as any)?.y?.toFixed(1)
            })) : 'undefined'
        });

        // 🚨 DEBUG: Check RIR data in export
        console.log('🎨 RIR EXPORT CHECK:', {
          hasSiteNotesData: !!exportData.siteNotesData,
          hasFormData: !!exportData.siteNotesData?.formData,
          hasRoomInRoofData: !!exportData.siteNotesData?.formData?.roomInRoofData,
          rirMeasurementsCount: exportData.siteNotesData?.formData?.roomInRoofData?.measurements?.length,
          rirDiagramImageExists: !!exportData.siteNotesData?.formData?.roomInRoofData?.diagramImage,
          rirDiagramImageLength: exportData.siteNotesData?.formData?.roomInRoofData?.diagramImage?.length,
          rirDiagramImagePreview: exportData.siteNotesData?.formData?.roomInRoofData?.diagramImage?.substring(0, 50)
        });

        // Mark preparing as completed
        await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause for UX
        setExportSteps(prev => prev.map(step => 
          step.id === 'preparing' 
            ? { ...step, status: 'completed' as const }
            : step
        ));

        // Step 2: Generate PDF
        setCurrentExportStep('pdf');
        setExportSteps(prev => prev.map(step => 
          step.id === 'pdf' 
            ? { ...step, status: 'in-progress' as const }
            : step
        ));

        // Format filename: [FloorPlan] Address
        const filename = propertyAddress 
          ? `[FloorPlan] ${propertyAddress}.pdf`
          : `[FloorPlan] Estate-${Date.now()}.pdf`;
        await exportToPDFOffline(exportData, filename, { ...options, isEstate: true });
        
        // Mark PDF as completed
        setExportSteps(prev => prev.map(step => 
          step.id === 'pdf' 
            ? { ...step, status: 'completed' as const }
            : step
        ));

        // 🏢 ESTATE MODE: No XML generation for estate agency PDFs (removed)

        // Wait a moment to show completion, then close modal
        setCurrentExportStep(undefined);
        await new Promise(resolve => setTimeout(resolve, 1500));
        setShowExportLoadingModal(false);
        
      } catch (error) {
        console.error('Export failed:', error);
        // Mark current step as error
        if (currentExportStep) {
          setExportSteps(prev => prev.map(step => 
            step.id === currentExportStep 
              ? { ...step, status: 'error' as const, description: 'Export failed - please try again' }
              : step
          ));
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
        setShowExportLoadingModal(false);
        alert('Export failed. Please try again.');
      }
    } else {
      // Floor plan only export - use simple export without loading modal
      try {
        const exportData = {
          currentWalls: currentWalls.map(({ tempId, ...wall }) => ({
            ...wall,
            // Preserve split wall metadata
            ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
            ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
            ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
            ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
            ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
            ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector })
          })), 
          elements,
          textBoxes,
          rooms: roomsForExport, 
          roomCustomLabels,
          propertyAddress,
          currentFloor,
          hlpLength,
          pwLength,
          totalArea,
          propertyOrientation,
          siteNotesData,
          roomLabelPositions,
          exteriorMeasurements,
          allFloorPlans: floorPlans,
        };
        
        // Format filename as [FloorPlan] address
        const address = propertyAddress || 'Property';
        const formattedFilename = `[FloorPlan] ${address}.pdf`;
        
        await exportToPDFOffline(exportData, formattedFilename, { ...(options || {}), isEstate: true });
      } catch (error) {
        console.error('Export failed:', error);
        alert('Export failed. Please try again.');
      }
    }
  };

  const handleDownload = async (exportType: 'image' | 'pdf' | 'comprehensive' = 'comprehensive') => {
    try {
      // 🔧 ROOM LABEL CONSISTENCY FIX: Use the same room data source as UI display
      const roomsForExport = currentRooms;
      const { hlpLength, pwLength, totalArea } = getDisplayMeasurements();
      const exteriorMeasurements = getExteriorMeasurements();
      
      // All exports now use offline PDF export
      const exportData = {
        currentWalls: currentWalls.map(({ tempId, ...wall }) => ({
          ...wall,
          // Preserve split wall metadata
          ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
          ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
          ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
          ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
          ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
          ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector })
        })), 
        elements,
        textBoxes,
        rooms: roomsForExport, 
        roomCustomLabels,
        propertyAddress,
        currentFloor,
        hlpLength,
        pwLength,
        totalArea,
        propertyOrientation,
        siteNotesData,
        roomLabelPositions,
        exteriorMeasurements,
        allFloorPlans: floorPlans,
      };

      await exportToPDFOffline(exportData, 'property-report.pdf', { isEstate: true });
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };



  const constrainViewOffset = (offset: Point, newScale: number) => {
    const containerSize = 750;
    const scaledCanvasSize = CANVAS_SIZE * newScale;
    const maxOffset = (scaledCanvasSize - containerSize) / (2 * newScale);

    return {
      x: Math.max(-maxOffset, Math.min(maxOffset, offset.x)),
      y: Math.max(-maxOffset, Math.min(maxOffset, offset.y))
    };
  };

  const handleZoomIn = () => {
    setScale(prev => {
      const newScale = Math.min(prev * SCALE_FACTOR, MAX_SCALE);
      const containerWidth = canvasRef.current?.clientWidth || 750; 
      setViewOffset(current => constrainViewOffsetUtil(current, newScale, containerWidth));
      return newScale;
    });
  };

  const handleZoomOut = () => {
    setScale(prev => {
      const newScale = Math.max(prev / SCALE_FACTOR, INITIAL_SCALE);
      const containerWidth = canvasRef.current?.clientWidth || 750;
      setViewOffset(current => constrainViewOffsetUtil(current, newScale, containerWidth));
      return newScale;
    });
  };

  const findElementAtPoint = (point: Point): FloorPlanElement | null => {
    // Filter elements to current floor first
    const elementsOnCurrentFloor = elements.filter(element => {
      const el = element as any;
      return el.floor === currentFloor || el.floor === undefined; // Include elements without floor specified
    });

    // Check each element from last to first (reverse order for proper layering)
    for (let i = elementsOnCurrentFloor.length - 1; i >= 0; i--) {
      const element = elementsOnCurrentFloor[i];
      const bounds = getElementBounds(element, undefined, scale); // Pass scale as interactionScale for touch-friendly bounds
      
      if (point.x >= bounds.minX && point.x <= bounds.maxX && 
          point.y >= bounds.minY && point.y <= bounds.maxY) {
        return element;
      }
    }
    return null;
  };

  const findRoomLabelAtPoint = useCallback((point: Point, rooms: Room[]): { id: string } | null => {
    for (const room of rooms) {
      // Use stableId for consistent room identification
      const roomKey = room.stableId || room.id;
      const labelPosition = roomLabelPositions[roomKey] || getRoomCenter(room.walls);
      const labelText = roomCustomLabels[roomKey] || room.name;
      
      // 🚀 PERFORMANCE: Use pre-calculated area instead of expensive calculateArea call
      const area = room.area || 0;
      const areaText = `${area.toFixed(2)}m²`;
      
      // Use same calculation as canvas renderer for accurate bounds
      const scaleFactor = Math.max(0.5, Math.min(2.5, 1 / scale * 2));
      const padding = Math.max(8, 10 * scaleFactor);
      const lineSpacing = Math.max(4, 5 * scaleFactor);
      
      // Calculate font sizes (same as canvas renderer)
      const roomNameFontSize = Math.max(12, 16 * scaleFactor);
      const areaFontSize = Math.max(10, 14 * scaleFactor);
      
      // Estimate text widths more accurately
      const nameWidth = labelText.length * (roomNameFontSize * 0.6); // Rough approximation
      const areaWidth = areaText.length * (areaFontSize * 0.6);
      const maxWidth = Math.max(nameWidth, areaWidth);
      
      const totalHeight = roomNameFontSize + lineSpacing + areaFontSize;
      const textBgHeight = totalHeight + (padding * 2);
      
      const bounds = {
        minX: labelPosition.x - maxWidth / 2 - padding,
        maxX: labelPosition.x + maxWidth / 2 + padding,
        minY: labelPosition.y - textBgHeight / 2,
        maxY: labelPosition.y + textBgHeight / 2
      };
      
      if (point.x >= bounds.minX && point.x <= bounds.maxX && 
          point.y >= bounds.minY && point.y <= bounds.maxY) {
        return { id: roomKey }; // Return the consistent room key
      }
    }
    return null;
  }, [roomLabelPositions, roomCustomLabels, scale]);

  const TOOLS = {
    WALL: 'wall' as ElementType,
    ROOM_RESIZE: 'roomResize' as ElementType,
    MEASURE: 'measure' as ElementType,
    ERASER: 'eraser' as ElementType,
    DOOR: 'door' as ElementType,
    WINDOW: 'window' as ElementType,
    RADIATOR: 'radiator' as ElementType,
    BOILER: 'boiler' as ElementType,
    HLP: 'hlp' as ElementType,
    PW: 'pw' as ElementType,
    SW: 'sw' as ElementType,  
    SPLIT: 'split' as ElementType,
    LIGHT: 'light' as ElementType,
    SMOKE_ALARM: 'smoke-alarm' as ElementType,
    EMERGENCY_LIGHT: 'emergency-light' as ElementType,
    FIRE_CALL_POINT: 'fire-call-point' as ElementType,
    FIRE_PANEL: 'fire-panel' as ElementType,
    HEAT_ALARM: 'heat-alarm' as ElementType,
    ALARM_SOUNDER: 'alarm-sounder' as ElementType,
    WC_CALL_POINT: 'wc-call-point' as ElementType,
    AC_UNIT: 'ac-unit' as ElementType,
    FIRE_ALARM: 'fire-alarm' as ElementType,
    ICE_MACHINE: 'ice-machine' as ElementType,
    COFFEE_MACHINE: 'coffee-machine' as ElementType,
    GAS_METER: 'gas-meter' as UtilityType,
    ELECTRIC_METER: 'electric-meter' as UtilityType,
    STOP_TAP: 'stop-tap' as UtilityType,
    TANK_CYLINDER: 'tank-cylinder' as UtilityType,
    CONSUMER_UNIT: 'consumer-unit' as UtilityType,
    FAN: 'fan' as UtilityType,
    BACKGROUND_VENT: 'background-vent' as UtilityType,
    THERMOSTAT: 'thermostat' as UtilityType,
    AIR_SOURCE_HP: 'air-source-hp' as UtilityType,
  };

  const UTILITY_TOOLS: UtilityType[] = [
    'gas-meter', 'electric-meter', 'stop-tap', 'tank-cylinder', 'consumer-unit',
    'fan', 'background-vent', 'thermostat', 'air-source-hp',
    'single-sink', 'double-sink', 'garden',
    'hob', 'bathtub', 'shower', 'toilet'
  ];

  const handleToolSelect = (tool: ElementType) => {
    if (tool === TOOLS.MEASURE) {
      setShowMeasurements(!showMeasurements);
      return;
    }

    // Special handling for window tool - show type selection prompt
    if (tool === 'window') {
      setIsWindowTypePromptOpen(true);
      return;
    }
    
    // Reset placement states when switching tools
    setIsPlacingLight(false);
    setLightPreview(null);
    setIsPlacingSmokeAlarm(false);
    setSmokeAlarmPreview(null);
    setIsPlacingFireSafety(false);
    setFireSafetyPreview(null);
    setIsPlacingUtility(false);
    setUtilityPreview(null);
    setWallForSink(null);
    setIsPlacingLoftHatch(false);
    setLoftHatchPreview(null);
    setIsPlacingCompass(false);
    setCompassPreview(null);
    setIsPlacingRadiator(false);
    setRadiatorPreview(null);
    setWallForRadiator(null);
    setIsPlacingBoiler(false);
    setBoilerPreview(null);
    setWallForBoiler(null);
    setIsPlacingDoor(false);
    setDoorPreview(null);
    setWallForDoor(null);
    setIsHighlighting(false);
    setHighlightStart(null);
    setHighlightPreview(null);
    setHighlightSnap(null);
    setIsPlacingWindow(false);
    setWindowPreview(null);
    setWallForWindow(null);
    
    setCurrentTool(tool); 
    currentToolInternalRef.current = tool;

    // console.log('🔧 handleToolSelect in FloorPlanCreator:', { // Keep this for tool changes
    //   requestedTool: tool,
    //   previousToolState: previousToolStateBeforeSet,
    //   previousToolRefBeforeSet: previousToolRefBeforeSet,
    //   currentToolStateAfterSet: tool, 
    //   currentToolRefAfterSet: currentToolInternalRef.current, 
    //   timestamp: new Date().toISOString()
    // });
    
    setIsDraggingRoom(false);
    isDraggingRoomRef.current = false;
    setRoomStart(null);
    roomStartPointRef.current = null;
    setRoomEnd(null);
    roomEndPointRef.current = null;
    
    setSelectedRoom(null); 
    setIsResizing(false);
    isResizingRef.current = false;
    setResizeHandle(null);
    setTempWalls(null);
    
    // 🔧 TOOL CLEANUP: Reset all element placement states when switching tools
    setIsPlacingLight(false);
    setLightPreview(null);
    setIsPlacingSmokeAlarm(false);
    setSmokeAlarmPreview(null);
    setIsPlacingFireSafety(false);
    setFireSafetyPreview(null);
    setIsPlacingUtility(false);
    setUtilityPreview(null);
    setWallForSink(null);
    setIsPlacingLoftHatch(false);
    setLoftHatchPreview(null);
    setIsPlacingCompass(false);
    setCompassPreview(null);
    setIsPlacingRadiator(false);
    setRadiatorPreview(null);
    setWallForRadiator(null);
    setIsPlacingBoiler(false);
    setBoilerPreview(null);
    setWallForBoiler(null);
    setIsPlacingDoor(false);
    setDoorPreview(null);
    setWallForDoor(null);
    setIsHighlighting(false);
    setHighlightStart(null);
    setHighlightPreview(null);
    setHighlightSnap(null);
    setIsPlacingWindow(false);
    setWindowPreview(null);
    setWallForWindow(null);
    
    // Conditionally reset selectedWall and splitPoint
    if (tool !== 'hlp' && tool !== 'pw' && tool !== 'sw' && tool !== 'split' && tool !== 'select') {
      setSelectedWall(null);
      setSplitPoint(null);
    }
    
    setIsDraggingCorner(false);
    activeCornerInfoRef.current = null;
    draggedWallSegmentRef.current = null; 
    setActivelyResizedWalls(null);
    setResizingRoomInfo(null); 
    setSplitPreviewDimensions(null); 
    
    setCurrentPoint(null); 
    setHoveredCorner(null); 
    
    onDebug('FPC_TOOL_CHANGE_COMPLETE', { newToolSetOnRef: currentToolInternalRef.current, newToolSetOnState: tool });
  };

  const handleWallTypeConversion = (point: Point, newType: WallType) => {
    onDebug('SMART_WALL_CONVERSION_START', {
      type: newType,
      point,
      timestamp: new Date().toISOString()
    });

    const clickedWall = currentWalls.find(wall => {
      const distanceToWall = distanceToLine(point, wall.start, wall.end); 
      return distanceToWall < SNAP_THRESHOLD;
    });

    if (!clickedWall) {
      onDebug('WALL_NOT_FOUND', {
        point,
        threshold: SNAP_THRESHOLD,
        timestamp: new Date().toISOString()
      });
      return;
    }

    // Calculate current rooms to find which room this wall belongs to
    const currentRooms = memoizedCalculateRooms(currentWalls.map(({tempId, ...w}) => w), roomCustomLabels, 'geometryUpdate', currentFloor, labelsOnlyOperation);
    
    onDebug('SMART_WALL_CONVERSION_DEBUG', {
      totalRooms: currentRooms.length,
      roomNames: currentRooms.map(r => r.name),
      clickedWallDetails: {
        tempId: clickedWall.tempId,
        start: clickedWall.start,
        end: clickedWall.end,
        type: clickedWall.type
      }
    });
    
    // Find which room this wall belongs to
    const clickedWallRoom = currentRooms.find(room => 
      room.walls.some(roomWall => {
        const tolerance = SNAP_THRESHOLD;
        const match = (Math.abs(roomWall.start.x - clickedWall.start.x) < tolerance &&
                Math.abs(roomWall.start.y - clickedWall.start.y) < tolerance &&
                Math.abs(roomWall.end.x - clickedWall.end.x) < tolerance &&
                Math.abs(roomWall.end.y - clickedWall.end.y) < tolerance) ||
               (Math.abs(roomWall.start.x - clickedWall.end.x) < tolerance &&
                Math.abs(roomWall.start.y - clickedWall.end.y) < tolerance &&
                Math.abs(roomWall.end.x - clickedWall.start.x) < tolerance &&
                Math.abs(roomWall.end.y - clickedWall.start.y) < tolerance);
        if (match) {
          onDebug('WALL_ROOM_MATCH_FOUND', {
            roomName: room.name,
            roomWall: { start: roomWall.start, end: roomWall.end },
            clickedWall: { start: clickedWall.start, end: clickedWall.end }
          });
        }
        return match;
      })
    );

    if (!clickedWallRoom) {
      onDebug('NO_ROOM_FOUND_FOR_WALL', {
        clickedWall: { start: clickedWall.start, end: clickedWall.end, type: clickedWall.type },
        availableRooms: currentRooms.map(room => ({
          name: room.name,
          wallCount: room.walls.length,
          walls: room.walls.map(w => ({ start: w.start, end: w.end }))
        })),
        tolerance: SNAP_THRESHOLD
      });
      
      // Fallback to simple single wall conversion if no room found
      const updatedWalls = currentWalls.map(wall => {
        if (wall.tempId === clickedWall.tempId) {
          console.log('🎨 WALL TYPE ASSIGNMENT: Setting wall type in simple conversion', {
            wallId: wall.tempId,
            oldType: wall.type,
            newType: newType,
            coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
            timestamp: new Date().toISOString()
          });
          return { ...wall, type: newType };
        }
        return wall;
      });
      
      console.log('🎨 WALL TYPE ASSIGNMENT: Updated walls state', {
        totalWalls: updatedWalls.length,
        updatedWall: updatedWalls.find(w => w.tempId === clickedWall.tempId),
        allWallTypes: updatedWalls.map(w => ({ id: w.tempId, type: w.type }))
      });
      
      recordUndoState();
      setFloorPlans(prev => ({
        ...prev,
        [currentFloor]: updatedWalls as any,
      }));
      
      // 🔍 IMMEDIATE VERIFICATION: Check if wall type persisted immediately after setting
      setTimeout(() => {
        console.log('🔍 WALL TYPE PERSISTENCE CHECK: Verifying wall type persisted after 100ms', {
          clickedWallId: clickedWall.tempId,
          currentWallsFromState: currentWalls.find(w => w.tempId === clickedWall.tempId),
          expectedType: newType,
          timestamp: new Date().toISOString()
        });
      }, 100);
      
      onDebug('WALL_CONVERTED_SIMPLE', {
        from: clickedWall.type,
        to: newType,
        wall: { start: clickedWall.start, end: clickedWall.end },
        reason: 'no_room_found'
      });
      return;
    }

    // Smart wall conversion: find connected walls of the same type within the room only
    const originalType = clickedWall.type;
    const wallsToConvert = new Set<string>();

    onDebug('STARTING_SMART_CONVERSION', {
      clickedWallRoom: clickedWallRoom.name,
      originalType,
      roomWallCount: clickedWallRoom.walls.length,
      totalCurrentWalls: currentWalls.length
    });

    // Simple approach: only convert walls that belong to the same room and have the same type
    // This is more conservative but more reliable
    for (const roomWall of clickedWallRoom.walls) {
      // Find the corresponding wall in currentWalls
      const matchingCurrentWall = currentWalls.find(cw => {
        const tolerance = SNAP_THRESHOLD;
        return (Math.abs(cw.start.x - roomWall.start.x) < tolerance &&
                Math.abs(cw.start.y - roomWall.start.y) < tolerance &&
                Math.abs(cw.end.x - roomWall.end.x) < tolerance &&
                Math.abs(cw.end.y - roomWall.end.y) < tolerance) ||
               (Math.abs(cw.start.x - roomWall.end.x) < tolerance &&
                Math.abs(cw.start.y - roomWall.end.y) < tolerance &&
                Math.abs(cw.end.x - roomWall.start.x) < tolerance &&
                Math.abs(cw.end.y - roomWall.start.y) < tolerance);
      });

      if (matchingCurrentWall && matchingCurrentWall.type === originalType) {
        const wallId = matchingCurrentWall.tempId || `${matchingCurrentWall.start.x}_${matchingCurrentWall.start.y}_${matchingCurrentWall.end.x}_${matchingCurrentWall.end.y}`;
        wallsToConvert.add(wallId);
        
        onDebug('WALL_ADDED_TO_CONVERSION', {
          wallId,
          start: matchingCurrentWall.start,
          end: matchingCurrentWall.end,
          type: matchingCurrentWall.type
        });
      }
    }

    if (wallsToConvert.size === 0) {
      // Fallback to converting just the clicked wall
      wallsToConvert.add(clickedWall.tempId || `${clickedWall.start.x}_${clickedWall.start.y}_${clickedWall.end.x}_${clickedWall.end.y}`);
    }

    // Apply the type conversion to all identified walls
    const updatedWalls = currentWalls.map(wall => {
      const wallId = wall.tempId || `${wall.start.x}_${wall.start.y}_${wall.end.x}_${wall.end.y}`;
      if (wallsToConvert.has(wallId)) {
        return { ...wall, type: newType };
      }
      return wall;
    });

    recordUndoState();
    setFloorPlans(prev => ({
      ...prev,
      [currentFloor]: updatedWalls as any,
    }));

    onDebug('SMART_WALL_CONVERSION_COMPLETE', {
      from: originalType,
      to: newType,
      wallsConverted: wallsToConvert.size,
      roomId: clickedWallRoom.stableId,
      roomName: clickedWallRoom.name,
      convertedWallIds: Array.from(wallsToConvert),
      totalCurrentWalls: currentWalls.length,
      totalRoomsFound: currentRooms.length,
      clickedWallId: clickedWall.tempId,
      tolerance: SNAP_THRESHOLD,
      timestamp: new Date().toISOString()
    });
  };

  // Wall splitting is now handled entirely by useFloorPlanInteractions hook
  // This function is kept for compatibility but delegates to the hook
  const handleWallSplit = (pointFromCaller: Point) => {
    console.log('🔧 FloorPlanCreator: Delegating wall split to useFloorPlanInteractions hook');
    // The actual wall splitting is handled by the hook's handleWallSplit function
    // which is called through touch/mouse event handlers
  };

  const handleTextBoxCreation = (point: Point) => {
    // console.log('Creating text box at:', point);
    
    recordUndoState(); // Record state before adding new textbox

    const newTextBox: TextBox = {
      id: `text-${Date.now()}`,
      content: '',
      position: {
        x: point.x - 100, 
        y: point.y - 50
      },
      width: 200,
      height: 100,
      fontSize: 16,
      isDragging: false,
      isResizing: false,
      isEditing: true,
      floor: currentFloor
    };
    
    // console.log('New text box:', newTextBox);
    
    setTextBoxes((prev: TextBox[]) => {
      const updated = [...prev, newTextBox];
      // console.log('Updated text boxes:', updated);
      return updated;
    });
    setSelectedTextBox(newTextBox);
  };

  const drawTextBoxes = (ctx: CanvasRenderingContext2D) => {
    textBoxes.forEach((box: TextBox) => {
      if (!box.isEditing) { 
        ctx.save();
        
        if (selectedTextBox?.id === box.id) {
          ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
          ctx.fillRect(box.position.x, box.position.y, box.width, box.height);
        }
        
        // Use zoom-responsive font size but respect the text box's relative sizing
        const responsiveFont = getZoomResponsiveTextBoxFont(scale);
        // Extract the base font size from the responsive font and apply the box's relative size
        const baseFontSize = parseInt(responsiveFont.match(/(\d+)px/)?.[1] || '12');
        const scaledFontSize = Math.round(baseFontSize * (box.fontSize / 12)); // 12 is the base text box font size
        ctx.font = `${scaledFontSize}px Inter`;
        ctx.fillStyle = '#1E293B'; 
        ctx.textBaseline = 'top';
        
        const words = box.content.split(' ');
        let line = '';
        let y = box.position.y + 10;
        const lineHeight = scaledFontSize * 1.2;
        
        words.forEach((word: string) => {
          const testLine = line + word + ' ';
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > box.width - 20) {
            ctx.fillText(line, box.position.x + 10, y);
            line = word + ' ';
            y += lineHeight;
          } else {
            line = testLine;
          }
        });
        ctx.fillText(line, box.position.x + 10, y);
        
        ctx.restore();
      }
    });
  };

  const handleSaveRoomHeight = (height: number, fullHeightImages: any[] = []) => {
    if (roomToSetHeight) {
      // Log full height images (for future use if needed)
      if (fullHeightImages.length > 0) {
        console.log('📏 [Estate_floorplan] Full height images captured for room:', {
          roomName: roomToSetHeight.name,
          imageCount: fullHeightImages.length
        });
      }
      
      // Sync the room with the custom height AFTER state update
      // Use setTimeout to ensure this runs after React has processed the state update
      setTimeout(() => {
        roomSync.syncNewRoomWithHeight(roomToSetHeight, height, currentFloor);
        
        // Show sync indicator
        setSyncedRoomName(roomToSetHeight.name);
        setShowRoomSyncIndicator(true);
      }, 0);
      
      // Reset state
      setRoomToSetHeight(null);
      setIsRoomHeightPromptOpen(false);
    }
  };

  // Memoized helper function to get all existing rooms across all floors for intelligent labeling
  const getAllExistingRoomsAcrossFloors = useMemo((): Room[] => {
    console.log('🔍 GETALLEXISTINGROOMSACROSSFLOORS: Function called', {
      isWallSplitting,
      labelsOnlyOperation: labelsOnlyOperation.current,
      floorPlanCount: Object.keys(floorPlans).length
    });
    
    // Skip expensive calculations during wall splitting
    if (isWallSplitting) {
      console.log('🔍 GETALLEXISTINGROOMSACROSSFLOORS: Skipping due to wall splitting');
      return [];
    }
    
    // 🔧 ROOM LABEL PROMPT FIX: Always calculate fresh rooms for RoomLabelPrompt to see existing labels
    // This is needed so the prompt can suggest next available room numbers (e.g., BR1, BR2, BR3)
    // Even during labelsOnlyOperation, we need this for the prompt to work correctly
    if (labelsOnlyOperation.current) {
      console.log('🔧 LABEL PROMPT: Calculating fresh rooms during label-only operation for prompt to see existing labels');
      // Continue to calculate rooms so RoomLabelPrompt can see what labels already exist
    }
    
    const allRooms: Room[] = [];
    Object.entries(floorPlans).forEach(([floorNum, walls]) => {
      // 🛡️ CRITICAL FIX: Two-pass approach to fix wall-room membership corruption
      // Pass 1: Calculate rooms from raw walls to get reference data
      const preliminaryRooms = calculateRooms(walls, roomCustomLabels, 'explicitRoomCreation', parseInt(floorNum));
      
      // Pass 2: Apply wall-room membership enforcement using calculated rooms
      let wallsForCalculation = walls;
      
      try {
        console.log(`🔍 ALLROOMS_MEMBERSHIP_CHECK floor ${floorNum}`, {
          wallCount: walls.length,
          preliminaryRoomCount: preliminaryRooms.length,
          checkingMembership: true
        });
        
        const membershipResult = enforceWallRoomMembership(walls, preliminaryRooms);
        
        console.log(`🔍 ALLROOMS_MEMBERSHIP_RESULT floor ${floorNum}`, {
          repairsApplied: membershipResult.repairsApplied,
          totalIssues: membershipResult.issues.length,
          wallCount: walls.length
        });
        
        if (membershipResult.repairsApplied > 0) {
          console.log(`🛡️ ALLROOMS_MEMBERSHIP_REPAIRS_APPLIED floor ${floorNum}`, {
            repairsApplied: membershipResult.repairsApplied,
            totalIssues: membershipResult.issues.length,
            wallCount: walls.length
          });
          wallsForCalculation = membershipResult.walls;
        }
      } catch (error) {
        console.warn(`🚨 ALLROOMS_MEMBERSHIP_ENFORCEMENT_FAILED floor ${floorNum}`, {
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
      
      // Pass 3: Recalculate rooms with repaired walls (or use preliminary if no repairs)
      const finalFloorRooms = wallsForCalculation === walls 
        ? preliminaryRooms  // No repairs, use preliminary calculation
        : calculateRooms(wallsForCalculation, roomCustomLabels, 'explicitRoomCreation', parseInt(floorNum)); // Recalculate with repaired walls
      
      allRooms.push(...finalFloorRooms);
    });
    

    
    return allRooms;
  }, [floorPlans, roomCustomLabels, isWallSplitting, currentRooms, showDiagnosticsPanel]);

  // Handle multi-room label collection
  const handleMultiRoomLabelSave = useCallback((label: string, roomStableId: string) => {
    console.log(`[Multi-Room Label] Collected label for room ${currentLabelingRoomIndex + 1}/${multiRoomCount}: "${label}"`);
    
    // Store the label
    const updatedLabels = {
      ...collectedRoomLabels,
      [roomStableId]: label
    };
    setCollectedRoomLabels(updatedLabels);
    
    // Move to next room
    const nextIndex = currentLabelingRoomIndex + 1;
    
    if (nextIndex < multiRoomCount) {
      // More rooms to label - match by AR scan order
      setCurrentLabelingRoomIndex(nextIndex);
      
      // Calculate rooms and match to multiroom identifier
      const allFloorWalls = floorPlans[currentFloor] || [];
      const rooms = calculateRooms(allFloorWalls, roomCustomLabels, 'explicitRoomCreation', currentFloor);
      
      // Find the room with multiroom_{nextIndex} identifier using wall count voting
      const targetId = `multiroom_${nextIndex}`;
      
      // Count walls from this AR scan in each calculated room
      const roomScores = rooms.map(room => {
        const wallCount = room.walls.filter((wall: any) => 
          wall.originatingRoomId === targetId || 
          wall.assignedRoomIds?.includes(targetId)
        ).length;
        return { room, score: wallCount };
      });
      
      // Find room with highest score
      const bestMatch = roomScores.reduce((best, current) => 
        current.score > best.score ? current : best
      );
      
      const nextRoom = bestMatch.score > 0 ? bestMatch.room : null;
      
      if (nextRoom) {
        console.log(`[Multi-Room Label] Moving to room ${nextIndex + 1}/${multiRoomCount} (${targetId} -> ${nextRoom.stableId})`);
        setRoomToLabel(nextRoom);
        setIsRoomLabelPromptOpen(true);
      }
    } else {
      // All rooms labeled - apply labels and finish
      console.log('[Multi-Room Label] All rooms labeled - applying labels:', updatedLabels);
      
      setRoomCustomLabels(prev => ({
        ...prev,
        ...updatedLabels
      }));
      
      // Close the label collection flow
      setIsCollectingMultiRoomLabels(false);
      setIsRoomLabelPromptOpen(false);
      setRoomToLabel(null);
      setCurrentLabelingRoomIndex(0);
      setMultiRoomCount(0);
      setCollectedRoomLabels({});
      
      // Now do the auto-zoom that was skipped earlier
      const allFloorWalls = floorPlans[currentFloor] || [];
      if (allFloorWalls.length > 0) {
        const xCoords = allFloorWalls.flatMap(wall => [wall.start.x, wall.end.x]);
        const yCoords = allFloorWalls.flatMap(wall => [wall.start.y, wall.end.y]);
        
        const minX = Math.min(...xCoords);
        const maxX = Math.max(...xCoords);
        const minY = Math.min(...yCoords);
        const maxY = Math.max(...yCoords);
        
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        
        const containerWidth = canvasRef.current?.clientWidth || 750;
        const containerHeight = canvasRef.current?.clientHeight || 600;
        
        const roomWidth = maxX - minX;
        const roomHeight = maxY - minY;
        const padding = 100;
        
        const scaleX = (containerWidth - padding) / roomWidth;
        const scaleY = (containerHeight - padding) / roomHeight;
        const newScale = Math.min(scaleX, scaleY, 2);
        
        if (Math.abs(newScale - scale) > 0.1) {
          setScale(newScale);
        }
        
        const newViewOffset = {
          x: centerX - containerWidth / (2 * (newScale || scale)),
          y: centerY - containerHeight / (2 * (newScale || scale))
        };
        
        setViewOffset(newViewOffset);
        
        console.log('[Multi-Room Label] ✅ All labels applied and view centered');
      }
      
      console.log('[Multi-Room Label] ✅ Label collection complete - user on canvas');
    }
  }, [currentLabelingRoomIndex, multiRoomCount, collectedRoomLabels, floorPlans, currentFloor, roomCustomLabels, scale]);

  const handleSaveRoomLabel = (temporaryRoomId: string, label: string, wallImages?: any[]) => {
    if (roomToLabel && roomToLabel.stableId) {
      console.log('🏷️ DEBUG: Saving room label:', { roomId: roomToLabel.stableId, label, wallImagesCount: wallImages?.length || 0 });
      console.log('🏷️ DEBUG: Room details being labeled:', {
        id: roomToLabel.id,
        stableId: roomToLabel.stableId,
        name: roomToLabel.name,
        wallsCount: roomToLabel.walls?.length,
        center: roomToLabel.center,
        area: roomToLabel.area
      });
      
      // Store wall images if provided
      if (wallImages && wallImages.length > 0) {
        setRoomWallImages(prev => ({
          ...prev,
          [label]: wallImages
        }));
        console.log('🏷️ DEBUG: Stored wall images for room:', { label, imageCount: wallImages.length });
      }
      
      // 🔧 RACE CONDITION FIX: Create the updated labels immediately
      const updatedLabels = { ...roomCustomLabels };
      
      // 🔧 MULTI-FLOOR FIX: Use floor-aware keys to prevent cross-floor label collisions
      // Primary key: floor-aware stableId (prevents cross-floor collisions)
      const floorAwareStableId = `${currentFloor}_${roomToLabel.stableId!}`;
      updatedLabels[floorAwareStableId] = label;
      console.log('🏷️ DEBUG: Key used for labeling (floor-aware stableId):', floorAwareStableId);
      
      // Legacy support: Also save with plain stableId for backward compatibility (but geometric key takes priority)
      // This is only for within-floor matching
      updatedLabels[roomToLabel.stableId!] = label;
      console.log('🏷️ DEBUG: Key used for labeling (legacy stableId):', roomToLabel.stableId);
      
      // Backup key: id (fallback for stableId mismatches)
      if (roomToLabel.id && roomToLabel.id !== roomToLabel.stableId) {
        const floorAwareId = `${currentFloor}_${roomToLabel.id}`;
        updatedLabels[floorAwareId] = label;
        console.log('🏷️ DEBUG: Key used for labeling (floor-aware id backup):', floorAwareId);
      }
      
      // 🔧 GEOMETRIC SIGNATURE: Create a simplified geometric signature for ultra-reliable matching
      const walls = roomToLabel.walls || [];
      if (walls.length > 0 && roomToLabel.center && roomToLabel.area) {
        // Create a simplified signature based on room center, area, AND floor number to prevent cross-floor collisions
        const center = roomToLabel.center;
        const area = roomToLabel.area || 0;
        const geometricKey = `geo_${currentFloor}_${Math.round(center.x)}_${Math.round(center.y)}_${area.toFixed(1)}`;
        updatedLabels[geometricKey] = label;
        console.log('🏷️ DEBUG: Key used for labeling (geometric):', geometricKey);
      }
      
      console.log('🏷️ DEBUG: Room labels after update:', updatedLabels);
      
      // 🔧 ROOM LABEL POSITION FIX: Initialize label position at room center
      if (roomToLabel.center && roomToLabel.stableId) {
        setRoomLabelPositions(prev => ({
          ...prev,
          [roomToLabel.stableId!]: roomToLabel.center!
        }));
        console.log('🏷️ DEBUG: Initialized label position at room center:', {
          roomId: roomToLabel.stableId,
          center: roomToLabel.center
        });
      }
      
      // 🔧 RACE CONDITION FIX: Extend the label-only operation flag duration to prevent
      // premature room recalculations while React processes the state update
      labelsOnlyOperation.current = true;
      
      // PERFORMANCE OPTIMIZATION: Use direct update with optimized logging
      recordUndoState();
      setRoomCustomLabels(updatedLabels);
      
      // 🔧 FIX: Force cache invalidation to ensure immediate UI update
      invalidateRoomCalculationCache('room label saved');
      
      // Force canvas re-render to show the newly labeled room immediately
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          console.log('🔧 FIX: Forcing canvas re-render after room label save');
          // Trigger a canvas redraw by updating a dependency
          setScale(prevScale => prevScale); // This will trigger a re-render
        }
      }, 0);
      
      // Remove from prompted rooms since it now has a label
      setPromptedRoomIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(roomToLabel.stableId!);
        return newSet;
      });
      
      // 🏢 ESTATE/ENERGY FLOORPLAN: Skip height and wall images - directly save label
      // For estate agency floorplans, we don't need room heights, wall images, or site notes syncing
      const updatedRoom = { ...roomToLabel, name: label };
      
      // Show success indicator without syncing to site notes (estate floorplans don't use site notes)
      setSyncedRoomName(updatedRoom.name);
      setShowRoomSyncIndicator(true);
      
      // 🔧 FIX: Close modal and reset state BEFORE attempting AR positioning
      // This ensures the modal always closes, even if positioning fails
      setIsRoomLabelPromptOpen(false);
      
      // Check if we're in multi-room label collection mode
      if (isCollectingMultiRoomLabels && roomToLabel?.stableId) {
        handleMultiRoomLabelSave(label, roomToLabel.stableId);
      } else {
        setRoomToLabel(null);
      }
      
      // NEW WORKFLOW: After labeling, activate room positioning mode (only for AR scans)
      const roomWallIds = updatedRoom.walls.map(w => w.id).filter(Boolean) as string[];
      console.log('[Room Positioning] 🔍 DEBUG: Extracting wall IDs from room:', {
        roomId: roomToLabel.stableId,
        totalWalls: updatedRoom.walls.length,
        wallsWithIds: updatedRoom.walls.filter(w => w.id).length,
        wallIds: roomWallIds,
        wallIdsCount: roomWallIds.length,
        sampleWall: updatedRoom.walls[0] ? { id: updatedRoom.walls[0].id, hasId: !!updatedRoom.walls[0].id } : null
      });
      
      if (roomWallIds.length === 0) {
        console.log('[Room Positioning] ⏭️ Skipping positioning mode - no wall IDs (normal room creation, not AR scan)');
        
        // 🔧 CRITICAL FIX: Reset labelsOnlyOperation flag BEFORE early return
        // Otherwise the flag stays true permanently, preventing subsequent room labels from displaying
        setTimeout(() => {
          labelsOnlyOperation.current = false;
          console.log('🏷️ DEBUG: Label-only operation flag reset - normal room calculations resumed');
        }, 1500); // Extended to 1500ms to cover all debounced room calculations
        
        return; // Exit early - this is normal for non-AR rooms
      }
      
      // ✅ Wall IDs exist - activate positioning mode
      console.log('[Room Positioning] ✅ Activating positioning mode for room:', roomToLabel.stableId, {
        wallIds: roomWallIds
      });
      
      // 🔧 RACE CONDITION FIX: Extend the label preservation duration to ensure 
      // React has time to process the state update and all debounced effects complete
      setTimeout(() => {
        labelsOnlyOperation.current = false;
        console.log('🏷️ DEBUG: Label-only operation flag reset - normal room calculations resumed');
        
        // 🚫 AUTO-MERGE DISABLED: Automatic room merging has been disabled to preserve room geometry
        // Users can manually merge rooms if desired through a dedicated merge tool (to be implemented)
        console.log('🚫 AUTO-MERGE: Disabled - rooms with same label will remain separate');
        
        /* DISABLED AUTO-MERGE CODE - Can be re-enabled if manual merge tool is added
        setTimeout(() => {
          const mergeResult = checkForRoomMerge(
            label,
            roomToLabel.stableId!,
            currentWalls,
            updatedLabels,
            currentFloor
          );
          
          if (mergeResult.shouldMerge) {
            console.log('🔗 ROOM MERGE:', getMergeDescription(mergeResult));
            // ... merge logic ...
          }
        }, 100);
        */
      }, 1500); // Extended to 1500ms to cover all debounced room calculations (auto-sync: 500ms, floor calc: 1000ms)
    } else {
      console.warn("Could not save room label: stableId missing on roomToLabel", roomToLabel);
      // Close modal even if stableId is missing
      setIsRoomLabelPromptOpen(false);
      setRoomToLabel(null);
    }
  };

  // CRITICAL: Force tool state sync when entering positioning mode
  useEffect(() => {
    if (isPositioningRoom && currentTool !== 'roomResize') {
      console.log('[Room Positioning] 🔧 FORCE SYNC: Correcting tool state mismatch', {
        currentTool,
        expectedTool: 'roomResize',
        isPositioningRoom
      });
      setCurrentTool('roomResize');
      currentToolInternalRef.current = 'roomResize';
    }
  }, [isPositioningRoom]); // Only depend on isPositioningRoom to trigger when entering mode

  // Room rename handlers
  const handleRoomRename = (roomId: string, newLabel: string) => {
    console.log('🔄 DEBUG: Renaming room:', { roomId, newLabel });
    
    // 🔧 EXTRUSION FIX: Safety check to ensure room ID exists
    if (!roomId || roomId.trim() === '') {
      console.error('🔧 EXTRUSION FIX: Cannot rename room - invalid room ID:', roomId);
      return;
    }
    
    // Get the old room label before renaming
    const oldLabel = roomCustomLabels[roomId];
    console.log('🔄 DEBUG: Old label:', oldLabel, 'New label:', newLabel);
    
    // 🔧 LABEL PRESERVATION FIX: Flag that this is a label-only operation
    labelsOnlyOperation.current = true;
    
    recordUndoState();
    setRoomCustomLabels(prev => {
      console.log('🔄 DEBUG: Room labels before rename:', prev);
      const newLabels = { ...prev, [roomId]: newLabel };
      console.log('🔄 DEBUG: Room labels after rename:', newLabels);
      return newLabels;
    });
    
    // Update the selected room if it matches
    if (selectedRoom && selectedRoom.stableId === roomId) {
      const updatedRoom = { ...selectedRoom, name: newLabel };
      setSelectedRoom(updatedRoom);
    }
    
    // 📝 UPDATE SITE NOTES: Rename room in siteNotesData floors structure
    if (oldLabel && oldLabel !== newLabel) {
      console.log('📝 Updating site notes data: renaming room from', oldLabel, 'to', newLabel);
      setSiteNotesData(prev => {
        const updatedFloors = prev.floors.map(floor => ({
          ...floor,
          rooms: floor.rooms.map(room => {
            // Match by room name and update it
            if (room.name === oldLabel) {
              console.log(`📝 Found room "${oldLabel}" on floor ${floor.id}, renaming to "${newLabel}"`);
              
              // Update room name and all associated elements
              const updatedRoom = {
                ...room,
                name: newLabel,
                // Update radiators room names
                radiators: room.radiators?.map(rad => ({
                  ...rad,
                  roomName: newLabel
                })) || [],
                // Update windows room names  
                windows: room.windows?.map(win => ({
                  ...win,
                  roomName: newLabel
                })) || []
              };
              
              console.log(`📝 Updated room radiators (${updatedRoom.radiators.length}) and windows (${updatedRoom.windows.length})`);
              return updatedRoom;
            }
            return room;
          })
        }));
        
        return {
          ...prev,
          floors: updatedFloors
        };
      });
      
      setHasSiteNotesChanges(true);
    }
    
    // 💾 TRIGGER AUTOSAVE: Mark floor plan as changed and trigger autosave
    setHasFloorPlanChanges(true);
    autosave.markAsChanged();
    console.log('💾 Room rename autosave triggered');
    
    // Close the rename modal
    setIsRoomRenameModalOpen(false);
    setRoomToRename(null);
    
    // 🔧 LABEL PRESERVATION FIX: Reset the flag after a short delay
    setTimeout(() => {
      labelsOnlyOperation.current = false;
      
      // 🚫 AUTO-MERGE DISABLED: Automatic room merging has been disabled to preserve room geometry
      // Users can manually merge rooms if desired through a dedicated merge tool (to be implemented)
      console.log('🚫 AUTO-MERGE: Disabled - rooms with same label will remain separate');
      
      /* DISABLED AUTO-MERGE CODE - Can be re-enabled if manual merge tool is added
      setTimeout(() => {
        const updatedLabels = { ...roomCustomLabels, [roomId]: newLabel };
        const mergeResult = checkForRoomMerge(
          newLabel,
          roomId,
          currentWalls,
          updatedLabels,
          currentFloor
        );
        
        if (mergeResult.shouldMerge) {
          console.log('🔗 ROOM MERGE:', getMergeDescription(mergeResult));
          // ... merge logic ...
        }
      }, 100);
      */
    }, 100);
  };

  const handleOpenRoomContextMenu = (room: Room, x: number, y: number) => {
    setContextMenuRoom(room);
    setContextMenuPosition({ x, y });
    setIsRoomContextMenuOpen(true);
  };

  const handleCloseRoomContextMenu = () => {
    setIsRoomContextMenuOpen(false);
    setContextMenuRoom(null);
  };

  const handleInitiateRoomRename = (room: Room) => {
    setRoomToRename(room);
    setIsRoomRenameModalOpen(true);
  };

  const handleInitiateRoomLabel = (room: Room) => {
    setRoomToLabel(room);
    setIsRoomLabelPromptOpen(true);
  };

  const handleCloseRoomRenameModal = () => {
    setIsRoomRenameModalOpen(false);
    setRoomToRename(null);
  };

  const handleRoomSelect = (room: Room) => {
    setSelectedRoom(room);
    console.log('🚪 ROOM SELECTED:', {
      roomId: room.stableId,
      roomName: room.name
    });
    // Show floating actions on touch devices when a room is selected
    if (hasTouchScreen) {
      setShowFloatingActions(true);
    }
  };



  const handleCloseFloatingActions = () => {
    setShowFloatingActions(false);
    setSelectedRoom(null);
  };

  // Touch-specific room interaction handlers
  const handleTouchRoomInteraction = (room: Room, touchPosition: Point, touchType: 'start' | 'end') => {
    const now = Date.now();
    
    if (touchType === 'start') {
      // Start tracking touch interaction
      setTouchRoomInteraction({
        room,
        startTime: now,
        position: touchPosition,
        isLongPress: false
      });
      
      // 🔧 NEW: For select tool, enable immediate room dragging
      if (currentTool === 'select') {
        setSelectedRoom(room);
        setIsDraggingRoom(true);
        setRoomStart(touchPosition);
        console.log('🚪 ROOM DRAG ENABLED ON TOUCH START:', {
          roomId: room.stableId,
          roomName: room.name,
          touchPosition
        });
      }
      
      // Set timer for long press detection
      setTimeout(() => {
        setTouchRoomInteraction(prev => {
          if (prev && prev.room === room && prev.startTime === now) {
            // This is a long press
            return { ...prev, isLongPress: true };
          }
          return prev;
        });
      }, 500); // 500ms for long press detection
    } else if (touchType === 'end' && touchRoomInteraction) {
      const touchDuration = now - touchRoomInteraction.startTime;
      const isQuickTap = touchDuration < 500;
      const isDoubleTap = (now - lastTouchEndTime) < 300 && lastTouchedRoom === room;
      
      // Handle labelRoom tool specifically
              if (currentTool === 'labelRoom' && isQuickTap && !touchRoomInteraction.isLongPress) {
        // For labelRoom tool, always open labeling prompt on quick tap
        console.log('🏷️ LABEL_ROOM_TOUCH_DEBUG:', {
          roomId: room.stableId,
          roomName: room.name,
          isQuickTap,
          touchDuration,
          isLongPress: touchRoomInteraction.isLongPress,
          currentTool,
          aboutToOpenPrompt: true
        });
        setRoomToLabel(room);
        setIsRoomLabelPromptOpen(true);
        
        // 🔧 FIX: Don't add to promptedRoomIds for manual labelRoom tool touches
        // promptedRoomIds should only prevent automatic prompts, not manual user interactions
        setTouchRoomInteraction(null);
        return;
      }
      
      // Handle select tool for unlabeled rooms
      if (currentTool === 'select' && isQuickTap && !touchRoomInteraction.isLongPress) {
        // Check if room is unlabeled or has default label
        const hasCustomLabel = room.stableId && roomCustomLabels[room.stableId] && 
                              roomCustomLabels[room.stableId].trim() !== '' && 
                              !roomCustomLabels[room.stableId].startsWith('Room ');
        
        // 🔧 FIX: Allow select tool to work with labeled rooms for selection and movement
        // Match the mouse behavior for consistency
        if (hasCustomLabel) {
          // Room was already selected and drag enabled on touch start
          // Just clear the touch interaction state
          setTouchRoomInteraction(null);
          return;
        } else {
          // For unlabeled rooms, open the labeling prompt
          if (!isRoomLabelPromptOpen || roomToLabel?.stableId !== room.stableId) {
            // Open labeling prompt for unlabeled rooms
            setRoomToLabel(room);
            setIsRoomLabelPromptOpen(true);
            
            // 🔧 FIX: Don't add to promptedRoomIds for manual select tool touches on unlabeled rooms
            // promptedRoomIds should only prevent automatic prompts, not manual user interactions
            setTouchRoomInteraction(null);
            return;
          }
        }
      }
      
      if (touchRoomInteraction.isLongPress) {
        // Long press detected - show context menu
        const canvasRect = canvasRef.current?.getBoundingClientRect();
        if (canvasRect) {
          const screenX = canvasRect.left + (touchPosition.x - viewOffset.x) * scale;
          const screenY = canvasRect.top + (touchPosition.y - viewOffset.y) * scale;
          handleOpenRoomContextMenu(room, screenX, screenY);
        }
      } else if (isDoubleTap) {
        // Double tap detected - directly open rename modal
        handleInitiateRoomRename(room);
      } else if (isQuickTap) {
        // Single tap - select room (default behavior)
        handleRoomSelect(room);
      }
      
      // Update last touch info for double tap detection
      setLastTouchEndTime(now);
      setLastTouchedRoom(room);
      setTouchRoomInteraction(null);
    }
  };

  const getDisplayMeasurements = () => {
    // Skip expensive calculations during split tool hover to improve performance
    if (currentTool === 'split' && !selectedWall && !splitPoint) {
      return { hlpLength: 0, pwLength: 0, totalArea: 0 };
    }
    
    // 🔧 NEW: Pass creation context to area calculation
    const isEndpointCreation = lastOperationTypeRef.current === 'explicitRoomCreation';
    const creationContext = isEndpointCreation ? { isFromEndpoint: true } : undefined;
    
    // 🚨 CRITICAL AREA DISPLAY BUG FIX: Use complete floor plan walls during resize for display calculations
    // During resize drag preview, currentWalls may only contain ~4 walls from tempWalls (resizing room)
    // but area display calculations need ALL 8+ floor plan walls for accurate area display in panels
    // Without this fix, area display panels show incorrect summed areas during resize
    const isCurrentlyResizing = isResizing || justResizedRef.current || resizingRoomInfo !== null;
    const wallsForAreaCalculation = isCurrentlyResizing 
      ? (floorPlans[currentFloor] || currentWalls) // Use complete floor plan during resize
      : currentWalls; // Use current/temp walls for normal operations
    
    const { hlpLength, pwLength, totalArea } = calculateOverallWallLengthsAndArea(
      wallsForAreaCalculation, 
      roomCustomLabels, 
      lastOperationTypeRef.current, 
      currentFloor,
      creationContext
    );
    
    return { 
      hlpLength,
      pwLength,
      totalArea
    };
  };

  // New function to get exterior measurements for export
  const getExteriorMeasurements = () => {
    // Use the enhanced perimeter detection from canvasRenderer
    const exteriorWalls = currentWalls.filter(wall => isWallExternal(wall, currentWalls));
    
    console.log('🔍 [DEBUG] Strict perimeter-based exterior walls detection:', {
      totalWalls: currentWalls.length,
      exteriorWalls: exteriorWalls.length,
      exteriorWallsDetails: exteriorWalls.map(w => ({
        type: w.type,
        start: w.start,
        end: w.end,
        lengthPixels: Math.hypot(w.end.x - w.start.x, w.end.y - w.start.y),
        lengthMeters: calculateWallLength(w.start, w.end)
      }))
    });
    
    const exteriorMeasurements = exteriorWalls.map(wall => {
      // calculateWallLength already converts to meters, so don't divide again!
      const length = calculateWallLength(wall.start, wall.end);
      const wallCenter = {
        x: (wall.start.x + wall.end.x) / 2,
        y: (wall.start.y + wall.end.y) / 2
      };
      
      // Determine wall orientation for better measurement placement
      const isHorizontal = Math.abs(wall.end.x - wall.start.x) > Math.abs(wall.end.y - wall.start.y);
      
      return {
        id: `ext-${wall.start.x}-${wall.start.y}-${wall.end.x}-${wall.end.y}`,
        wall,
        length: length,
        measurement: `${length.toFixed(2)}m`,
        position: wallCenter,
        isHorizontal,
        start: wall.start,
        end: wall.end
      };
    });

    const totalExteriorLength = exteriorMeasurements.reduce((sum, measurement) => sum + measurement.length, 0);

    console.log('🔍 [DEBUG] Exterior measurements:', {
      measurementCount: exteriorMeasurements.length,
      totalLength: totalExteriorLength,
      measurements: exteriorMeasurements
    });

    return {
      measurements: exteriorMeasurements,
      totalLength: totalExteriorLength
    };
  };

  // Custom rounding function that handles floating point precision properly
  const customRound = (value: number, decimals: number = 2): string => {
    // Handle floating point precision issues by using parseFloat to clean up the number
    const cleanValue = parseFloat(value.toPrecision(12)); // Use 12 significant digits
    const factor = Math.pow(10, decimals);
    const rounded = Math.round(cleanValue * factor) / factor;
    
    return rounded.toFixed(decimals);
  };

  // Enhance the findRoomAfterResize function with better fallback logic:
  const findRoomAfterResize = (
    originalId: string | undefined,
    newIdFromMap: string | undefined,
    newRooms: Record<string, Room>,
    originalSelectedRoomState: Room | null,
    onDebug?: (event: string, data: any) => void
  ): Room | null => {
    onDebug?.('FINDING_ROOM_AFTER_RESIZE', { originalId, newIdFromMap, availableRoomIds: Object.keys(newRooms), roomCount: Object.keys(newRooms).length, originalSelectedRoom: originalSelectedRoomState });

    if (!originalId) return null;

    if (newIdFromMap && newRooms[newIdFromMap]) {
      onDebug?.('FOUND_BY_MIGRATION_MAP', { originalId, newId: newIdFromMap, roomName: newRooms[newIdFromMap]?.name, originalLabel: roomCustomLabels[originalId] });
      return newRooms[newIdFromMap];
    }

    const availableRoomObjects = Object.values(newRooms);
    if (availableRoomObjects.length === 0) { // <--- GUARD based on objects
      onDebug?.('FINDING_ROOM_AFTER_RESIZE_ERROR', { message: "No available rooms to search in after resize.", originalId, newIdFromMap });
      return null; 
    }

    if (availableRoomObjects.length === 1) {
      const singleRoom = availableRoomObjects[0];
      
      // CRITICAL FIX: Only apply single room special case if we're confident this is the right room
      // Check if the single room's geometry is similar to the original room being resized
      // This prevents the bug where resizing the second room causes the first room to take its ID
      if (originalSelectedRoomState && originalSelectedRoomState.walls.length > 0) {
        const originalCenter = getRoomCenter(originalSelectedRoomState.walls);
        const newCenter = getRoomCenter(singleRoom.walls);
        const centerDistance = Math.hypot(newCenter.x - originalCenter.x, newCenter.y - originalCenter.y);
        
        // If the centers are very far apart, this is likely a different room that absorbed the resized room
        // In this case, don't apply the label migration to avoid merging rooms incorrectly
        const maxAcceptableDistance = 100; // pixels - adjust based on typical room sizes
        if (centerDistance > maxAcceptableDistance) {
          onDebug?.('SINGLE_ROOM_SPECIAL_CASE_REJECTED_GEOMETRY', { 
            singleRoomId: singleRoom.stableId,
            originalRoomId: originalId,
            centerDistance,
            maxAcceptableDistance,
            message: "Single room too far from original - likely a different room absorbed the resized room"
          });
          return null; // Don't select any room to avoid incorrect merging
        }
      }
      
      onDebug?.('FOUND_BY_SINGLE_ROOM_SPECIAL_CASE', { 
        roomId: singleRoom.stableId, 
        roomName: singleRoom.name,
        originalLabel: roomCustomLabels[originalId]
      });
      
      // Preserve the original label only if geometry check passed
      if (roomCustomLabels[originalId] && singleRoom.stableId) {
        // PERFORMANCE OPTIMIZATION: Use throttled batch update instead of direct setRoomCustomLabels
        batchRoomLabelUpdates([{roomId: singleRoom.stableId, label: roomCustomLabels[originalId]}]);
      }
      
      return singleRoom;
    }

    // Try new ID first
    if (newIdFromMap) {
      const roomByNewId = availableRoomObjects.find((r: Room) => r.stableId === newIdFromMap);
      if (roomByNewId) {
        onDebug?.('FOUND_BY_NEW_ID', { roomId: newIdFromMap, roomName: roomByNewId.name });
        return roomByNewId;
      }
    }

    // Try original ID
    const roomByOriginalId = availableRoomObjects.find((r: Room) => r.stableId === originalId);
    if (roomByOriginalId) {
      onDebug?.('FOUND_BY_ORIGINAL_ID', { roomId: originalId, roomName: roomByOriginalId.name });
      return roomByOriginalId;
    }

    // Enhanced fallback: try to find by geometric and label similarity
    if (originalSelectedRoomState && originalSelectedRoomState.walls.length > 0) {
      const originalLabel = roomCustomLabels[originalId] || originalSelectedRoomState.name;
      
      // First try: find room with same label
      const roomWithSameLabel = availableRoomObjects.find((r: Room) => {
        const roomLabel = roomCustomLabels[r.stableId || ''] || r.name;
        return roomLabel === originalLabel && r.stableId !== originalId;
      });
      
      if (roomWithSameLabel) {
        onDebug?.('FOUND_BY_LABEL_MATCH', { 
          roomId: roomWithSameLabel.stableId, 
          roomName: roomWithSameLabel.name,
          matchedLabel: originalLabel
        });
        return roomWithSameLabel;
      }
      
      // Second try: find by geometric similarity
      const similarRoom = availableRoomObjects.find((r: Room) => {
        // Check if wall count is close
        const wallCountDiff = Math.abs(r.walls.length - originalSelectedRoomState.walls.length);
        if (wallCountDiff > 2) return false; // Allow some difference due to splitting
        
        // Check if area is similar (within 10%)
        const areaDiff = Math.abs(r.area - originalSelectedRoomState.area) / originalSelectedRoomState.area;
        if (areaDiff > 0.1) return false;
        
        // Check if center is close
        const originalCenter = getRoomCenter(originalSelectedRoomState.walls);
        const newCenter = getRoomCenter(r.walls);
        const centerDistance = Math.hypot(newCenter.x - originalCenter.x, newCenter.y - originalCenter.y);
        const maxDistance = Math.max(
          Math.abs(originalSelectedRoomState.walls[0]?.start.x - originalSelectedRoomState.walls[0]?.end.x) || 100,
          Math.abs(originalSelectedRoomState.walls[0]?.start.y - originalSelectedRoomState.walls[0]?.end.y) || 100
        ) * 0.5;
        
        return centerDistance < maxDistance;
      });
      
      if (similarRoom) {
        onDebug?.('FOUND_BY_GEOMETRY_FALLBACK', { 
          roomId: similarRoom.stableId, 
          roomName: similarRoom.name,
          areaOriginal: originalSelectedRoomState.area,
          areaFound: similarRoom.area,
          wallCountOriginal: originalSelectedRoomState.walls.length,
          wallCountFound: similarRoom.walls.length
        });
        return similarRoom;
      }
      
      // Last resort: find the largest room if we had a selected room before
      const largestRoom = availableRoomObjects.reduce((largest: Room, current: Room) => 
        current.area > largest.area ? current : largest
      );
      
      if (largestRoom && largestRoom.area > 1) { // At least 1 square meter
        onDebug?.('FOUND_BY_LARGEST_ROOM_FALLBACK', { 
          roomId: largestRoom.stableId, 
          roomName: largestRoom.name,
          area: largestRoom.area
        });
        return largestRoom;
      }
    }

    onDebug?.('NO_ROOM_FOUND_AFTER_RESIZE', { originalId, newIdFromMap, totalRoomsAvailable: availableRoomObjects.length });
    return null;
  };

  const handleSaveWindowDimensions = (dimensions: { width: number; height: number; trickleVents: number; isDraughtProofed: boolean; windowType?: 'pvc' | 'wood' | 'metal'; orientation?: string; glazingGap?: string; glazingType?: string; windowCategory?: string; condition?: 'good' | 'average' | 'poor' }) => {
    if (!windowToEdit) return;

    console.log('🪟 DEBUG: handleSaveWindowDimensions called with:', {
      dimensions,
      windowToEdit: windowToEdit.id,
      glazingGap: dimensions.glazingGap,
      glazingType: dimensions.glazingType,
      windowCategory: dimensions.windowCategory
    });

    // Create the updated elements array with the new window dimensions
    const updatedElements = elements.map(el => {
      if ((el as any).id === windowToEdit.id) {
        // If manual orientation is provided, use it; otherwise keep existing orientation
        const updatedElement = { ...el, ...dimensions };
        if (dimensions.orientation) {
          updatedElement.orientation = dimensions.orientation;
          console.log(`🧭 Manual orientation set for window: ${dimensions.orientation}`);
        }
        console.log('🪟 DEBUG: Updated window element:', updatedElement);
        return updatedElement;
      }
      return el;
    });

    // Update state and sync immediately
    setElements(updatedElements);

    // Sync immediately using current state values and the updated elements
    const roomContainingWindow = findRoomAtPoint(windowToEdit.position, currentRooms);
    
    console.log('🪟 DEBUG Window sync check (immediate):', {
      roomContainingWindow: roomContainingWindow?.name || 'NO_ROOM',
      hasRoomSync: !!roomSync,
      windowPosition: windowToEdit.position,
      totalRooms: currentRooms.length,
      currentFloor: currentFloor,
      dimensions: dimensions
    });
    
    if (roomContainingWindow && roomContainingWindow.name && roomSync) {
      // Update window dimensions for this specific room
      console.log('🪟 DEBUG: About to call roomSync.updateWindowDimensions with:', {
        roomName: roomContainingWindow.name,
        width: dimensions.width,
        height: dimensions.height,
        trickleVents: dimensions.trickleVents,
        currentFloor,
        orientation: dimensions.orientation,    // ✅ FIX: Use dimensions.orientation
        isDraughtProofed: dimensions.isDraughtProofed,
        windowType: dimensions.windowType,
        glazingGap: dimensions.glazingGap,
        glazingType: dimensions.glazingType,
        windowCategory: dimensions.windowCategory
      });
      
      roomSync.updateWindowDimensions(
        roomContainingWindow.name, 
        dimensions.width, 
        dimensions.height, 
        dimensions.trickleVents || false,
        currentFloor,
        dimensions.orientation,
        dimensions.isDraughtProofed,
        dimensions.windowType,
        dimensions.glazingGap,
        dimensions.glazingType,
        dimensions.windowCategory,
        dimensions.condition
      );
      
      console.log('🪟 DEBUG: roomSync.updateWindowDimensions called - siteNotesData state updated automatically');
      
      // Force a state refresh to ensure SurveyorSiteNotes gets the updated data
      setTimeout(() => {
        setSiteNotesData(prevData => ({ ...prevData }));
        console.log('🪟 DEBUG: Forced state refresh for SurveyorSiteNotes sync');
      }, 50);
      
      // Also sync overall window data (count and trickle vents) when dimensions are saved
      // Use the updated elements array we just created
      const allWindows = updatedElements.filter(el => el.type === 'window') as unknown as WindowElement[];
      
      console.log('🪟 DEBUG: Elements before smartSyncWindows call:', {
        allWindowsCount: allWindows.length,
        updatedWindow: allWindows.find(w => (w as any).id === windowToEdit.id),
        glazingDataCheck: {
          glazingGap: (allWindows.find(w => (w as any).id === windowToEdit.id) as any)?.glazingGap,
          glazingType: (allWindows.find(w => (w as any).id === windowToEdit.id) as any)?.glazingType,
          windowCategory: (allWindows.find(w => (w as any).id === windowToEdit.id) as any)?.windowCategory
        }
      });
      
      // Skip smartSyncWindows for this specific window to prevent overwriting our manual glazing data
      // Only sync windows other than the one we just manually updated
      const otherWindows = allWindows.filter(w => (w as any).id !== windowToEdit.id);
      
      console.log('🪟 DEBUG: Skipping smartSyncWindows for updated window to preserve glazing data');
      
      // Only sync other windows to avoid overwriting our manually stored data
      if (otherWindows.length > 0) {
        roomSync.smartSyncWindows(otherWindows, currentRooms, currentFloor);
      }
      
      // Sync overall window counts across ALL floors for site notes (but exclude updated window)
      const allFloorsWindows = elements.filter(el => el.type === 'window' && (el as any).id !== windowToEdit.id) as unknown as WindowElement[];
      if (allFloorsWindows.length > 0) {
        roomSync.syncAllFloorsWindows(allFloorsWindows, floorPlans, roomCustomLabels);
      }
      
      // Show sync indicator
      setSyncedRoomName(roomContainingWindow.name);
      setShowRoomSyncIndicator(true);
      
            console.log(`🪟 Window updated for room "${roomContainingWindow.name}": ${dimensions.width}cm × ${dimensions.height}cm, Trickle vents: ${dimensions.trickleVents ? 'Yes' : 'No'}`);  
    }

    setWindowToEdit(null);
    setIsWindowPromptOpen(false);
  };

  const handleDeleteWindow = () => {
    if (!windowToEdit) return;

    // Remove the window from elements
    const updatedElements = elements.filter(el => (el as any).id !== windowToEdit.id);
    setElements(updatedElements);

    // Update window counts (remove legacy counter)
    setWindowCount(prev => ({
      single: prev.single > 0 ? prev.single - 1 : 0,
      double: prev.double
    }));

    // Sync the removal to room data immediately
    const roomContainingWindow = findRoomAtPoint(windowToEdit.position, currentRooms);
    
    console.log('🪟 DEBUG Window deletion sync:', {
      roomContainingWindow: roomContainingWindow?.name || 'NO_ROOM',
      hasRoomSync: !!roomSync,
      windowPosition: windowToEdit.position,
      deletedWindowId: windowToEdit.id,
      remainingWindows: updatedElements.filter(el => el.type === 'window').length
    });
    
    if (roomSync) {
      // Use setTimeout to ensure state update has completed before sync
      setTimeout(() => {
        const allWindows = updatedElements.filter(el => el.type === 'window') as unknown as WindowElement[];
        
        // Sync room-specific windows for current floor
        roomSync.smartSyncWindows(allWindows, currentRooms, currentFloor);
        
        // Sync overall window counts across ALL floors for site notes
        roomSync.syncAllFloorsWindows(allWindows, floorPlans, roomCustomLabels);
        
        console.log(`🪟 Window deletion sync completed - Remaining windows: ${allWindows.length}`);
      }, 50);
      
      // Show sync indicator
      if (roomContainingWindow && roomContainingWindow.name) {
        setSyncedRoomName(roomContainingWindow.name);
        setShowRoomSyncIndicator(true);
      }
    }

    // Save to undo stack
    recordUndoState();

    setWindowToEdit(null);
    setIsWindowPromptOpen(false);
  };

  const handleDeleteDoor = () => {
    if (!doorToEdit) return;

    // Remove the door from elements
    const updatedElements = elements.filter(el => (el as any).id !== doorToEdit.id);
    setElements(updatedElements);

    // Sync the removal to room data (prefer non-hallway rooms)
    const currentFloorWalls = floorPlans[currentFloor] || [];
    const roomContainingDoor = findBestRoomForDoor(doorToEdit.position, currentRooms, doorToEdit, currentFloorWalls);
    
    console.log('🚪 DEBUG Door deletion sync:', {
      roomContainingDoor: roomContainingDoor?.name || 'NO_ROOM',
      hasRoomSync: !!roomSync,
      doorPosition: doorToEdit.position,
      deletedDoorId: doorToEdit.id,
      wasFrontDoor: (doorToEdit as any).isFrontDoor
    });
    
    if (roomSync) {
      // Sync updated door data after deletion
      const allDoors = updatedElements.filter(el => el.type === 'door') as unknown as DoorElement[];
      
      // Sync door undercuts for current floor
      const currentFloorWalls = floorPlans[currentFloor] || [];
      roomSync.syncDoorUndercuts(allDoors, currentRooms, currentFloor, currentFloorWalls);
      
      // Sync overall door counts across ALL floors for site notes
      const allFloorsDoors = updatedElements.filter(el => el.type === 'door') as unknown as DoorElement[];
      roomSync.syncAllFloorsDoorUndercuts(allFloorsDoors, floorPlans, roomCustomLabels);
      
      // Show sync indicator
      if (roomContainingDoor && roomContainingDoor.name) {
        setSyncedRoomName(roomContainingDoor.name);
        setShowRoomSyncIndicator(true);
      }
      
      console.log(`🚪 Door deleted from room "${roomContainingDoor?.name || 'Unknown'}", remaining doors: ${allDoors.length}`);
    }

    // If the deleted door was a front door, recalculate window orientations
    if ((doorToEdit as any).isFrontDoor && orientationHasBeenSet) {
      console.log('🧭 Front door deleted - recalculating all window orientations');
      
      const updatedElementsWithOrientations = autoCalculateWindowOrientationsAllFloors(
        floorPlans,
        updatedElements, // Use the updated elements without the deleted front door
        propertyOrientation || 0,
        false, // No front door now
        orientationHasBeenSet
      );
      
      // Update elements again with the orientation calculations
      setElements(updatedElementsWithOrientations);
      console.log('🧭 Recalculated orientations for all windows in building after front door deletion');
    }

    // Save to undo stack
    recordUndoState();

    setDoorToEdit(null);
    setIsDoorPromptOpen(false);
  };

  const handleDeleteRadiator = () => {
    // If we're in the middle of placing a radiator, cancel the placement
    if (isPlacingRadiator) {
      console.log('🔥 Canceling radiator placement');
      setIsPlacingRadiator(false);
      setRadiatorPreview(null);
      setWallForRadiator(null);
      setRadiatorToEdit(null);
      setIsRadiatorPromptOpen(false);
      return;
    }

    // If we're editing an existing radiator, delete it
    if (!radiatorToEdit) return;

    // Remove the radiator from elements
    const updatedElements = elements.filter(el => (el as any).id !== radiatorToEdit.id);
    setElements(updatedElements);

    // Sync the removal to room data immediately
    const roomContainingRadiator = findRoomContainingRadiatorText(radiatorToEdit, currentRooms);
    
    console.log('🔥 DEBUG Radiator deletion sync:', {
      roomContainingRadiator: roomContainingRadiator?.name || 'NO_ROOM',
      hasRoomSync: !!roomSync,
      radiatorTextPosition: `(${radiatorToEdit.position.x.toFixed(1)}, ${radiatorToEdit.position.y.toFixed(1)})`,
      deletedRadiatorId: radiatorToEdit.id,
      remainingRadiators: updatedElements.filter(el => el.type === 'radiator').length
    });
    
    if (roomSync) {
      // Use smart sync to handle radiator removal (current floor only)
      const allRadiators = updatedElements.filter(el => el.type === 'radiator') as unknown as RadiatorElement[];
      roomSync.smartSyncRadiators(allRadiators, currentRooms, currentFloor);
      
      console.log(`🔥 Radiator deletion sync completed - Remaining radiators: ${allRadiators.length}`);
      
      // Show sync indicator
      if (roomContainingRadiator && roomContainingRadiator.name) {
        setSyncedRoomName(roomContainingRadiator.name);
        setShowRoomSyncIndicator(true);
      }
    }

    // Save to undo stack
    recordUndoState();

    setRadiatorToEdit(null);
    setIsRadiatorPromptOpen(false);
  };

  const handleSaveRadiatorDimensions = (dimensions: { width: number; height: number; radiatorType: RadiatorType; }) => {
    if (!radiatorToEdit) return;

    // Create the updated elements array with the new radiator dimensions
    const updatedElements = elements.map(el => {
      if ((el as any).id === radiatorToEdit.id) {
      return {
          ...el, 
          width: dimensions.width, 
          height: dimensions.height, 
          radiatorType: dimensions.radiatorType 
        };
      }
      return el;
    });

    // Update state and sync immediately
    setElements(updatedElements);
    
    // Use text position for more accurate room detection
    const roomContainingRadiator = findRoomContainingRadiatorText(radiatorToEdit, currentRooms);
    
    console.log('🔥 DEBUG Radiator sync check (text-based):', {
      roomContainingRadiator: roomContainingRadiator?.name || 'NO_ROOM',
      hasRoomSync: !!roomSync,
      radiatorTextPosition: `(${radiatorToEdit.position.x.toFixed(1)}, ${radiatorToEdit.position.y.toFixed(1)})`,
      totalRooms: currentRooms.length,
      currentFloor: currentFloor
    });
    
    if (roomContainingRadiator && roomContainingRadiator.name && roomSync) {
      // Update single radiator dimensions for backward compatibility
      roomSync.updateRadiatorDimensions(roomContainingRadiator.name, dimensions.width, dimensions.height, dimensions.radiatorType, currentFloor);
      
      // Also run smart sync to handle multiple radiators per room
      const allRadiators = updatedElements.filter(el => el.type === 'radiator') as unknown as RadiatorElement[];
      roomSync.smartSyncRadiators(allRadiators, currentRooms, currentFloor);
      
      // Show sync indicator
      setSyncedRoomName(roomContainingRadiator.name);
      setShowRoomSyncIndicator(true);
      
      console.log(`🔥 Radiator updated for room "${roomContainingRadiator.name}": ${dimensions.width}mm × ${dimensions.height}mm (${dimensions.radiatorType})`);
    }

    setRadiatorToEdit(null);
    setIsRadiatorPromptOpen(false);
  };

  const handleSaveBoilerInfo = (details: { heatingType: HeatingType; brand: string; model: string; gcNumber: string; pcdbSystem?: import('./PCDBSearch').PCDBHeatingSystem; }) => {
  if (!boilerToEdit) return;

  const wall = currentWalls.find(w => (w as any).stableId === boilerToEdit.wallId);
  if (!wall) return;

  // Recalculate position based on offset
  const wallDx = wall.end.x - wall.start.x;
  const wallDy = wall.end.y - wall.start.y;
  const wallAngle = Math.atan2(wallDy, wallDx);
  const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
  
  const pointOnWall = {
    x: wall.start.x + wallDx * boilerToEdit.positionOnWall,
    y: wall.start.y + wallDy * boilerToEdit.positionOnWall
  };

  const newPosition = {
    x: pointOnWall.x + perpVector.x * BOILER_WALL_OFFSET,
    y: pointOnWall.y + perpVector.y * BOILER_WALL_OFFSET
  };

  setElements(prev => {
    const updatedElements = prev.map(el => {
      if ((el as any).id === boilerToEdit.id) {
        return { ...el, ...details, position: newPosition };
      }
      return el;
    });
    
    // Sync updated boiler location to heating system
    const updatedBoiler = updatedElements.find(el => (el as any).id === boilerToEdit.id) as BoilerElement;
    if (roomSync && updatedBoiler && Object.values(currentRooms).length > 0) {
      setTimeout(() => {
        roomSync.syncBoilerLocations([updatedBoiler], Object.values(currentRooms));
      }, 100); // Small delay to ensure state update is complete
    }
    
    return updatedElements;
  });
  setBoilerToEdit(null);
  setIsBoilerPromptOpen(false);
};

// Handle window type selection
const handleWindowTypeSelect = (windowType: WindowType) => {
  setSelectedWindowType(windowType);
  setIsWindowTypePromptOpen(false);
  
  // Reset placement states and activate window tool
  setIsPlacingLight(false);
  setLightPreview(null);
  setIsPlacingSmokeAlarm(false);
  setSmokeAlarmPreview(null);
  setIsPlacingFireSafety(false);
  setFireSafetyPreview(null);
  setIsPlacingUtility(false);
  setUtilityPreview(null);
  setWallForSink(null);
  setIsPlacingLoftHatch(false);
  setLoftHatchPreview(null);
  setIsPlacingRadiator(false);
  setRadiatorPreview(null);
  setWallForRadiator(null);
  setIsPlacingBoiler(false);
  setBoilerPreview(null);
  setWallForBoiler(null);
  setIsPlacingDoor(false);
  setDoorPreview(null);
  setWallForDoor(null);
  setIsHighlighting(false);
  setHighlightStart(null);
  setHighlightPreview(null);
  setHighlightSnap(null);
  setIsPlacingWindow(false);
  setWindowPreview(null);
  setWallForWindow(null);
  
  setCurrentTool('window'); 
  currentToolInternalRef.current = 'window';
};

const handleWindowTypePromptClose = () => {
  setIsWindowTypePromptOpen(false);
  setSelectedWindowType(null);
};

// Handle PCDB system sync to SurveyorSiteNotes from boiler prompt
const handleBoilerPCDBSystemSync = (pcdbSystem: import('./PCDBSearch').PCDBHeatingSystem) => {
  console.log('🔥 Syncing PCDB boiler system to SurveyorSiteNotes:', pcdbSystem);
  
  // Update site notes data with the PCDB system
  setSiteNotesData(prev => {
    // Create or update heating system entry
    const existingHeatingSystems = prev.heatingSystems || [];
    let updatedHeatingSystems = [...existingHeatingSystems];
    
    // Find existing heating system or create new one
    const existingSystemIndex = updatedHeatingSystems.findIndex(sys => sys.id === 'main-heating-system');
    const heatingSystemData = {
      id: 'main-heating-system',
      isPrimary: true,
      isManualEntry: false,
      isRadiatorCountManuallyOverridden: false,
      heatingType: pcdbSystem.type,
      heatingSystemType: pcdbSystem.installation_type === 'combi' ? 'combi-boiler' : 
                        pcdbSystem.installation_type === 'system' ? 'system-boiler' :
                        pcdbSystem.installation_type === 'regular' ? 'regular-boiler' : 
                        'other-boiler',
      heatingFuelType: pcdbSystem.fuel || 'gas',
      heatingBrand: pcdbSystem.brand,
      heatingModel: pcdbSystem.model,
      heatingSerialNumber: pcdbSystem.pcdb_reference || '',
      heatingSourceLocation: '', // Will be updated by room sync
      tankLocation: '',
      totalRads: '0',
      trvs: 'no',
      tRad: '20',
      heatingSource: pcdbSystem.type,
      heatEmitterType: 'radiators',
      fuelType: pcdbSystem.fuel || 'gas',
      heatingControl: 'auto',
      winterEfficiency: pcdbSystem.sap_winter_seasonal_efficiency,
      summerEfficiency: pcdbSystem.sap_summer_seasonal_efficiency,
      flowTemperature: 70,
      isCondensing: pcdbSystem.condensing,
      isLowTemperature: false,
      pcdbSystem: pcdbSystem
    };
    
    if (existingSystemIndex >= 0) {
      updatedHeatingSystems[existingSystemIndex] = { 
        ...updatedHeatingSystems[existingSystemIndex],
        ...heatingSystemData
      };
    } else {
      updatedHeatingSystems.push(heatingSystemData);
    }
    
    const updated = {
      ...prev,
      selectedPCDBSystem: pcdbSystem,
      heatingSystems: updatedHeatingSystems,
      formData: {
        ...prev.formData,
        heatingType: pcdbSystem.type,
        heatingBrand: pcdbSystem.brand,
        heatingModel: pcdbSystem.model,
        heatingSerialNumber: pcdbSystem.pcdb_reference || '',
        heatingSystemType: pcdbSystem.installation_type === 'combi' ? 'combi-boiler' : 
                          pcdbSystem.installation_type === 'system' ? 'system-boiler' :
                          pcdbSystem.installation_type === 'regular' ? 'regular-boiler' : 
                          'other-boiler',
        winterEfficiency: pcdbSystem.sap_winter_seasonal_efficiency,
        summerEfficiency: pcdbSystem.sap_summer_seasonal_efficiency,
        isCondensing: pcdbSystem.condensing,
        isLowTemperature: false // Default, can be updated later
      }
    };
    
    // Trigger autosave with updated data
    if (autosave) {
      setTimeout(() => {
        autosave.markAsChanged();
      }, 100);
    }
    
    console.log('🔥 ✅ PCDB boiler system synced to SurveyorSiteNotes with heating systems:', updatedHeatingSystems.length);
    return updated;
  });
};

  const handleSaveDoorUndercut = (hasUndercut: boolean, photos?: any[], roomId?: string) => {
    if (!doorToEdit) return;

    // Create the updated elements array with the new door settings including roomId
    const updatedElements = elements.map(el => {
      if ((el as any).id === doorToEdit.id) {
        const updatedDoor = { ...el, hasUndercut, roomId };
        return updatedDoor;
      }
      return el;
    });

    // Update state and sync immediately
    setElements(updatedElements);
    
    // Sync immediately using current state values and the updated elements
    if (roomSync) {
      const allDoors = updatedElements.filter(el => el.type === 'door') as unknown as DoorElement[];
      const currentFloorWalls = floorPlans[currentFloor] || [];
      roomSync.syncDoorUndercuts(allDoors, currentRooms, currentFloor, currentFloorWalls);
      
      // Sync overall door counts across ALL floors for site notes
      const allFloorsDoors = updatedElements.filter(el => el.type === 'door') as unknown as DoorElement[];
      roomSync.syncAllFloorsDoorUndercuts(allFloorsDoors, floorPlans, roomCustomLabels);
      
      // Show sync indicator
      setSyncedRoomName('Door Undercuts');
      setShowRoomSyncIndicator(true);
      
      console.log(`🚪 Door undercut data synced to site notes (immediate): ${hasUndercut ? 'has undercut' : 'no undercut'}, total doors across all floors: ${allFloorsDoors.length}`);
    }
  };

  const handleFlipDoorSwing = () => {
    if (!doorToEdit) return;

    // Create the updated elements array with flipped door swing
    const updatedElements = elements.map(el => {
      if ((el as any).id === doorToEdit.id) {
        const updatedDoor = { ...el, swingRight: !doorToEdit.swingRight };
        return updatedDoor;
      }
      return el;
    });

    // Update state and redraw
    setElements(updatedElements);
    
    // Update the doorToEdit state so the modal shows the new direction
    setDoorToEdit({ ...doorToEdit, swingRight: !doorToEdit.swingRight });
    
    // Trigger redraw
    drawCanvas();
    
    // Sync the door swing change to site notes
    if (roomSync) {
      const allDoors = updatedElements.filter(el => el.type === 'door') as unknown as DoorElement[];
      const currentFloorWalls = floorPlans[currentFloor] || [];
      roomSync.syncDoorUndercuts(allDoors, currentRooms, currentFloor, currentFloorWalls);
      
      console.log(`🚪 Door swing flipped and synced to site notes`);
    }
  };

  // Handle address submission from modal
  const handleAddressSubmit = (address: string) => {
    setPropertyAddress(address);
    setShowAddressModal(false);
    // Pass the new address directly for immediate validation context
    autosave.enableAutosave({ address }); 
  };

  // Handle skipping address input
  const handleSkipAddress = () => {
    setShowAddressModal(false);
    autosave.disableAutosave();
  };

  // Handle orientation submission from modal
  const handleOrientationSubmit = (orientation: number) => {
    setPropertyOrientation(orientation);
    setOrientationHasBeenSet(true);
    setShowOrientationModal(false);
  };

  // Handle skipping orientation input
  const handleSkipOrientation = () => {
    setPropertyOrientation(0); // Default to North
    setOrientationHasBeenSet(true);
    setShowOrientationModal(false);
  };
  
  // Handle onboarding modal submission
  const handleOnboardingSubmit = (data: { address: { buildingName?: string; houseNumber: string; street: string; town: string; postcode: string } }) => {
    const combinedAddress = combineAddressFields(data.address);
    console.log('📝 Onboarding submit with buildingName:', data.address.buildingName, '-> Combined:', combinedAddress);
    setPropertyAddress(combinedAddress);
    setShowOnboardingModal(false);
    autosave.enableAutosave({ address: combinedAddress }); 
  };

  // Handle skipping onboarding modal
  const handleSkipOnboarding = () => {
    setShowOnboardingModal(false);
    autosave.disableAutosave();
  };

  // Handle opening orientation modal from toolbar
  const handleOpenOrientationModal = () => {
    setShowOrientationModal(true);
  };


  const getFloorHeightFromSiteNotes = (floorNumber: number): string => {
    try {
      // Find the floor in site notes data that matches the floor number
      const targetFloor = siteNotesData.floors.find(floor => {
        // Map floor levels to numbers for comparison
        const floorLevelToNumber: Record<string, number> = {
          'basement': 0,
          'ground floor': 1,
          'first floor': 2,
          'second floor': 3,
          'third floor': 4,
          'fourth floor': 5,
          'fifth floor': 6
        };
        return floorLevelToNumber[floor.floorLevel] === floorNumber;
      });

      if (!targetFloor) {
        console.log(`No floor data found for floor ${floorNumber}`);
        return 'N/A'; // No data available
      }

      // Get floor height from dimensions section
      if (targetFloor.height && targetFloor.height.trim() !== '') {
        const floorHeight = parseFloat(targetFloor.height);
        if (!isNaN(floorHeight) && floorHeight > 0) {
          console.log(`Floor height for floor ${floorNumber}: ${targetFloor.height}m (from floor dimensions)`);
          return targetFloor.height;
        }
      }

      console.log(`No floor height entered for floor ${floorNumber}`);
      return 'N/A'; // No manual data available
      
    } catch (error) {
      console.error('Error getting floor height:', error);
      return 'N/A'; // No data available on error
    }
  };

  // Simplified for Estate Agency - area and height only (hlp/pw included for compatibility with SurveyorSiteNotes)
  const handleGetFloorCalculations = async (floorNumber: number): Promise<{ area: string; hlp: string; pw: string; height: string } | null> => {
    try {
      // 🎯 EXTENSION FIX: Check if there's a highlighted extension area for this floor
      const highlightedExtension = elements.find(
        el => el.type === 'highlight' && (el as HighlightArea).floor === floorNumber && (el as HighlightArea).savedToNotes
      ) as HighlightArea | undefined;

      if (highlightedExtension) {
        console.log(`🎨 Extension area detected for floor ${floorNumber}, using highlight intersection calculation`);
        
        // Get walls and rooms for this floor
        const floorWalls = floorNumber === currentFloor 
          ? currentWalls 
          : (floorPlans[floorNumber] || []);
        
        const wallsWithTempIds = floorWalls.map((wall, index) => ({
          ...wall,
          tempId: wall.tempId || `wall-${floorNumber}-${index}`,
          ...(wall.isExtrusionConnector && { isExtrusionConnector: true }),
          ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: true }),
          ...(wall.isSplitOrigin && { isSplitOrigin: true })
        }));
        
        // Calculate rooms for this floor
        const floorRooms = calculateRooms(wallsWithTempIds, roomCustomLabels, 'explicitRoomCreation', floorNumber);
        
        console.log(`🎨 Calculating extension area with ${wallsWithTempIds.length} walls and ${floorRooms.length} rooms`);
        
        // Use calculateHighlightMetrics to get the intersection area (including partial rooms)
        const extensionMetrics = calculateHighlightMetrics(
          highlightedExtension,
          wallsWithTempIds,
          floorRooms
        );
        
        // Get floor height from site notes dimensions section
        const floorHeight = getFloorHeightFromSiteNotes(floorNumber);
        
        console.log(`✅ Extension area calculations for floor ${floorNumber}:`, {
          area: extensionMetrics.area.toFixed(2) + ' m²',
          height: floorHeight,
          source: 'highlightIntersectionCalculation'
        });
        
        return {
          area: extensionMetrics.area.toFixed(2),
          hlp: '0', // Not used in Estate Agency
          pw: '0',  // Not used in Estate Agency
          height: floorHeight
        };
      }

      // No highlighted extension - use full floor calculation
      // Check if the requested floor is the current floor
      if (floorNumber === currentFloor) {
        // Use the same calculations that are displayed in the UI
        const { totalArea } = getDisplayMeasurements();
        
        // Get floor height from site notes dimensions section
        const floorHeight = getFloorHeightFromSiteNotes(floorNumber);
        
        console.log(`📐 Using current floor calculations for floor ${floorNumber}:`, {
          area: totalArea,
          height: floorHeight,
          source: 'getDisplayMeasurements'
        });
        
        return {
          area: totalArea.toFixed(2),
          hlp: '0', // Not used in Estate Agency
          pw: '0',  // Not used in Estate Agency
          height: floorHeight
        };
      } else {
        // For other floors, get walls and calculate area only
        const floorWalls = floorPlans[floorNumber] || [];
        const wallsWithTempIds = floorWalls.map((wall, index) => ({
          ...wall,
          tempId: `wall-${floorNumber}-${index}`,
          ...(wall.isExtrusionConnector && { isExtrusionConnector: true }),
          ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: true }),
          ...(wall.isSplitOrigin && { isSplitOrigin: true })
        }));
        
        // Use the same calculation method as getDisplayMeasurements (only need area)
        const { totalArea } = calculateOverallWallLengthsAndArea(
          wallsWithTempIds, 
          roomCustomLabels, 
          'explicitRoomCreation',
          floorNumber
        );
        
        // Get floor height from site notes dimensions section
        const floorHeight = getFloorHeightFromSiteNotes(floorNumber);
        
        console.log(`📐 Calculated for floor ${floorNumber}:`, {
          area: totalArea,
          height: floorHeight,
          wallCount: floorWalls.length,
          source: 'cross-floor calculation'
        });
        
        return {
          area: totalArea.toFixed(2),
          hlp: '0', // Not used in Estate Agency
          pw: '0',  // Not used in Estate Agency
          height: floorHeight
        };
      }
    } catch (error) {
      console.error('Error getting floor calculations:', error);
      return null;
    }
  };

  const handleComprehensiveSync = async () => {
    // 🔧 PERFORMANCE FIX: Skip expensive comprehensive sync during label-only operations
    if (labelsOnlyOperation.current) {
      console.log('🔧 PERFORMANCE: Skipping comprehensive sync during label-only operation');
      return 0;
    }
    
    console.log('🔄 Starting ultra-comprehensive sync with advanced detection...');
    
    try {
      // Use enhanced sync with advanced re-detection and validation
      const syncResult = roomSync.comprehensiveSyncWithRedetection(
        elements,
        floorPlans,
        roomCustomLabels,
        siteNotesData
      );
      
      // Update elements with re-detected locations
      setElements(syncResult.updatedElements);
      
      // Update site notes data
      setSiteNotesData(syncResult.updatedSiteNotesData);
      
      console.log(`✅ Ultra-comprehensive sync completed!`, {
        report: syncResult.redetectionReport,
        accuracy: syncResult.accuracyReport
      });
      
      // Show enhanced success indicator with accuracy information
      const accuracyInfo = `${syncResult.accuracyReport.accuracyPercentage.toFixed(1)}% accuracy (${syncResult.accuracyReport.wallBasedAssignments} wall-based, ${syncResult.accuracyReport.geometricAssignments} geometric)`;
      setSyncedRoomName(`All Floors - ${accuracyInfo}`);
      setShowRoomSyncIndicator(true);
      
      // If accuracy is below 100%, log detailed validation for troubleshooting
      if (syncResult.accuracyReport.accuracyPercentage < 100) {
        console.warn('🔍 Running detailed validation for accuracy below 100%...');
        const validation = roomSync.validateSyncAccuracy(elements, floorPlans, roomCustomLabels);
        console.log('📊 Detailed validation report:', validation.detailedReport);
        
        if (validation.recommendations.length > 0) {
          console.log('💡 Recommendations for improvement:', validation.recommendations);
        }
      }
      
      return syncResult.updatedElements.length;
      
    } catch (error) {
      console.error('❌ Error during enhanced comprehensive sync:', error);
      
      // Fallback to original sync method
      console.log('🔄 Falling back to original sync...');
      
      try {
        // Get all elements organized by type
        const allWindows = elements.filter(el => el.type === 'window') as WindowElement[];
        const allDoors = elements.filter(el => el.type === 'door') as DoorElement[];
        const allRadiators = elements.filter(el => el.type === 'radiator') as RadiatorElement[];
        const allBoilers = elements.filter(el => el.type === 'boiler') as BoilerElement[];
        const allLights = elements.filter(el => el.type === 'light') as LightElement[];
        const allSmokeAlarms = elements.filter(el => el.type === 'smoke-alarm') as SmokeAlarmElement[];
        const allUtilities = elements.filter(el => el.type === 'utility') as UtilityElement[];
        
        // Sync each floor
        const floorNumbers = Object.keys(floorPlans).map(Number).sort();
        let totalSyncedItems = 0;
        
        // Calculate all rooms upfront to avoid duplicate calculations that could create duplicates
        const allFloorRooms: Record<number, Room[]> = {};
        for (const floorNum of floorNumbers) {
          const wallsForFloor = floorPlans[floorNum] || [];
          allFloorRooms[floorNum] = labelsOnlyOperation.current ? [] : calculateRooms(wallsForFloor, roomCustomLabels, 'explicitRoomCreation', floorNum);
        }
        
        // FIXED: Chain all sync operations instead of making separate setSiteNotesData calls
        setSiteNotesData(prevData => {
          let updatedData = { ...prevData };
          
          for (const floorNum of floorNumbers) {
            const wallsForFloor = floorPlans[floorNum] || [];
            const roomsForFloor = allFloorRooms[floorNum]; // Use pre-calculated rooms
            
            // Get elements for this floor
            const floorWindows = allWindows.filter(w => w.floor === floorNum);
            const floorDoors = allDoors.filter(d => d.floor === floorNum);
            const floorRadiators = allRadiators.filter(r => r.floor === floorNum);
            const floorBoilers = allBoilers.filter(b => b.floor === floorNum);
            const floorLights = allLights.filter(l => l.floor === floorNum);
            const floorSmokeAlarms = allSmokeAlarms.filter(s => s.floor === floorNum);
            const floorUtilities = allUtilities.filter(u => u.floor === floorNum);
            
            // Sync ALL rooms for this floor (not just named ones) with consolidation
            if (roomsForFloor.length > 0) {
              updatedData = syncAllRoomsToSurveyorDataWithConsolidation(roomsForFloor, updatedData, floorNum);
              totalSyncedItems += roomsForFloor.length;
            }
            
            // Removed: Sync floor dimensions (area, HLP, PW) for this floor
            // updatedData = syncFloorDimensions(roomsForFloor, wallsForFloor, updatedData, floorNum);
            // totalSyncedItems += 1;
            
            // Sync windows with trickle vents for this floor
            if (floorWindows.length > 0) {
              updatedData = smartSyncWindowsToRoom(floorWindows, roomsForFloor, updatedData, floorNum, true);
              totalSyncedItems += floorWindows.length;
            }
            
            // Sync door undercuts for this floor
            if (floorDoors.length > 0) {
              updatedData = syncDoorUndercutsToSiteNotes(floorDoors, roomsForFloor, updatedData, floorNum, wallsForFloor);
              totalSyncedItems += floorDoors.length;
            }
            
            // Sync radiator dimensions for this floor
            if (floorRadiators.length > 0) {
              updatedData = smartSyncRadiatorsToRoom(floorRadiators, roomsForFloor, updatedData, floorNum, true);
              totalSyncedItems += floorRadiators.length;
            }
            
            // Sync boiler information for this floor
            if (floorBoilers.length > 0) {
              updatedData = syncBoilerLocationsToSiteNotes(floorBoilers, roomsForFloor, updatedData);
              totalSyncedItems += floorBoilers.length;
            }
            
            // Sync light counts for this floor using smart sync to prevent duplication
            if (floorLights.length > 0) {
              updatedData = smartSyncLightsToRooms(floorLights, roomsForFloor, updatedData, floorNum, true);
              totalSyncedItems += floorLights.length;
            }
            
            // Sync utility elements for this floor
            if (floorUtilities.length > 0) {
              updatedData = syncUtilityLocationsToSiteNotes(floorUtilities, roomsForFloor, updatedData);
              totalSyncedItems += floorUtilities.length;
            }
          }
          
          // Clean up any duplicate rooms BEFORE aggregations to prevent over-counting
          updatedData = cleanupDuplicateRooms(updatedData);
          
          // Sync cross-floor aggregations (now with clean data)
          updatedData = syncAllFloorsWindowsToSiteNotesWithFloorPlans(allWindows, floorPlans, roomCustomLabels, updatedData);
          updatedData = syncAllFloorsDoorUndercutsToSiteNotesWithFloorPlans(allDoors, floorPlans, roomCustomLabels, updatedData);
          updatedData = syncAllFloorsLightsToAdditionalInfo(updatedData);
          updatedData = syncAllFloorsRadiatorsToHeatingSystem(updatedData);
          
          return updatedData;
        });
      
      // Sync floor calculations for all floors
      for (const floorNum of floorNumbers) {
        try {
          const calculations = await handleGetFloorCalculations(floorNum);
          if (calculations) {
            // Apply the calculations to site notes data
            setSiteNotesData(prevData => {
              const updatedData = { ...prevData };
              const targetFloor = updatedData.floors.find(floor => {
                const floorLevelToNumber: Record<string, number> = {
                  'basement': 0,
                  'ground floor': 1,
                  'first floor': 2,
                  'second floor': 3,
                  'third floor': 4,
                  'fourth floor': 5,
                  'fifth floor': 6
                };
                return floorLevelToNumber[floor.floorLevel] === floorNum;
              });
              
              if (targetFloor) {
                targetFloor.floorArea = calculations.area + ' m²';
                // Removed: targetFloor.hlp = calculations.hlp + ' m';
                // Removed: targetFloor.pw = calculations.pw + ' m';
                
                // 🔧 ROOM HEIGHT FIX: Check if height already contains units to prevent double unit addition
                const heightValue = calculations.height.trim();
                if (heightValue.includes('m') || heightValue.includes('M')) {
                  // Height already has units - use as is
                  targetFloor.height = heightValue;
                } else {
                  // Height is just a number - add meters unit
                  targetFloor.height = heightValue + ' m';
                }
              }
              
              return updatedData;
            });
            
            console.log(`  📐 Synced calculations for floor ${floorNum}: ${calculations.area}m², ${calculations.height}m height`);
            totalSyncedItems += 1;
          }
        } catch (error) {
          console.warn(`  ⚠️ Failed to sync calculations for floor ${floorNum}:`, error);
        }
      }
      
      // Clean up any duplicate rooms that might have been created
      if (roomSync.cleanupDuplicateRooms) {
        roomSync.cleanupDuplicateRooms();
        console.log('  🧹 Cleaned up duplicate rooms');
      }
      
        console.log(`✅ Fallback comprehensive sync completed! Synced ${totalSyncedItems} items across ${floorNumbers.length} floors.`);
        
        // Show success indicator
        setSyncedRoomName('All Floors (Fallback)');
        setShowRoomSyncIndicator(true);
        
        return totalSyncedItems;
        
      } catch (fallbackError) {
        console.error('❌ Error during fallback comprehensive sync:', fallbackError);
        throw fallbackError;
      }
    }
  };

  // Add function to sync all labeled rooms across all floors AND their elements
  const handleSyncAllLabeledRooms = (): number => {
    console.log('🔄 Syncing all labeled rooms and elements across all floors...');
    
    // DEBUG: Check if roomSync is available
    if (!roomSync) {
      console.error('❌ roomSync is not available!');
      alert('Error: Room sync service is not available. Please refresh the page and try again.');
      return 0;
    }
    
    console.log('✅ roomSync is available:', Object.keys(roomSync));
    
    const floorNumbers = Object.keys(floorPlans).map(Number).sort();
    let totalSyncedRooms = 0;
    let totalSyncedElements = 0;
    let debugInfo: { floor: number; total: number; labeled: number; filtered: any[] }[] = [];
    
    // Get all elements organized by type
    const allWindows = elements.filter(el => el.type === 'window') as WindowElement[];
    const allDoors = elements.filter(el => el.type === 'door') as DoorElement[];
    const allRadiators = elements.filter(el => el.type === 'radiator') as RadiatorElement[];
    const allLights = elements.filter(el => el.type === 'light') as LightElement[];
    const allUtilities = elements.filter(el => el.type === 'utility') as UtilityElement[];
    
    // DEBUG: Log all elements found
    console.log('🔍 DEBUG: All elements found:', {
      totalElements: elements.length,
      windows: allWindows.length,
      doors: allDoors.length,
      radiators: allRadiators.length,
      lights: allLights.length,
      utilities: allUtilities.length
    });
    
    // 🔍 ROOM DELETION DETECTION: Compare current rooms with site notes to detect deletions
    const currentSiteNotesData = siteNotesData;
    const roomDeletionReport: { floor: number; deletedRooms: string[]; orphanedElements: number }[] = [];
    
    // DEBUG: Log sample elements with their properties
    if (allWindows.length > 0) {
      console.log('🪟 Sample windows:', allWindows.slice(0, 3).map(w => ({
        id: w.id,
        position: w.position,
        floor: w.floor,
        width: w.width,
        height: w.height
      })));
    }
    
    if (allRadiators.length > 0) {
      console.log('🔥 Sample radiators:', allRadiators.slice(0, 3).map(r => ({
        id: r.id,
        position: r.position,
        floor: r.floor,
        width: r.width,
        height: r.height
      })));
    }
    
    if (allLights.length > 0) {
      console.log('💡 Sample lights:', allLights.slice(0, 3).map(l => ({
        id: l.id,
        position: l.position,
        floor: l.floor,
        lightType: l.lightType
      })));
    }
    
    for (const floorNum of floorNumbers) {
      const wallsForFloor = floorPlans[floorNum] || [];
      
      if (wallsForFloor.length === 0) {
        console.log(`📋 No walls found on floor ${floorNum}, skipping...`);
        continue;
      }
      
      // Try multiple calculation contexts to get comprehensive room list
      let roomsForFloor: Room[] = [];
      
      // Strategy 1: Try explicit room creation first (most comprehensive)
      try {
        const explicitRooms = calculateRooms(wallsForFloor, roomCustomLabels, 'explicitRoomCreation', floorNum);
        console.log(`🏠 Found ${explicitRooms.length} rooms using explicitRoomCreation on floor ${floorNum}`);
        roomsForFloor = [...explicitRooms];
      } catch (error) {
        console.warn(`Failed to calculate rooms with explicitRoomCreation on floor ${floorNum}:`, error);
      }
      
      // Strategy 2: Try label preservation if we don't have enough rooms
      if (roomsForFloor.length === 0) {
        try {
          const preservationRooms = calculateRooms(wallsForFloor, roomCustomLabels, 'labelPreservation', floorNum);
          console.log(`🏠 Found ${preservationRooms.length} rooms using labelPreservation on floor ${floorNum}`);
          roomsForFloor = [...preservationRooms];
        } catch (error) {
          console.warn(`Failed to calculate rooms with labelPreservation on floor ${floorNum}:`, error);
        }
      }
      
      // Strategy 3: Try geometry update as fallback
      if (roomsForFloor.length === 0) {
        try {
          const geometryRooms = calculateRooms(wallsForFloor, roomCustomLabels, 'geometryUpdate', floorNum);
          console.log(`🏠 Found ${geometryRooms.length} rooms using geometryUpdate on floor ${floorNum}`);
          roomsForFloor = [...geometryRooms];
        } catch (error) {
          console.warn(`Failed to calculate rooms with geometryUpdate on floor ${floorNum}:`, error);
        }
      }
      
      // If we still have no rooms, try combining multiple strategies
      if (roomsForFloor.length === 0) {
        console.log(`🔄 Trying combined room calculation strategies for floor ${floorNum}...`);
        try {
          const explicitRooms = calculateRooms(wallsForFloor, roomCustomLabels, 'explicitRoomCreation', floorNum);
          const preservationRooms = calculateRooms(wallsForFloor, roomCustomLabels, 'labelPreservation', floorNum);
          
          // Combine and deduplicate rooms by stableId
          const combinedRoomsMap = new Map<string, Room>();
          [...explicitRooms, ...preservationRooms].forEach(room => {
            if (room.stableId) {
              combinedRoomsMap.set(room.stableId, room);
            } else if (room.id) {
              // Fallback to room.id if no stableId
              combinedRoomsMap.set(room.id, room);
            }
          });
          roomsForFloor = Array.from(combinedRoomsMap.values());
          console.log(`🏠 Combined strategies found ${roomsForFloor.length} rooms on floor ${floorNum}`);
        } catch (error) {
          console.error(`Failed to calculate rooms with combined strategies on floor ${floorNum}:`, error);
        }
      }
      
      if (roomsForFloor.length === 0) {
        console.warn(`⚠️ No rooms found on floor ${floorNum} with ${wallsForFloor.length} walls - elements on this floor will not be synced`);
        continue;
      }
      
      // 🔍 ROOM DELETION DETECTION: Check for rooms that exist in site notes but not in floor plan
      const siteNotesFloor = currentSiteNotesData.floors.find((f: any) => f.id === floorNum);
      const deletedRoomNames: string[] = [];
      
      if (siteNotesFloor && siteNotesFloor.rooms.length > 0) {
        const currentRoomNames = new Set(roomsForFloor.map(r => r.name));
        
        siteNotesFloor.rooms.forEach((siteRoom: any) => {
          if (siteRoom.name && !currentRoomNames.has(siteRoom.name)) {
            // Room exists in site notes but not in current floor plan
            const hasData = !!(
              siteRoom.length || siteRoom.width || siteRoom.height ||
              (siteRoom.radiators && siteRoom.radiators.length > 0) ||
              (siteRoom.windows && siteRoom.windows.length > 0) ||
              siteRoom.lowEnergyLights || siteRoom.nonLowEnergyLights
            );
            
            if (hasData) {
              deletedRoomNames.push(siteRoom.name);
              console.log(`🗑️ ROOM DELETION DETECTED: Room "${siteRoom.name}" exists in site notes but not in floor plan on floor ${floorNum}`);
            }
          }
        });
      }
      
      // 🔍 ORPHANED ELEMENT DETECTION: Find elements that no longer belong to any room
      const floorElements = elements.filter(el => (el as any).floor === floorNum);
      let orphanedElementCount = 0;
      
      if (floorElements.length > 0 && roomsForFloor.length > 0) {
        floorElements.forEach(element => {
          const typedElement = element as any;
          const roomContainingElement = findRoomAtPoint(typedElement.position, roomsForFloor);
          
          if (!roomContainingElement || !roomContainingElement.name || /^Room \d+$/.test(roomContainingElement.name)) {
            orphanedElementCount++;
          }
        });
      }
      
      if (deletedRoomNames.length > 0 || orphanedElementCount > 0) {
        roomDeletionReport.push({
          floor: floorNum,
          deletedRooms: deletedRoomNames,
          orphanedElements: orphanedElementCount
        });
        
        // 🧹 COMPREHENSIVE CLEANUP: Handle room deletion and orphaned elements
        if (deletedRoomNames.length > 0) {
          console.log(`🧹 Performing comprehensive cleanup for floor ${floorNum}:`, {
            deletedRooms: deletedRoomNames,
            orphanedElements: orphanedElementCount,
            remainingRooms: roomsForFloor.length,
            remainingElements: floorElements.length
          });
          
          roomSync.syncRoomDeletionAndCleanup(deletedRoomNames, floorElements, roomsForFloor, floorNum);
        } else if (orphanedElementCount > 0) {
          // Just clean up orphaned elements without room deletion
          roomSync.syncOrphanedElementsCleanup(floorElements, roomsForFloor, floorNum);
        }
      }

      // DEBUG: Log rooms found for this floor
      console.log(`🏠 DEBUG: Rooms found on floor ${floorNum}:`, roomsForFloor.map(r => ({
        name: r.name,
        id: r.id,
        stableId: r.stableId,
        area: r.area?.toFixed(1)
      })));


      // More flexible filtering for labeled rooms
      const labeledRooms = roomsForFloor.filter(room => {
        // Check for valid ID (either id or stableId is sufficient)
        const hasValidId = (room.id && room.id.trim() !== '') || (room.stableId && room.stableId.trim() !== '');
        
        // Check for valid name
        const hasValidName = room.name && room.name.trim() !== '';
        
        // Improved auto-generated name detection (more patterns)
        const isAutoGeneratedName = /^(Room \d+|room \d+|Room\d+|Unnamed Room \d*|Untitled \d*)$/i.test(room.name || '');
        
        // Check if room has a custom label in roomCustomLabels
        const hasCustomLabel = room.stableId && roomCustomLabels[room.stableId] && roomCustomLabels[room.stableId].trim() !== '';
        
        // A room is considered "labeled" if:
        // 1. It has a custom label in roomCustomLabels, OR
        // 2. It has a valid name that's not auto-generated
        const isLabeled = hasCustomLabel || (hasValidName && !isAutoGeneratedName);
        
        const shouldSync = hasValidId && hasValidName && isLabeled;
        
        // Debug info for rooms that aren't synced
        if (!shouldSync) {
          console.log(`🚫 Room "${room.name}" (ID: ${room.id}, StableID: ${room.stableId}) filtered out:`, {
            hasValidId,
            hasValidName,
            isAutoGeneratedName,
            hasCustomLabel,
            customLabel: room.stableId ? roomCustomLabels[room.stableId] : null,
            isLabeled
          });
        }
        
        return shouldSync;
      });
      
      debugInfo.push({
        floor: floorNum,
        total: roomsForFloor.length,
        labeled: labeledRooms.length,
        filtered: roomsForFloor.map(r => ({
          name: r.name,
          id: r.id,
          stableId: r.stableId,
          hasCustomLabel: r.stableId ? !!roomCustomLabels[r.stableId] : false,
          customLabelValue: r.stableId ? roomCustomLabels[r.stableId] : null
        }))
      });
      
      if (labeledRooms.length > 0) {
        console.log(`📋 Syncing ${labeledRooms.length} labeled rooms on floor ${floorNum}:`, labeledRooms.map(r => ({
          name: r.name,
          customLabel: r.stableId ? roomCustomLabels[r.stableId] : null
        })));
        
        // Sync labeled rooms for this floor
        setSiteNotesData(prevData => {
          return syncAllRoomsToSurveyorDataWithConsolidation(labeledRooms, prevData, floorNum, roomCustomLabels);
        });
        
        totalSyncedRooms += labeledRooms.length;
      } else {
        console.log(`📋 No labeled rooms found on floor ${floorNum} (total rooms: ${roomsForFloor.length})`);
      }
      
      // NOW SYNC ELEMENTS FOR THIS FLOOR using ALL rooms (not just labeled ones)
      // This ensures elements can be assigned to any room, even if the room isn't labeled
      
      // Get elements for this floor
      const floorWindows = allWindows.filter(w => w.floor === floorNum);
      const floorDoors = allDoors.filter(d => d.floor === floorNum);
      const floorRadiators = allRadiators.filter(r => r.floor === floorNum);
      const floorLights = allLights.filter(l => l.floor === floorNum);
      const floorUtilities = allUtilities.filter(u => u.floor === floorNum);
      
      // DEBUG: Log elements found for this floor
      console.log(`🔍 DEBUG: Elements on floor ${floorNum}:`, {
        windows: floorWindows.length,
        doors: floorDoors.length,
        radiators: floorRadiators.length,
        lights: floorLights.length,
        utilities: floorUtilities.length
      });
      
      // Sync windows with enhanced room detection and per-room glazing data protection
      if (floorWindows.length > 0) {
        console.log(`🪟 DEBUG: About to sync ${floorWindows.length} windows on floor ${floorNum} with ${roomsForFloor.length} rooms`);
        
        // 🛡️ PER-ROOM GLAZING DATA PROTECTION: Let smartSyncWindows handle protection per room
        // This allows syncing multiple windows while preserving manual glazing data per room
        roomSync.smartSyncWindows(floorWindows, roomsForFloor, floorNum, true);
        console.log(`  🪟 Synced ${floorWindows.length} windows on floor ${floorNum} with per-room glazing protection`);
        
        totalSyncedElements += floorWindows.length;
      }
      
      // Sync door undercuts
      if (floorDoors.length > 0) {
        console.log(`🚪 DEBUG: About to sync ${floorDoors.length} doors on floor ${floorNum} with ${roomsForFloor.length} rooms`);
        roomSync.syncDoorUndercuts(floorDoors, roomsForFloor, floorNum);
        totalSyncedElements += floorDoors.length;
        console.log(`  🚪 Synced ${floorDoors.length} doors on floor ${floorNum}`);
      }
      
      // Sync radiator dimensions with migration for improved accuracy
      if (floorRadiators.length > 0) {
        console.log(`🔥 DEBUG: About to migrate and sync ${floorRadiators.length} radiators on floor ${floorNum} with ${roomsForFloor.length} rooms`);
        // First migrate existing radiators to use improved text-based detection
        roomSync.migrateRadiatorAssignments(floorRadiators, roomsForFloor, floorNum);
        // Then run smart sync to ensure everything is up to date
        roomSync.smartSyncRadiators(floorRadiators, roomsForFloor, floorNum, true);
        totalSyncedElements += floorRadiators.length;
        console.log(`  🔥 Migrated and synced ${floorRadiators.length} radiators on floor ${floorNum}`);
      }
      
      // Sync light counts using smart sync to prevent duplication
      if (floorLights.length > 0) {
        console.log(`💡 DEBUG: About to smart sync ${floorLights.length} lights on floor ${floorNum} with ${roomsForFloor.length} rooms`);
        
        roomSync.smartSyncLights(floorLights, roomsForFloor, floorNum, true);
        
        totalSyncedElements += floorLights.length;
        console.log(`  💡 Smart synced ${floorLights.length} lights on floor ${floorNum}`);
      }
      
      // Sync utility elements
      if (floorUtilities.length > 0) {
        console.log(`⚡ DEBUG: About to sync ${floorUtilities.length} utilities on floor ${floorNum} with ${roomsForFloor.length} rooms`);
        roomSync.syncUtilityLocations(floorUtilities, roomsForFloor);
        totalSyncedElements += floorUtilities.length;
        console.log(`  ⚡ Synced ${floorUtilities.length} utilities on floor ${floorNum}`);
      }
      
      // Removed: Sync floor dimensions (area, HLP, PW) for this floor
      // roomSync.syncFloorDimensions(roomsForFloor, wallsForFloor, floorNum, true); // immediate sync
      // console.log(`  📐 Synced floor dimensions for floor ${floorNum}`);
    }
    
    // Sync cross-floor aggregations
    console.log('🌍 Syncing cross-floor aggregations...');
    
    // Sync all floors windows to maintain trickle vent counts
    roomSync.syncAllFloorsWindows(allWindows, floorPlans, roomCustomLabels);
    
    // Sync all floors door undercuts
    roomSync.syncAllFloorsDoorUndercuts(allDoors, floorPlans, roomCustomLabels);
    
    // Sync total light counts to additional information section
    roomSync.syncAllFloorsLightsToAdditionalInfo();
    
    // Enhanced debugging output
    console.log('🔍 Detailed sync analysis:', debugInfo);
    console.log('🏷️ Current room custom labels:', roomCustomLabels);
    
    // 🧹 ROOM DELETION SUMMARY: Log cleanup results
    if (roomDeletionReport.length > 0) {
      console.log('🧹 Room deletion and cleanup summary:', roomDeletionReport);
      const totalDeletedRooms = roomDeletionReport.reduce((sum, floor) => sum + floor.deletedRooms.length, 0);
      const totalOrphanedElements = roomDeletionReport.reduce((sum, floor) => sum + floor.orphanedElements, 0);
      
      if (totalDeletedRooms > 0 || totalOrphanedElements > 0) {
        console.log(`🧹 Cleanup completed: ${totalDeletedRooms} room(s) removed, ${totalOrphanedElements} orphaned element(s) cleaned up`);
      }
      
      // 🧹 Additional cleanup: Ensure orphaned windows are removed from site notes
      Object.keys(floorPlans).forEach(floorKey => {
        const floorNum = parseInt(floorKey, 10);
        const floorElements = elements.filter(el => (el as any).floor === floorNum);
        const roomsForFloor = calculateRooms(floorPlans[floorNum] || [], roomCustomLabels, 'explicitRoomCreation', floorNum);
        
        // Clean up orphaned elements for this floor
        roomSync.syncOrphanedElementsCleanup(floorElements, roomsForFloor, floorNum);
        
        // 🧹 EXTRA CLEANUP: Remove window data from rooms that don't have any windows
        const windowsOnFloor = floorElements.filter(el => el.type === 'window') as WindowElement[];
        const roomsWithWindows = new Set<string>();
        
        // Track which rooms actually have windows
        windowsOnFloor.forEach(window => {
          const roomContainingWindow = findRoomAtPoint(window.position, roomsForFloor);
          if (roomContainingWindow && roomContainingWindow.name) {
            roomsWithWindows.add(roomContainingWindow.name);
          }
        });
        
        // Clear window data from rooms without windows
        roomsForFloor.forEach(room => {
          if (room.name && !roomsWithWindows.has(room.name)) {
            console.log(`🧹 Clearing window data from room "${room.name}" on floor ${floorNum} (no windows on floor plan)`);
            roomSync.updateMultipleWindowDimensions(room.name, [], floorNum);
          }
        });
      });
    }
    
    console.log(`✅ Synced ${totalSyncedRooms} labeled rooms and ${totalSyncedElements} elements across ${floorNumbers.length} floors`);
    
    if (totalSyncedRooms > 0 || totalSyncedElements > 0) {
      // Show success indicator
      setSyncedRoomName(`${totalSyncedRooms} Rooms + ${totalSyncedElements} Elements`);
      setShowRoomSyncIndicator(true);
    } else {
      // Show user-friendly message about why no rooms were synced
      const totalRoomsAcrossFloors = debugInfo.reduce((sum, floor) => sum + floor.total, 0);
      const hasRooms = totalRoomsAcrossFloors > 0;
      
      if (hasRooms) {
        alert(`No labeled rooms found to sync.\n\nTotal rooms detected: ${totalRoomsAcrossFloors}\n\nTo sync rooms:\n1. Label your rooms with custom names (not "Room 1", "Room 2", etc.)\n2. Make sure rooms are properly enclosed by walls\n3. Check that room labels are saved\n\nTip: Use the room label tool to add custom names to your rooms.`);
      } else {
        alert('No rooms detected on any floor.\n\nMake sure you have:\n1. Created enclosed spaces with walls\n2. Rooms are properly formed (minimum 3 walls)\n3. Try refreshing the floor plan if rooms should exist');
      }
    }
    
    return totalSyncedRooms + totalSyncedElements;
  };

  const handleFileRepair = () => {
    setShowFileRepairDialog(true);
  };

  const handleApplyFileRepair = (repairedWalls: WallWithTempId[], repairedElements: FloorPlanElement[], repairedRoomLabels: Record<string, string>) => {
    // Record the current state for undo before applying repairs
    recordUndoState();
    
    // Apply the repaired walls and elements
    const updatedFloorPlans = {
      ...floorPlans,
              [currentFloor]: repairedWalls.map(({ tempId, ...wall }) => ({
          ...wall,
          // Preserve split wall metadata
          ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
          ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
          ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
          ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
          ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
          ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector })
        }))
    };
    
    setFloorPlans(updatedFloorPlans);
    setElements(repairedElements);
    
    // Apply the repaired room labels
    setRoomCustomLabels(repairedRoomLabels);
    
    // Clear any selection states that might reference old wall IDs
    setSelectedWall(null);
    setSelectedRoom(null);
    setSelectedElement(null);
    setTempWalls(null);
    setIsResizing(false);
    setResizeHandle(null);
    
    // Mark floor plan as changed to trigger autosave and ensure permanent storage
    setHasFloorPlanChanges(true);
    
    // OPTIMIZATION: Use scheduled redraw instead of forced timeout
    scheduleCanvasDraw(true); // High priority for file repair completion
    
    // Trigger autosave to save the repaired floor plan permanently
    setTimeout(() => {
      autosave.markAsChanged();
    }, 200);
    
    console.log('[FileRepair] Successfully applied repairs and triggered autosave:', {
      wallsCount: repairedWalls.length,
      elementsCount: repairedElements.length
    });
  };

  const handleCloseElementInfoModal = () => {
    setIsElementInfoModalOpen(false);
    setElementToView(null);
    setSelectedElement(null);
  };

  const handleEditElement = (element: FloorPlanElement) => {
    // Handle editing different element types
    switch (element.type) {
      case 'radiator':
        // 🏢 ESTATE MODE: Skip radiator modal
        console.log('🏢 ESTATE: Radiator edit disabled for estate floorplan');
        break;
      case 'boiler':
        // 🏢 ESTATE MODE: Skip boiler modal
        console.log('🏢 ESTATE: Boiler edit disabled for estate floorplan');
        break;
      case 'window':
        // 🏢 ESTATE MODE: Skip window modal
        console.log('🏢 ESTATE: Window edit disabled for estate floorplan');
        break;
      case 'door':
        setDoorToEdit(element as DoorElement);
        setIsDoorPromptOpen(true);
        break;
      default:
        console.log('Edit not implemented for element type:', element.type);
        break;
    }
  };

  // Handle radiator type sync from site notes to floor plan elements
  const handleRadiatorTypeSync = (roomName: string, newRadiatorType: string) => {
    console.log(`🔄 Syncing radiator type for room "${roomName}" to ${newRadiatorType}`);
    
    // Calculate current rooms to find radiators by position
    const currentFloorRooms = labelsOnlyOperation.current ? [] : calculateRooms(currentWalls, roomCustomLabels, 'geometryUpdate', currentFloor);
    const targetRoom = currentFloorRooms.find(room => room.name === roomName);
    
    if (!targetRoom) {
      console.log(`⚠️ Room "${roomName}" not found on floor ${currentFloor}`);
      return;
    }
    
    // Find radiator elements in the specified room by position
    const radiatorElements = elements.filter(element => {
      if (element.type !== 'radiator' || element.floor !== currentFloor) return false;
      
      const radiator = element as RadiatorElement;
      // Check if radiator position is inside the target room
      return isPointInsideRoom(radiator.position, targetRoom.walls);
    }) as RadiatorElement[];
    
    if (radiatorElements.length > 0) {
      console.log(`📡 Found ${radiatorElements.length} radiator(s) to update in room "${roomName}"`);
      
      // Update radiator type for all radiators in the room
      recordUndoState();
      const updatedElements = elements.map(element => {
        if (element.type === 'radiator' && element.floor === currentFloor) {
          const radiator = element as RadiatorElement;
          if (isPointInsideRoom(radiator.position, targetRoom.walls)) {
            console.log(`🔧 Updating radiator ${element.id} from ${radiator.radiatorType} to ${newRadiatorType}`);
            return {
              ...element,
              radiatorType: newRadiatorType as RadiatorType
            };
          }
        }
        return element;
      });
      
      setElements(updatedElements);
      setHasFloorPlanChanges(true);
      autosave.markAsChanged();
    } else {
      console.log(`⚠️ No radiators found in room "${roomName}" to sync type`);
    }
  };

  // 🪟 NEW: Handle window deletion from site notes dimensions section
  const handleDeleteWindowFromSiteNotes = (floorId: number, roomId: number, windowId: number) => {
    console.log(`🪟 Deleting window from site notes: Floor ${floorId}, Room ${roomId}, Window ${windowId}`);
    
    // Find the room name from site notes data
    const targetFloor = siteNotesData.floors.find((f: any) => f.id === floorId);
    if (!targetFloor) {
      console.warn(`Floor ${floorId} not found when deleting window`);
      return;
    }
    
    const targetRoom = targetFloor.rooms.find((r: any) => r.id === roomId);
    if (!targetRoom) {
      console.warn(`Room ${roomId} not found when deleting window`);
      return;
    }
    
    const roomName = targetRoom.name;
    console.log(`🪟 Found room name: "${roomName}" for window deletion`);
    
    // Find the window in the site notes to get its properties for matching
    // Handle single windows (stored in individual fields) vs multiple windows (stored in array)
    let windowToDelete = null;
    
    if (windowId === 1 && (!targetRoom.windows || targetRoom.windows.length === 0)) {
      // This is likely a single window stored in individual fields
      // Check for window dimensions OR glazing data (windows can exist with only glazing info)
      const hasWindowDimensions = targetRoom.windowWidth || targetRoom.windowHeight;
      const hasGlazingData = targetRoom.windowGlazingGap || targetRoom.windowGlazingType || targetRoom.windowCategory;
      
      if (hasWindowDimensions || hasGlazingData) {
        windowToDelete = {
          id: 1,
          width: (targetRoom as any).windowWidth || '',
          height: (targetRoom as any).windowHeight || '',
          trickleVents: (targetRoom as any).windowTrickleVents,
          orientation: (targetRoom as any).windowOrientation,
          glazingGap: (targetRoom as any).windowGlazingGap,
          glazingType: (targetRoom as any).windowGlazingType,
          category: (targetRoom as any).windowCategory
        };
        console.log(`🪟 Found single window in individual fields (dimensions: ${hasWindowDimensions}, glazing: ${hasGlazingData}):`, windowToDelete);
      }
    } else {
      // Regular multiple windows stored in array
      windowToDelete = targetRoom.windows?.find((w: any) => w.id === windowId);
    }
    
    if (!windowToDelete) {
      console.warn(`Window ${windowId} not found in site notes for deletion (neither in windows array nor individual fields)`);
      return;
    }
    
    // Find the corresponding window on the floor plan canvas
    // We need to find windows in the room that match the window being deleted
    const currentRooms = calculateRooms(currentWalls, roomCustomLabels);
    const floorPlanRoom = currentRooms.find(room => room.name === roomName);
    
    if (!floorPlanRoom) {
      console.warn(`Floor plan room "${roomName}" not found for window deletion`);
      return;
    }
    
    // Try to find windows in this room on the floor plan canvas
    const roomWindows = elements.filter(el => {
      if (el.type !== 'window') return false;
      const windowEl = el as any;
      
      // Check if window is on the correct floor
      if (windowEl.floor !== floorId) return false;
      
      // Check if window is in this room using enhanced detection for split walls
      const roomContainingWindow = findElementRoomWithConsolidation(windowEl.position, currentRooms, 'window');
      return roomContainingWindow && roomContainingWindow.name === roomName;
    });
    
    console.log(`🪟 Found ${roomWindows.length} windows in room "${roomName}" on floor ${floorId}`);
    
    // Try to match the window by dimensions if available
    let windowToDeleteFromCanvas = null;
    
    if (windowToDelete.width && windowToDelete.height) {
      // Match by dimensions
      const targetWidth = parseFloat(windowToDelete.width);
      const targetHeight = parseFloat(windowToDelete.height);
      
      windowToDeleteFromCanvas = roomWindows.find(w => {
        const windowEl = w as any;
        return Math.abs((windowEl.width || 0) - targetWidth) < 1 && 
               Math.abs((windowEl.height || 0) - targetHeight) < 1;
      });
      
      if (windowToDeleteFromCanvas) {
        console.log(`🎯 Matched window by dimensions: ${targetWidth}x${targetHeight}cm`);
      }
    }
    
    // If no match by dimensions, delete the first window in the room (for windows without dimensions)
    if (!windowToDeleteFromCanvas && roomWindows.length > 0) {
      // Use the window index from site notes to match the canvas window
      const windowIndex = windowId - 1; // Site notes IDs are 1-based, array indices are 0-based
      if (windowIndex >= 0 && windowIndex < roomWindows.length) {
        windowToDeleteFromCanvas = roomWindows[windowIndex];
        console.log(`🎯 Matched window by index: ${windowIndex + 1}`);
      } else {
        // Fallback to first window
        windowToDeleteFromCanvas = roomWindows[0];
        console.log(`🎯 Using first window as fallback`);
      }
    }
    
    if (!windowToDeleteFromCanvas) {
      console.warn(`No matching window found on floor plan canvas for deletion`);
      return;
    }
    
    // Remove the window from the floor plan canvas
    const windowIdToDelete = (windowToDeleteFromCanvas as any).id;
    const updatedElements = elements.filter(el => (el as any).id !== windowIdToDelete);
    setElements(updatedElements);
    
    // Update window counts
    setWindowCount(prev => ({
      single: prev.single > 0 ? prev.single - 1 : 0,
      double: prev.double
    }));
    
    // Use setTimeout to ensure state update has completed before sync and save
    setTimeout(() => {
      // Sync the removal to room data
      if (roomSync) {
        const allWindows = updatedElements.filter(el => el.type === 'window') as unknown as WindowElement[];
        
        // Use smart sync to handle window removal (current floor only)
        roomSync.smartSyncWindows(allWindows, currentRooms, floorId);
        
        // Sync overall window counts across ALL floors for site notes
        roomSync.syncAllFloorsWindows(allWindows, floorPlans, roomCustomLabels);
        
        console.log(`🪟 Window deletion sync completed - Remaining windows: ${allWindows.length}`);
      }
      
      // Show sync indicator
      if (roomName) {
        setSyncedRoomName(roomName);
        setShowRoomSyncIndicator(true);
      }
      
      // Save to undo stack and trigger autosave
      recordUndoState();
      
      console.log(`🪟 ✅ Window deleted from floor plan canvas and synced to site notes`);
    }, 100); // 100ms delay to ensure state updates complete
  };

  // Handle radiator deletion from site notes (callback from SurveyorSiteNotes)
  const handleDeleteRadiatorFromSiteNotes = (floorId: number, roomId: number, radiatorId: number) => {
    console.log(`🔥 Deleting radiator ${radiatorId} from room ${roomId} on floor ${floorId}`);
    
    // Find the target floor and room in site notes
    const targetFloor = siteNotesData.floors.find((f: any) => f.id === floorId);
    if (!targetFloor) {
      console.warn(`Floor ${floorId} not found in site notes`);
      return;
    }
    
    const targetRoom = targetFloor.rooms.find((r: any) => r.id === roomId);
    if (!targetRoom) {
      console.warn(`Room ${roomId} not found on floor ${floorId}`);
      return;
    }
    
    const roomName = targetRoom.name;
    console.log(`🔥 Found room name: "${roomName}" for radiator deletion`);
    
    // Find the radiator in the site notes
    const radiatorToDelete = targetRoom.radiators?.find((r: any) => r.id === radiatorId);
    
    if (!radiatorToDelete) {
      console.warn(`Radiator ${radiatorId} not found in site notes for deletion`);
      return;
    }
    
    // Find matching radiators in the floor plan canvas
    const roomRadiators = elements.filter(el => {
      if (el.type !== 'radiator') return false;
      const radiatorEl = el as any;
      
      // Check if radiator is on the correct floor
      if (radiatorEl.floor !== floorId) return false;
      
      // Check if radiator is in this room
      const roomContainingRadiator = findRoomAtPoint(radiatorEl.position, currentRooms);
      return roomContainingRadiator && roomContainingRadiator.name === roomName;
    });
    
    console.log(`🔥 Found ${roomRadiators.length} radiators in room "${roomName}" on floor ${floorId}`);
    
    // Try to match the radiator by type and output if available
    let radiatorToDeleteFromCanvas = null;
    
    if (radiatorToDelete.type) {
      // Match by type 
      const targetType = radiatorToDelete.type;
      
      radiatorToDeleteFromCanvas = roomRadiators.find(r => {
        const radiatorEl = r as any;
        return radiatorEl.radiatorType === targetType;
      });
      
      if (radiatorToDeleteFromCanvas) {
        console.log(`🎯 Matched radiator by type: ${targetType}`);
      }
    }
    
    // If no match by properties, use index-based matching
    if (!radiatorToDeleteFromCanvas && roomRadiators.length > 0) {
      // Use the radiator index from site notes to match the canvas radiator
      const radiatorIndex = radiatorId - 1; // Site notes IDs are 1-based, array indices are 0-based
      if (radiatorIndex >= 0 && radiatorIndex < roomRadiators.length) {
        radiatorToDeleteFromCanvas = roomRadiators[radiatorIndex];
        console.log(`🎯 Matched radiator by index: ${radiatorIndex + 1}`);
      } else {
        // Fallback to first radiator
        radiatorToDeleteFromCanvas = roomRadiators[0];
        console.log(`🎯 Using first radiator as fallback`);
      }
    }
    
    if (!radiatorToDeleteFromCanvas) {
      console.warn(`No matching radiator found on floor plan canvas for deletion`);
      return;
    }
    
    // Remove the radiator from the floor plan canvas
    const radiatorIdToDelete = (radiatorToDeleteFromCanvas as any).id;
    const updatedElements = elements.filter(el => (el as any).id !== radiatorIdToDelete);
    setElements(updatedElements);
    
    // Use setTimeout to ensure state update has completed before sync and save
    setTimeout(() => {
      // Sync the removal to room data
      if (roomSync) {
        const allRadiators = updatedElements.filter(el => el.type === 'radiator') as unknown as RadiatorElement[];
        
        // Use smart sync to handle radiator removal (current floor only)
        roomSync.smartSyncRadiators(allRadiators, currentRooms, floorId);
        
        console.log(`🔥 Radiator deletion sync completed - Remaining radiators: ${allRadiators.length}`);
      }
      
      // Show sync indicator
      if (roomName) {
        setSyncedRoomName(roomName);
        setShowRoomSyncIndicator(true);
      }
      
      // Save to undo stack and trigger autosave
      recordUndoState();
      
      console.log(`🔥 ✅ Radiator deleted from floor plan canvas and synced to site notes`);
    }, 100); // 100ms delay to ensure state updates complete
  };

  // Use refs to always get the current values for saving
  const propertyAddressRef = useRef(propertyAddress);
  const propertyOrientationRef = useRef(propertyOrientation);
  const siteNotesDataRef = useRef(siteNotesData);
  const floorPlansRef = useRef(floorPlans);
  const elementsRef = useRef(elements);
  const textBoxesRef = useRef(textBoxes);
  const roomCustomLabelsRef = useRef(roomCustomLabels);
  const roomLabelPositionsRef = useRef(roomLabelPositions); // Add ref for room label positions
  const windowCountRef = useRef(windowCount);
  const lightCountRef = useRef(lightCount);
  const currentFloorRef = useRef(currentFloor);

  // Keep refs updated with current state
  useEffect(() => {
    propertyAddressRef.current = propertyAddress;
  }, [propertyAddress]);

  useEffect(() => {
    propertyOrientationRef.current = propertyOrientation;
  }, [propertyOrientation]);

  useEffect(() => {
    siteNotesDataRef.current = siteNotesData;
  }, [siteNotesData]);

  useEffect(() => {
    floorPlansRef.current = floorPlans;
  }, [floorPlans]);

  useEffect(() => {
    elementsRef.current = elements;
  }, [elements]);

  useEffect(() => {
    textBoxesRef.current = textBoxes;
  }, [textBoxes]);

  useEffect(() => {
    roomCustomLabelsRef.current = roomCustomLabels;
  }, [roomCustomLabels]);

  useEffect(() => {
    roomLabelPositionsRef.current = roomLabelPositions; // Keep ref updated
  }, [roomLabelPositions]);

  useEffect(() => {
    windowCountRef.current = windowCount;
  }, [windowCount]);

  useEffect(() => {
    lightCountRef.current = lightCount;
  }, [lightCount]);

  useEffect(() => {
    currentFloorRef.current = currentFloor;
  }, [currentFloor]);

  const handleSiteNotesChange = useCallback((newSiteNotesData: SiteNotesData) => {
    // Set the data first
    setSiteNotesData(newSiteNotesData);
    
    // Mark that site notes have unsaved changes
    setHasSiteNotesChanges(true);
    
    // Get the current address from ref (always current value)
    const currentAddress = propertyAddressRef.current;
    
    // Ensure autosave is enabled if we have a valid address
    if (currentAddress && currentAddress.trim().length >= 3 && !autosave.isAutosaveEnabled) {
      autosave.enableAutosave({ address: currentAddress });
      // Give autosave a moment to enable, then trigger save
      setTimeout(() => {
        autosave.markAsChanged();
      }, 100);
    } else {
      autosave.markAsChanged(); // Trigger autosave when site notes change
    }
  }, [autosave]); // No dependencies to avoid stale closures

  const handleSiteNotesSave = useCallback(async () => {
    console.log('[FloorPlanCreator] Save & Close triggered - current siteNotesData:', siteNotesData);
    
    try {
      // Mark that site notes have changes to ensure they're included in the save
      setHasSiteNotesChanges(true);
      
      // Trigger autosave to capture any final changes
      autosave.markAsChanged();
      
      // Wait a brief moment for the autosave to process the latest changes
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Trigger manual save when user clicks Save & Close
      await autosave.triggerManualSave();
      
      console.log('[FloorPlanCreator] Save & Close completed successfully');
    } catch (error) {
      console.error('Failed to save site notes:', error);
      // Don't close the modal if save failed
      return;
    }
    setShowSiteNotes(false);
  }, [autosave, siteNotesData]);



  const [addressInput, setAddressInput] = useState('');




  // Auto-update property orientation when front door is set
  useEffect(() => {
    const frontDoorElement = findFrontDoor(elements);
    if (frontDoorElement && !orientationHasBeenSet) {
      const newOrientation = autoUpdatePropertyOrientation(
        frontDoorElement,
        currentWalls,
        propertyOrientation,
        orientationHasBeenSet
      );
      
      if (newOrientation !== propertyOrientation) {
        setPropertyOrientation(newOrientation);
        console.log(`Auto-updated property orientation to ${newOrientation}° based on front door`);
      }
    }
  }, [elements, currentWalls, propertyOrientation, orientationHasBeenSet]);

  // Function to calculate orientation for a single new window
  const calculateWindowOrientationIfReady = useCallback((window: WindowElement): WindowElement => {
    // Check if we have the required data for orientation calculation
    const hasFrontDoor = elements.some(el => el.type === 'door' && (el as DoorElement).isFrontDoor);
    
    if (!hasFrontDoor || !orientationHasBeenSet) {
      console.log('🧭 Skipping orientation for new window - missing front door or orientation not set');
      return window; // Return window without orientation
    }
    
    // Find the front door element
    const frontDoorElement = elements.find(el => el.type === 'door' && (el as DoorElement).isFrontDoor) as DoorElement;
    
    // Find the wall for this window
    const floorWalls = floorPlans[window.floor] || [];
    const wall = floorWalls.find(w => (w as any).stableId === window.wallId);
    
    if (!wall) {
      console.warn(`🧭 Warning: Could not find wall for new window ${window.id}`);
      return window;
    }
    
    // Find the front door wall
    let frontDoorWall: Wall | undefined;
    if (frontDoorElement && frontDoorElement.wallId) {
      frontDoorWall = floorWalls.find(w => (w as any).stableId === frontDoorElement.wallId);
      
      // If not found on current floor, search other floors
      if (!frontDoorWall) {
        for (const [floorNum, walls] of Object.entries(floorPlans)) {
          const foundWall = walls.find(w => (w as any).stableId === frontDoorElement.wallId);
          if (foundWall) {
            frontDoorWall = foundWall;
            break;
          }
        }
      }
    }
    
    const orientation = calculateWindowOrientation(window, wall, propertyOrientation || 0, floorWalls, frontDoorWall);
    console.log(`🧭 Calculated orientation "${orientation}" for new window ${window.id} using front door reference`);
    
    return {
      ...window,
      orientation
    };
  }, [elements, orientationHasBeenSet, floorPlans, propertyOrientation]);

  // Function to recalculate orientations for all windows (called only when needed)
  const recalculateAllWindowOrientations = useCallback(() => {
    // Skip during file loading
    if (isFileLoading) {
      console.log('🧭 Skipping orientation calculation during file loading');
      return;
    }
    
    // Check for front door across ALL floors in the building
    const hasFrontDoor = elements.some(el => el.type === 'door' && (el as DoorElement).isFrontDoor);
    
    // Apply orientation to all windows across all floors
    const updatedElements = autoCalculateWindowOrientationsAllFloors(
      floorPlans,
      elements,
      propertyOrientation || 0,
      hasFrontDoor,
      orientationHasBeenSet
    );
    
    // Update elements with new window orientations
    setElements(updatedElements);
    console.log('🧭 Recalculated orientations for all windows in building');
    
  }, [floorPlans, elements, propertyOrientation, orientationHasBeenSet, isFileLoading]);

  // Only recalculate orientations when property orientation is manually changed
  useEffect(() => {
    // Skip if this is the initial load or during file loading
    if (isFileLoading || propertyOrientation === 0) {
      return;
    }
    
    // Only recalculate if orientation has been manually set (not auto-calculated from door)
    if (orientationHasBeenSet) {
      // Create a key to track this calculation
      const calculationKey = `${propertyOrientation}-${orientationHasBeenSet}-${elements.length}`;
      
      // Skip if we already calculated for this exact state
      if (lastOrientationCalculationRef.current === calculationKey) {
        return;
      }
      
      console.log('🧭 Property orientation manually changed, recalculating all window orientations');
      lastOrientationCalculationRef.current = calculationKey;
      recalculateAllWindowOrientations();
    }
  }, [propertyOrientation, orientationHasBeenSet]); // Removed recalculateAllWindowOrientations to prevent loop

  // Initialize room label positions when rooms change
  useEffect(() => {
    const roomsArray = Object.values(currentRooms);
    const newPositions: Record<string, Point> = {};
    let hasChanges = false;
    
    // 🔧 LABEL DISPLAY FIX: Allow position initialization during label-only operations
    // BUT only for rooms that don't have positions yet (new rooms)
    // This ensures default labels display at room center immediately after room creation
    if (labelsOnlyOperation.current && Object.keys(roomLabelPositions).length > 0) {
      console.log('🔧 LABEL POSITIONS: Checking for new rooms during label-only operation');
      // Continue to initialize positions for new rooms only
    }
    
    // 🐛 DEBUG: Track room label position synchronization
    console.log('🔍 ROOM LABEL POSITIONS DEBUG - BEFORE UPDATE:', {
      currentRoomsCount: roomsArray.length,
      roomCustomLabelsCount: Object.keys(roomCustomLabels).length,
      roomLabelPositionsCount: Object.keys(roomLabelPositions).length,
      roomKeys: roomsArray.map(r => (r.stableId || r.id).substring(0, 20) + '...'),
      labelKeys: Object.keys(roomCustomLabels).map(k => k.substring(0, 20) + '...'),
      positionKeys: Object.keys(roomLabelPositions).map(k => k.substring(0, 20) + '...')
    });
    
    roomsArray.forEach(room => {
      const roomKey = room.stableId || room.id;
      // Only initialize if we don't already have a position for this room
      if (!roomLabelPositions[roomKey] && room.center) {
        newPositions[roomKey] = room.center;
        hasChanges = true;
        console.log('🔍 ADDING NEW ROOM POSITION AT CENTER:', {
          roomKey: roomKey.substring(0, 20) + '...',
          center: room.center,
          hasLabel: !!roomCustomLabels[roomKey],
          roomBounds: room.walls ? `${Math.min(...room.walls.map(w => w.start.x)).toFixed(1)},${Math.min(...room.walls.map(w => w.start.y)).toFixed(1)} -> ${Math.max(...room.walls.map(w => w.end.x)).toFixed(1)},${Math.max(...room.walls.map(w => w.end.y)).toFixed(1)}` : 'no walls'
        });
      }
    });
    
    // Only update if we have new positions to add
    if (hasChanges) {

      setRoomLabelPositions(prev => ({ ...prev, ...newPositions }));
    } else {

    }
  }, [currentWalls, roomCustomLabels, currentRooms]); // Re-run when walls, labels, or rooms change

  // Trigger autosave when room label positions change
  useEffect(() => {
    if (Object.keys(roomLabelPositions).length > 0) {
      setHasFloorPlanChanges(true); // Mark that floor plan has changes
      autosave.markAsChanged(); // Trigger autosave
    }
  }, [roomLabelPositions, autosave]); // Re-run when room label positions change

  // Debug function to diagnose window sync issues (available in console)
  (window as any).debugWindowSync = () => {
    const windows = elements.filter(el => el.type === 'window') as WindowElement[];
    console.log('🔍 Starting window sync debug for current floor...');
    if (roomSync) {
      roomSync.debugAndForceWindowSync(windows, currentRooms, currentFloor);
    } else {
      console.error('❌ Room sync not available');
    }
  };

  // Debug function to clear window sync state (available in console)
  (window as any).clearWindowSyncState = () => {
    if (roomSync) {
      roomSync.clearWindowSyncState();
      console.log('✅ Window sync state cleared');
    } else {
      console.error('❌ Room sync not available');
    }
  };

  // Debug function to get window sync info (available in console)
  (window as any).getWindowSyncInfo = () => {
    if (roomSync) {
      const info = roomSync.getWindowSyncDebugInfo();
      console.log('🔍 Window sync state:', info);
      return info;
    } else {
      console.error('❌ Room sync not available');
      return null;
    }
  };

  // Debug function to test room deletion sync (available in console)
  (window as any).testRoomDeletionSync = () => {
    console.log('🧪 Testing room deletion sync functionality...');
    
    if (!roomSync) {
      console.error('❌ roomSync is not available!');
      return;
    }
    
    // Get current site notes data
    const currentSiteNotesData = siteNotesData;
    const floorNumbers = Object.keys(floorPlans).map(Number).sort();
    
    console.log('🔍 Current site notes floors:', currentSiteNotesData.floors.map((f: any) => ({
      id: f.id,
      roomCount: f.rooms.length,
      rooms: f.rooms.map((r: any) => ({ name: r.name, hasData: !!(r.length || r.width) }))
    })));
    
    // Calculate current rooms from floor plan
    floorNumbers.forEach(floorNum => {
      const wallsForFloor = floorPlans[floorNum] || [];
      if (wallsForFloor.length === 0) return;
      
      try {
        const roomsForFloor = calculateRooms(wallsForFloor, roomCustomLabels, 'explicitRoomCreation', floorNum);
        console.log(`🏠 Floor ${floorNum} current rooms:`, roomsForFloor.map(r => r.name));
        
        // Compare with site notes
        const siteNotesFloor = currentSiteNotesData.floors.find((f: any) => f.id === floorNum);
        if (siteNotesFloor) {
          const currentRoomNames = new Set(roomsForFloor.map(r => r.name));
          const deletedRooms = siteNotesFloor.rooms.filter((sr: any) => sr.name && !currentRoomNames.has(sr.name));
          
          if (deletedRooms.length > 0) {
            console.log(`🗑️ Floor ${floorNum} deleted rooms detected:`, deletedRooms.map((r: any) => r.name));
          } else {
            console.log(`✅ Floor ${floorNum} - no deleted rooms detected`);
          }
        }
      } catch (error) {
        console.error(`Failed to test floor ${floorNum}:`, error);
      }
    });
    
    console.log('🧪 Room deletion sync test completed. Check logs above for results.');
  };

  // Debug function to manually clean up orphaned elements (available in console)
  (window as any).cleanupOrphanedElements = (floorNumber = currentFloor) => {
    console.log(`🧹 Cleaning up orphaned elements on floor ${floorNumber}...`);
    
    if (!roomSync) {
      console.error('❌ roomSync is not available!');
      return;
    }
    
    const wallsForFloor = floorPlans[floorNumber] || [];
    if (wallsForFloor.length === 0) {
      console.log(`No walls found on floor ${floorNumber}`);
      return;
    }
    
    try {
      const roomsForFloor = calculateRooms(wallsForFloor, roomCustomLabels, 'explicitRoomCreation', floorNumber);
      const floorElements = elements.filter(el => (el as any).floor === floorNumber);
      
      console.log(`🔍 Found ${roomsForFloor.length} rooms and ${floorElements.length} elements on floor ${floorNumber}`);
      
      roomSync.syncOrphanedElementsCleanup(floorElements, roomsForFloor, floorNumber);
      
      console.log(`✅ Orphaned elements cleanup completed for floor ${floorNumber}`);
    } catch (error) {
      console.error(`Failed to cleanup orphaned elements on floor ${floorNumber}:`, error);
    }
  };

  // Handle comprehensive element sync to fix phantom elements
  const handleSyncElements = useCallback(() => {
    console.log('🔄 Starting comprehensive element sync to fix phantom elements...');
    
    if (!roomSync) {
      console.error('❌ roomSync is not available!');
      return;
    }
    
    try {
      // Sync all floors to fix phantom elements
      Object.keys(floorPlans).forEach(floorNumStr => {
        const floorNum = parseInt(floorNumStr);
        const wallsForFloor = floorPlans[floorNum] || [];
        
        if (wallsForFloor.length === 0) return;
        
        const roomsForFloor = calculateRooms(wallsForFloor, roomCustomLabels, 'explicitRoomCreation', floorNum);
        const floorElements = elements.filter(el => (el as any).floor === floorNum);
        
        console.log(`🔍 Floor ${floorNum}: ${roomsForFloor.length} rooms, ${floorElements.length} elements`);
        
        // Step 1: Clean up orphaned elements first
        roomSync.syncOrphanedElementsCleanup(floorElements, roomsForFloor, floorNum);
        
        // Step 2: Re-sync all elements to correct rooms (force sync)
        const windowElements = floorElements.filter(el => el.type === 'window') as WindowElement[];
        const radiatorElements = floorElements.filter(el => el.type === 'radiator') as RadiatorElement[];
        const lightElements = floorElements.filter(el => el.type === 'light') as LightElement[];
        
        if (windowElements.length > 0) {
          roomSync.smartSyncWindows(windowElements, roomsForFloor, floorNum, true); // force sync
        }
        
        if (radiatorElements.length > 0) {
          roomSync.smartSyncRadiators(radiatorElements, roomsForFloor, floorNum, true); // force sync
        }
        
        if (lightElements.length > 0) {
          console.log(`🔄 SYNC_ELEMENTS: Re-syncing ${lightElements.length} lights on floor ${floorNum} after room deletion`);
          roomSync.smartSyncLights(lightElements, roomsForFloor, floorNum, true); // force sync
        }
      });
      
      // Step 3: Aggregate final light counts after all floors are synced
      console.log('🔄 SYNC_ELEMENTS: Aggregating light totals after comprehensive sync');
      roomSync.syncAllFloorsLightsToAdditionalInfo();
      
      console.log('✅ Comprehensive element sync completed - phantom elements should be fixed');
    } catch (error) {
      console.error('❌ Failed to sync elements:', error);
    }
  }, [roomSync, floorPlans, elements, roomCustomLabels]);

  // Debug function to recalculate all window orientations (available in console)
  (window as any).recalculateWindowOrientations = () => {
    console.log('🧭 Recalculating window orientations for entire building...');
    const hasFrontDoor = elements.some(el => el.type === 'door' && (el as DoorElement).isFrontDoor);
    
    const updatedElements = autoCalculateWindowOrientationsAllFloors(
      floorPlans,
      elements,
      propertyOrientation || 0,
      hasFrontDoor,
      orientationHasBeenSet
    );
    
    setElements(updatedElements);
    
    const windowCount = updatedElements.filter(el => el.type === 'window').length;
    console.log(`✅ Recalculated orientations for ${windowCount} windows across all floors`, {
      hasFrontDoor,
      orientationHasBeenSet,
      propertyOrientation,
      totalFloors: Object.keys(floorPlans).length
    });
    
    // Also trigger a sync to update site notes for current floor
    if (roomSync) {
      const currentFloorWindows = updatedElements.filter(el => 
        el.type === 'window' && (el as any).floor === currentFloor
      ) as WindowElement[];
      roomSync.smartSyncWindows(currentFloorWindows, currentRooms, currentFloor, true);
    }
  };

  // Debug function to test extrusion orientation detection (available in console)
  (window as any).testExtrusionOrientations = () => {
    console.log('🧭 Testing extrusion orientation detection...');
    
    const allWindows = elements.filter(el => el.type === 'window') as WindowElement[];
    const frontDoorElement = elements.find(el => el.type === 'door' && (el as DoorElement).isFrontDoor) as DoorElement;
    
    // Find front door wall
    let frontDoorWall: Wall | undefined;
    if (frontDoorElement && frontDoorElement.wallId) {
      for (const [floorNum, floorWalls] of Object.entries(floorPlans)) {
        const wall = floorWalls.find(w => (w as any).stableId === frontDoorElement.wallId);
        if (wall) {
          frontDoorWall = wall;
          break;
        }
      }
    }
    
    const extrusionWindows = allWindows.filter(window => {
      const floorWalls = floorPlans[window.floor] || [];
      const wall = floorWalls.find(w => (w as any).stableId === window.wallId);
      return wall && ((wall as any).isMainExtrusionSegment || (wall as any).isExtrusionConnector);
    });
    
    console.log(`🔍 Found ${extrusionWindows.length} windows on extrusion walls out of ${allWindows.length} total windows`);
    
    extrusionWindows.forEach(window => {
      const floorWalls = floorPlans[window.floor] || [];
      const wall = floorWalls.find(w => (w as any).stableId === window.wallId);
      
      if (wall) {
        const allBuildingWalls = Object.values(floorPlans).flat();
        const orientation = calculateWindowOrientation(
          window, 
          wall, 
          propertyOrientation || 0, 
          allBuildingWalls, 
          frontDoorWall
        );
        
        console.log(`🪟 Extrusion Window Analysis:`, {
          windowId: window.id,
          floor: window.floor,
          currentOrientation: window.orientation,
          recalculatedOrientation: orientation,
          orientationChanged: window.orientation !== orientation,
          extrusionWallType: (wall as any).isMainExtrusionSegment ? 'main segment' : 'connector',
          wallAngle: calculateWallAngle(wall).toFixed(1) + '°',
          parentWallId: (wall as any).parentWallId,
          parentRoomIds: (wall as any).parentRoomIds
        });
      }
    });
    
    if (extrusionWindows.length === 0) {
      console.log('✅ No windows found on extrusion walls - all orientations should be correct');
    } else {
      console.log(`📊 Extrusion orientation test complete. Check individual window logs above for details.`);
    }
    
    return {
      totalWindows: allWindows.length,
      extrusionWindows: extrusionWindows.length,
      frontDoorWallFound: !!frontDoorWall,
      propertyOrientation: propertyOrientation || 0
    };
  };

  // Debug function to identify and remove phantom windows
  (window as any).findPhantomWindows = () => {
    console.log('🔍 Analyzing phantom windows...');
    
    const allWindows = elements.filter(el => el.type === 'window') as WindowElement[];
    const phantomWindows: Array<{ window: WindowElement; reason: string }> = [];
    
    allWindows.forEach(window => {
      const floorWalls = floorPlans[window.floor] || [];
      const wall = floorWalls.find(w => (w as any).stableId === window.wallId);
      
      if (!wall) {
        phantomWindows.push({
          window,
          reason: `Wall ID "${window.wallId}" not found on floor ${window.floor}`
        });
      }
    });
    
    console.log(`Found ${phantomWindows.length} phantom windows:`, phantomWindows.map(p => ({
      id: p.window.id,
      floor: p.window.floor,
      position: p.window.position,
      reason: p.reason
    })));
    
    return phantomWindows;
  };

  // Debug function to remove phantom windows
  (window as any).removePhantomWindows = () => {
    console.log('🧹 Removing phantom windows...');
    
    const allWindows = elements.filter(el => el.type === 'window') as WindowElement[];
    const validWindows: WindowElement[] = [];
    const removedWindows: WindowElement[] = [];
    
    allWindows.forEach(window => {
      const floorWalls = floorPlans[window.floor] || [];
      const wall = floorWalls.find(w => (w as any).stableId === window.wallId);
      
      if (wall) {
        validWindows.push(window);
      } else {
        removedWindows.push(window);
        console.log(`🗑️ Removing phantom window: ${window.id} at (${window.position.x.toFixed(1)}, ${window.position.y.toFixed(1)}) on floor ${window.floor}`);
      }
    });
    
    if (removedWindows.length > 0) {
      // Update elements array to remove phantom windows
      const updatedElements = elements.filter(el => {
        if (el.type === 'window') {
          return validWindows.some(vw => vw.id === (el as WindowElement).id);
        }
        return true;
      });
      
      setElements(updatedElements);
      
      // Trigger re-sync after cleanup
      if (roomSync) {
        const currentFloorWindows = validWindows.filter(w => w.floor === currentFloor);
        roomSync.smartSyncWindows(currentFloorWindows, currentRooms, currentFloor, true);
        roomSync.syncAllFloorsWindows(validWindows, floorPlans, roomCustomLabels);
      }
      
      console.log(`✅ Removed ${removedWindows.length} phantom windows. Valid windows remaining: ${validWindows.length}`);
    } else {
      console.log('✅ No phantom windows found - all windows have valid walls');
    }
    
    return { removed: removedWindows.length, remaining: validWindows.length };
  };

  // 🔄 NEW: Force window sync function for debugging stale data issues
  (window as any).forceWindowSync = () => {
    console.log('🔄 Force window sync triggered...');
    
    if (!roomSync) {
      console.warn('⚠️ roomSync not available');
      return;
    }
    
    const allWindows = elements.filter(el => el.type === 'window') as unknown as WindowElement[];
    
    console.log('🔍 Current window data:', {
      totalWindows: allWindows.length,
      windowsWithTrickleVents: allWindows.filter(w => (typeof w.trickleVents === 'number' && w.trickleVents > 0)).length,
      windowsWithoutTrickleVents: allWindows.filter(w => w.trickleVents === 0 || w.trickleVents === undefined).length,
      windowsWithUndefinedTrickleVents: allWindows.filter(w => w.trickleVents === undefined).length,
      windowsByFloor: Array.from(new Set(allWindows.map(w => w.floor))).map(floor => ({
        floor,
        count: allWindows.filter(w => w.floor === floor).length,
        trickleVentDetails: allWindows.filter(w => w.floor === floor).map(w => ({
          id: w.id,
          trickleVents: w.trickleVents
        }))
      }))
    });
    
    // Force fresh sync
    roomSync.syncAllFloorsWindows(allWindows, floorPlans, roomCustomLabels);
    
    // Log the current site notes data after sync
    setTimeout(() => {
      console.log('🔍 Site notes data after force sync:', {
        totalWindows: siteNotesData?.formData?.totalWindows || 'NOT_SET',
        windowsMissingTrickleVents: siteNotesData?.formData?.windowsMissingTrickleVents || 'NOT_SET',
        missingTrickleVentsLocation: siteNotesData?.formData?.missingTrickleVentsLocation || 'NOT_SET'
      });
    }, 100);
    
    console.log('✅ Force window sync completed');
    
    return {
      totalWindows: allWindows.length,
      syncCompleted: true
    };
  };

  // Debug function to test improved window sync for windows without dimensions
  (window as any).testWindowSyncFix = () => {
    console.log('🪟 Testing improved window sync for windows without dimensions...');
    
    const allWindows = elements.filter(el => el.type === 'window') as WindowElement[];
    const windowsWithDimensions = allWindows.filter(w => w.width && w.height);
    const windowsWithoutDimensions = allWindows.filter(w => !w.width || !w.height);
    
    console.log(`📊 Window Analysis:`, {
      totalWindows: allWindows.length,
      withDimensions: windowsWithDimensions.length,
      withoutDimensions: windowsWithoutDimensions.length,
      windowsByFloor: allWindows.reduce((acc, w) => {
        acc[w.floor] = (acc[w.floor] || 0) + 1;
        return acc;
      }, {} as Record<number, number>)
    });
    
    if (windowsWithoutDimensions.length > 0) {
      console.log('🪟 Windows without dimensions:', windowsWithoutDimensions.map(w => ({
        id: w.id,
        floor: w.floor,
        position: w.position,
        wallId: w.wallId,
        orientation: w.orientation,
        hasWall: !!floorPlans[w.floor]?.find(wall => (wall as any).stableId === w.wallId)
      })));
      
      // Test the sync to see if placeholders are created
      if (roomSync) {
        console.log('🔄 Testing sync with improved logic...');
        roomSync.smartSyncWindows(allWindows, currentRooms, currentFloor);
        console.log('✅ Sync completed - check site notes for preserved windows');
        
        // Also sync all floors to test comprehensive behavior
        roomSync.syncAllFloorsWindows(allWindows, floorPlans, roomCustomLabels);
        console.log('✅ All-floors sync completed');
      } else {
        console.warn('⚠️ roomSync not available - cannot test sync functionality');
      }
    } else {
      console.log('ℹ️ All windows have dimensions - no placeholders needed');
    }
    
    return {
      totalWindows: allWindows.length,
      withDimensions: windowsWithDimensions.length,
      withoutDimensions: windowsWithoutDimensions.length,
      syncAvailable: !!roomSync
    };
  };

  // Debug function to test window editing field population
  (window as any).testWindowEditingFields = () => {
    console.log('🪟 Testing window editing field population...');
    
    const allWindows = elements.filter(el => el.type === 'window') as WindowElement[];
    
    if (allWindows.length === 0) {
      console.log('ℹ️ No windows found to test editing');
      return;
    }
    
    console.log(`📊 Found ${allWindows.length} windows to test:`, allWindows.map(w => ({
      id: w.id,
      floor: w.floor,
      position: w.position,
      currentDimensions: {
        width: w.width,
        height: w.height,
        trickleVents: w.trickleVents,
        orientation: w.orientation
      },
      hasAllDimensions: !!(w.width && w.height),
      hasPartialDimensions: !!(w.width || w.height)
    })));
    
    // Test editing the first window with dimensions
    const windowWithDimensions = allWindows.find(w => w.width && w.height);
    if (windowWithDimensions) {
      console.log('🔧 To test editing, click on this window:', {
        id: windowWithDimensions.id,
        position: windowWithDimensions.position,
        currentValues: {
          width: windowWithDimensions.width,
          height: windowWithDimensions.height,
          trickleVents: windowWithDimensions.trickleVents
        }
      });
      console.log('✅ When the edit dialog opens, these values should be pre-filled in the form fields');
    } else {
      console.log('⚠️ No windows with dimensions found - create some windows with dimensions first to test editing');
    }
    
    return {
      totalWindows: allWindows.length,
      windowsWithDimensions: allWindows.filter(w => w.width && w.height).length,
      windowsWithPartialDimensions: allWindows.filter(w => (w.width || w.height) && !(w.width && w.height)).length,
      windowsWithoutDimensions: allWindows.filter(w => !w.width && !w.height).length
    };
  };


  // 🪟 NEW: Handle window dimension sync from site notes to floor plan
  const handleFloorDelete = (floorLevel: string, buildingType: string) => {
    console.log(`🗑️ Floor deleted from site notes: ${buildingType} ${floorLevel}`);
    
    // Find all highlighted areas that were saved to this floor and unmark them
    setElements(prev => prev.map(el => {
      if (el.type === 'highlight' && (el as HighlightArea).savedToNotes) {
        // For now, we'll unmark all saved highlights when any floor is deleted
        // In a more sophisticated implementation, we could track which floor each highlight was saved to
        console.log(`🎨 Unmarking highlighted area ${el.id} due to floor deletion`);
        return { ...el, savedToNotes: false };
      }
      return el;
    }));
    
    // Mark changes for autosave
    setHasFloorPlanChanges(true);
    autosave.markAsChanged();
  };

  // 🔧 ACCURATE HIGHLIGHTER FORMULAS: Using shared calculateHighlightMetrics for compatibility with HighlightCalculationsPopup component

  const handleWindowDimensionSyncFromSiteNotes = (roomName: string, floorId: number, windowIndex: number, dimensions: { width: number; height: number; trickleVents: number | boolean; isDraughtProofed?: boolean; windowType?: 'pvc' | 'wood' | 'metal'; orientation?: string; glazingGap?: string; glazingType?: string; windowCategory?: string; condition?: 'good' | 'average' | 'poor' }) => {
    console.log(`🪟 Syncing window dimensions from site notes: room "${roomName}", floor ${floorId}, window ${windowIndex}`, dimensions);
    
    // Find the room on the specified floor
    const floorWalls = floorPlans[floorId] || [];
    const roomsOnFloor = calculateRooms(floorWalls, roomCustomLabels, 'labelPreservation', floorId);
    const targetRoom = roomsOnFloor.find(room => room.name === roomName);
    
    if (!targetRoom) {
      console.warn(`🪟 Room "${roomName}" not found on floor ${floorId} for window dimension sync`);
      return;
    }
    
    // Find windows in this room on this floor
    const roomWindows = elements.filter(el => {
      if (el.type !== 'window') return false;
      const windowEl = el as any;
      
      // Check if window is on the correct floor
      if (windowEl.floor !== floorId) return false;
      
      // Check if window is in this room using enhanced detection for split walls
      const roomContainingWindow = findElementRoomWithConsolidation(windowEl.position, roomsOnFloor, 'window');
      return roomContainingWindow && roomContainingWindow.name === roomName;
    });
    
    if (roomWindows.length === 0) {
      console.warn(`🪟 No windows found in room "${roomName}" on floor ${floorId} for dimension sync`);
      return;
    }
    
    // Get the specific window to update (windowIndex is 0-based)
    const windowToUpdate = roomWindows[windowIndex];
    if (!windowToUpdate) {
      console.warn(`🪟 Window at index ${windowIndex} not found in room "${roomName}" (total windows: ${roomWindows.length})`);
      return;
    }
    
    // Update the window dimensions and all properties
    const updatedElements = elements.map(el => {
      if ((el as any).id === (windowToUpdate as any).id) {
        return { 
          ...el, 
          width: dimensions.width,
          height: dimensions.height, 
          trickleVents: typeof dimensions.trickleVents === 'boolean' ? (dimensions.trickleVents ? 1 : 0) : dimensions.trickleVents,
          isDraughtProofed: dimensions.isDraughtProofed,
          windowType: dimensions.windowType,
          condition: dimensions.condition || (el as any).condition,
          orientation: dimensions.orientation || (el as any).orientation,
          glazingGap: dimensions.glazingGap || (el as any).glazingGap,
          glazingType: dimensions.glazingType || (el as any).glazingType,
          windowCategory: dimensions.windowCategory || (el as any).windowCategory
        };
      }
      return el;
    });
    
    setElements(updatedElements);
    
    // Mark as changed
    setHasFloorPlanChanges(true);
    autosave.markAsChanged();
  };

  // Set up global functions for touch double-tap element editing
  useEffect(() => {
    (window as any).__triggerWindowEdit = (element: WindowElement) => {
      // 🏢 ESTATE MODE: Skip window modal on double-tap
      console.log('🏢 ESTATE: Window double-tap edit disabled for estate floorplan');
    };

    (window as any).__triggerRadiatorEdit = (element: RadiatorElement) => {
      // 🏢 ESTATE MODE: Skip radiator modal on double-tap
      console.log('🏢 ESTATE: Radiator double-tap edit disabled for estate floorplan');
    };

    (window as any).__triggerDoorEdit = (element: DoorElement) => {
      console.log('🚪 TOUCH_DOOR_EDIT: Triggered via double-tap', { elementId: (element as any).id });
      setDoorToEdit(element);
      setIsDoorPromptOpen(true);
    };

    (window as any).__triggerBoilerEdit = (element: BoilerElement) => {
      // 🏢 ESTATE MODE: Skip boiler modal on double-tap
      console.log('🏢 ESTATE: Boiler double-tap edit disabled for estate floorplan');
    };

    // Cleanup on unmount
    return () => {
      delete (window as any).__triggerWindowEdit;
      delete (window as any).__triggerRadiatorEdit;
      delete (window as any).__triggerDoorEdit;
      delete (window as any).__triggerBoilerEdit;
    };
  }, []);

  // Debug function to test door deletion functionality
  (window as any).testDoorDeletion = () => {
    console.log('🚪 Testing door deletion functionality...');
    
    const allDoors = elements.filter(el => el.type === 'door') as unknown as DoorElement[];
    console.log(`🚪 Total doors found: ${allDoors.length}`);
    
    if (allDoors.length === 0) {
      console.log('ℹ️ No doors found to test deletion. Please place a door first, then run this test again.');
      return;
    }
    
    console.log(`📊 Found ${allDoors.length} doors to test:`, allDoors.map(d => ({
      id: d.id,
      floor: d.floor,
      position: d.position,
      hasUndercut: d.hasUndercut,
      isFrontDoor: (d as any).isFrontDoor,
      wallId: d.wallId,
      width: d.width
    })));
    
    // Test with the first door
    const testDoor = allDoors[0];
    console.log('🔧 Opening door configuration dialog for:', {
      id: testDoor.id,
      position: testDoor.position,
      hasUndercut: testDoor.hasUndercut,
      isFrontDoor: (testDoor as any).isFrontDoor
    });
    
    // Simulate door selection for editing
    setDoorToEdit(testDoor);
    setIsDoorPromptOpen(true);
    console.log('✅ Door configuration dialog opened - you can now test the delete button');
    console.log('🗑️ Click the red "Delete Door" button to test the deletion functionality');
    
    return {
      totalDoors: allDoors.length,
      frontDoors: allDoors.filter(d => (d as any).isFrontDoor).length,
      doorsWithUndercuts: allDoors.filter(d => d.hasUndercut).length,
      doorsWithoutUndercuts: allDoors.filter(d => d.hasUndercut === false).length,
      testDoorId: testDoor.id
    };
  };

  // Debug function to test room completion functionality
  (window as any).testRoomCompletion = async () => {
    console.log('🔧 Testing room completion functionality...');
    
    const currentWalls = floorPlans[currentFloor] || [];
    if (currentWalls.length === 0) {
      console.log('ℹ️ No walls found to test room completion. Please create some walls first.');
      return;
    }
    
    console.log(`📊 Current floor has ${currentWalls.length} walls`);
    
    try {
      // Import the file repair tool
      const { repairFloorPlanFile } = await import('../utils/fileRepairTool');
      
      // Test room completion specifically
      const repairOptions = {
        fixExtrusionIssues: false,
        fixLegacyParentWallIds: false,
        removeOrphanedWalls: false,
        removeDuplicateWalls: false,
        generateMissingTempIds: false,
        generateMissingElementIds: false,
        fixBrokenElementReferences: false,
        fixElementProperties: false,
        validateGeometry: false,
        fixZeroLengthWalls: false,
        labelUnnamedRooms: false,
        completeIncompleteRooms: true, // Only test room completion
        relocateElementsToValidFloors: false,
        preserveAllElementTypes: true,
      };
      
      const repairResult = repairFloorPlanFile(currentWalls, elements, repairOptions, roomCustomLabels);
      
      console.log('🔧 Room completion test results:', {
        roomsCompleted: repairResult.reportSummary.incompleteRoomsCompleted,
        wallsAdded: repairResult.reportSummary.missingWallsAdded,
        issuesFound: repairResult.issuesFound,
        issuesFixed: repairResult.issuesFixed,
        originalWallCount: currentWalls.length,
        newWallCount: repairResult.repairedWalls.length
      });
      
      if (repairResult.reportSummary.incompleteRoomsCompleted > 0) {
        console.log(`✅ Successfully completed ${repairResult.reportSummary.incompleteRoomsCompleted} incomplete rooms by adding ${repairResult.reportSummary.missingWallsAdded} walls`);
        
        // Apply the repairs to the floor plan
        const updatedWalls = repairResult.repairedWalls.map(({tempId, ...wall}) => ({
        ...wall,
        // Preserve split wall metadata
        ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
        ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
        ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
        ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
        ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
        ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector })
      }));
        
        // Save to undo stack and update floor plans
        const currentState: UndoRedoState = {
          floorPlans,
          elements,
          textBoxes,
          roomCustomLabels,
          propertyAddress,
          windowCount,
          lightCount,
          currentFloor
        };
        setUndoStack(prevUndoStack => [...prevUndoStack, currentState]);
        setRedoStack([]);
        setFloorPlans({
          ...floorPlans,
          [currentFloor]: updatedWalls
        });
        
        console.log('✅ Applied room completion repairs to floor plan');
        
        // Show success message
        alert(`Room completion successful!\n\nCompleted ${repairResult.reportSummary.incompleteRoomsCompleted} incomplete rooms by adding ${repairResult.reportSummary.missingWallsAdded} walls.`);
      } else {
        console.log('ℹ️ No incomplete rooms found that could be completed automatically');
        alert('No incomplete rooms found that could be completed automatically.');
      }
      
    } catch (error) {
      console.error('❌ Error testing room completion:', error);
      alert('Error testing room completion. Check console for details.');
    }
  };

  // Debug function to test split wall restoration functionality
  (window as any).testSplitWallRestoration = () => {
    console.log('🔧 Testing split wall restoration functionality...');
    
    const currentWalls = floorPlans[currentFloor] || [];
    const currentWallsWithTempId = currentWalls.map((wall, index) => ({
      ...wall,
      tempId: `wall-${index}-${Date.now()}`
    }));
    
    // Find split walls
    const splitWalls = currentWallsWithTempId.filter(wall => 
      (wall as any).isSplitOrigin && (wall as any).parentWallId
    );
    
    console.log(`🔧 Found ${splitWalls.length} split walls:`, splitWalls.map(w => ({
      tempId: w.tempId,
      start: w.start,
      end: w.end,
      type: w.type,
      parentWallId: (w as any).parentWallId,
      isSplitOrigin: (w as any).isSplitOrigin
    })));
    
    if (splitWalls.length === 0) {
      console.log('ℹ️ No split walls found. To test this feature:');
      console.log('1. Use the split tool to split a wall');
      console.log('2. Then use the eraser tool and tap on one of the split segments');
      console.log('3. The eraser should restore the original wall');
      return {
        splitWallsFound: 0,
        message: 'No split walls found. Split a wall first to test restoration.'
      };
    }
    
    // Group split walls by parent wall ID
    const splitWallGroups = new Map<string, typeof splitWalls>();
    splitWalls.forEach(wall => {
      const parentId = (wall as any).parentWallId;
      if (!splitWallGroups.has(parentId)) {
        splitWallGroups.set(parentId, []);
      }
      splitWallGroups.get(parentId)!.push(wall);
    });
    
    console.log(`🔧 Split wall groups by parent ID:`, Array.from(splitWallGroups.entries()).map(([parentId, segments]) => ({
      parentWallId: parentId,
      segmentCount: segments.length,
      segments: segments.map(s => ({
        tempId: s.tempId,
        coordinates: `(${s.start.x.toFixed(1)},${s.start.y.toFixed(1)})->(${s.end.x.toFixed(1)},${s.end.y.toFixed(1)})`
      }))
    })));
    
    // Find extrusion connectors
    const extrusionConnectors = currentWallsWithTempId.filter(wall => 
      (wall as any).isExtrusionConnector && (wall as any).parentWallId
    );
    
    console.log(`🔧 Found ${extrusionConnectors.length} extrusion connectors:`, extrusionConnectors.map(w => ({
      tempId: w.tempId,
      start: w.start,
      end: w.end,
      parentWallId: (w as any).parentWallId,
      isExtrusionConnector: (w as any).isExtrusionConnector
    })));
    
    // Check if eraser tool is currently selected
    const isEraserSelected = currentTool === 'eraser';
    console.log(`🔧 Current tool: ${currentTool} ${isEraserSelected ? '✅' : '❌'}`);
    
    if (!isEraserSelected) {
      console.log('⚠️ Switch to the eraser tool first, then tap on a split wall segment');
    }
    
    console.log('ℹ️ To test split wall restoration:');
    console.log('1. Switch to the eraser tool' + (isEraserSelected ? ' ✅' : ' ❌'));
    console.log('2. Tap on any of the split wall segments listed above');
    console.log('3. The eraser should detect it as a split wall and restore the original wall');
    console.log('4. All related segments and extrusion connectors should be removed');
    
    return {
      totalSplitWalls: splitWalls.length,
      splitWallGroups: Array.from(splitWallGroups.keys()),
      extrusionConnectors: extrusionConnectors.length,
      currentTool: currentTool,
      isEraserSelected: isEraserSelected,
      testInstructions: 'Switch to eraser tool and tap on a split wall segment'
    };
  };

  return (
    <div 
      ref={layoutRef} 
      className={`
        flex flex-col min-h-screen 
        ${activeFullscreenState ? 'bg-white' : 'bg-gray-100'} 
        ${!isCssFullscreen ? 'relative' : ''}
        ${!activeFullscreenState ? 'pb-16' : ''} 
      `}
    >
      {/* Unified Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboardingModal}
        onComplete={handleOnboardingSubmit}
        onExit={() => onNavigate({ page: 'start' })}
        title="Create Floor Plan"
        description="Enter the property address to get started with auto-save enabled."
        initialData={{
          address: parseAddressFromString(propertyAddress || '')
        }}
      />
      
      {/* Estate Agency: SurveyorSiteNotes disabled - not needed for property listings
      {siteNotesDataReady && (
        <SurveyorSiteNotes
          isOpen={showSiteNotes}
          onClose={() => setShowSiteNotes(false)}
          onSave={handleSiteNotesSave}
          propertyAddress={propertyAddress}
          onPropertyAddressChange={(address) => {
            setPropertyAddress(address);
            setHasFloorPlanChanges(true);
            autosave.markAsChanged();
          }}
          initialData={siteNotesData}
          onDataChange={handleSiteNotesChange}
          autosaveStatus={autosave.autosaveStatus}
          lastSavedAt={autosave.lastSavedAt}
          lastError={autosave.lastError}
          retryCount={autosave.retryCount}
          canEnableAutosave={autosave.canEnableAutosave}
          onGetFloorCalculations={handleGetFloorCalculations}
          walls={currentWalls}
          roomCustomLabels={roomCustomLabels}
          onRadiatorTypeSync={handleRadiatorTypeSync}
          onDeleteWindowFromFloorPlan={handleDeleteWindowFromSiteNotes}
          onDeleteRadiatorFromFloorPlan={handleDeleteRadiatorFromSiteNotes}
          onWindowDimensionSync={handleWindowDimensionSyncFromSiteNotes}
          onFloorDelete={handleFloorDelete}
          onSyncElements={handleSyncElements}
          onNavigate={(path) => {
            console.log('🏠 Navigation requested from Site Notes:', path);
            if (path === 'retrofit') {
              setShowSiteNotes(false);
            } else {
              switch (path) {
                case 'start':
                  onNavigate({ page: 'start' });
                  break;
                case 'settings':
                  onNavigate({ page: 'settings' });
                  break;
                case 'myFiles':
                  onNavigate({ page: 'myFiles' });
                  break;
                default:
                  console.warn('Unknown navigation path:', path);
                  onNavigate({ page: 'start' });
              }
            }
          }}
        />
      )}
      */}

      {!activeFullscreenState && (
         <header
           className="bg-white shadow-md sticky top-0 z-40 flex-shrink-0 header-safe-area rounded-b-2xl border-b-2 border-yellow-400"
         >
           <div className="w-full px-4 sm:px-6 py-4 flex items-center justify-between">
             <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
               Floor Plan Creator
             </h1>
             <div className="flex items-center space-x-2 sm:space-x-3">
               {/* Autosave indicator - unified for all screen sizes */}
               <AutosaveIndicator
                 status={autosave.autosaveStatus}
                 lastSavedAt={autosave.lastSavedAt}
                 lastError={autosave.lastError}
                 retryCount={autosave.retryCount}
                 canEnableAutosave={autosave.canEnableAutosave}
                 onEnableAutosave={() => setShowOnboardingModal(true)}
                 showLastSaved={false}
               />
               {/* Estate Agency: Site Notes and Diagnostics buttons disabled - not needed for property listings
               <button
                  onClick={() => {
                    if (siteNotesDataReady) {
                      setShowSiteNotes(true);
                    } else {
                      console.log('⏳ Site notes data not ready yet, please wait...');
                    }
                  }}
                  disabled={!siteNotesDataReady}
                  title={siteNotesDataReady ? "Open Site Notes" : "Loading site notes data..."}
                  className={`p-2.5 rounded-xl transition-all duration-200 border ${
                    siteNotesDataReady 
                      ? 'text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 border-gray-200 hover:border-yellow-300' 
                      : 'text-gray-400 border-gray-100 cursor-not-allowed'
                  }`}
                >
                  <ClipboardList size={hasTouchScreen ? 18 : 20} />
                </button>
                <button
                  onClick={() => setShowDiagnosticsPanel(!showDiagnosticsPanel)}
                  title={showDiagnosticsPanel ? "Hide Diagnostics" : "Show Floor Plan Diagnostics"}
                  className={`p-2.5 rounded-xl transition-all duration-200 border ${
                    showDiagnosticsPanel
                      ? 'text-blue-600 bg-blue-50 border-blue-300'
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600 border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <Database size={hasTouchScreen ? 18 : 20} />
                </button>
               */}
               <button
                 onClick={() => onNavigate({ page: 'start' })}
                 title="Go to Home"
                 className="p-2.5 rounded-xl text-gray-600 hover:bg-yellow-50 hover:text-yellow-600 transition-all duration-200 border border-gray-200 hover:border-yellow-300"
               >
                 <Home size={hasTouchScreen ? 18 : 20} />
               </button>
             </div>
           </div>
         </header>
       )}

      {/* Estate Agency: Floor Plan Diagnostics Panel disabled - not needed for property listings
      {showDiagnosticsPanel && !activeFullscreenState && (
        <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-4">
          <FloorPlanDiagnosticsOnly
            currentWalls={currentWalls}
            allRooms={getAllExistingRoomsAcrossFloors}
            roomCustomLabels={roomCustomLabels}
            currentFloor={currentFloor}
            floorPlans={floorPlans}
            onDebug={onDebug}
          />
        </div>
      )}
      */}
       
      <div className="flex-1" style={{ background: '#f6f7fb' }}>

          <div className={`
            flex-1 main-content flex flex-col 
            ${activeFullscreenState ? '' : 'px-[18px] pt-[22px] pb-[22px]'} 
          `}>
            <div 
              className={`
                w-full 
                ${activeFullscreenState ? 'p-0' : ''}
                ${hasTouchScreen ? 'touch-layout' : ''} 
                grid 
                flex-grow 
                min-h-0
              `}
              style={
                activeFullscreenState 
                  ? { gridTemplateRows: '1fr' } 
                  : { 
                      display: 'grid', 
                      gridTemplateRows: 'auto 1fr',  // Panels auto-size, canvas takes remaining
                      minHeight: '100vh'  // Ensure full viewport height is used
                    }
              }
            >
              {/* Fullscreen elements table at the top */}
              {activeFullscreenState && !showExportModal && !isPositioningRoom && (
                <div 
                  className="fixed top-0 left-0 right-0 z-[10001] bg-white border-b border-gray-200 shadow-md flex-shrink-0"
                  style={{
                    // Add safe area padding for iOS devices since we removed it from the container
                    paddingTop: 'env(safe-area-inset-top)',
                    paddingLeft: 'env(safe-area-inset-left)', 
                    paddingRight: 'env(safe-area-inset-right)'
                  }}
                >
                  <div className="flex flex-col py-2 px-2">
                    <div className="flex flex-col gap-2">
                      {/* First row: Floor Navigation + Export + AR Scan */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Modern Floor Navigation */}
                        <div className="bg-gradient-to-br from-white via-slate-50 to-white rounded-xl shadow-lg border border-slate-200/50 flex items-center overflow-hidden">
                          <button
                            onClick={() => switchFloor('prev')}
                            disabled={(() => {
                              const floors = Object.keys(floorPlans).map(Number).sort((a, b) => a - b);
                              const currentIndex = floors.indexOf(currentFloor);
                              return currentIndex <= 0;
                            })()}
                            className="group p-1 sm:p-1.5 text-slate-600 hover:text-white hover:bg-gradient-to-r hover:from-amber-500 hover:to-yellow-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-all duration-200 border-r border-slate-200/50"
                            title="Previous Floor"
                          >
                            <ArrowLeft size={hasTouchScreen ? 14 : 16} className="group-hover:scale-110 transition-transform duration-200" />
                          </button>
                          
                          {/* Modern Floor Indicator */}
                          <div className="flex items-center px-1.5 sm:px-2 py-1 sm:py-1.5 border-r border-slate-300/50">
                            <span className="text-[10px] sm:text-xs font-bold text-slate-700 mr-0.5 sm:mr-1">FLOOR</span>
                            <span className="text-sm sm:text-lg font-black text-slate-800">{currentFloor}</span>
                            <span className="text-[10px] sm:text-xs text-slate-600 ml-0.5 sm:ml-1">of {Math.max(...Object.keys(floorPlans).map(Number))}</span>
                          </div>
                          
                          <button
                            onClick={() => switchFloor('next')}
                            disabled={currentFloor === Math.max(...Object.keys(floorPlans).map(Number))}
                            className="group p-1 sm:p-1.5 text-slate-600 hover:text-white hover:bg-gradient-to-r hover:from-amber-500 hover:to-yellow-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-all duration-200 border-r border-slate-200/50"
                            title="Next Floor"
                          >
                            <ArrowRight size={hasTouchScreen ? 14 : 16} className="group-hover:scale-110 transition-transform duration-200" />
                          </button>
                          
                          <div className="w-px h-3 sm:h-4 bg-slate-200/50 mx-0.5"></div>
                          <button
                            onClick={addNewFloor}
                            className="group p-1 sm:p-1.5 text-slate-600 hover:text-white hover:bg-gradient-to-r hover:from-emerald-500 hover:to-teal-600 transition-all duration-200 border-r border-slate-200/50"
                            title="Add New Floor"
                          >
                            <PlusSquare size={hasTouchScreen ? 12 : 14} className="group-hover:scale-110 transition-transform duration-200" />
                          </button>
                          
                          <button
                            onClick={() => deleteFloor()}
                            disabled={Object.keys(floorPlans).length <= 1}
                            className="group p-1 sm:p-1.5 text-slate-600 hover:text-white hover:bg-gradient-to-r hover:from-rose-500 hover:to-red-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-all duration-200"
                            title={Object.keys(floorPlans).length <= 1 ? "Cannot delete the last floor" : `Delete Floor ${currentFloor}`}
                          >
                            <Trash2 size={hasTouchScreen ? 12 : 14} className="group-hover:scale-110 transition-transform duration-200" />
                          </button>
                        </div>

                        {/* Modern Export Button */}
                        <button
                          className="group px-2 py-1 text-xs text-slate-700 hover:text-blue-600 rounded-xl transition-all duration-200 font-semibold border border-slate-300/50 hover:border-blue-400/50 hover:shadow-lg hover:scale-[1.02] flex items-center"
                          onClick={() => setShowExportModal(true)}
                          title="Export Floor Plan"
                        >
                          <Download size={12} className="inline mr-1 group-hover:scale-110 transition-transform duration-200" />
                          <span>Export</span>
                        </button>

                        {/* Modern Multi-Room AR Scan Button */}
                        {arScanning.openNativeARScanner && (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={handleStartARScan}
                              disabled={!arScanning.openNativeARScanner || (isStandardPlan && !arScan.canUse)}
                              className={`group px-2 py-1 text-xs rounded-xl transition-all duration-200 font-semibold border flex items-center ${
                                isStandardPlan && !arScan.canUse
                                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                  : 'text-slate-700 hover:text-emerald-600 border-slate-300/50 hover:border-emerald-400/50 hover:shadow-lg hover:scale-[1.02]'
                              }`}
                              title={isStandardPlan && !arScan.canUse ? 'Scan limit reached this month' : 'Multi-Room AR Scanning'}
                            >
                              <Scan size={12} className="group-hover:scale-110 transition-transform duration-200" />
                              <span className="text-[10px] opacity-75 group-hover:opacity-100">Scan Room</span>
                            </button>
                            {isStandardPlan && arScan.limit && (
                              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${
                                arScan.remaining === 0 ? 'bg-red-100 text-red-700' :
                                arScan.remaining <= 5 ? 'bg-amber-100 text-amber-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {arScan.used}/{arScan.limit}
                              </span>
                            )}
                          </div>
                        )}

                        {/* No duplicate button needed - removed */}

                        {/* Recover Floors Button */}
                        {(() => {
                          const existingFloors = Object.keys(floorPlans).map(Number).sort((a, b) => a - b);
                          const hasGaps = existingFloors.length > 0 && existingFloors.some((floor, index) => floor !== index + 1);
                          return hasGaps ? (
                            <button
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 border border-green-500 transition-colors font-medium shadow-sm"
                              onClick={recoverMissingFloors}
                              title="Recover missing floors"
                            >
                              Recover Floors
                            </button>
                          ) : null;
                        })()}
                      </div>

                      {/* Second row: Element Tools */}
                      <div className="bg-gradient-to-br from-white via-slate-50 to-white rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden">
                        {/* Modern Header */}
                        <div className="relative px-2 py-0.5 border-b border-slate-200/50">
                          <div className="flex items-center justify-center">
                            <div className="flex items-center gap-1">
                              <div className="p-0.5 bg-slate-100 rounded shadow-sm">
                                <Settings size={8} className="text-slate-700" />
                              </div>
                              <h3 className="font-bold text-[10px] text-slate-700">Element Tools</h3>
                            </div>
                          </div>
                        </div>

                        {/* Compact Element Grid */}
                        <div className="p-1.5">
                          {/* Unified element grid - expands horizontally when showing more */}
                          <div className={`grid gap-1.5 ${
                            isIconsExpanded ? 'grid-cols-9' : 'grid-cols-5'
                          }`}>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'window'
                                  ? 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300/50 shadow-md shadow-blue-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300/50 hover:shadow-md hover:shadow-blue-200/30'
                              }`}
                              onClick={() => handleToolSelect('window' as ElementType)}
                              title="Window"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'window'
                                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 scale-110'
                                  : 'bg-gradient-to-r from-blue-400 to-blue-500 group-hover:scale-105'
                              }`}>
                                <RectangleVertical size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'door'
                                  ? 'bg-gradient-to-br from-orange-50 to-amber-100 border-orange-300/50 shadow-md shadow-orange-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-orange-50 hover:to-orange-100 hover:border-orange-300/50 hover:shadow-md hover:shadow-orange-200/30'
                              }`}
                              onClick={() => handleToolSelect('door' as ElementType)}
                              title="Door"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'door'
                                  ? 'bg-gradient-to-r from-orange-500 to-amber-600 scale-110'
                                  : 'bg-gradient-to-r from-orange-400 to-orange-500 group-hover:scale-105'
                              }`}>
                                <DoorOpen size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'radiator'
                                  ? 'bg-gradient-to-br from-rose-50 to-red-100 border-rose-300/50 shadow-md shadow-rose-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-rose-50 hover:to-rose-100 hover:border-rose-300/50 hover:shadow-md hover:shadow-rose-200/30'
                              }`}
                              onClick={() => handleToolSelect('radiator' as ElementType)}
                              title="Radiator"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'radiator'
                                  ? 'bg-gradient-to-r from-rose-500 to-red-600 scale-110'
                                  : 'bg-gradient-to-r from-rose-400 to-rose-500 group-hover:scale-105'
                              }`}>
                                <Square size={8} className="text-white" />
                              </div>
                            </div>
                            {/* 🏢 New Estate Elements: Hob, Bathtub, Shower, Toilet */}
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'hob'
                                  ? 'bg-gradient-to-br from-cyan-50 to-blue-100 border-cyan-300/50 shadow-md shadow-cyan-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-cyan-50 hover:to-cyan-100 hover:border-cyan-300/50 hover:shadow-md hover:shadow-cyan-200/30'
                              }`}
                              onClick={() => handleToolSelect('hob' as ElementType)}
                              title="Hob"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'hob'
                                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 scale-110'
                                  : 'bg-gradient-to-r from-cyan-400 to-cyan-500 group-hover:scale-105'
                              }`}>
                                <Grid3X3 size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'bathtub'
                                  ? 'bg-gradient-to-br from-sky-50 to-cyan-100 border-sky-300/50 shadow-md shadow-sky-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-sky-50 hover:to-sky-100 hover:border-sky-300/50 hover:shadow-md hover:shadow-sky-200/30'
                              }`}
                              onClick={() => handleToolSelect('bathtub' as ElementType)}
                              title="Bathtub"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'bathtub'
                                  ? 'bg-gradient-to-r from-sky-500 to-cyan-600 scale-110'
                                  : 'bg-gradient-to-r from-sky-400 to-sky-500 group-hover:scale-105'
                              }`}>
                                <Bath size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'shower'
                                  ? 'bg-gradient-to-br from-blue-50 to-sky-100 border-blue-300/50 shadow-md shadow-blue-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300/50 hover:shadow-md hover:shadow-blue-200/30'
                              }`}
                              onClick={() => handleToolSelect('shower' as ElementType)}
                              title="Shower"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'shower'
                                  ? 'bg-gradient-to-r from-blue-500 to-sky-600 scale-110'
                                  : 'bg-gradient-to-r from-blue-400 to-blue-500 group-hover:scale-105'
                              }`}>
                                <Droplets size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'toilet'
                                  ? 'bg-gradient-to-br from-slate-50 to-gray-100 border-slate-300/50 shadow-md shadow-slate-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-slate-50 hover:to-slate-100 hover:border-slate-300/50 hover:shadow-md hover:shadow-slate-200/30'
                              }`}
                              onClick={() => handleToolSelect('toilet' as ElementType)}
                              title="Toilet"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'toilet'
                                  ? 'bg-gradient-to-r from-slate-500 to-gray-600 scale-110'
                                  : 'bg-gradient-to-r from-slate-400 to-slate-500 group-hover:scale-105'
                              }`}>
                                <CircleDot size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'boiler'
                                  ? 'bg-gradient-to-br from-red-50 to-rose-100 border-red-300/50 shadow-md shadow-red-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-red-50 hover:to-red-100 hover:border-red-300/50 hover:shadow-md hover:shadow-red-200/30'
                              }`}
                              onClick={() => handleToolSelect('boiler' as ElementType)}
                              title="Boiler"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'boiler'
                                  ? 'bg-gradient-to-r from-red-500 to-rose-600 scale-110'
                                  : 'bg-gradient-to-r from-red-400 to-red-500 group-hover:scale-105'
                              }`}>
                                <Flame size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'counter'
                                  ? 'bg-gradient-to-br from-slate-50 to-gray-100 border-slate-300/50 shadow-md shadow-slate-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-slate-50 hover:to-gray-100 hover:border-slate-300/50 hover:shadow-md hover:shadow-slate-200/30'
                              }`}
                              onClick={() => handleToolSelect('counter' as ElementType)}
                              title="Counter"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'counter'
                                  ? 'bg-gradient-to-r from-slate-500 to-gray-600 scale-110'
                                  : 'bg-gradient-to-r from-slate-400 to-slate-500 group-hover:scale-105'
                              }`}>
                                <Minus size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'stairs'
                                  ? 'bg-gradient-to-br from-purple-50 to-violet-100 border-purple-300/50 shadow-md shadow-purple-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-purple-50 hover:to-violet-100 hover:border-purple-300/50 hover:shadow-md hover:shadow-purple-200/30'
                              }`}
                              onClick={() => handleToolSelect('stairs' as ElementType)}
                              title="Stairs"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'stairs'
                                  ? 'bg-gradient-to-r from-purple-500 to-violet-600 scale-110'
                                  : 'bg-gradient-to-r from-purple-400 to-purple-500 group-hover:scale-105'
                              }`}>
                                <ArrowUp size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'light'
                                  ? 'bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-300/50 shadow-md shadow-amber-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-amber-50 hover:to-yellow-100 hover:border-amber-300/50 hover:shadow-md hover:shadow-amber-200/30'
                              }`}
                              onClick={() => handleToolSelect('light' as ElementType)}
                              title="Light"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'light'
                                  ? 'bg-gradient-to-r from-amber-500 to-yellow-600 scale-110'
                                  : 'bg-gradient-to-r from-amber-400 to-yellow-500 group-hover:scale-105'
                              }`}>
                                <Lightbulb size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'smoke-alarm'
                                  ? 'bg-gradient-to-br from-red-50 to-rose-100 border-red-300/50 shadow-md shadow-red-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-red-50 hover:to-red-100 hover:border-red-300/50 hover:shadow-md hover:shadow-red-200/30'
                              }`}
                              onClick={() => handleToolSelect('smoke-alarm' as ElementType)}
                              title="Smoke Alarm"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'smoke-alarm'
                                  ? 'bg-gradient-to-r from-red-500 to-rose-600 scale-110'
                                  : 'bg-gradient-to-r from-red-400 to-red-500 group-hover:scale-105'
                              }`}>
                                <AlertTriangle size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'emergency-light'
                                  ? 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-300/50 shadow-md shadow-green-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-green-50 hover:to-green-100 hover:border-green-300/50 hover:shadow-md hover:shadow-green-200/30'
                              }`}
                              onClick={() => handleToolSelect('emergency-light' as FireSafetyType)}
                              title="Emergency Light"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'emergency-light'
                                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 scale-110'
                                  : 'bg-gradient-to-r from-green-400 to-green-500 group-hover:scale-105'
                              }`}>
                                <Zap size={8} className="text-white" />
                              </div>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                (currentTool as string) === 'fire-call-point'
                                  ? 'bg-gradient-to-br from-red-50 to-orange-100 border-red-400/50 shadow-md shadow-red-300/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-red-50 hover:to-orange-100 hover:border-red-400/50 hover:shadow-md hover:shadow-red-300/30'
                              }`}
                              onClick={() => handleToolSelect('fire-call-point' as FireSafetyType)}
                              title="Fire Call Point"
                            >
                              <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                (currentTool as string) === 'fire-call-point'
                                  ? 'bg-gradient-to-r from-red-600 to-orange-600 scale-110'
                                  : 'bg-gradient-to-r from-red-500 to-orange-500 group-hover:scale-105'
                              }`}>
                                <Target size={8} className="text-white" />
                              </div>
                            </div>

                            {/* Expandable section - shows inline when expanded */}
                            {isIconsExpanded && (
                              <>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'fire-panel'
                                    ? 'bg-gradient-to-br from-orange-50 to-amber-100 border-orange-300/50 shadow-md shadow-orange-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-orange-50 hover:to-amber-100 hover:border-orange-300/50 hover:shadow-md hover:shadow-orange-200/30'
                                }`}
                                onClick={() => handleToolSelect('fire-panel' as FireSafetyType)}
                                title="Fire Panel"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'fire-panel'
                                    ? 'bg-gradient-to-r from-orange-600 to-amber-600 scale-110'
                                    : 'bg-gradient-to-r from-orange-500 to-amber-500 group-hover:scale-105'
                                }`}>
                                  <Database size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'heat-alarm'
                                    ? 'bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-300/50 shadow-md shadow-amber-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-amber-50 hover:to-yellow-100 hover:border-amber-300/50 hover:shadow-md hover:shadow-amber-200/30'
                                }`}
                                onClick={() => handleToolSelect('heat-alarm' as ElementType)}
                                title="Heat Alarm"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'heat-alarm'
                                    ? 'bg-gradient-to-r from-amber-500 to-yellow-600 scale-110'
                                    : 'bg-gradient-to-r from-amber-400 to-amber-500 group-hover:scale-105'
                                }`}>
                                  <Thermometer size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'alarm-sounder'
                                    ? 'bg-gradient-to-br from-violet-50 to-purple-100 border-violet-300/50 shadow-md shadow-violet-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-violet-50 hover:to-purple-100 hover:border-violet-300/50 hover:shadow-md hover:shadow-violet-200/30'
                                }`}
                                onClick={() => handleToolSelect('alarm-sounder' as ElementType)}
                                title="Alarm Sounder"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'alarm-sounder'
                                    ? 'bg-gradient-to-r from-violet-500 to-purple-600 scale-110'
                                    : 'bg-gradient-to-r from-violet-400 to-violet-500 group-hover:scale-105'
                                }`}>
                                  <Volume2 size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'wc-call-point'
                                    ? 'bg-gradient-to-br from-sky-50 to-cyan-100 border-sky-300/50 shadow-md shadow-sky-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-sky-50 hover:to-cyan-100 hover:border-sky-300/50 hover:shadow-md hover:shadow-sky-200/30'
                                }`}
                                onClick={() => handleToolSelect('wc-call-point' as ElementType)}
                                title="W/C Call Point"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'wc-call-point'
                                    ? 'bg-gradient-to-r from-sky-500 to-cyan-600 scale-110'
                                    : 'bg-gradient-to-r from-sky-400 to-sky-500 group-hover:scale-105'
                                }`}>
                                  <Users size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'ac-unit'
                                    ? 'bg-gradient-to-br from-cyan-50 to-blue-100 border-cyan-300/50 shadow-md shadow-cyan-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-cyan-50 hover:to-blue-100 hover:border-cyan-300/50 hover:shadow-md hover:shadow-cyan-200/30'
                                }`}
                                onClick={() => handleToolSelect('ac-unit' as ElementType)}
                                title="AC Unit"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'ac-unit'
                                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 scale-110'
                                    : 'bg-gradient-to-r from-cyan-400 to-cyan-500 group-hover:scale-105'
                                }`}>
                                  <Fan size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'fire-alarm'
                                    ? 'bg-gradient-to-br from-red-50 to-rose-100 border-red-400/50 shadow-md shadow-red-300/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-red-50 hover:to-rose-100 hover:border-red-400/50 hover:shadow-md hover:shadow-red-300/30'
                                }`}
                                onClick={() => handleToolSelect('fire-alarm' as ElementType)}
                                title="Fire Alarm"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'fire-alarm'
                                    ? 'bg-gradient-to-r from-red-600 to-rose-700 scale-110'
                                    : 'bg-gradient-to-r from-red-500 to-red-600 group-hover:scale-105'
                                }`}>
                                  <Bell size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'ice-machine'
                                    ? 'bg-gradient-to-br from-sky-50 to-blue-100 border-sky-300/50 shadow-md shadow-sky-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-sky-50 hover:to-blue-100 hover:border-sky-300/50 hover:shadow-md hover:shadow-sky-200/30'
                                }`}
                                onClick={() => handleToolSelect('ice-machine' as ElementType)}
                                title="Ice Machine"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'ice-machine'
                                    ? 'bg-gradient-to-r from-sky-500 to-blue-600 scale-110'
                                    : 'bg-gradient-to-r from-sky-400 to-sky-500 group-hover:scale-105'
                                }`}>
                                  <Snowflake size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'coffee-machine'
                                    ? 'bg-gradient-to-br from-amber-50 to-orange-100 border-amber-300/50 shadow-md shadow-amber-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-amber-50 hover:to-orange-100 hover:border-amber-300/50 hover:shadow-md hover:shadow-amber-200/30'
                                }`}
                                onClick={() => handleToolSelect('coffee-machine' as ElementType)}
                                title="Coffee Machine"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'coffee-machine'
                                    ? 'bg-gradient-to-r from-amber-600 to-orange-700 scale-110'
                                    : 'bg-gradient-to-r from-amber-500 to-amber-600 group-hover:scale-105'
                                }`}>
                                  <Coffee size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'gas-meter'
                                    ? 'bg-gradient-to-br from-orange-50 to-amber-100 border-orange-300/50 shadow-md shadow-orange-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-orange-50 hover:to-orange-100 hover:border-orange-300/50 hover:shadow-md hover:shadow-orange-200/30'
                                }`}
                                onClick={() => handleToolSelect('gas-meter' as ElementType)}
                                title="Gas Meter"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'gas-meter'
                                    ? 'bg-gradient-to-r from-orange-500 to-amber-600 scale-110'
                                    : 'bg-gradient-to-r from-orange-400 to-orange-500 group-hover:scale-105'
                                }`}>
                                  <Gauge size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'electric-meter'
                                    ? 'bg-gradient-to-br from-yellow-50 to-amber-100 border-yellow-300/50 shadow-md shadow-yellow-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-yellow-50 hover:to-yellow-100 hover:border-yellow-300/50 hover:shadow-md hover:shadow-yellow-200/30'
                                }`}
                                onClick={() => handleToolSelect('electric-meter' as ElementType)}
                                title="Electric Meter"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'electric-meter'
                                    ? 'bg-gradient-to-r from-yellow-500 to-amber-600 scale-110'
                                    : 'bg-gradient-to-r from-yellow-400 to-yellow-500 group-hover:scale-105'
                                }`}>
                                  <Zap size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'stop-tap'
                                    ? 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300/50 shadow-md shadow-blue-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300/50 hover:shadow-md hover:shadow-blue-200/30'
                                }`}
                                onClick={() => handleToolSelect('stop-tap' as ElementType)}
                                title="Stop Tap"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'stop-tap'
                                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 scale-110'
                                    : 'bg-gradient-to-r from-blue-400 to-blue-500 group-hover:scale-105'
                                }`}>
                                  <RotateCcw size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'tank-cylinder'
                                    ? 'bg-gradient-to-br from-slate-50 to-gray-100 border-slate-300/50 shadow-md shadow-slate-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-slate-50 hover:to-slate-100 hover:border-slate-300/50 hover:shadow-md hover:shadow-slate-200/30'
                                }`}
                                onClick={() => handleToolSelect('tank-cylinder' as ElementType)}
                                title="Tank/Cylinder"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'tank-cylinder'
                                    ? 'bg-gradient-to-r from-slate-500 to-gray-600 scale-110'
                                    : 'bg-gradient-to-r from-slate-400 to-slate-500 group-hover:scale-105'
                                }`}>
                                  <Database size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'thermostat'
                                    ? 'bg-gradient-to-br from-purple-50 to-violet-100 border-purple-300/50 shadow-md shadow-purple-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-purple-50 hover:to-purple-100 hover:border-purple-300/50 hover:shadow-md hover:shadow-purple-200/30'
                                }`}
                                onClick={() => handleToolSelect('thermostat' as ElementType)}
                                title="Thermostat"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'thermostat'
                                    ? 'bg-gradient-to-r from-purple-500 to-violet-600 scale-110'
                                    : 'bg-gradient-to-r from-purple-400 to-purple-500 group-hover:scale-105'
                                }`}>
                                  <Thermometer size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'consumer-unit'
                                    ? 'bg-gradient-to-br from-indigo-50 to-purple-100 border-indigo-300/50 shadow-md shadow-indigo-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-indigo-50 hover:to-indigo-100 hover:border-indigo-300/50 hover:shadow-md hover:shadow-indigo-200/30'
                                }`}
                                onClick={() => handleToolSelect('consumer-unit' as ElementType)}
                                title="Consumer Unit"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'consumer-unit'
                                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 scale-110'
                                    : 'bg-gradient-to-r from-indigo-400 to-indigo-500 group-hover:scale-105'
                                }`}>
                                  <Grid3X3 size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'air-source-hp'
                                    ? 'bg-gradient-to-br from-teal-50 to-cyan-100 border-teal-300/50 shadow-md shadow-teal-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-teal-50 hover:to-teal-100 hover:border-teal-300/50 hover:shadow-md hover:shadow-teal-200/30'
                                }`}
                                onClick={() => handleToolSelect('air-source-hp' as ElementType)}
                                title="Air Source Heat Pump"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'air-source-hp'
                                    ? 'bg-gradient-to-r from-teal-500 to-cyan-600 scale-110'
                                    : 'bg-gradient-to-r from-teal-400 to-teal-500 group-hover:scale-105'
                                }`}>
                                  <Fan size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'fan'
                                    ? 'bg-gradient-to-br from-cyan-50 to-sky-100 border-cyan-300/50 shadow-md shadow-cyan-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-cyan-50 hover:to-cyan-100 hover:border-cyan-300/50 hover:shadow-md hover:shadow-cyan-200/30'
                                }`}
                                onClick={() => handleToolSelect('fan' as ElementType)}
                                title="Fan"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'fan'
                                    ? 'bg-gradient-to-r from-cyan-500 to-sky-600 scale-110'
                                    : 'bg-gradient-to-r from-cyan-400 to-cyan-500 group-hover:scale-105'
                                }`}>
                                  <Fan size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'background-vent'
                                    ? 'bg-gradient-to-br from-sky-50 to-blue-100 border-sky-300/50 shadow-md shadow-sky-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-sky-50 hover:to-sky-100 hover:border-sky-300/50 hover:shadow-md hover:shadow-sky-200/30'
                                }`}
                                onClick={() => handleToolSelect('background-vent' as ElementType)}
                                title="Background Vent"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'background-vent'
                                    ? 'bg-gradient-to-r from-sky-500 to-blue-600 scale-110'
                                    : 'bg-gradient-to-r from-sky-400 to-sky-500 group-hover:scale-105'
                                }`}>
                                  <Wind size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'loft-hatch'
                                    ? 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300/50 shadow-md shadow-blue-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300/50 hover:shadow-md hover:shadow-blue-200/30'
                                }`}
                                onClick={() => handleToolSelect('loft-hatch' as ElementType)}
                                title="Loft Hatch"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'loft-hatch'
                                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 scale-110'
                                    : 'bg-gradient-to-r from-blue-400 to-blue-500 group-hover:scale-105'
                                }`}>
                                  <Square size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'garden'
                                    ? 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-300/50 shadow-md shadow-green-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-green-50 hover:to-green-100 hover:border-green-300/50 hover:shadow-md hover:shadow-green-200/30'
                                }`}
                                onClick={() => handleToolSelect('garden' as ElementType)}
                                title="Garden"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'garden'
                                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 scale-110'
                                    : 'bg-gradient-to-r from-green-400 to-green-500 group-hover:scale-105'
                                }`}>
                                  <CircleDot size={8} className="text-white" />
                                </div>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 cursor-pointer h-8 border ${
                                  (currentTool as string) === 'compass'
                                    ? 'bg-gradient-to-br from-red-50 to-orange-100 border-red-300/50 shadow-md shadow-red-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-red-50 hover:to-red-100 hover:border-red-300/50 hover:shadow-md hover:shadow-red-200/30'
                                }`}
                                onClick={() => handleToolSelect('compass' as ElementType)}
                                title="Compass (True North)"
                              >
                                <div className={`p-0.5 rounded shadow-sm transition-all duration-200 ${
                                  (currentTool as string) === 'compass'
                                    ? 'bg-gradient-to-r from-red-500 to-orange-600 scale-110'
                                    : 'bg-gradient-to-r from-red-400 to-red-500 group-hover:scale-105'
                                }`}>
                                  <Compass size={8} className="text-white" />
                                </div>
                              </div>
                              </>
                            )}
                          </div>

                          {/* Compact Expand/Collapse button */}
                          <button
                            onClick={() => setIsIconsExpanded(!isIconsExpanded)}
                            className="flex items-center justify-center w-full p-0.5 mt-1 rounded border border-slate-200/30 hover:border-slate-300/50 hover:bg-slate-50/50 transition-all duration-200 group"
                          >
                            <div className="flex items-center gap-0.5">
                              <div className="transition-all duration-200">
                                {isIconsExpanded ? (
                                  <ChevronUp size={6} className="text-slate-500 group-hover:text-slate-600" />
                                ) : (
                                  <ChevronDown size={6} className="text-slate-500 group-hover:text-slate-600" />
                                )}
                              </div>
                              <span className="text-[8px] font-medium text-slate-500 group-hover:text-slate-600">
                                {isIconsExpanded ? 'Less' : 'More (10 more)'}
                              </span>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!activeFullscreenState && (
                <div className="flex-shrink-0">
                                      <div className="flex flex-col mb-1 px-[18px]">
                      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-1.5 sm:gap-2 md:gap-3">
                        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
                        {/* Modern Floor Navigation */}
                        <div className="bg-gradient-to-br from-white via-slate-50 to-white rounded-xl shadow-lg border border-slate-200/50 flex items-center overflow-hidden">
                          <button
                            onClick={() => switchFloor('prev')}
                            disabled={(() => {
                              const floors = Object.keys(floorPlans).map(Number).sort((a, b) => a - b);
                              const currentIndex = floors.indexOf(currentFloor);
                              return currentIndex <= 0;
                            })()}
                            className="group p-1 sm:p-1.5 text-slate-600 hover:text-white hover:bg-gradient-to-r hover:from-amber-500 hover:to-yellow-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-all duration-200 border-r border-slate-200/50"
                            title="Previous Floor"
                          >
                            <ArrowLeft size={hasTouchScreen ? 14 : 16} className="group-hover:scale-110 transition-transform duration-200" />
                          </button>
                          
                          {/* Modern Floor Indicator */}
                          <div className="flex items-center px-1.5 sm:px-2 py-1 sm:py-1.5 border-r border-slate-300/50">
                            <span className="text-[10px] sm:text-xs font-bold text-slate-700 mr-0.5 sm:mr-1">FLOOR</span>
                            <span className="text-sm sm:text-lg font-black text-slate-800">{currentFloor}</span>
                            <span className="text-[10px] sm:text-xs text-slate-600 ml-0.5 sm:ml-1">of {Math.max(...Object.keys(floorPlans).map(Number))}</span>
                          </div>
                          
                          <button
                            onClick={() => switchFloor('next')}
                            disabled={currentFloor === Math.max(...Object.keys(floorPlans).map(Number))}
                            className="group p-1 sm:p-1.5 text-slate-600 hover:text-white hover:bg-gradient-to-r hover:from-amber-500 hover:to-yellow-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-all duration-200 border-r border-slate-200/50"
                            title="Next Floor"
                          >
                            <ArrowRight size={hasTouchScreen ? 14 : 16} className="group-hover:scale-110 transition-transform duration-200" />
                          </button>
                          
                          <div className="w-px h-3 sm:h-4 bg-slate-200/50 mx-0.5"></div>
                          <button
                            onClick={addNewFloor}
                            className="group p-1 sm:p-1.5 text-slate-600 hover:text-white hover:bg-gradient-to-r hover:from-emerald-500 hover:to-teal-600 transition-all duration-200 border-r border-slate-200/50"
                            title="Add New Floor"
                          >
                            <PlusSquare size={hasTouchScreen ? 12 : 14} className="group-hover:scale-110 transition-transform duration-200" />
                          </button>
                          
                          <button
                            onClick={() => deleteFloor()}
                            disabled={Object.keys(floorPlans).length <= 1}
                            className="group p-1 sm:p-1.5 text-slate-600 hover:text-white hover:bg-gradient-to-r hover:from-rose-500 hover:to-red-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-all duration-200"
                            title={Object.keys(floorPlans).length <= 1 ? "Cannot delete the last floor" : `Delete Floor ${currentFloor}`}
                          >
                            <Trash2 size={hasTouchScreen ? 12 : 14} className="group-hover:scale-110 transition-transform duration-200" />
                          </button>
                        </div>
                        
                        {/* Modern Export Button */}
                        <button
                          className="group px-2 sm:px-3 py-1 text-xs sm:text-sm text-slate-700 hover:text-blue-600 rounded-xl transition-all duration-200 font-semibold border border-slate-300/50 hover:border-blue-400/50 hover:shadow-lg hover:scale-[1.02] flex items-center"
                          onClick={() => setShowExportModal(true)}
                          title="Export Floor Plan"
                        >
                          <Download size={12} className="inline mr-1 group-hover:scale-110 transition-transform duration-200" />
                          <span>Export</span>
                        </button>

                        {/* Modern Multi-Room AR Scan Button (Mobile) */}
                        {arScanning.openNativeARScanner && (
                          <button
                            onClick={handleStartARScan}
                            disabled={!arScanning.openNativeARScanner}
                            className="group px-2 py-1 text-xs text-slate-700 hover:text-emerald-600 rounded-xl transition-all duration-200 font-semibold border border-slate-300/50 hover:border-emerald-400/50 hover:shadow-lg hover:scale-[1.02] flex items-center"
                            title="Multi-Room AR Scanning"
                          >
                            <Zap size={12} className="inline mr-1 group-hover:scale-110 transition-transform duration-200" />
                            <span className="text-[10px] font-semibold">Room Scan</span>
                          </button>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2 flex-1 justify-start sm:justify-end">
                        {selectedWall && (
                          <button
                            className="px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm rounded-lg bg-red-100 text-red-700 hover:bg-red-200 border border-red-200"
                            onClick={() => { /* ... delete logic ... */ }}
                          >
                            Delete Wall
                          </button>
                                                  )}
                          {(() => {
                          const existingFloors = Object.keys(floorPlans).map(Number).sort((a, b) => a - b);
                          const hasGaps = existingFloors.length > 0 && existingFloors.some((floor, index) => floor !== index + 1);
                          return hasGaps ? (
                            <button
                              className="px-3 py-1 text-xs sm:px-4 sm:py-2 sm:text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 border border-green-600
                                         transition-colors font-medium shadow-sm"
                              onClick={recoverMissingFloors}
                              title="Recover missing floors (like Floor 1)"
                            >
                              Recover Floors
                            </button>
                          ) : null;
                        })()}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-7 xl:grid-cols-8 gap-2 sm:gap-3 lg:gap-4 w-full items-start">
                      <div className={`bg-gradient-to-br from-white via-slate-50 to-white rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden flex flex-col h-full ${
                        isIconsExpanded ? 'lg:col-span-7 xl:col-span-8' : 'lg:col-span-4 xl:col-span-5'
                      }`}>
                        {/* Modern Header */}
                        <div className="relative px-2 py-1 border-b border-slate-200/50">
                          <div className="flex items-center justify-center">
                            <div className="flex items-center gap-1">
                              <div className="p-0.5 bg-slate-100 rounded shadow-sm">
                                <Settings size={12} className="text-slate-700" />
                              </div>
                              <h3 className="font-bold text-xs text-slate-700">Element Tools</h3>
                            </div>
                          </div>
                        </div>

                        {/* Compact Element Grid */}
                        <div className="p-2 flex-grow flex flex-col">
                          {/* Main element grid - expands horizontally when showing more */}
                          <div className={`grid gap-1 lg:gap-1.5 xl:gap-2 p-0.5 ${
                            isIconsExpanded ? 'grid-cols-7' : 'grid-cols-5'
                          }`}>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'window'
                                  ? 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300/50 shadow-lg shadow-blue-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300/50 hover:shadow-lg hover:shadow-blue-200/30'
                              }`}
                              onClick={() => handleToolSelect('window' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'window'
                                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 scale-110'
                                  : 'bg-gradient-to-r from-blue-400 to-blue-500 group-hover:scale-105'
                              }`}>
                                <RectangleVertical size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Window</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'door'
                                  ? 'bg-gradient-to-br from-orange-50 to-amber-100 border-orange-300/50 shadow-lg shadow-orange-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-orange-50 hover:to-orange-100 hover:border-orange-300/50 hover:shadow-lg hover:shadow-orange-200/30'
                              }`}
                              onClick={() => handleToolSelect('door' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'door'
                                  ? 'bg-gradient-to-r from-orange-500 to-amber-600 scale-110'
                                  : 'bg-gradient-to-r from-orange-400 to-orange-500 group-hover:scale-105'
                              }`}>
                                <DoorOpen size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Door</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'radiator'
                                  ? 'bg-gradient-to-br from-rose-50 to-red-100 border-rose-300/50 shadow-lg shadow-rose-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-rose-50 hover:to-rose-100 hover:border-rose-300/50 hover:shadow-lg hover:shadow-rose-200/30'
                              }`}
                              onClick={() => handleToolSelect('radiator' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'radiator'
                                  ? 'bg-gradient-to-r from-rose-500 to-red-600 scale-110'
                                  : 'bg-gradient-to-r from-rose-400 to-rose-500 group-hover:scale-105'
                              }`}>
                                <Square size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Radiator</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'boiler'
                                  ? 'bg-gradient-to-br from-red-50 to-rose-100 border-red-300/50 shadow-lg shadow-red-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-red-50 hover:to-red-100 hover:border-red-300/50 hover:shadow-lg hover:shadow-red-200/30'
                              }`}
                              onClick={() => handleToolSelect('boiler' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'boiler'
                                  ? 'bg-gradient-to-r from-red-500 to-rose-600 scale-110'
                                  : 'bg-gradient-to-r from-red-400 to-red-500 group-hover:scale-105'
                              }`}>
                                <Flame size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Boiler</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'counter'
                                  ? 'bg-gradient-to-br from-slate-50 to-gray-100 border-slate-300/50 shadow-lg shadow-slate-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-slate-50 hover:to-gray-100 hover:border-slate-300/50 hover:shadow-lg hover:shadow-slate-200/30'
                              }`}
                              onClick={() => handleToolSelect('counter' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'counter'
                                  ? 'bg-gradient-to-r from-slate-500 to-gray-600 scale-110'
                                  : 'bg-gradient-to-r from-slate-400 to-slate-500 group-hover:scale-105'
                              }`}>
                                <Minus size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Counter</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'stairs'
                                  ? 'bg-gradient-to-br from-purple-50 to-violet-100 border-purple-300/50 shadow-lg shadow-purple-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-purple-50 hover:to-violet-100 hover:border-purple-300/50 hover:shadow-lg hover:shadow-purple-200/30'
                              }`}
                              onClick={() => handleToolSelect('stairs' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'stairs'
                                  ? 'bg-gradient-to-r from-purple-500 to-violet-600 scale-110'
                                  : 'bg-gradient-to-r from-purple-400 to-purple-500 group-hover:scale-105'
                              }`}>
                                <ArrowUp size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Stairs</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'light'
                                  ? 'bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-300/50 shadow-lg shadow-amber-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-amber-50 hover:to-yellow-100 hover:border-amber-300/50 hover:shadow-lg hover:shadow-amber-200/30'
                              }`}
                              onClick={() => handleToolSelect('light' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'light'
                                  ? 'bg-gradient-to-r from-amber-500 to-yellow-600 scale-110'
                                  : 'bg-gradient-to-r from-amber-400 to-yellow-500 group-hover:scale-105'
                              }`}>
                                <Lightbulb size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Light</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'smoke-alarm'
                                  ? 'bg-gradient-to-br from-red-50 to-rose-100 border-red-300/50 shadow-lg shadow-red-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-red-50 hover:to-red-100 hover:border-red-300/50 hover:shadow-lg hover:shadow-red-200/30'
                              }`}
                              onClick={() => handleToolSelect('smoke-alarm' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'smoke-alarm'
                                  ? 'bg-gradient-to-r from-red-500 to-rose-600 scale-110'
                                  : 'bg-gradient-to-r from-red-400 to-red-500 group-hover:scale-105'
                              }`}>
                                <AlertTriangle size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Smoke Alarm</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'gas-meter'
                                  ? 'bg-gradient-to-br from-orange-50 to-amber-100 border-orange-300/50 shadow-lg shadow-orange-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-orange-50 hover:to-orange-100 hover:border-orange-300/50 hover:shadow-lg hover:shadow-orange-200/30'
                              }`}
                              onClick={() => handleToolSelect('gas-meter' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'gas-meter'
                                  ? 'bg-gradient-to-r from-orange-500 to-amber-600 scale-110'
                                  : 'bg-gradient-to-r from-orange-400 to-orange-500 group-hover:scale-105'
                              }`}>
                                <Gauge size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Gas Meter</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'electric-meter'
                                  ? 'bg-gradient-to-br from-yellow-50 to-amber-100 border-yellow-300/50 shadow-lg shadow-yellow-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-yellow-50 hover:to-yellow-100 hover:border-yellow-300/50 hover:shadow-lg hover:shadow-yellow-200/30'
                              }`}
                              onClick={() => handleToolSelect('electric-meter' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'electric-meter'
                                  ? 'bg-gradient-to-r from-yellow-500 to-amber-600 scale-110'
                                  : 'bg-gradient-to-r from-yellow-400 to-yellow-500 group-hover:scale-105'
                              }`}>
                                <Zap size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Electric Meter</span>
                            </div>

                            {/* Expandable section - shows inline when expanded */}
                            {isIconsExpanded && (
                              <>
                              <div
                                className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                  (currentTool as string) === 'stop-tap'
                                    ? 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300/50 shadow-lg shadow-blue-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300/50 hover:shadow-lg hover:shadow-blue-200/30'
                                }`}
                                onClick={() => handleToolSelect('stop-tap' as ElementType)}
                              >
                                <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                  (currentTool as string) === 'stop-tap'
                                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 scale-110'
                                    : 'bg-gradient-to-r from-blue-400 to-blue-500 group-hover:scale-105'
                                }`}>
                                  <RotateCcw size={10} className="text-white" />
                                </div>
                                <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Stop Tap</span>
                              </div>
                              <div
                                className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                  (currentTool as string) === 'heat-alarm'
                                    ? 'bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-300/50 shadow-lg shadow-amber-200/50'
                                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-amber-50 hover:to-yellow-100 hover:border-amber-300/50 hover:shadow-lg hover:shadow-amber-200/30'
                                }`}
                                onClick={() => handleToolSelect('heat-alarm' as ElementType)}
                              >
                                <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                  (currentTool as string) === 'heat-alarm'
                                    ? 'bg-gradient-to-r from-amber-500 to-yellow-600 scale-110'
                                    : 'bg-gradient-to-r from-amber-400 to-amber-500 group-hover:scale-105'
                                }`}>
                                  <Thermometer size={10} className="text-white" />
                                </div>
                                <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Heat Alarm</span>
                              </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'alarm-sounder'
                                  ? 'bg-gradient-to-br from-violet-50 to-purple-100 border-violet-300/50 shadow-lg shadow-violet-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-violet-50 hover:to-purple-100 hover:border-violet-300/50 hover:shadow-lg hover:shadow-violet-200/30'
                              }`}
                              onClick={() => handleToolSelect('alarm-sounder' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'alarm-sounder'
                                  ? 'bg-gradient-to-r from-violet-500 to-purple-600 scale-110'
                                  : 'bg-gradient-to-r from-violet-400 to-violet-500 group-hover:scale-105'
                              }`}>
                                <Volume2 size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Alarm Sounder</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'wc-call-point'
                                  ? 'bg-gradient-to-br from-blue-50 to-sky-100 border-blue-300/50 shadow-lg shadow-blue-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-blue-50 hover:to-sky-100 hover:border-blue-300/50 hover:shadow-lg hover:shadow-blue-200/30'
                              }`}
                              onClick={() => handleToolSelect('wc-call-point' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'wc-call-point'
                                  ? 'bg-gradient-to-r from-blue-500 to-sky-600 scale-110'
                                  : 'bg-gradient-to-r from-blue-400 to-blue-500 group-hover:scale-105'
                              }`}>
                                <Users size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">W/C Call Point</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'ac-unit'
                                  ? 'bg-gradient-to-br from-cyan-50 to-sky-100 border-cyan-300/50 shadow-lg shadow-cyan-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-cyan-50 hover:to-sky-100 hover:border-cyan-300/50 hover:shadow-lg hover:shadow-cyan-200/30'
                              }`}
                              onClick={() => handleToolSelect('ac-unit' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'ac-unit'
                                  ? 'bg-gradient-to-r from-cyan-500 to-sky-600 scale-110'
                                  : 'bg-gradient-to-r from-cyan-400 to-cyan-500 group-hover:scale-105'
                              }`}>
                                <Wind size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">AC Unit</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'fire-alarm'
                                  ? 'bg-gradient-to-br from-red-50 to-rose-100 border-red-300/50 shadow-lg shadow-red-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-red-50 hover:to-red-100 hover:border-red-300/50 hover:shadow-lg hover:shadow-red-200/30'
                              }`}
                              onClick={() => handleToolSelect('fire-alarm' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'fire-alarm'
                                  ? 'bg-gradient-to-r from-red-500 to-rose-600 scale-110'
                                  : 'bg-gradient-to-r from-red-400 to-red-500 group-hover:scale-105'
                              }`}>
                                <Bell size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Fire Alarm</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'ice-machine'
                                  ? 'bg-gradient-to-br from-sky-50 to-blue-100 border-sky-300/50 shadow-lg shadow-sky-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-sky-50 hover:to-blue-100 hover:border-sky-300/50 hover:shadow-lg hover:shadow-sky-200/30'
                              }`}
                              onClick={() => handleToolSelect('ice-machine' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'ice-machine'
                                  ? 'bg-gradient-to-r from-sky-500 to-blue-600 scale-110'
                                  : 'bg-gradient-to-r from-sky-400 to-sky-500 group-hover:scale-105'
                              }`}>
                                <Snowflake size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Ice Machine</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'coffee-machine'
                                  ? 'bg-gradient-to-br from-amber-50 to-orange-100 border-amber-300/50 shadow-lg shadow-amber-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-amber-50 hover:to-orange-100 hover:border-amber-300/50 hover:shadow-lg hover:shadow-amber-200/30'
                              }`}
                              onClick={() => handleToolSelect('coffee-machine' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'coffee-machine'
                                  ? 'bg-gradient-to-r from-amber-600 to-orange-700 scale-110'
                                  : 'bg-gradient-to-r from-amber-500 to-amber-600 group-hover:scale-105'
                              }`}>
                                <Coffee size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Coffee Machine</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'emergency-light'
                                  ? 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-300/50 shadow-lg shadow-green-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-green-50 hover:to-green-100 hover:border-green-300/50 hover:shadow-lg hover:shadow-green-200/30'
                              }`}
                              onClick={() => handleToolSelect('emergency-light' as FireSafetyType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'emergency-light'
                                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 scale-110'
                                  : 'bg-gradient-to-r from-green-400 to-green-500 group-hover:scale-105'
                              }`}>
                                <Zap size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Emergency</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'fire-call-point'
                                  ? 'bg-gradient-to-br from-red-50 to-orange-100 border-red-400/50 shadow-lg shadow-red-300/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-red-50 hover:to-orange-100 hover:border-red-400/50 hover:shadow-lg hover:shadow-red-300/30'
                              }`}
                              onClick={() => handleToolSelect('fire-call-point' as FireSafetyType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'fire-call-point'
                                  ? 'bg-gradient-to-r from-red-600 to-orange-600 scale-110'
                                  : 'bg-gradient-to-r from-red-500 to-orange-500 group-hover:scale-105'
                              }`}>
                                <Target size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Call Point</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'fire-panel'
                                  ? 'bg-gradient-to-br from-orange-50 to-amber-100 border-orange-300/50 shadow-lg shadow-orange-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-orange-50 hover:to-amber-100 hover:border-orange-300/50 hover:shadow-lg hover:shadow-orange-200/30'
                              }`}
                              onClick={() => handleToolSelect('fire-panel' as FireSafetyType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'fire-panel'
                                  ? 'bg-gradient-to-r from-orange-600 to-amber-600 scale-110'
                                  : 'bg-gradient-to-r from-orange-500 to-amber-500 group-hover:scale-105'
                              }`}>
                                <Database size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Fire Panel</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'tank-cylinder'
                                  ? 'bg-gradient-to-br from-slate-50 to-gray-100 border-slate-300/50 shadow-lg shadow-slate-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-slate-50 hover:to-slate-100 hover:border-slate-300/50 hover:shadow-lg hover:shadow-slate-200/30'
                              }`}
                              onClick={() => handleToolSelect('tank-cylinder' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'tank-cylinder'
                                  ? 'bg-gradient-to-r from-slate-500 to-gray-600 scale-110'
                                  : 'bg-gradient-to-r from-slate-400 to-slate-500 group-hover:scale-105'
                              }`}>
                                <Database size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Tank/Cylinder</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'thermostat'
                                  ? 'bg-gradient-to-br from-purple-50 to-violet-100 border-purple-300/50 shadow-lg shadow-purple-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-purple-50 hover:to-purple-100 hover:border-purple-300/50 hover:shadow-lg hover:shadow-purple-200/30'
                              }`}
                              onClick={() => handleToolSelect('thermostat' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'thermostat'
                                  ? 'bg-gradient-to-r from-purple-500 to-violet-600 scale-110'
                                  : 'bg-gradient-to-r from-purple-400 to-purple-500 group-hover:scale-105'
                              }`}>
                                <Thermometer size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Thermostat</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'consumer-unit'
                                  ? 'bg-gradient-to-br from-indigo-50 to-purple-100 border-indigo-300/50 shadow-lg shadow-indigo-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-indigo-50 hover:to-indigo-100 hover:border-indigo-300/50 hover:shadow-lg hover:shadow-indigo-200/30'
                              }`}
                              onClick={() => handleToolSelect('consumer-unit' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'consumer-unit'
                                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 scale-110'
                                  : 'bg-gradient-to-r from-indigo-400 to-indigo-500 group-hover:scale-105'
                              }`}>
                                <Grid3X3 size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Consumer Unit</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'air-source-hp'
                                  ? 'bg-gradient-to-br from-teal-50 to-cyan-100 border-teal-300/50 shadow-lg shadow-teal-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-teal-50 hover:to-teal-100 hover:border-teal-300/50 hover:shadow-lg hover:shadow-teal-200/30'
                              }`}
                              onClick={() => handleToolSelect('air-source-hp' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'air-source-hp'
                                  ? 'bg-gradient-to-r from-teal-500 to-cyan-600 scale-110'
                                  : 'bg-gradient-to-r from-teal-400 to-teal-500 group-hover:scale-105'
                              }`}>
                                <Fan size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Air Source HP</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'loft-hatch'
                                  ? 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300/50 shadow-lg shadow-blue-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300/50 hover:shadow-lg hover:shadow-blue-200/30'
                              }`}
                              onClick={() => handleToolSelect('loft-hatch' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'loft-hatch'
                                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 scale-110'
                                  : 'bg-gradient-to-r from-blue-400 to-blue-500 group-hover:scale-105'
                              }`}>
                                <Square size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Loft Hatch</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'fan'
                                  ? 'bg-gradient-to-br from-cyan-50 to-sky-100 border-cyan-300/50 shadow-lg shadow-cyan-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-cyan-50 hover:to-cyan-100 hover:border-cyan-300/50 hover:shadow-lg hover:shadow-cyan-200/30'
                              }`}
                              onClick={() => handleToolSelect('fan' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'fan'
                                  ? 'bg-gradient-to-r from-cyan-500 to-sky-600 scale-110'
                                  : 'bg-gradient-to-r from-cyan-400 to-cyan-500 group-hover:scale-105'
                              }`}>
                                <Fan size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Fan</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'background-vent'
                                  ? 'bg-gradient-to-br from-sky-50 to-blue-100 border-sky-300/50 shadow-lg shadow-sky-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-sky-50 hover:to-sky-100 hover:border-sky-300/50 hover:shadow-lg hover:shadow-sky-200/30'
                              }`}
                              onClick={() => handleToolSelect('background-vent' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'background-vent'
                                  ? 'bg-gradient-to-r from-sky-500 to-blue-600 scale-110'
                                  : 'bg-gradient-to-r from-sky-400 to-sky-500 group-hover:scale-105'
                              }`}>
                                <Wind size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Background Vent</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'single-sink'
                                  ? 'bg-gradient-to-br from-blue-50 to-cyan-100 border-blue-300/50 shadow-lg shadow-blue-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-blue-50 hover:to-cyan-100 hover:border-blue-300/50 hover:shadow-lg hover:shadow-blue-200/30'
                              }`}
                              onClick={() => handleToolSelect('single-sink' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'single-sink'
                                  ? 'bg-gradient-to-r from-blue-500 to-cyan-600 scale-110'
                                  : 'bg-gradient-to-r from-blue-400 to-blue-500 group-hover:scale-105'
                              }`}>
                                <Square size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Single Sink</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'double-sink'
                                  ? 'bg-gradient-to-br from-cyan-50 to-blue-100 border-cyan-300/50 shadow-lg shadow-cyan-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-cyan-50 hover:to-blue-100 hover:border-cyan-300/50 hover:shadow-lg hover:shadow-cyan-200/30'
                              }`}
                              onClick={() => handleToolSelect('double-sink' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'double-sink'
                                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 scale-110'
                                  : 'bg-gradient-to-r from-cyan-400 to-cyan-500 group-hover:scale-105'
                              }`}>
                                <Square size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Double Sink</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'garden'
                                  ? 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-300/50 shadow-lg shadow-green-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-green-50 hover:to-green-100 hover:border-green-300/50 hover:shadow-lg hover:shadow-green-200/30'
                              }`}
                              onClick={() => handleToolSelect('garden' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'garden'
                                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 scale-110'
                                  : 'bg-gradient-to-r from-green-400 to-green-500 group-hover:scale-105'
                              }`}>
                                <CircleDot size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Garden</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'compass'
                                  ? 'bg-gradient-to-br from-red-50 to-orange-100 border-red-300/50 shadow-lg shadow-red-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-red-50 hover:to-red-100 hover:border-red-300/50 hover:shadow-lg hover:shadow-red-200/30'
                              }`}
                              onClick={() => handleToolSelect('compass' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'compass'
                                  ? 'bg-gradient-to-r from-red-500 to-orange-600 scale-110'
                                  : 'bg-gradient-to-r from-red-400 to-red-500 group-hover:scale-105'
                              }`}>
                                <Compass size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Compass</span>
                            </div>
                            {/* 🏢 New Estate Elements: Hob, Bathtub, Shower, Toilet */}
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'hob'
                                  ? 'bg-gradient-to-br from-cyan-50 to-blue-100 border-cyan-300/50 shadow-lg shadow-cyan-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-cyan-50 hover:to-cyan-100 hover:border-cyan-300/50 hover:shadow-lg hover:shadow-cyan-200/30'
                              }`}
                              onClick={() => handleToolSelect('hob' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'hob'
                                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 scale-110'
                                  : 'bg-gradient-to-r from-cyan-400 to-cyan-500 group-hover:scale-105'
                              }`}>
                                <Grid3X3 size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Hob</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'bathtub'
                                  ? 'bg-gradient-to-br from-sky-50 to-cyan-100 border-sky-300/50 shadow-lg shadow-sky-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-sky-50 hover:to-sky-100 hover:border-sky-300/50 hover:shadow-lg hover:shadow-sky-200/30'
                              }`}
                              onClick={() => handleToolSelect('bathtub' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'bathtub'
                                  ? 'bg-gradient-to-r from-sky-500 to-cyan-600 scale-110'
                                  : 'bg-gradient-to-r from-sky-400 to-sky-500 group-hover:scale-105'
                              }`}>
                                <Bath size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Bathtub</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'shower'
                                  ? 'bg-gradient-to-br from-blue-50 to-sky-100 border-blue-300/50 shadow-lg shadow-blue-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300/50 hover:shadow-lg hover:shadow-blue-200/30'
                              }`}
                              onClick={() => handleToolSelect('shower' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'shower'
                                  ? 'bg-gradient-to-r from-blue-500 to-sky-600 scale-110'
                                  : 'bg-gradient-to-r from-blue-400 to-blue-500 group-hover:scale-105'
                              }`}>
                                <Droplets size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Shower</span>
                            </div>
                            <div
                              className={`group flex flex-col items-center justify-start p-0.5 lg:p-1 xl:p-1.5 rounded-xl transition-all duration-200 cursor-pointer h-10 lg:h-12 xl:h-14 border ${
                                (currentTool as string) === 'toilet'
                                  ? 'bg-gradient-to-br from-slate-50 to-gray-100 border-slate-300/50 shadow-lg shadow-slate-200/50'
                                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/50 hover:from-slate-50 hover:to-slate-100 hover:border-slate-300/50 hover:shadow-lg hover:slate-200/30'
                              }`}
                              onClick={() => handleToolSelect('toilet' as ElementType)}
                            >
                              <div className={`p-1 rounded-lg shadow-md transition-all duration-200 mb-1 ${
                                (currentTool as string) === 'toilet'
                                  ? 'bg-gradient-to-r from-slate-500 to-gray-600 scale-110'
                                  : 'bg-gradient-to-r from-slate-400 to-slate-500 group-hover:scale-105'
                              }`}>
                                <CircleDot size={10} className="text-white" />
                              </div>
                              <span className="text-[8px] font-medium text-slate-700 text-center leading-none">Toilet</span>
                            </div>
                            </>
                            )}
                          </div>

                        {/* Expand/Collapse Button */}
                        <button
                          onClick={() => setIsIconsExpanded(!isIconsExpanded)}
                          className="w-full py-1 px-2 text-[10px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors flex items-center justify-center gap-1 border-t border-slate-200/50"
                        >
                          {isIconsExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          {isIconsExpanded ? 'Show Less' : 'Show More (14 more)'}
                        </button>
                        </div>
                      </div>

                      {/* Estate Area Panel - Hidden when elements are expanded */}
                      {!isIconsExpanded && (
                        <div className="lg:col-span-3 xl:col-span-3">
                          <EstateAreaPanel
                            currentWalls={currentWalls}
                            roomCustomLabels={roomCustomLabels}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div 
                className={`
                  flex w-full h-full 
                  ${hasTouchScreen ? 'flex-col' : 'gap-4'}
                  overflow-hidden flex-1 min-h-0
                `}
              >
                <div className={`flex flex-col w-full h-full min-h-0`}>
                  <div
                    ref={containerRef}
                    className={`
                      bg-white overflow-hidden relative w-full h-full flex-1 min-h-0
                      ${activeFullscreenState 
                        ? 'fixed inset-0 z-[9999] rounded-none shadow-none border-none' 
                        : 'rounded-lg shadow-lg'
                      }
                    `}
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'flex-start', 
                      alignItems: 'flex-start',
                      // Enhanced responsive sizing - let grid cell determine height
                      minHeight: activeFullscreenState ? '100vh' : '600px', // Higher min height for better UX
                      height: activeFullscreenState ? '100vh' : '100%', // Fill grid cell in normal mode
                      // Add safe area support for devices with notches
                      ...(activeFullscreenState && {
                        // Remove padding - let canvas fill entire screen
                        // Toolbars will overlay on top with higher z-index
                        paddingLeft: 'env(safe-area-inset-left)',
                        paddingRight: 'env(safe-area-inset-right)',
                        height: '100vh',
                        width: '100vw'
                      })
                    }}
                  >
                    {/* Hide fullscreen button when any modal is open or AR scanner is active */}
                    {!isWindowPromptOpen && !isRadiatorPromptOpen && !isBoilerPromptOpen && !isDoorPromptOpen && !isRoomLabelPromptOpen && !isRoomHeightPromptOpen && !isRoomRenameModalOpen && !isElementInfoModalOpen && !showOnboardingModal && !showSiteNotes && !showExportModal && !showExportLoadingModal && !showARScanLimitModal && !arScanning.isNativeARScannerOpen && !arScanning.isARScannerOpen && !showAROrientationPreview && !isFileLoading && (
                      <button
                        onClick={handleToggleFullscreen}
                        title={activeFullscreenState ? "Exit Fullscreen Mode" : "Enter Fullscreen Mode"}
                        className={`
                          absolute z-[10002] px-2.5 py-1.5 text-xs bg-white text-gray-700 rounded-lg hover:bg-purple-50 hover:text-purple-600 transition-all duration-200 font-medium shadow-sm border border-gray-200 hover:border-purple-300 flex items-center gap-1.5 group
                          ${activeFullscreenState ? 'right-4' : 'top-4 right-4'}
                        `}
                        style={activeFullscreenState ? {
                          // Position below the safe area, toolbar, and element tools table
                          top: 'calc(env(safe-area-inset-top) + 280px)'
                        } : {}}
                        aria-label={activeFullscreenState ? "Exit Fullscreen" : "Enter Fullscreen"}
                      >
                        {activeFullscreenState ? (
                          <>
                            <Minimize size={13} className="group-hover:scale-110 transition-transform duration-200" />
                            <span>Exit</span>
                          </>
                        ) : (
                          <>
                            <Maximize size={13} className="group-hover:scale-110 transition-transform duration-200" />
                            <span>Fullscreen</span>
                          </>
                        )}
                      </button>
                    )}

                    <div
                      className="relative w-full h-full flex-1 min-h-0"
                      style={{ overflow: 'hidden' }}
                    >
                      <canvas
                        ref={canvasRef}
                        style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            background: '#ffffff',
                            touchAction: 'none',
                            WebkitTouchCallout: 'none',
                            WebkitUserSelect: 'none',
                            WebkitTapHighlightColor: 'transparent',
                            userSelect: 'none',
                            msUserSelect: 'none',
                            cursor: getCursorStyleUtil(hasTouchScreen, isPanning, currentTool),
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleCanvasMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        onDoubleClick={handleCanvasDoubleClick}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEndEvent}
                        onWheel={handleWheel}
                        onContextMenu={(e) => e.preventDefault()}
                      />

                      {textBoxes.filter((box: TextBox) => box.isEditing).map((box: TextBox) => (
                        <TextBoxEditor 
                          key={box.id} 
                          textBox={box} 
                          setTextBoxes={setTextBoxes}
                          setSelectedTextBox={setSelectedTextBox}
                          canvasSize={CANVAS_SIZE}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <RoomLabelPrompt
                isOpen={isRoomLabelPromptOpen}
                onClose={() => {
                  setIsRoomLabelPromptOpen(false);
                  setRoomToLabel(null);
                }}
                onSave={handleSaveRoomLabel}
                roomToLabel={roomToLabel} 
                existingRooms={getAllExistingRoomsAcrossFloors}
                isFullscreen={activeFullscreenState}
                skipWallImages={true}
                multiRoomMode={isCollectingMultiRoomLabels}
                currentRoomNumber={currentLabelingRoomIndex + 1}
                totalRoomCount={multiRoomCount}
                isEstate={true}
              />

              <RoomHeightPrompt
                isOpen={isRoomHeightPromptOpen}
                onClose={() => {
                  setIsRoomHeightPromptOpen(false);
                  setRoomToSetHeight(null);
                }}
                onSave={handleSaveRoomHeight}
                roomName={roomToSetHeight?.name || 'New Room'}
                defaultHeight={currentFloor === 0 ? 2.2 : 2.4} // Basement vs standard height
                isFullscreen={activeFullscreenState}
              />

              <RoomRenameModal
                isOpen={isRoomRenameModalOpen}
                onClose={handleCloseRoomRenameModal}
                onRename={handleRoomRename}
                roomToRename={roomToRename}
                existingRooms={getAllExistingRoomsAcrossFloors}
                existingLabels={roomCustomLabels}
                isFullscreen={activeFullscreenState}
              />

                      <RoomContextMenu
          isVisible={isRoomContextMenuOpen}
          position={contextMenuPosition}
          room={contextMenuRoom}
          onClose={handleCloseRoomContextMenu}
          onRename={handleInitiateRoomRename}
          onSelect={handleRoomSelect}
          onLabel={handleInitiateRoomLabel}
          onDelete={(room) => {
            setSelectedRoom(room);
            deleteSelectedRoom();
          }}
          roomLabels={roomCustomLabels}
        />

              <RoomFloatingActions
                selectedRoom={selectedRoom}
                isVisible={showFloatingActions && hasTouchScreen}
                onRename={handleInitiateRoomRename}
                onClose={handleCloseFloatingActions}
              />

              {/* Gap Fill Suggestion Prompt */}
              {gapFillSuggestion && cursorPosition && (
                <GapFillPrompt
                  suggestion={gapFillSuggestion}
                  position={cursorPosition}
                  onAccept={() => {
                    // Apply the gap fill walls
                    if (gapFillSuggestion.proposedWalls.length > 0) {
                      const newWalls = [...currentWalls, ...gapFillSuggestion.proposedWalls];
                      setFloorPlans(prev => ({
                        ...prev,
                        [currentFloor]: newWalls
                      }));
                      recordUndoState();
                      console.log('✅ Gap filled with', gapFillSuggestion.proposedWalls.length, 'new walls');
                    }
                    setGapFillSuggestion(null);
                  }}
                  onReject={() => setGapFillSuggestion(null)}
                  scale={scale}
                />
              )}

              {/* NEW: Room Positioning Mode Controls - Responsive Compact Design */}
              {isPositioningRoom && positioningRoomId && (
                <div 
                  className="fixed left-1/2 transform -translate-x-1/2 z-[9999] bg-white rounded-lg shadow-2xl border-2 border-blue-500 w-auto mx-2"
                  style={{ 
                    top: 'calc(env(safe-area-inset-top) + 1rem)',
                    maxWidth: 'min(90vw, 900px)' // Responsive: 90% viewport width, max 900px
                  }}
                >
                  <div className="flex items-center justify-between px-2 sm:px-4 py-2 gap-2 sm:gap-4 flex-wrap sm:flex-nowrap">
                    {/* Title and Expand/Collapse Button */}
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-gray-800 whitespace-nowrap">Position AR Room</h3>
                      <button
                        onClick={() => setIsPositioningModalExpanded(!isPositioningModalExpanded)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title={isPositioningModalExpanded ? "Minimize" : "Expand"}
                      >
                        {isPositioningModalExpanded ? (
                          <ChevronUp size={16} className="text-gray-600" />
                        ) : (
                          <ChevronDown size={16} className="text-gray-600" />
                        )}
                      </button>
                    </div>
                    
                    {/* Compact Instructions (when expanded) */}
                    {isPositioningModalExpanded && (
                      <div className="flex items-center gap-4 text-xs text-gray-700">
                        <span className="flex items-center gap-1">
                          <span className="font-semibold">📍 Move:</span>
                          <span>Drag center</span>
                        </span>
                        <span className="w-px h-4 bg-gray-300"></span>
                        <span className="flex items-center gap-1">
                          <span className="font-semibold">📐 Adjust:</span>
                          <span>Drag corners</span>
                        </span>
                        <span className="w-px h-4 bg-gray-300"></span>
                        <span className="text-gray-500 italic">Orange lines = measurements</span>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                    <button
                      onClick={() => {
                        // Undo would need to restore original positions - for now just exit
                        console.log('[Room Positioning] Cancelled - user should use undo to restore original position');
                        
                        // Exit positioning mode
                        setIsPositioningRoom(false);
                        setPositioningRoomId('');
                        setRoomDragOffset({ x: 0, y: 0 });
                        setRoomDragStart(null);
                        setResizeHandle(null);
                        setRoomStart(null);
                        setPositioningTransformedWalls(null);
                        
                        // Reset tool back to select
                        setCurrentTool('select');
                        setSelectedRoom(null);
                        console.log('[Room Positioning] Cancelled positioning, reset tool to select');
                      }}
                      className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        // Walls already updated in real-time, just exit positioning mode
                        console.log('[Room Positioning] Position confirmed - changes already applied');
                        
                        // Exit positioning mode
                        setIsPositioningRoom(false);
                        setPositioningRoomId('');
                        setRoomDragOffset({ x: 0, y: 0 });
                        setRoomDragStart(null);
                        setResizeHandle(null);
                        setRoomStart(null);
                        setPositioningTransformedWalls(null);
                        
                        // Exit fullscreen mode and restore view state
                        if (activeFullscreenState) {
                          handleToggleFullscreen();
                          console.log('[Room Positioning] Exited fullscreen mode and restored view state');
                        }
                        
                        // Reset tool back to select
                        setCurrentTool('select');
                        currentToolInternalRef.current = 'select';
                        setSelectedRoom(null);
                        console.log('[Room Positioning] Exited positioning mode, reset tool to select');
                      }}
                      className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors shadow-md"
                    >
                      Set Position ✓
                    </button>
                  </div>
                  </div>
                </div>
              )}

              {/* Room Merge Notification */}
              {showMergeNotification && mergeNotificationData && (
                <RoomMergeNotification
                  message={mergeNotificationData.message}
                  roomCount={mergeNotificationData.roomCount}
                  wallCount={mergeNotificationData.wallCount}
                  onClose={() => {
                    setShowMergeNotification(false);
                    setMergeNotificationData(null);
                  }}
                />
              )}

              <div
                className={`fixed bottom-0 left-0 right-0 ${activeFullscreenState ? 'bg-black/80 border-t border-gray-700 z-[10000]' : 'bg-white border-t border-gray-200 z-[60]'} shadow-md flex-shrink-0 bottom-toolbar-safe-area ${arScanning.isARScannerOpen || arScanning.isNativeARScannerOpen || (activeFullscreenState && showExportModal) || isPositioningRoom ? 'hidden' : ''} ${isWindowPromptOpen || isRadiatorPromptOpen || isBoilerPromptOpen || isDoorPromptOpen || isElementInfoModalOpen ? 'opacity-30 pointer-events-none' : 'opacity-100'} transition-opacity duration-300`}
              >
                <Toolbar
                  currentTool={currentTool}
                  onToolSelect={handleToolSelect}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                  canUndo={undoStack.length > 0}
                  canRedo={redoStack.length > 0}
                  onDownload={handleDownload}
                  hasTouchScreen={hasTouchScreen}
                  showMeasurements={showMeasurements}
                  onFileRepair={handleFileRepair}
                  onOpenOrientationModal={handleOpenOrientationModal}
                  propertyOrientation={propertyOrientation}
                  hideTools={['hlp' as ElementType, 'pw' as ElementType, 'sw' as ElementType, 'highlight' as ElementType]}
                />
              </div>

      {/* AR Room Scanner */}
      {arScanning.isARScannerOpen && (
        <ARRoomScanner
          onScanComplete={arScanning.handleScanComplete}
          onClose={arScanning.closeARScanner}
          onIntegrateToFloorPlan={arScanning.integrateScanToFloorPlan}
        />
      )}

      {/* Native AR Room Scanner */}
      {arScanning.isNativeARScannerOpen && (
        <NativeARRoomScanner
          onScanComplete={arScanning.handleScanComplete}
          onClose={arScanning.closeNativeARScanner}
          onIntegrateToFloorPlan={arScanning.integrateScanToFloorPlan}
          onMultiRoomIntegrate={(result: ARMultiRoomScanResult) => arScanning.integrateMultiRoomScan(result, setRoomCustomLabels)}
        />
      )}

      {/* AR Wall Angle Prompt - REMOVED: Using raw AR scanning */}
        
      {/* AR Orientation Preview */}
      {showAROrientationPreview && (
        <AROrientationPreview
          walls={pendingARWalls}
          existingWalls={[]}
          onWallSelected={(wall) => handleAROrientationComplete(wall)}
          onSkip={() => handleAROrientationComplete(null)}
        />
      )}
        
      {/* Window Type Prompt */}
      <WindowTypePrompt
        isOpen={isWindowTypePromptOpen}
        onSelect={handleWindowTypeSelect}
        onClose={handleWindowTypePromptClose}
        isFullscreen={activeFullscreenState}
        hideSolidBox={true}
      />

      <WindowDimensionPrompt
        isOpen={isWindowPromptOpen}
        windowElement={windowToEdit}
        onSave={handleSaveWindowDimensions}
        onDelete={handleDeleteWindow}
        onClose={() => {
          setIsWindowPromptOpen(false);
          setWindowToEdit(null);
        }}
        isFullscreen={activeFullscreenState}
      />
      <RadiatorDimensionPrompt
        isOpen={isRadiatorPromptOpen}
        radiatorElement={radiatorToEdit}
        onSave={handleSaveRadiatorDimensions}
        onDelete={handleDeleteRadiator}
        onClose={() => setIsRadiatorPromptOpen(false)}
        isFullscreen={activeFullscreenState}
      />
      <BoilerInfoPrompt
        isOpen={isBoilerPromptOpen}
        boilerElement={boilerToEdit}
        onSave={handleSaveBoilerInfo}
        onClose={() => setIsBoilerPromptOpen(false)}
        onPCDBSystemSync={handleBoilerPCDBSystemSync}
        isFullscreen={activeFullscreenState}
      />
      <DoorUndercutPrompt
        isOpen={isDoorPromptOpen}
        doorElement={doorToEdit}
        rooms={currentRooms}
        onSave={handleSaveDoorUndercut}
        onFlip={handleFlipDoorSwing}
        onDelete={handleDeleteDoor}
        onClose={() => setIsDoorPromptOpen(false)}
        isFullscreen={activeFullscreenState}
        skipWallImages={true}
      />
      <ElementInfoModal
        isVisible={isElementInfoModalOpen}
        element={elementToView}
        onClose={handleCloseElementInfoModal}
        onEdit={handleEditElement}
        isFullscreen={activeFullscreenState}
      />
      
      {/* Highlight Calculations Popup */}
      {showHighlightPopup && highlightPopupData && (
        <HighlightCalculationsPopup
          highlightArea={highlightPopupData.highlight}
          calculations={highlightPopupData.calculations}
          onClose={() => {
            // Keep the highlight on canvas with orange border (don't remove it)
            // The highlight will persist and show the orange border to distinguish extensions
            setShowHighlightPopup(false);
            setHighlightPopupData(null);
          }}
          onSubmit={(floorLevel: string, buildingType: string) => {
            console.log('📝 Populating to site notes:', {
              floorLevel,
              buildingType,
              area: highlightPopupData.calculations.area,
              hlp: highlightPopupData.calculations.hlp,
              pw: highlightPopupData.calculations.pw,
              sw: highlightPopupData.calculations.sw
            });
            
            // Update site notes data with the highlight calculations
            setSiteNotesData(prevData => {
              const updatedData = { ...prevData };
              
              // Get the calculations for the specific floor level being assigned to (not the current viewing floor)
              const floorLevelToNumber: Record<string, number> = {
                'basement': 0,
                'ground floor': 1,
                'first floor': 2,
                'second floor': 3,
                'third floor': 4,
                'fourth floor': 5,
                'fifth floor': 6
              };
              const targetFloorNumber = floorLevelToNumber[floorLevel];
              
              // Get calculations for the target floor level, not the current viewing floor
              let targetFloorCalculations;
              if (targetFloorNumber === currentFloor) {
                // If it's the current floor, use current calculations
                targetFloorCalculations = getDisplayMeasurements();
              } else {
                // If it's a different floor, calculate for that specific floor
                const targetFloorWalls = floorPlans[targetFloorNumber] || [];
                const wallsWithTempIds = targetFloorWalls.map((wall, index) => ({
                  ...wall,
                  tempId: `wall-${targetFloorNumber}-${index}`,
                  ...(wall.isExtrusionConnector && { isExtrusionConnector: true }),
                  ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: true }),
                  ...(wall.isSplitOrigin && { isSplitOrigin: true })
                }));
                
                targetFloorCalculations = calculateOverallWallLengthsAndArea(
                  wallsWithTempIds, 
                  roomCustomLabels, 
                  'explicitRoomCreation',
                  targetFloorNumber
                );
              }
              
              console.log(`🎯 TARGETED FLOOR CALCULATIONS: Using floor ${targetFloorNumber} calculations for ${floorLevel}:`, {
                targetFloorNumber,
                currentViewingFloor: currentFloor,
                calculations: targetFloorCalculations,
                source: targetFloorNumber === currentFloor ? 'current floor' : 'calculated for target floor'
              });
              
              // Find or create the floor with matching floorLevel and buildingType
              let targetFloor = updatedData.floors.find(f => 
                f.floorLevel === floorLevel && f.buildingType === buildingType
              );
              
              if (!targetFloor) {
                // Create new floor if it doesn't exist
                const newFloorId = Math.max(...updatedData.floors.map(f => f.id), 0) + 1;
                targetFloor = {
                  id: newFloorId,
                  floorLevel,
                  buildingType,
                  height: '',
                  floorArea: '',
                  hlp: '',
                  pw: '',
                  rooms: []
                };
                updatedData.floors.push(targetFloor);
              }
              
              // Update the target floor calculations (extension)
              targetFloor.floorArea = highlightPopupData.calculations.area.toFixed(2);
              targetFloor.hlp = highlightPopupData.calculations.hlp.toFixed(2);
              targetFloor.pw = highlightPopupData.calculations.pw.toFixed(2);
              
              // 🔧 ACCURATE HIGHLIGHTER FORMULAS: Store detailed breakdown data
              if (highlightPopupData?.highlight) {
                const highlight = highlightPopupData.highlight;
                const calculations = highlightPopupData.calculations;
                
                // Calculate actual rectangle dimensions from highlight coordinates
                const rectWidth = Math.abs(highlight.endPoint.x - highlight.startPoint.x);
                const rectHeight = Math.abs(highlight.endPoint.y - highlight.startPoint.y);
                
                // Convert pixel dimensions to meters using the same conversion as the calculations
                const PIXELS_PER_METER = 100; // Same as in highlightCalculations.ts
                const widthInMeters = (rectWidth / PIXELS_PER_METER);
                const heightInMeters = (rectHeight / PIXELS_PER_METER);
                
                // Get the individual wall segments from the detailed calculation
                // Store simple area breakdown for Estate Agency
                console.log('🎯 STORING AREA BREAKDOWN:', {
                  dimensions: { width: widthInMeters, height: heightInMeters },
                  totalArea: calculations.area
                });
                
                targetFloor.highlighterBreakdown = {
                  dimensions: {
                    width: widthInMeters,
                    height: heightInMeters
                  },
                  hlpSegments: [], // Not used in Estate Agency
                  pwSegments: [],  // Not used in Estate Agency
                  swSegments: []   // Not used in Estate Agency
                };
              }
              
              // If assigning to an extension, subtract from main building
              if (buildingType !== 'main building') {
                // Find or create the main building floor
                let mainBuildingFloor = updatedData.floors.find(f => 
                  f.floorLevel === floorLevel && f.buildingType === 'main building'
                );
                
                if (!mainBuildingFloor) {
                  // Create main building floor with target floor calculations
                  const mainFloorId = Math.max(...updatedData.floors.map(f => f.id), 0) + 1;
                  mainBuildingFloor = {
                    id: mainFloorId,
                    floorLevel,
                    buildingType: 'main building',
                    height: '',
                    floorArea: targetFloorCalculations.totalArea.toFixed(2),
                    hlp: targetFloorCalculations.hlpLength.toFixed(2),
                    pw: targetFloorCalculations.pwLength.toFixed(2),
                    rooms: []
                  };
                  updatedData.floors.push(mainBuildingFloor);
                }
                
                // Calculate net values for main building (subtract assigned area)
                const currentMainArea = parseFloat(mainBuildingFloor.floorArea?.replace(' m²', '')) || targetFloorCalculations.totalArea;
                const currentMainHlp = parseFloat(mainBuildingFloor.hlp?.replace(' m', '')) || targetFloorCalculations.hlpLength;
                const currentMainPw = parseFloat(mainBuildingFloor.pw?.replace(' m', '')) || targetFloorCalculations.pwLength;
                
                // Calculate total assigned to extensions for this floor level
                const totalAssignedToExtensions = updatedData.floors
                  .filter(f => f.floorLevel === floorLevel && f.buildingType !== 'main building')
                  .reduce((total, floor) => ({
                    area: total.area + (parseFloat(floor.floorArea?.replace(' m²', '')) || 0),
                    hlp: total.hlp + (parseFloat(floor.hlp?.replace(' m', '')) || 0),
                    pw: total.pw + (parseFloat(floor.pw?.replace(' m', '')) || 0)
                  }), { area: 0, hlp: 0, pw: 0 });
                
                // Update main building with net values
                mainBuildingFloor.floorArea = Math.max(0, targetFloorCalculations.totalArea - totalAssignedToExtensions.area).toFixed(2);
                mainBuildingFloor.hlp = Math.max(0, targetFloorCalculations.hlpLength - totalAssignedToExtensions.hlp).toFixed(2);
                mainBuildingFloor.pw = Math.max(0, targetFloorCalculations.pwLength - totalAssignedToExtensions.pw).toFixed(2);
                
                console.log('🏗️ Main building adjusted - COMPREHENSIVE DEBUG:', {
                  targetFloorLevel: floorLevel,
                  targetFloorNumber: targetFloorNumber,
                  currentViewingFloor: currentFloor,
                  originalCalculations: {
                    totalArea: targetFloorCalculations.totalArea,
                    hlpLength: targetFloorCalculations.hlpLength,
                    pwLength: targetFloorCalculations.pwLength
                  },
                  totalAssignedToExtensions: {
                    area: totalAssignedToExtensions.area,
                    hlp: totalAssignedToExtensions.hlp,
                    pw: totalAssignedToExtensions.pw
                  },
                  newMainBuildingValues: {
                    area: parseFloat(mainBuildingFloor.floorArea),
                    hlp: parseFloat(mainBuildingFloor.hlp),
                    pw: parseFloat(mainBuildingFloor.pw)
                  },
                  currentExtensionBeingAdded: {
                    buildingType,
                    area: highlightPopupData.calculations.area,
                    hlp: highlightPopupData.calculations.hlp,
                    pw: highlightPopupData.calculations.pw
                  },
                  calculationCheck: {
                    areaSubtraction: `${targetFloorCalculations.totalArea} - ${totalAssignedToExtensions.area} = ${targetFloorCalculations.totalArea - totalAssignedToExtensions.area}`,
                    hlpSubtraction: `${targetFloorCalculations.hlpLength} - ${totalAssignedToExtensions.hlp} = ${targetFloorCalculations.hlpLength - totalAssignedToExtensions.hlp}`,
                    pwSubtraction: `${targetFloorCalculations.pwLength} - ${totalAssignedToExtensions.pw} = ${targetFloorCalculations.pwLength - totalAssignedToExtensions.pw}`
                  }
                });
              }
              
              console.log('✅ Site notes data updated with highlight calculations:', {
                targetFloor,
                updatedFloors: updatedData.floors
              });
              
              return updatedData;
            });
            
            // Mark the highlight as saved to notes
            if (highlightPopupData?.highlight) {
              setElements(prev => prev.map(el => 
                el.type === 'highlight' && el.id === (highlightPopupData.highlight as HighlightArea).id
                  ? { ...el, savedToNotes: true }
                  : el
              ));
            }
            
            // Mark as changed for autosave
            setHasFloorPlanChanges(true);
            autosave.markAsChanged();
            
            // Close the popup
            setShowHighlightPopup(false);
            setHighlightPopupData(null);
            
            // Open the SurveyorSiteNotes modal with the populated data
            setShowSiteNotes(true);
          }}
          position={highlightPopupData.position}
        />
      )}
      
      <div>
        <AddressInputModal
          isOpen={showAddressModal}
          onAddressSubmit={handleAddressSubmit}
          onSkip={handleSkipAddress}
          onExit={() => onNavigate({ page: 'start' })}
          title="Enter Address"
          description="Please enter the address for this floor plan (optional)."
        />
        <OrientationModal
          isOpen={showOrientationModal}
          onOrientationSubmit={handleOrientationSubmit}
          onSkip={handleSkipOrientation}
          title="Set Orientation"
          description="Set the orientation of your property's front door."
        />
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleModalExport}
          currentUser={currentUser}
          isEstate={true}
        />
        <ExportLoadingModal
          isOpen={showExportLoadingModal}
          steps={exportSteps}
          currentStep={currentExportStep}
        />
      </div>
      
      {/* Room Sync Indicator */}
      <RoomSyncIndicator
        show={showRoomSyncIndicator}
        roomName={syncedRoomName}
        onClose={() => setShowRoomSyncIndicator(false)}
        isFullscreen={activeFullscreenState}
      />
      
      {/* AR Scan Limit Modal */}
      <ARScanLimitModal
        isOpen={showARScanLimitModal}
        onClose={() => setShowARScanLimitModal(false)}
        onStartScan={handleARScanConfirm}
      />



      {/* Selection Indicator */}
      {currentTool === 'select' && (selectedRoom || selectedElement) && (
        <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
          <div className="w-2 h-2 bg-white rounded-full"></div>
          <span className="text-sm font-medium">
            {selectedElement ? (
              `${selectedElement.type.charAt(0).toUpperCase() + selectedElement.type.slice(1)} selected - Click to view info`
            ) : selectedRoom ?(
              `Room "${(selectedRoom.stableId && roomCustomLabels[selectedRoom.stableId]) || selectedRoom.name || 'Unnamed'}" selected`
            ) : ''}
          </span>
        </div>
      )}

      {/* Label Room Tool Indicator */}
      {currentTool === 'labelRoom' && (
        <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 bg-amber-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          <span className="text-sm font-medium">
            Click on any room to add or edit its label. Unlabeled rooms are highlighted in yellow.
          </span>
        </div>
      )}
      

      {/* Garden Edit Dialog - Label and Area */}
      {isGardenEditPromptOpen && gardenToEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsGardenEditPromptOpen(false)}>
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Edit Garden</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Label
              </label>
              <input
                type="text"
                value={gardenLabelInput}
                onChange={(e) => setGardenLabelInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="GARDEN"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Area Display
              </label>
              <input
                type="text"
                value={gardenAreaInput}
                onChange={(e) => setGardenAreaInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="0.00 m²"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const updatedGarden = { 
                      ...gardenToEdit, 
                      customLabel: gardenLabelInput.trim() || undefined,
                      customArea: gardenAreaInput.trim() || undefined
                    };
                    setElements(elements.map(el => 
                      (el.type === 'utility' && (el as any).id === (gardenToEdit as any).id) 
                        ? updatedGarden 
                        : el
                    ));
                    setIsGardenEditPromptOpen(false);
                    setGardenToEdit(null);
                  } else if (e.key === 'Escape') {
                    setIsGardenEditPromptOpen(false);
                    setGardenToEdit(null);
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank to show calculated area</p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsGardenEditPromptOpen(false);
                  setGardenToEdit(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const updatedGarden = { 
                    ...gardenToEdit, 
                    customLabel: gardenLabelInput.trim() || undefined,
                    customArea: gardenAreaInput.trim() || undefined
                  };
                  setElements(elements.map(el => 
                    (el.type === 'utility' && (el as any).id === (gardenToEdit as any).id) 
                      ? updatedGarden 
                      : el
                  ));
                  setIsGardenEditPromptOpen(false);
                  setGardenToEdit(null);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Repair Dialog */}
      <FileRepairDialog
        isOpen={showFileRepairDialog}
        onClose={() => setShowFileRepairDialog(false)}
        walls={currentWalls.map(({ tempId, ...wall }) => wall)}
        elements={elements}
        roomCustomLabels={roomCustomLabels}
        floorPlans={floorPlans}
        onApplyRepair={handleApplyFileRepair}
        onApplyOwnershipRepair={(repairedFloorPlans) => {
          console.log(' APPLYING WALL OWNERSHIP REPAIR:', repairedFloorPlans);
          setFloorPlans(repairedFloorPlans);
          // Room recalculation will be triggered automatically by the floor plan state change
        }}
      />

          </div>
        </div>
      </div>
    </div>
  );
}; 

export default RetrofitFloorPlan;
